// Articles complets rédigés pour le blog Bozzam - Données 2026

export const fullArticles = {
  'guide-complet-creer-entreprise-2026': {
    id: '1',
    title: 'Guide complet pour créer son entreprise en 2026',
    slug: 'guide-complet-creer-entreprise-2026',
    excerpt: 'Tout ce qu\'il faut savoir pour créer son entreprise en 2026 : statuts, démarches, aides, et pièges à éviter.',
    category: 'Guides complets',
    tags: ['Création', 'Entrepreneuriat', '2026', 'Démarches', 'Statuts'],
    readTime: 12,
    publishedAt: '2026-01-15',
    featured: true,
    author: 'Équipe Bozzam',
    fullContent: `Créer son entreprise en 2026 n'a jamais été aussi accessible. Entre les nouvelles réformes fiscales, l'évolution des statuts juridiques et les aides disponibles, voici votre guide complet pour réussir.

## Pourquoi créer son entreprise en 2026 ?

L'entrepreneuriat français bat des records. En 2025, plus de 1,3 million d'entreprises ont été créées. Cette dynamique s'explique par :

**Les opportunités du marché :**
- L'intelligence artificielle transforme tous les secteurs
- Le télétravail est devenu la norme
- L'économie verte explose
- Les services aux seniors se développent

**Un environnement favorable :**
- Démarches 100% en ligne
- ACRE renforcé jusqu'en 2027
- Accompagnement digitalisé
- Financement facilité

## Choisir le bon statut juridique

### Micro-entreprise

**Pour qui ?**
Idéal pour tester une activité ou générer un revenu complémentaire.

**Chiffres clés 2026 :**
- Plafond CA services : 77 700€
- Plafond CA vente : 188 700€
- Charges sociales : 21,1% (services) / 12,3% (vente)
- Versement libératoire IR : 2,2% (si éligible)

**Avantages :**
- Création gratuite et immédiate
- Comptabilité ultra-simplifiée
- Pas de TVA jusqu'à 37 500€

**Inconvénients :**
- Pas de déduction des charges réelles
- Protection sociale limitée
- Plafonds de CA contraignants

### SASU à l'IS

**Pour qui ?**
Freelances et consultants générant plus de 50 000€ de CA annuel.

**Chiffres clés 2026 :**
- PASS (Plafond Sécurité Sociale) : 46 368€
- IS taux réduit : 15% jusqu'à 42 500€ de bénéfice
- IS taux normal : 25% au-delà
- Flat tax dividendes : 30%

**Avantages :**
- Protection du patrimoine personnel
- Régime général de la Sécurité sociale
- Optimisation salaire/dividendes possible
- Crédibilité renforcée

**Inconvénients :**
- Charges sociales élevées (~45% du brut)
- Comptabilité obligatoire
- Frais de structure (expert-comptable)

### EURL à l'IS

**Pour qui ?**
Entrepreneurs souhaitant des charges sociales modérées.

**Chiffres clés 2026 :**
- Charges sociales TNS : ~30-35%
- Option IR possible 5 premières années
- Mêmes taux IS que la SASU

**Avantages :**
- Charges sociales inférieures à la SASU
- Flexibilité fiscale (IS ou IR)
- Protection du patrimoine

**Inconvénients :**
- Protection sociale TNS (moins avantageuse)
- Formalisme administratif lourd

## Les démarches de création

### Étape 1 : Préparation (1-2 semaines)

**À faire :**
- Valider votre idée avec des prospects
- Réaliser une étude de marché
- Établir un prévisionnel financier
- Choisir votre statut juridique

**Outils recommandés :**
- Bozzam : simulation de statut optimale
- Business Model Canvas
- Lean Startup méthodologie

### Étape 2 : Création (1-2 semaines)

**Micro-entreprise :**
1. Inscription sur autoentrepreneur.urssaf.fr
2. Réception du SIRET sous 1-2 semaines
3. C'est tout !

**SASU / EURL :**
1. Rédiger les statuts (modèles disponibles)
2. Déposer le capital social (1€ minimum)
3. Publier l'annonce légale (121€)
4. Déposer le dossier sur guichet-entreprises.fr
5. Recevoir le K-bis (5-10 jours)

### Étape 3 : Lancement (2-4 semaines)

**Obligatoire :**
- Ouvrir un compte bancaire professionnel
- Souscrire une assurance RC Professionnelle
- Mettre en place la comptabilité

**Recommandé :**
- Créer son site web / LinkedIn pro
- Définir ses tarifs et offres
- Préparer ses modèles de devis/factures

## Les aides disponibles en 2026

### ACRE (Aide aux Créateurs et Repreneurs d'Entreprise)

**Qui peut en bénéficier ?**
- Demandeurs d'emploi indemnisés
- Bénéficiaires RSA, ASS
- Jeunes de 18 à 25 ans
- Créateurs en ZRR ou QPV

**Avantages :**
- Exonération de 50% des charges sociales en année 1
- Économie moyenne : 3 000 à 5 000€

### ARE maintenue

**Conditions :**
- Rester inscrit à France Travail
- Déclarer vos revenus mensuellement
- Cumul ARE + revenus activité possible

**Calcul du cumul :**
ARE versée = ARE totale - (70% des revenus activité)

### ARCE (Aide à la Reprise ou Création d'Entreprise)

**Principe :**
Versement de 60% de vos droits ARE restants en 2 fois.

**Quand la choisir ?**
- Besoin de capital de départ
- Projet nécessitant des investissements
- Confiance dans la viabilité du projet

## Les erreurs à éviter

### Erreur 1 : Mal choisir son statut

**Coût de l'erreur :** 3 000 à 8 000€/an

Utilisez les simulateurs Bozzam pour comparer les statuts selon votre situation réelle.

### Erreur 2 : Sous-estimer les charges

**Les charges à prévoir :**
- Charges sociales : 22 à 45% selon statut
- Impôts : IR ou IS selon situation
- Frais fixes : 1 500 à 4 000€/an en société
- Protection sociale complémentaire : 150-300€/mois

### Erreur 3 : Négliger la trésorerie

**Règle d'or :**
Prévoyez 6 mois de charges courantes en trésorerie de départ.

### Erreur 4 : Oublier la protection sociale

**Indispensable :**
- Mutuelle santé adaptée
- Prévoyance (maintien de revenus)
- RC Professionnelle

## Calendrier type de création

**Semaine 1-2 :** Validation du projet et choix du statut

**Semaine 3-4 :** Création administrative

**Semaine 5-6 :** Mise en place opérationnelle

**Mois 2-3 :** Lancement commercial et premiers clients

## Conclusion

Créer son entreprise en 2026 est une opportunité exceptionnelle. Les outils et aides n'ont jamais été aussi accessibles.

**Nos conseils clés :**
1. Prenez le temps de bien choisir votre statut
2. Simulez différents scénarios avec Bozzam
3. Anticipez vos charges et votre trésorerie
4. Entourez-vous dès le départ (expert-comptable, réseau)

Votre réussite dépend autant de votre préparation que de vos compétences. Lancez-vous avec méthode !

---

**Sources officielles 2026 :**
- URSSAF - Barèmes cotisations sociales 2026 : urssaf.fr
- DGFiP - Taux IS et IR 2026 : impots.gouv.fr
- INSEE - Statistiques création d'entreprises : insee.fr/fr/statistiques
- France Travail - Dispositifs ARE/ARCE : francetravail.org
- Service-public.fr - Créer son entreprise : entreprendre.service-public.fr
- Legifrance - Code de commerce : legifrance.gouv.fr`
  },

  'erreurs-freelances-debutants': {
    id: '2',
    title: 'Les 10 erreurs fatales des freelances débutants',
    slug: 'erreurs-freelances-debutants',
    excerpt: 'Évitez ces erreurs courantes qui peuvent coûter cher aux freelances qui se lancent. Guide pratique basé sur l\'analyse de parcours réels.',
    category: 'Erreurs à éviter',
    tags: ['Freelance', 'Erreurs', 'Débutant', 'TJM', 'Gestion'],
    readTime: 10,
    publishedAt: '2026-01-10',
    featured: true,
    author: 'Équipe Bozzam',
    fullContent: `Se lancer en freelance est excitant, mais les pièges sont nombreux. Voici les 10 erreurs les plus coûteuses et comment les éviter.

## Erreur 1 : Fixer un TJM trop bas

**Le problème :**
Par peur de ne pas trouver de clients, 73% des freelances débutants sous-évaluent leurs tarifs.

**L'impact financier :**
Un TJM à 250€ au lieu de 400€ représente une perte de 33 000€ sur une année (220 jours facturés).

**Comment calculer son vrai TJM :**

1. Calculez votre objectif de revenu net annuel
2. Ajoutez les charges sociales (22 à 45% selon statut)
3. Ajoutez les frais professionnels (5 à 15%)
4. Divisez par le nombre de jours facturables (150-200)

**Exemple concret :**
- Objectif net : 40 000€
- Charges sociales (30%) : 12 000€
- Frais pro (10%) : 4 000€
- Total à facturer : 56 000€
- Jours facturables : 180
- **TJM minimum : 311€**

**TJM recommandés 2026 par secteur :**
- Développement web : 400-700€
- Design UX/UI : 350-550€
- Consulting stratégie : 600-1 200€
- Rédaction/Content : 300-500€
- Marketing digital : 350-600€

## Erreur 2 : Négliger la prospection

**Le problème :**
57% des freelances passent moins de 20% de leur temps à prospecter. Résultat : l'effet "montagnes russes" avec des périodes de surcharge et des creux sans revenus.

**La règle des 30% :**
Consacrez 30% de votre temps à la prospection et au développement commercial, même quand vous êtes débordé.

**Techniques de prospection efficaces :**

**LinkedIn (le plus rentable) :**
- Profil optimisé avec mots-clés
- 3-5 publications par semaine
- Engagement quotidien (commentaires, partages)
- Messages personnalisés (pas de spam)

**Réseau et recommandations :**
- Demandez des recommandations à chaque fin de mission
- Participez à des événements sectoriels
- Rejoignez des communautés de freelances

**Contenu et expertise :**
- Articles de blog sur votre expertise
- Études de cas détaillées
- Webinaires gratuits

## Erreur 3 : Mauvaise gestion de trésorerie

**Le problème :**
68% des freelances n'ont pas de visibilité au-delà de 2 mois. Un impayé ou un retard de paiement peut créer une crise majeure.

**La méthode des 4 comptes :**

**Compte courant pro (40%) :** Dépenses courantes

**Compte charges (35%) :** 
- Charges sociales (provision trimestrielle)
- Impôts (provision mensuelle)

**Compte épargne (15%) :**
- Trésorerie de sécurité (3-6 mois)
- Investissements futurs

**Compte congés (10%) :**
- Provision pour vacances
- Arrêts maladie éventuels

**Bonnes pratiques :**
- Facturez immédiatement à la fin de chaque prestation
- Exigez un acompte de 30-50% pour les grosses missions
- Relancez dès J+7 après échéance
- Utilisez des outils de facturation avec relances automatiques

## Erreur 4 : Choisir le mauvais statut

**Le problème :**
45% des freelances restent en micro-entreprise alors qu'un autre statut serait plus avantageux. Perte moyenne : 3 500€/an.

**Quand quitter la micro-entreprise ?**

**Signaux d'alerte :**
- CA > 50 000€ : Analysez SASU/EURL
- Frais professionnels > 15% du CA
- Besoin de crédibilité (gros clients)
- Projets d'investissement importants

**Comparatif simplifié (CA 70 000€) :**

| Statut | Charges | Impôts | Net final |
|--------|---------|--------|-----------|
| Micro | 15 400€ | 1 540€ | 53 060€ |
| SASU optimisée | 18 000€ | 2 500€ | 49 500€ |
| EURL IS | 14 000€ | 3 200€ | 52 800€ |

**Important :** Ces chiffres sont indicatifs. Utilisez les simulateurs Bozzam pour votre situation précise.

## Erreur 5 : Sous-estimer l'administratif

**Le problème :**
L'administratif représente 15-25% du temps d'un freelance. Le négliger crée du stress, des oublis et des pénalités.

**Tâches à ne pas oublier :**

**Quotidien/Hebdomadaire :**
- Suivi des heures et prestations
- Mise à jour du tableau de bord financier

**Mensuel :**
- Facturation et relances
- Déclaration URSSAF (si micro)
- Rapprochement bancaire

**Trimestriel :**
- Déclaration de TVA (si assujetti)
- Point comptable

**Annuel :**
- Clôture comptable
- Déclaration de revenus
- Bilan et liasse fiscale (sociétés)

**Outils recommandés :**
- Facturation : Freebe, Shine, Qonto
- Comptabilité : Indy, Pennylane
- Gestion du temps : Toggl, Clockify

## Erreur 6 : Négliger la formation continue

**Le problème :**
Dans un monde qui évolue vite, ne pas se former c'est reculer. Les freelances qui se forment régulièrement augmentent leur TJM de 15% par an en moyenne.

**Budget formation recommandé :**
5-10% du CA annuel, soit 3 000 à 7 000€ pour un CA de 70 000€.

**Sources de financement :**
- CPF (Compte Personnel de Formation)
- FAF (Fonds d'Assurance Formation)
- Crédit d'impôt formation (dirigeants)
- Auto-financement (investissement rentable)

**Types de formations à privilégier :**
- Compétences techniques (métier)
- Compétences business (vente, négociation)
- Soft skills (communication, leadership)
- Outils et méthodologies

## Erreur 7 : Accepter tous les clients

**Le problème :**
Par peur de manquer, les freelances débutants acceptent tous les projets, même les mauvais. Résultat : épuisement, sous-rémunération, insatisfaction.

**Les signaux d'alerte :**
- Client qui négocie trop agressivement
- Brief flou ou changeant
- Délais irréalistes
- Manque de respect dès le premier contact

**La règle des 3 oui :**
Acceptez une mission uniquement si :
1. Le projet vous intéresse
2. Le budget est correct
3. Le client vous inspire confiance

**Apprenez à dire non :**
"Merci pour votre proposition, mais je ne suis pas disponible / ce projet ne correspond pas à mon positionnement actuel."

## Erreur 8 : Travailler sans contrat

**Le problème :**
34% des freelances travaillent sans contrat écrit. En cas de litige, ils n'ont aucune protection.

**Ce que doit contenir un contrat/devis :**
- Description précise de la prestation
- Livrables attendus
- Planning et jalons
- Tarifs et conditions de paiement
- Nombre de révisions incluses
- Conditions d'annulation
- Propriété intellectuelle

**Conseil pratique :**
Utilisez des modèles de contrats adaptés à votre activité. Des plateformes comme Captain Contrat ou LegalStart proposent des modèles personnalisables.

## Erreur 9 : S'isoler professionnellement

**Le problème :**
47% des freelances souffrent de solitude professionnelle. L'isolement impacte la motivation, la créativité et les opportunités.

**Solutions anti-isolement :**

**Coworking :**
- Travaillez 1-2 jours par semaine en espace partagé
- Rencontrez d'autres entrepreneurs
- Maintenez un rythme de travail

**Communautés en ligne :**
- Groupes Slack/Discord de freelances
- Communautés LinkedIn sectorielles
- Forums et groupes Facebook

**Réseau physique :**
- Événements et meetups locaux
- Conférences sectorielles
- Associations professionnelles

## Erreur 10 : Négliger sa protection sociale

**Le problème :**
En tant qu'indépendant, votre protection sociale de base est limitée. Sans couverture complémentaire, un arrêt maladie ou un accident peut être catastrophique.

**Couvertures indispensables :**

**Mutuelle santé :**
Budget : 100-200€/mois
Couvre les frais médicaux non remboursés.

**Prévoyance :**
Budget : 50-150€/mois
Maintien de revenus en cas d'arrêt, capital décès, rente invalidité.

**RC Professionnelle :**
Budget : 200-600€/an
Couvre les dommages causés à vos clients (erreurs, retards, etc.).

**Total recommandé :** 3-5% du CA

## Plan d'action en 30 jours

**Semaine 1 : Diagnostic**
- Calculez votre TJM réel
- Analysez votre temps de prospection actuel
- Faites le point sur votre trésorerie

**Semaine 2 : Optimisation financière**
- Simulez différents statuts avec Bozzam
- Mettez en place les 4 comptes
- Automatisez votre facturation

**Semaine 3 : Développement commercial**
- Optimisez votre profil LinkedIn
- Planifiez 3 publications par semaine
- Identifiez 10 prospects à contacter

**Semaine 4 : Sécurisation**
- Vérifiez vos couvertures (mutuelle, prévoyance, RC Pro)
- Créez vos modèles de contrats
- Rejoignez une communauté de freelances

## Conclusion

Ces 10 erreurs coûtent en moyenne 15 000€ par an aux freelances qui les commettent. En les évitant, vous rejoindrez les 36% qui réussissent durablement.

**Les 3 règles d'or du freelance :**
1. Valorisez votre expertise au juste prix
2. Anticipez et gérez comme une entreprise
3. Ne restez jamais seul(e)

Votre succès dépend autant de vos compétences techniques que de votre vision business. Prenez le temps de structurer votre activité.

---

**Sources :**
- Étude Malt/BCG - Le freelancing en France 2025 : malt.fr/resources
- Baromètre des freelances Comet 2025 : comet.co/fr/blog
- URSSAF - Statistiques auto-entrepreneurs : autoentrepreneur.urssaf.fr
- Service-public.fr - Statuts juridiques : entreprendre.service-public.fr`
  },

  'sasu-avantages-inconvenients': {
    id: '3',
    title: 'SASU : avantages et inconvénients - Guide complet 2026',
    slug: 'sasu-avantages-inconvenients',
    excerpt: 'Analyse complète du statut SASU : fiscalité, cotisations, avantages et inconvénients. Tous les chiffres 2026 pour bien choisir.',
    category: 'Fiscalité',
    tags: ['SASU', 'Statut', 'Fiscalité', 'Société', '2026'],
    readTime: 7,
    publishedAt: '2026-01-08',
    featured: false,
    author: 'Équipe Bozzam',
    fullContent: `La SASU (Société par Actions Simplifiée Unipersonnelle) est devenue le statut préféré des freelances et consultants. Mais est-elle adaptée à votre situation ? Décryptage complet avec les chiffres 2026.

## Qu'est-ce qu'une SASU ?

La SASU est une société par actions avec un seul associé. Elle combine la protection d'une société et la flexibilité d'une entreprise individuelle.

**Chiffres clés 2026 :**
- 290 000 créations prévues
- CA moyen : 152 000€
- 80% optent pour l'IS

## Les 6 avantages majeurs de la SASU

### 1. Protection du patrimoine personnel

Votre responsabilité est limitée au montant de vos apports. Vos biens personnels sont protégés.

**Exemple :**
- Capital social : 1 000€
- Dettes de la société : 50 000€
- Perte maximale : 1 000€

**Exceptions :**
- Cautions personnelles
- Faute de gestion grave
- Confusion de patrimoine

### 2. Régime social du dirigeant

Le président de SASU est "assimilé salarié" : vous bénéficiez du régime général de la Sécurité sociale.

**Avantages :**
- Couverture maladie complète
- Indemnités journalières dès J+4
- Retraite du régime général
- Droits au chômage possibles (sous conditions)

**Comparaison avec le TNS (EURL) :**

| Protection | SASU | EURL (TNS) |
|------------|------|------------|
| Maladie IJ | Dès J+4 | Dès J+8 |
| Retraite | Régime général | SSI (moins favorable) |
| Chômage | Possible* | Non |

### 3. Optimisation salaire/dividendes

En SASU, vous pouvez combiner :
- Un salaire (charges sociales mais déductible)
- Des dividendes (flat tax 30%, pas de charges sociales)

**Exemple d'optimisation (CA 100 000€, bénéfice 60 000€) :**

**Tout en salaire :**
- Salaire brut : 41 500€
- Charges : 26 100€
- Net : 32 400€

**Optimisé (salaire 46 368€ PASS + dividendes) :**
- Salaire net : 29 200€
- Dividendes nets : 10 850€
- **Total net : 40 050€ (+7 650€)**

### 4. Crédibilité renforcée

Une SASU inspire davantage confiance qu'un auto-entrepreneur pour :
- Les grands comptes et entreprises
- Les appels d'offres
- Les partenariats

### 5. Flexibilité juridique

- Organisation libre de la société
- Évolution facile vers SAS multi-associés
- Transmission par cession d'actions

### 6. Pas de plafond de CA

Contrairement à la micro-entreprise (77 700€), la SASU n'a aucune limite de chiffre d'affaires.

## Les 5 inconvénients de la SASU

### 1. Charges sociales élevées

Sur un salaire, les charges totales représentent environ 64% du net.

**Décomposition :**
- Charges patronales : ~42% du brut
- Charges salariales : ~22% du brut

**Comparaison (pour 30 000€ net) :**

| Statut | Charges | CA nécessaire |
|--------|---------|---------------|
| Micro | 6 600€ | 36 600€ |
| SASU | 19 200€ | 49 200€ |

### 2. Complexité administrative

**Obligations :**
- Comptabilité complète obligatoire
- Bilan et compte de résultat annuels
- Dépôt des comptes au greffe
- PV de décisions

**Coûts annuels :**
- Expert-comptable : 1 500-3 500€
- Dépôt de comptes : 42€
- Modifications éventuelles : 150-500€

### 3. Seuil de rentabilité élevé

Avec les frais fixes, la SASU devient intéressante à partir d'un certain CA.

**Seuils indicatifs :**
- CA < 45 000€ : Micro-entreprise préférable
- CA 45-70 000€ : À analyser selon les charges
- CA > 70 000€ : SASU souvent optimale

### 4. Pas de chômage automatique

Le président de SASU n'a droit au chômage que s'il détient moins de 50% du capital et est révoqué. En pratique, c'est rare.

### 5. Délai de création plus long

Comptez 2-3 semaines contre 48h en micro-entreprise.

## SASU vs autres statuts

### SASU vs Micro-entreprise

| Critère | SASU | Micro |
|---------|------|-------|
| Création | 208€+ | Gratuit |
| Charges | 45% | 22% |
| Protection patrimoine | ✅ | ❌ |
| Déduction charges | ✅ Réelles | ❌ Forfait |
| Plafond CA | Aucun | 77 700€ |
| Comptabilité | Obligatoire | Simplifiée |

**Verdict :** Passez en SASU si CA > 50 000€ et frais > 15% du CA.

### SASU vs EURL

| Critère | SASU | EURL |
|---------|------|------|
| Régime social | Assimilé salarié | TNS |
| Charges sociales | 45% | 30-35% |
| Protection sociale | ++ | + |
| Flexibilité | ++ | + |

**Verdict :** SASU si priorité protection sociale, EURL si priorité économie de charges.

## Création d'une SASU : étapes et coûts

### Les étapes

1. **Rédiger les statuts** (modèle ou avocat)
2. **Déposer le capital** (1€ minimum, 1 000€ conseillé)
3. **Publier l'annonce légale** (121€)
4. **Déposer le dossier** sur guichet-entreprises.fr (37,45€)
5. **Recevoir le K-bis** (5-10 jours)

### Les coûts

**Minimum incompressible :** 158,45€ (publication + greffe)

**Budget réaliste avec accompagnement :** 500-1 500€

## Optimisation fiscale en SASU

### Le salaire optimal

En 2026, le PASS (Plafond Annuel de la Sécurité Sociale) est de 46 368€.

**Stratégie recommandée :**
- Salaire brut = 46 368€ (PASS)
- Au-delà, privilégier les dividendes

### IS vs IR

**Option IR (5 ans max) :**
- Avantages : Déficits imputables, pas d'IS
- Inconvénients : Charges sur tout le bénéfice

**IS (défaut) :**
- 15% jusqu'à 42 500€
- 25% au-delà
- Optimisation salaire/dividendes possible

### Frais déductibles

Pensez à déduire :
- Bureau à domicile (quote-part)
- Matériel informatique
- Formations professionnelles
- Déplacements professionnels
- Frais de représentation

## Pour qui la SASU est-elle idéale ?

**SASU recommandée si :**
- CA prévisible > 50 000€
- Besoin de crédibilité (grands comptes)
- Frais professionnels importants (> 15% CA)
- Protection sociale prioritaire
- Évolution en équipe envisagée

**SASU déconseillée si :**
- CA < 40 000€
- Activité occasionnelle ou test
- Budget serré pour les frais fixes
- Recherche de simplicité absolue

## Conclusion

La SASU est un excellent statut pour les freelances qui :
1. Génèrent un CA significatif (> 50k€)
2. Recherchent protection et crédibilité
3. Acceptent la complexité pour l'optimisation

**Notre conseil :** Ne choisissez pas la SASU par défaut. Simulez votre situation précise avec les outils Bozzam avant de décider.

---

**Sources officielles 2026 :**
- Code de commerce - Articles L227-1 et suivants : legifrance.gouv.fr
- URSSAF - PASS 2026 = 46 368€ : urssaf.fr
- DGFiP - Taux IS 2026 : impots.gouv.fr
- Bofip - Flat tax dividendes : bofip.impots.gouv.fr
- Service-public.fr - Créer une SASU : entreprendre.service-public.fr`
  },

  'chomage-creation-entreprise': {
    id: '4',
    title: 'Chômage et création d\'entreprise : comment ça marche ?',
    slug: 'chomage-creation-entreprise',
    excerpt: 'Tout comprendre sur les droits au chômage lors de la création d\'entreprise : ARE, ARCE, cumul, conditions. Guide complet 2026.',
    category: 'Chômage & aides',
    tags: ['Chômage', 'ARE', 'ARCE', 'Création', 'Aides'],
    readTime: 9,
    publishedAt: '2026-01-05',
    featured: false,
    author: 'Équipe Bozzam',
    fullContent: `Vous êtes au chômage et souhaitez créer votre entreprise ? Bonne nouvelle : vous pouvez conserver vos droits ARE tout en vous lançant. Voici comment optimiser cette transition.

## Les 2 options principales

### Option 1 : Maintien de l'ARE

Vous continuez à percevoir vos allocations chômage tout en développant votre activité.

**Principe :**
- Vous restez inscrit à France Travail
- Vous déclarez vos revenus d'activité chaque mois
- Vos allocations sont recalculées

**Calcul du cumul :**
ARE versée = ARE journalière × jours du mois - 70% des revenus d'activité

**Exemple :**
- ARE mensuelle complète : 1 500€
- Revenus d'activité : 800€
- ARE versée : 1 500 - (800 × 70%) = 1 500 - 560 = 940€
- **Revenu total : 940 + 800 = 1 740€**

**Avantages :**
- Sécurité financière pendant le lancement
- Droits ARE reportés (non consommés = conservés)
- Couverture sociale maintenue
- Pas d'engagement définitif

**Inconvénients :**
- Revenus plafonnés (cumul limité)
- Obligations déclaratives mensuelles
- Contrôles France Travail possibles

### Option 2 : ARCE (Aide à la Reprise ou Création d'Entreprise)

Vous recevez 60% de vos droits ARE restants sous forme de capital.

**Versement en 2 fois :**
- 50% à la création de l'entreprise
- 50% six mois après (si activité toujours en cours)

**Exemple :**
- Droits ARE restants : 20 000€
- ARCE totale : 20 000 × 60% = 12 000€
- 1er versement : 6 000€
- 2e versement (6 mois après) : 6 000€

**Avantages :**
- Capital de départ immédiat
- Financement des investissements
- Sortie du système ARE

**Inconvénients :**
- Perte de 40% des droits
- Pas de retour possible à l'ARE
- Risque si l'activité échoue rapidement

## Comment choisir entre ARE et ARCE ?

### Choisissez le maintien ARE si :
- Vous testez une activité
- Vos revenus seront progressifs
- Vous avez peu d'investissements à faire
- Vous préférez la sécurité

### Choisissez l'ARCE si :
- Vous avez besoin de capital
- Votre projet est solide et validé
- Vous avez des investissements à réaliser
- Vos droits ARE sont conséquents (> 15 000€)

## L'ACRE : l'aide complémentaire

### Qu'est-ce que l'ACRE ?

L'ACRE (Aide aux Créateurs et Repreneurs d'Entreprise) est une exonération de charges sociales la première année.

**Taux d'exonération 2026 :** 50% des charges sociales

### Qui peut en bénéficier ?

**Bénéficiaires automatiques :**
- Demandeurs d'emploi indemnisés
- Demandeurs d'emploi non indemnisés inscrits depuis 6+ mois
- Bénéficiaires RSA, ASS, ATA
- Jeunes de 18 à 25 ans
- Créateurs en ZRR ou QPV

### Économie réalisée

**En micro-entreprise (CA 50 000€ services) :**
- Charges normales : 11 050€
- Charges avec ACRE : 5 525€
- **Économie : 5 525€**

**En SASU (salaire 36 000€ brut) :**
- Charges normales : 15 120€
- Charges avec ACRE : 7 560€
- **Économie : 7 560€**

## Le cumul ARE + activité en pratique

### Étape 1 : Créer votre entreprise

Inscrivez-vous auprès du guichet des entreprises tout en restant inscrit à France Travail.

### Étape 2 : Déclarer votre situation

Informez votre conseiller France Travail de votre création d'entreprise dans les 72h.

### Étape 3 : Actualiser chaque mois

Lors de votre actualisation mensuelle :
1. Déclarez que vous êtes toujours en recherche d'emploi
2. Indiquez vos heures travaillées
3. Renseignez vos revenus d'activité

### Étape 4 : Transmettre les justificatifs

France Travail peut vous demander :
- Attestation de chiffre d'affaires
- K-bis ou attestation INSEE
- Relevés bancaires professionnels

## Cas pratiques

### Cas 1 : Marie, consultante marketing

**Situation :**
- ARE mensuelle : 1 800€
- Droits restants : 18 mois
- CA prévu année 1 : 35 000€

**Choix : Maintien ARE**

**Résultat année 1 :**
- CA : 35 000€
- Revenus nets (après charges micro) : 27 300€
- ARE perçue : ~8 000€
- **Revenu total : 35 300€**
- Droits ARE restants : 10 mois (reportés)

### Cas 2 : Thomas, artisan

**Situation :**
- ARE mensuelle : 1 200€
- Droits restants : 24 mois = 28 800€
- Besoin d'investissement : 15 000€

**Choix : ARCE**

**Résultat :**
- Capital ARCE : 28 800 × 60% = 17 280€
- Investissements couverts : ✅
- Lancement rapide possible

## Les erreurs à éviter

### Erreur 1 : Ne pas informer France Travail

**Risque :** Remboursement des allocations indûment perçues

**Solution :** Déclarez votre création dans les 72h

### Erreur 2 : Mal calculer le cumul

**Risque :** Trop-perçu à rembourser

**Solution :** Utilisez les simulateurs officiels et conservez vos justificatifs

### Erreur 3 : Choisir l'ARCE trop vite

**Risque :** Perdre 40% des droits pour une activité qui ne démarre pas

**Solution :** Commencez par le maintien ARE, basculez en ARCE plus tard si besoin

### Erreur 4 : Oublier l'ACRE

**Risque :** Payer le plein tarif de charges sociales

**Solution :** Vérifiez votre éligibilité et faites la demande à la création

## Calendrier optimal

### Mois 1-2 : Préparation
- Validez votre projet
- Étudiez votre éligibilité aux aides
- Simulez ARE vs ARCE

### Mois 3 : Création
- Créez votre entreprise
- Informez France Travail
- Demandez l'ACRE

### Mois 4-12 : Développement
- Actualisez mensuellement
- Cumulez ARE + revenus
- Conservez tous les justificatifs

### Mois 12+ : Décision
- Si CA suffisant : sortie de France Travail
- Si besoin de capital : ARCE sur reliquat
- Si difficultés : droits ARE préservés

## Conclusion

Le chômage peut être un tremplin vers l'entrepreneuriat si vous utilisez intelligemment les dispositifs existants.

**Nos conseils :**
1. Commencez par le maintien ARE (plus flexible)
2. Demandez l'ACRE dès la création
3. Actualisez rigoureusement chaque mois
4. Conservez tous vos justificatifs
5. Basculez en ARCE uniquement si vous avez besoin de capital ET que votre projet est solide

Votre création d'entreprise peut ainsi être sécurisée financièrement pendant les premiers mois critiques.

---

**Sources officielles :**
- Code du travail - Articles L5141-1 à L5141-6 : legifrance.gouv.fr
- France Travail - Règlement assurance chômage 2026 : francetravail.org
- URSSAF - Conditions ACRE 2026 : urssaf.fr
- Unédic - Règles d'indemnisation : unedic.org
- Service-public.fr - ARE et création : service-public.fr/particuliers/vosdroits/F11677`
  },

  'tjm-fixer-debuter-freelance': {
    id: '5',
    title: 'Quel TJM fixer quand on débute en freelance ?',
    slug: 'tjm-fixer-debuter-freelance',
    excerpt: 'Conseils pratiques pour fixer son taux journalier moyen en tant que freelance débutant. Méthodes de calcul et références sectorielles 2026.',
    category: 'Freelance pratique',
    tags: ['TJM', 'Freelance', 'Tarifs', 'Débutant'],
    readTime: 5,
    publishedAt: '2026-01-03',
    featured: false,
    author: 'Équipe Bozzam',
    fullContent: `Fixer son TJM (Taux Journalier Moyen) est l'une des décisions les plus importantes pour un freelance. Trop bas, vous ne serez pas rentable. Trop haut, vous risquez de ne pas trouver de missions. Voici comment trouver le bon équilibre.

## Pourquoi le TJM est crucial

Votre TJM détermine :
- Votre revenu annuel
- Votre positionnement marché
- Votre image professionnelle
- Votre capacité à investir et vous former

**L'erreur classique :**
73% des freelances débutants fixent un TJM trop bas, perdant en moyenne 15 000€ par an.

## Méthode de calcul en 5 étapes

### Étape 1 : Définir votre objectif de revenu net

Quel salaire net souhaitez-vous vous verser ?

**Références 2026 :**
- Équivalent SMIC net : 1 400€/mois → 16 800€/an
- Salaire médian cadre : 3 000€/mois → 36 000€/an
- Salaire cadre expérimenté : 4 500€/mois → 54 000€/an

### Étape 2 : Ajouter les charges sociales

Selon votre statut :
- Micro-entreprise : +22% (services)
- SASU : +45%
- EURL : +35%

**Exemple (objectif 40 000€ net) :**
- Micro : 40 000 × 1,22 = 48 800€
- SASU : 40 000 × 1,45 = 58 000€

### Étape 3 : Ajouter les frais professionnels

Comptez 10 à 20% selon votre activité :
- Matériel informatique
- Logiciels et abonnements
- Formation continue
- Déplacements
- Comptabilité
- Assurances

**Exemple (frais 15%) :**
- Micro : 48 800 × 1,15 = 56 120€
- SASU : 58 000 × 1,15 = 66 700€

### Étape 4 : Estimer les jours facturables

Sur 365 jours, retirez :
- Week-ends : 104 jours
- Congés : 25-35 jours
- Jours fériés : 10 jours
- Maladie/imprévus : 10 jours
- Prospection/admin : 40-60 jours

**Jours facturables réalistes :**
- Débutant : 120-150 jours
- Confirmé : 150-180 jours
- Expert très demandé : 180-200 jours

### Étape 5 : Calculer le TJM minimum

TJM = CA nécessaire ÷ Jours facturables

**Exemple complet (micro, objectif 40k€ net, 150 jours) :**
- CA nécessaire : 56 120€
- Jours : 150
- **TJM minimum : 374€**

## Références TJM par secteur (2026)

### Développement / Tech

| Profil | Débutant | Confirmé | Expert |
|--------|----------|----------|--------|
| Dev front | 350€ | 500€ | 700€ |
| Dev back | 400€ | 550€ | 750€ |
| DevOps | 450€ | 600€ | 850€ |
| Data/IA | 500€ | 700€ | 1000€ |

### Design / Création

| Profil | Débutant | Confirmé | Expert |
|--------|----------|----------|--------|
| UX Design | 350€ | 500€ | 700€ |
| UI Design | 300€ | 450€ | 650€ |
| DA/Branding | 350€ | 550€ | 800€ |
| Motion | 400€ | 600€ | 900€ |

### Marketing / Communication

| Profil | Débutant | Confirmé | Expert |
|--------|----------|----------|--------|
| CM/Social | 250€ | 400€ | 600€ |
| SEO/SEA | 300€ | 500€ | 750€ |
| Content | 250€ | 400€ | 600€ |
| Growth | 400€ | 600€ | 900€ |

### Conseil / Stratégie

| Profil | Débutant | Confirmé | Expert |
|--------|----------|----------|--------|
| Consultant | 400€ | 700€ | 1200€ |
| Coach | 300€ | 600€ | 1000€ |
| Formateur | 600€ | 1000€ | 1500€ |

## Les 5 erreurs à éviter

### Erreur 1 : Se baser sur son ancien salaire

Votre ancien salaire horaire ≠ votre TJM

Un salarié à 3 000€ net touchait en réalité 5 000€ de coût entreprise. Votre TJM doit refléter ce coût total.

### Erreur 2 : Copier les tarifs des plateformes

Les plateformes (Malt, Fiverr...) tirent les prix vers le bas. Elles ne représentent pas le marché réel.

### Erreur 3 : Négocier à la baisse systématiquement

Chaque -50€ sur votre TJM = -11 000€ sur l'année (220 jours).

Apprenez à défendre vos tarifs ou à proposer des alternatives (périmètre réduit, délai allongé).

### Erreur 4 : Avoir un seul tarif

Adaptez votre TJM selon :
- La taille du client (grand compte = +20%)
- La complexité du projet
- L'urgence de la demande
- La durée de la mission (longue durée = -10%)

### Erreur 5 : Ne jamais augmenter

Augmentez vos tarifs de 5-10% chaque année minimum :
- Inflation
- Expérience acquise
- Compétences développées

## Stratégies pour débutants

### Stratégie 1 : Le tarif plancher

Calculez votre TJM minimum (méthode ci-dessus) et ne descendez JAMAIS en dessous, même pour "vous faire connaître".

### Stratégie 2 : La montée progressive

- Mois 1-3 : TJM minimum (pour constituer portfolio)
- Mois 4-6 : +10%
- Mois 7-12 : +15%
- Année 2 : Tarif marché normal

### Stratégie 3 : Le forfait projet

Au lieu d'un TJM, proposez un prix fixe par projet. Vous maîtrisez mieux votre rentabilité.

### Stratégie 4 : La spécialisation

Un expert de niche peut facturer 50% de plus qu'un généraliste. Trouvez votre spécialité.

## Comment justifier votre tarif

Quand un client trouve votre TJM élevé :

**Ne vous justifiez pas sur le prix, mais sur la valeur :**

- "Mon expertise permet de livrer en 5 jours ce que d'autres font en 10"
- "J'ai déjà réalisé 15 projets similaires avec succès"
- "Mon accompagnement inclut X, Y, Z que d'autres facturent en plus"

**Proposez des alternatives :**
- Périmètre réduit à budget constant
- Paiement échelonné
- Mission plus longue avec remise volume

## Plan d'action

### Semaine 1 : Calcul
- Définissez votre objectif de revenu
- Calculez votre TJM minimum
- Recherchez les tarifs de votre secteur

### Semaine 2 : Positionnement
- Identifiez votre différenciation
- Préparez vos arguments de valeur
- Créez votre grille tarifaire

### Semaine 3 : Test
- Proposez votre nouveau TJM
- Observez les réactions
- Ajustez si nécessaire

## Conclusion

Votre TJM n'est pas qu'un chiffre : c'est le reflet de la valeur que vous apportez. Ne le bradez pas.

**Les 3 règles d'or :**
1. Ne descendez jamais sous votre TJM minimum
2. Augmentez de 10% chaque année
3. Vendez de la valeur, pas du temps

---

**Sources :**
- Baromètre des tarifs Malt 2025 : malt.fr/resources
- Étude Comet - Freelances tech : comet.co/fr/blog
- Enquête Freelance.com 2025 : freelance.com/fr/blog
- INSEE - Revenus des indépendants : insee.fr/fr/statistiques
- URSSAF - Seuils micro-entreprise : autoentrepreneur.urssaf.fr`
  },

  'optimiser-charges-legalement': {
    id: '6',
    title: 'Optimiser ses charges légalement en 2026',
    slug: 'optimiser-charges-legalement',
    excerpt: 'Techniques légales pour optimiser ses charges et réduire ses impôts en tant qu\'entrepreneur. Guide pratique avec exemples chiffrés.',
    category: 'Bons plans',
    tags: ['Optimisation', 'Charges', 'Fiscalité', 'Légal'],
    readTime: 6,
    publishedAt: '2026-01-01',
    featured: false,
    author: 'Équipe Bozzam',
    fullContent: `Optimiser ses charges n'est pas de la "fraude" : c'est utiliser intelligemment les dispositifs légaux existants. Voici les meilleures stratégies pour 2026.

## Principe fondamental

L'optimisation fiscale légale consiste à :
- Choisir le statut le plus adapté à votre situation
- Déduire toutes les charges auxquelles vous avez droit
- Utiliser les dispositifs d'aide existants
- Organiser votre rémunération de manière optimale

**Ce qui est légal :** Choisir la forme juridique et les options fiscales les plus favorables.

**Ce qui est illégal :** Cacher des revenus, gonfler des charges fictives, travailler au noir.

## Stratégie 1 : Choisir le bon statut

### Quand la micro-entreprise est optimale

**Profil idéal :**
- CA < 50 000€
- Peu de frais professionnels (< 10% du CA)
- Recherche de simplicité

**Économie vs SASU :**
Pour 40 000€ de CA, la micro permet d'économiser ~5 000€/an en charges.

### Quand passer en société

**Profil idéal :**
- CA > 50 000€
- Frais professionnels importants (> 15% du CA)
- Besoin de crédibilité ou de protection

**Économie possible :**
À 80 000€ de CA avec 20% de frais, la SASU optimisée peut faire économiser 3 000 à 5 000€/an vs micro.

## Stratégie 2 : Déduire les frais professionnels

En société (SASU, EURL), vous pouvez déduire vos frais réels.

### Frais 100% déductibles

**Matériel professionnel :**
- Ordinateur, téléphone, tablette
- Logiciels et abonnements (Adobe, Figma, etc.)
- Mobilier de bureau
- Fournitures diverses

**Formation et documentation :**
- Formations professionnelles
- Livres et ressources
- Conférences et événements

**Déplacements professionnels :**
- Billets de train/avion
- Hôtels en déplacement
- Repas clients (avec justificatifs)

**Communication :**
- Site web et hébergement
- Publicité en ligne
- Cartes de visite, flyers

### Frais partiellement déductibles

**Bureau à domicile :**
Vous pouvez déduire une quote-part de vos charges de logement.

**Calcul :**
- Surface du bureau : 15m²
- Surface totale : 80m²
- Quote-part : 15/80 = 18,75%

**Charges déductibles à 18,75% :**
- Loyer ou intérêts d'emprunt
- Électricité, chauffage
- Internet (ou 100% si pro)
- Assurance habitation

**Exemple annuel :**
- Loyer : 12 000€ × 18,75% = 2 250€
- Charges : 2 400€ × 18,75% = 450€
- **Total déductible : 2 700€**

**Véhicule :**
Deux options :
1. Indemnités kilométriques (selon barème fiscal)
2. Frais réels (carburant, assurance, entretien au prorata pro)

## Stratégie 3 : L'optimisation salaire/dividendes (SASU)

En SASU à l'IS, vous pouvez combiner salaire et dividendes.

### Le principe

- **Salaire :** Charges sociales (~45%) mais déductible du résultat
- **Dividendes :** Flat tax 30%, pas de charges sociales

### L'optimisation

**Salaire optimal = 46 368€ brut (PASS 2026)**

Ce niveau permet :
- De maximiser les droits retraite
- De valider 4 trimestres
- De limiter les charges sur la tranche haute

Au-delà, privilégiez les dividendes.

### Exemple chiffré (bénéfice 80 000€)

**Scénario 1 : Tout en salaire**
- Salaire brut : 55 000€
- Charges sociales : 34 650€
- Net : 42 900€

**Scénario 2 : Optimisé**
- Salaire brut : 46 368€ (PASS)
- Charges salaire : 29 210€
- Dividendes bruts : 20 000€
- Flat tax : 6 000€
- **Net total : 47 600€ (+4 700€)**

## Stratégie 4 : Utiliser l'ACRE

L'ACRE permet une exonération de 50% des charges sociales la première année.

### Conditions

Être :
- Demandeur d'emploi (indemnisé ou non depuis 6+ mois)
- Bénéficiaire RSA, ASS, ATA
- Jeune de 18 à 25 ans
- Créateur en zone prioritaire (ZRR, QPV)

### Économie

**Micro-entreprise (CA 50 000€) :**
- Charges normales : 11 050€
- Avec ACRE : 5 525€
- **Économie : 5 525€**

**SASU (salaire 40 000€ brut) :**
- Charges normales : 16 800€
- Avec ACRE : 8 400€
- **Économie : 8 400€**

## Stratégie 5 : L'épargne retraite

### PER (Plan d'Épargne Retraite)

Les versements sur un PER sont déductibles de votre revenu imposable.

**Plafond 2026 :**
- 10% des revenus nets
- Minimum : 4 399€
- Maximum : 35 194€

**Exemple :**
- Revenu net : 50 000€
- Versement PER : 5 000€
- TMI : 30%
- **Économie d'impôt : 1 500€**

### Madelin (pour TNS)

Contrats similaires spécifiques aux travailleurs non-salariés avec avantages fiscaux.

## Stratégie 6 : Timing des dépenses

### En fin d'année

Si vous anticipez un bon résultat :
- Avancez des investissements prévus en janvier
- Réglez des formations à venir
- Renouvelez votre matériel

L'objectif : réduire le résultat imposable de l'année en cours.

### Le report de facturation

Si votre CA dépasse un seuil critique (plafond micro, tranche IS...) :
- Différez une facture de décembre à janvier
- Attention : c'est légal seulement si la prestation est réellement réalisée en janvier

## Stratégie 7 : La holding

Pour les revenus importants (> 100 000€), créer une holding peut être intéressant :

**Avantages :**
- Remontée de dividendes quasi exonérée (régime mère-fille)
- Mutualisation des frais
- Transmission patrimoniale facilitée

**Inconvénients :**
- Complexité et coûts de gestion
- Rentable uniquement à partir d'un certain niveau

## Récapitulatif des économies possibles

| Stratégie | Économie annuelle |
|-----------|-------------------|
| Bon choix de statut | 2 000 - 8 000€ |
| Frais déductibles | 1 000 - 5 000€ |
| Optimisation salaire/dividendes | 3 000 - 7 000€ |
| ACRE | 4 000 - 8 000€ (année 1) |
| Épargne retraite | 1 000 - 3 000€ |

**Total potentiel :** 10 000 à 25 000€/an

## Plan d'action

### Étape 1 : Audit de situation
- Listez tous vos frais professionnels
- Calculez la quote-part bureau à domicile
- Vérifiez votre éligibilité à l'ACRE

### Étape 2 : Simulation de statut
- Comparez micro vs société
- Simulez l'optimisation salaire/dividendes
- Utilisez les outils Bozzam

### Étape 3 : Mise en œuvre
- Changez de statut si nécessaire
- Mettez en place le suivi des frais
- Ouvrez un PER adapté

### Étape 4 : Suivi régulier
- Point trimestriel avec votre comptable
- Ajustement de la stratégie
- Anticipation des échéances fiscales

## Conclusion

L'optimisation fiscale légale peut vous faire économiser 10 000 à 25 000€ par an. Ce n'est pas de la fraude, c'est de la bonne gestion.

**Les clés du succès :**
1. Choisissez le bon statut pour votre situation
2. Déduisez tous les frais auxquels vous avez droit
3. Optimisez votre rémunération (salaire/dividendes)
4. Utilisez les dispositifs d'aide (ACRE, PER...)
5. Faites-vous accompagner par un expert-comptable

---

**Sources officielles :**
- Code général des impôts 2026 : legifrance.gouv.fr
- URSSAF - Assiettes et taux de cotisations : urssaf.fr
- Bofip - Bulletin officiel des finances publiques : bofip.impots.gouv.fr
- DGFiP - Simulateur IR : impots.gouv.fr
- Service-public.fr - Optimisation fiscale : entreprendre.service-public.fr`
  },

  'portage-salarial-vs-freelance': {
    id: '7',
    title: 'Portage salarial vs Freelance : le comparatif complet 2026',
    slug: 'portage-salarial-vs-freelance',
    excerpt: 'Freelance en direct ou portage salarial ? On compare tout : revenus nets, protection sociale, gestion, et on vous dit lequel choisir selon votre profil.',
    category: 'Statuts',
    tags: ['portage salarial', 'freelance', 'comparatif', 'statut'],
    readTime: 12,
    date: '2026-02-10',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Portage salarial vs Freelance : le vrai comparatif

Vous hésitez entre vous lancer en freelance pur ou passer par une société de portage salarial ? Ce choix impacte directement vos revenus, votre protection sociale et votre quotidien administratif. Voici un comparatif sans langue de bois.

## Le portage salarial, c'est quoi exactement ?

Le portage salarial est un statut hybride : vous trouvez vos missions comme un freelance, mais vous êtes juridiquement salarié d'une société de portage. Cette société facture votre client, vous reverse un salaire, et gère toute l'administration.

En contrepartie, la société prélève des frais de gestion (5 à 15% du CA HT) en plus des charges sociales classiques.

## Le comparatif chiffré

| Critère | Portage salarial | Micro-entreprise | SASU |
|---------|-----------------|-----------------|------|
| Charges sociales | ~50% | 21-26% | ~64% (patronales+salariales) |
| Frais de gestion | 5-15% | 0% | 0% |
| Comptabilité | Incluse | Très simple | Expert-comptable (~2 000€/an) |
| Assurance chômage | Oui | Non | Oui (si salaire) |
| Mutuelle | Incluse | À votre charge | Déductible |
| Retraite (trimestres) | 4/an garantis | Selon CA | 4/an si salaire suffisant |
| Plafond CA | Aucun | 77 700€ (services) | Aucun |

## Simulation : que reste-t-il dans votre poche ?

Prenons un freelance qui facture 500€/jour, 18 jours/mois, soit 108 000€/an HT.

| Élément | Portage (8% frais) | Micro-entreprise | SASU optimisée |
|---------|-------------------|-----------------|----------------|
| CA HT | 108 000€ | 77 700€ (plafond) | 108 000€ |
| Frais de gestion | -8 640€ | 0€ | 0€ |
| Charges sociales | -49 680€ | -19 891€ | -35 000€ |
| Impôt sur le revenu | -8 500€ | -5 200€ | -6 800€ |
| Expert-comptable | 0€ | 0€ | -2 000€ |
| Net disponible | ~41 180€ | ~52 609€ | ~64 200€ |

## Quand choisir le portage salarial ?

1. Vous sortez d'un CDI et voulez garder vos droits au chômage
2. Vous testez le freelancing sans risque pendant 6-12 mois
3. Vous facturez peu de jours et avez besoin d'une couverture sociale complète
4. Vous ne voulez aucune gestion administrative
5. Vous avez besoin d'un bulletin de paie (prêt immobilier en cours)

## Quand choisir le freelance en direct ?

1. Votre CA dépasse 50 000€/an : le gain net devient significatif
2. Vous êtes à l'aise avec la gestion (ou utilisez un outil comme Bozzam)
3. Vous voulez maximiser vos revenus nets
4. Vous avez des frais professionnels importants à déduire
5. Vous construisez un patrimoine via votre société

## Les pièges du portage salarial

- Certaines sociétés appliquent des frais cachés (assurance, gestion de compte, frais de mission)
- Le CA minimum est souvent de 250-300€/jour pour que le modèle soit rentable
- Vous ne cotisez pas pour la retraite complémentaire cadre chez toutes les sociétés
- Les congés payés (10%) sont inclus dans votre rémunération, pas en plus

## L'alternative : le portage comme tremplin

La meilleure stratégie pour beaucoup de freelances :

1. Démarrer en portage pour tester son activité (3-6 mois)
2. Une fois le CA stabilisé, créer sa micro-entreprise ou SASU
3. Utiliser l'ACRE pour réduire les cotisations la première année
4. Basculer ses clients vers sa propre structure

---

**Sources officielles 2026 :**
- Code du travail - Art. L1254-1 à L1254-31 (portage salarial) : legifrance.gouv.fr
- URSSAF - Taux cotisations 2026 : urssaf.fr
- Syntec - Convention collective du portage salarial : syntec.fr
- Service-public.fr - Le portage salarial : service-public.fr/particuliers/vosdroits/F31620
- PASS 2026 : 46 368€ (Plafond Annuel Sécurité Sociale)`
  },

  'retraite-freelance-trimestres': {
    id: '8',
    title: 'Retraite du freelance : comment valider vos trimestres en 2026',
    slug: 'retraite-freelance-trimestres',
    excerpt: 'Micro-entrepreneur, gérant de SASU ou EURL : combien de trimestres validez-vous ? Comment préparer votre retraite ? Le guide complet.',
    category: 'Protection sociale',
    tags: ['retraite', 'trimestres', 'freelance', 'prévoyance'],
    readTime: 10,
    date: '2026-02-08',
    author: 'Bozzam',
    popular: false,
    fullContent: `## Retraite du freelance : le sujet que tout le monde évite

Quand on se lance en freelance, on pense revenus, liberté, clients. Rarement à la retraite. Pourtant, c'est l'un des plus grands pièges : sans anticipation, vous risquez une pension ridicule après 20 ou 30 ans d'activité.

## Combien faut-il gagner pour valider 4 trimestres en 2026 ?

Pour valider 1 trimestre en 2026, vous devez avoir cotisé sur un revenu minimum d'environ 1 747€. Pour 4 trimestres (le maximum annuel), il faut un revenu cotisé d'au moins 6 990€.

| Trimestres validés | Revenu minimum cotisé |
|--------------------|-----------------------|
| 1 trimestre | 1 747€ |
| 2 trimestres | 3 494€ |
| 3 trimestres | 5 241€ |
| 4 trimestres | 6 990€ |

## Attention : le piège de la micro-entreprise

En micro-entreprise, c'est votre CA (après abattement) qui sert de base. L'abattement est de 34% pour les BNC et 50% pour les BIC.

Concrètement, pour valider 4 trimestres en micro BNC :
- Revenu cotisé nécessaire : 6 990€
- Avec l'abattement de 34% : il faut un CA minimum d'environ 10 590€/an
- Soit environ 883€/mois de CA minimum

| Activité | Abattement | CA minimum pour 4 trimestres |
|----------|-----------|------------------------------|
| BNC (services) | 34% | 10 590€/an |
| BIC (services) | 50% | 13 980€/an |
| Commerce | 71% | 24 100€/an |

## Combien toucherez-vous à la retraite ?

Un freelance en micro-entreprise qui gagne 50 000€/an pendant 25 ans touchera environ 800-1 000€/mois de pension de base. C'est 2 à 3 fois moins qu'un salarié au même revenu.

Pourquoi ? Parce que les cotisations retraite du micro-entrepreneur sont calculées sur le CA après abattement, pas sur le CA réel.

## Les 5 stratégies pour préparer sa retraite

1. Ouvrir un PER (Plan Épargne Retraite) : versements déductibles du revenu imposable, jusqu'à 10% du bénéfice (plafond ~4 637€ en micro ou 37 094€ au réel)
2. Passer en société (SASU/EURL) quand le CA le permet : cotisations retraite plus élevées = droits plus importants
3. Investir dans l'immobilier locatif pour des revenus complémentaires
4. Constituer une épargne diversifiée (assurance-vie, PEA, SCPI)
5. Racheter des trimestres manquants (coûteux mais possible)

## Le PER : l'arme secrète du freelance

Le PER individuel permet de :
- Déduire les versements de votre revenu imposable
- Capitaliser jusqu'à la retraite (sortie en capital ou rente)
- Réduire votre impôt dès cette année

| Tranche marginale | Économie d'impôt pour 5 000€ versés |
|-------------------|--------------------------------------|
| 11% | 550€ |
| 30% | 1 500€ |
| 41% | 2 050€ |

## La check-list retraite du freelance

1. Vérifiez vos trimestres sur info-retraite.fr chaque année
2. Ouvrez un PER dès la première année d'activité
3. Cotisez au minimum pour valider 4 trimestres par an
4. Envisagez le passage en société vers 50 000€ de CA
5. Diversifiez votre épargne (immobilier, bourse, assurance-vie)
6. Anticipez 20 ans à l'avance : chaque trimestre compte

---

**Sources officielles 2026 :**
- Info-retraite.fr - Simulateur officiel retraite : info-retraite.fr
- URSSAF - Cotisations retraite indépendants : urssaf.fr
- CIPAV - Retraite professions libérales : cipav-retraite.fr
- Service-public.fr - PER individuel : service-public.fr/particuliers/vosdroits/F34982
- DGFiP - Déduction PER : impots.gouv.fr`
  },

  'facture-auto-entrepreneur-2026': {
    id: '9',
    title: 'Facture auto-entrepreneur : modèle et mentions obligatoires 2026',
    slug: 'facture-auto-entrepreneur-2026',
    excerpt: 'Les 14 mentions obligatoires sur une facture en 2026, le modèle prêt à copier, et les erreurs qui peuvent vous coûter cher.',
    category: 'Gestion',
    tags: ['facture', 'auto-entrepreneur', 'mentions obligatoires', 'modèle'],
    readTime: 8,
    date: '2026-02-05',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Facture auto-entrepreneur : tout ce qui est obligatoire en 2026

Une facture non conforme peut entraîner une amende de 75 000€ pour une personne physique. Oui, vous avez bien lu. En 2026, les contrôles se sont renforcés et les mentions obligatoires ont évolué. Voici tout ce que vous devez savoir.

## Les 14 mentions obligatoires

| N° | Mention | Exemple |
|----|---------|---------|
| 1 | Numéro de facture (chronologique) | FACT-2026-001 |
| 2 | Date d'émission | 27/02/2026 |
| 3 | Date de la prestation | 25/02/2026 |
| 4 | Votre nom et prénom | Jean Dupont |
| 5 | Votre adresse complète | 12 rue de la Paix, 75002 Paris |
| 6 | Votre numéro SIRET | 123 456 789 00012 |
| 7 | Nom ou raison sociale du client | SAS TechCorp |
| 8 | Adresse du client | 5 avenue des Champs, 75008 Paris |
| 9 | Description détaillée | Développement site web - 10 jours |
| 10 | Prix unitaire HT | 500,00€ |
| 11 | Montant total HT | 5 000,00€ |
| 12 | Mention TVA | TVA non applicable, art. 293 B du CGI |
| 13 | Montant total TTC | 5 000,00€ |
| 14 | Conditions de paiement | Paiement sous 30 jours par virement |

## La mention TVA selon votre situation

Si vous êtes en franchise de base TVA (CA inférieur aux seuils), vous devez obligatoirement écrire :

**"TVA non applicable, article 293 B du Code Général des Impôts"**

Si vous avez dépassé les seuils et collectez la TVA :
- Indiquez votre numéro de TVA intracommunautaire
- Détaillez le montant HT, la TVA et le TTC

| Situation | Seuil 2026 | Mention sur la facture |
|-----------|-----------|------------------------|
| Services (franchise) | < 37 500€ | TVA non applicable, art. 293 B du CGI |
| Ventes (franchise) | < 85 000€ | TVA non applicable, art. 293 B du CGI |
| Au-dessus du seuil | Tout CA | N° TVA intra + montants HT/TVA/TTC |

## Les 5 erreurs qui coûtent cher

1. Oublier la mention TVA : amende de 15€ par mention manquante, plafonnée à 25% du montant
2. Numérotation non chronologique : rejet possible en cas de contrôle fiscal
3. Description trop vague ("Prestation de conseil") : le fisc peut requalifier
4. Pas de conditions de paiement : vous perdez le droit aux pénalités de retard
5. Facture envoyée trop tard : la facture doit être émise au plus tard à la livraison du bien ou à la fin de la prestation

## Nouveauté 2026 : la facturation électronique

La réforme de la facturation électronique se déploie progressivement :
- Depuis 2024 : obligation de recevoir des factures électroniques
- 2026 : obligation d'émettre pour les PME et micro-entreprises
- Format structuré obligatoire (Factur-X, UBL, CII)

## Les pénalités de retard obligatoires

Depuis 2013, toute facture doit mentionner :
- Le taux des pénalités de retard : minimum = 3x le taux d'intérêt légal BCE
- L'indemnité forfaitaire de recouvrement : 40€ minimum
- Le délai de paiement : 30 jours par défaut, 60 jours maximum

---

**Sources officielles 2026 :**
- Code de commerce - Art. L441-3 (mentions obligatoires) : legifrance.gouv.fr
- CGI - Art. 293 B (franchise TVA) : legifrance.gouv.fr
- DGFiP - Facturation électronique 2026 : impots.gouv.fr/professionnel
- Service-public.fr - Mentions obligatoires facture : entreprendre.service-public.fr
- DGCCRF - Sanctions facturation : economie.gouv.fr/dgccrf`
  },

  'banque-pro-comparatif-2026': {
    id: '10',
    title: 'Quelle banque pro choisir en 2026 ? Comparatif Qonto, Shine, Blank',
    slug: 'banque-pro-comparatif-2026',
    excerpt: 'Vous créez votre entreprise et cherchez la meilleure banque pro ? On compare Qonto, Shine et Blank : tarifs, fonctionnalités et verdict.',
    category: 'Gestion',
    tags: ['banque pro', 'Qonto', 'Shine', 'Blank', 'néobanque'],
    readTime: 9,
    date: '2026-02-03',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Faut-il ouvrir un compte bancaire pro ?

Première question à se poser : est-ce obligatoire ? En 2026, la réponse est non pour les auto-entrepreneurs. Mais c'est fortement recommandé dès que votre CA dépasse 10 000€/an pendant 2 années consécutives (obligation de compte dédié, pas forcément pro).

Pour les sociétés (SASU, EURL, SAS), un compte professionnel est obligatoire pour le dépôt du capital social.

## Le comparatif complet

| Critère | Qonto | Shine | Blank |
|---------|-------|-------|-------|
| Prix (entrée) | 9€/mois HT | 0€ (Free) / 9€ (Start) | 6€/mois HT |
| IBAN | Français | Français | Français |
| Virements SEPA inclus | 30 | 5 (Free) / 30 (Start) | 20 |
| Carte bancaire | Mastercard Business | Mastercard | Mastercard |
| Cartes virtuelles | Illimitées | Oui (Start+) | Non |
| Dépôt espèces | Non | Oui (buralistes) | Non |
| Dépôt de chèques | Non | Non | Non |
| Facturation intégrée | Oui (devis + factures) | Oui | Oui |
| Comptabilité auto | 80+ intégrations | Catégorisation auto | Déclaration URSSAF |
| Multi-utilisateurs | Oui (dès Basic) | Non (sauf Premium) | Non |
| Assurance RC Pro | En option | En option | Non |
| Dépôt de capital | Oui (gratuit) | Oui (gratuit) | Oui |
| Support client | Chat 24/7 | 7j/7 | Email + chat |
| Note globale | 8.2/10 | 7.6/10 | 7.0/10 |

## Pour qui ?

**Choisissez Qonto si :**
- Vous avez une SASU ou SAS avec des collaborateurs
- Vous avez besoin de cartes pour plusieurs personnes
- Vous voulez un maximum d'intégrations comptables
- Vous prévoyez de grossir rapidement

**Choisissez Shine si :**
- Vous êtes auto-entrepreneur et voulez commencer gratuitement
- Vous avez besoin de déposer des espèces
- Vous cherchez la simplicité avant tout
- Vous êtes sensible aux valeurs (B Corp certifié)

**Choisissez Blank si :**
- Vous êtes micro-entrepreneur et voulez automatiser vos déclarations URSSAF
- Vous cherchez un prix intermédiaire
- Vous n'avez pas besoin de virements internationaux

## Les frais cachés à surveiller

1. Virements hors forfait : 0,40€ à 1€ par virement supplémentaire
2. Retraits DAB : souvent limités à 3-5/mois, puis 1-2€/retrait
3. Virements internationaux : commission de change + frais fixes
4. Inactivité : certaines banques facturent après 3 mois sans mouvement
5. Clôture : vérifiez les conditions de sortie

## Notre verdict

Pour la majorité des freelances qui démarrent, Shine Free est imbattable : 0€/mois, IBAN français, facturation intégrée. Quand votre activité grossit et que vous passez en société, Qonto devient le choix évident avec ses outils avancés.

---

**Sources 2026 :**
- Qonto - Tarifs officiels : qonto.com/fr/tarifs
- Shine - Offres et tarifs : shine.fr/tarifs
- Blank (Crédit Agricole) - Tarifs : blank.app/tarifs
- UFC-Que Choisir - Comparatif banques pros : quechoisir.org
- Service-public.fr - Obligation compte dédié : service-public.fr`
  },

  'cfe-taxe-meconnue': {
    id: '11',
    title: 'La CFE : cette taxe surprise que tous les entrepreneurs oublient',
    slug: 'cfe-taxe-meconnue',
    excerpt: 'Vous avez reçu un avis de CFE et vous ne comprenez rien ? On vous explique cette taxe méconnue, comment l\'éviter, et combien elle coûte.',
    category: 'Fiscalité',
    tags: ['CFE', 'taxe', 'cotisation foncière', 'exonération'],
    readTime: 7,
    date: '2026-01-28',
    author: 'Bozzam',
    popular: false,
    fullContent: `## La CFE : la mauvaise surprise de la 2ème année

Vous venez de lancer votre micro-entreprise. Tout se passe bien. Et puis en novembre de votre deuxième année, vous recevez un avis d'imposition que vous n'attendiez pas : la CFE (Cotisation Foncière des Entreprises). Bienvenue dans le club.

## C'est quoi exactement la CFE ?

La CFE est un impôt local payé par toutes les entreprises, y compris les auto-entrepreneurs. Elle remplace l'ancienne taxe professionnelle (supprimée en 2010).

Son montant dépend de :
- La commune où vous exercez
- La valeur locative du bien que vous utilisez
- Votre chiffre d'affaires (pour la cotisation minimum)

## Les bonnes nouvelles : les exonérations

1. Première année d'activité : exonération totale automatique (pas de démarche si vous avez déposé le formulaire 1447-C)
2. CA inférieur à 5 000€ : exonération totale permanente
3. Certaines activités exonérées : artisans, pêcheurs, exploitants agricoles

| Situation | CFE due ? | Démarche |
|-----------|----------|----------|
| 1ère année d'activité | Non | Automatique (déclarer 1447-C) |
| CA < 5 000€ (N-2) | Non | Automatique |
| Artisan (exonération communale) | Selon commune | Vérifier mairie |
| Micro-entrepreneur classique | Oui (dès année 2) | Paiement en décembre |

## Combien ça coûte ?

La CFE dépend de votre commune. En 2026, la cotisation minimum selon le CA (sur l'année N-2) :

| CA (N-2) | Cotisation minimum |
|----------|-------------------|
| Jusqu'à 10 000€ | 243 à 579€ |
| De 10 001€ à 32 600€ | 243 à 1 158€ |
| De 32 601€ à 100 000€ | 243 à 2 433€ |
| De 100 001€ à 250 000€ | 243 à 4 060€ |

## Comment la payer ?

1. Créez votre espace professionnel sur impots.gouv.fr
2. L'avis est disponible en novembre
3. Paiement avant le 15 décembre (prélèvement automatique recommandé)
4. Possibilité de mensualisation (prélèvement le 15 de chaque mois)

## Les astuces pour réduire sa CFE

- Domiciliez votre entreprise dans une commune avec un taux faible
- Si vous travaillez de chez vous sans local dédié, déclarez une surface nulle
- Vérifiez les exonérations locales votées par votre commune
- Contestez si le montant vous semble anormalement élevé (réclamation avant le 31/12)

---

**Sources officielles 2026 :**
- DGFiP - CFE et IFER : impots.gouv.fr/professionnel
- CGI - Art. 1447 et suivants (CFE) : legifrance.gouv.fr
- Service-public.fr - CFE : entreprendre.service-public.fr
- Formulaire 1447-C-SD : impots.gouv.fr/formulaire/1447-C-SD`
  },

  'protection-sociale-freelance': {
    id: '12',
    title: 'Protection sociale du freelance : le guide complet 2026',
    slug: 'protection-sociale-freelance',
    excerpt: 'Maladie, maternité, retraite, prévoyance : quelle couverture quand on est indépendant en 2026 ? Les vrais chiffres et les solutions.',
    category: 'Protection sociale',
    tags: ['protection sociale', 'maladie', 'prévoyance', 'mutuelle', 'freelance'],
    readTime: 11,
    date: '2026-01-25',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Protection sociale : le vrai coût d'être freelance

Quand on quitte le salariat, on perd bien plus qu'un salaire fixe. On perd une mutuelle financée à 50%, des indemnités maladie généreuses, et une prévoyance complète. Voici exactement ce que vous avez et ce qu'il vous manque en tant que freelance en 2026.

## Ce que vous avez d'office (régime obligatoire)

Bonne nouvelle : depuis 2020, tous les indépendants sont rattachés au régime général de la Sécurité sociale. Vos remboursements maladie sont donc identiques à ceux d'un salarié.

| Prestation | Salarié | Freelance TNS | Freelance SASU |
|------------|---------|---------------|----------------|
| Remboursement médecin | 70% | 70% | 70% |
| Hospitalisation | 80% | 80% | 80% |
| Indemnités journalières | 50% du salaire (max 52,28€/j) | 1/730ème du revenu (max 63,52€/j) | 50% du salaire |
| Délai de carence maladie | 3 jours | 3 jours | 3 jours |
| Congé maternité | 16 semaines | 8 semaines (minimum) | 16 semaines |
| Allocation maternité | ~3 666€/mois | ~3 666€ (si revenus suffisants) | ~3 666€/mois |
| Assurance chômage | Oui | Non | Oui (si salaire) |

## Le trou béant : les indemnités journalières

C'est le point faible majeur du freelance. Si vous tombez malade :
- En micro-entreprise, vos IJ sont calculées sur votre revenu cotisé (après abattement)
- Avec un CA de 50 000€ en BNC : revenu cotisé = 33 000€, soit des IJ d'environ 45€/jour
- Un salarié au même revenu touche environ 90€/jour

## Les 3 couvertures à souscrire absolument

### La mutuelle santé (obligatoire de fait)

Le régime obligatoire ne rembourse que 70% des soins. Sans mutuelle, une hospitalisation peut vous coûter des milliers d'euros.

| Formule | Prix mensuel moyen | Couverture |
|---------|-------------------|------------|
| Basique | 30-50€ | Optique/dentaire minimal |
| Intermédiaire | 50-80€ | Bon remboursement |
| Premium | 80-150€ | Chambre individuelle, dépassements |

### La prévoyance (maintien de revenu)

C'est LA couverture la plus importante. Elle vous verse un complément de revenu en cas d'arrêt maladie, invalidité ou décès.

| Garantie | Ce qu'elle couvre | Prix indicatif |
|----------|------------------|----------------|
| Arrêt de travail | 60-100% du revenu pendant 3 ans | 50-150€/mois |
| Invalidité | Rente mensuelle à vie | Inclus |
| Décès | Capital versé aux proches | Inclus |

### La RC Professionnelle

La Responsabilité Civile Professionnelle couvre les dommages causés à vos clients dans l'exercice de votre activité.
- Obligatoire pour certaines professions (BTP, santé, conseil)
- Fortement recommandée pour tous les freelances
- Prix : 15-50€/mois selon l'activité et le CA

## Le budget protection sociale du freelance

Pour un freelance qui gagne 60 000€/an net :

| Poste | Montant mensuel | Annuel |
|-------|----------------|--------|
| Mutuelle (intermédiaire) | 65€ | 780€ |
| Prévoyance (80% du revenu) | 100€ | 1 200€ |
| RC Pro | 25€ | 300€ |
| PER (épargne retraite) | 300€ | 3 600€ |
| Total | 490€ | 5 880€ |

C'est environ 10% du revenu net. C'est le prix de la sécurité.

---

**Sources officielles 2026 :**
- Ameli.fr - Protection maladie indépendants : ameli.fr/assure/droits-demarches
- URSSAF - Cotisations et prestations : urssaf.fr
- Service-public.fr - Indemnités journalières TNS : service-public.fr
- CNAVPL/CIPAV - Retraite professions libérales : cipav-retraite.fr
- Code de la Sécurité Sociale : legifrance.gouv.fr`
  },

  'micro-entreprise-changements-2026': {
    id: '13',
    title: 'Micro-entreprise 2026 : tous les changements à connaître',
    slug: 'micro-entreprise-changements-2026',
    excerpt: 'Hausse des cotisations, nouveaux seuils TVA, ACRE modifiée : tout ce qui change pour les micro-entrepreneurs en 2026.',
    category: 'Actualités',
    tags: ['micro-entreprise', '2026', 'changements', 'cotisations', 'TVA'],
    readTime: 8,
    date: '2026-01-20',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Micro-entreprise 2026 : ce qui a changé (et ce qui va encore changer)

L'année 2026 apporte son lot de changements pour les 3,2 millions d'auto-entrepreneurs en France. Voici le résumé complet pour ne rien rater.

## La hausse des cotisations sociales

C'est le changement le plus impactant. Les taux de cotisations ont augmenté progressivement depuis 2024 pour les professions libérales non réglementées (BNC hors CIPAV).

| Activité | Taux 2024 | Taux 2025 | Taux 2026 |
|----------|----------|----------|----------|
| Vente de marchandises (BIC) | 12.3% | 12.3% | 12.3% |
| Prestations services BIC | 21.2% | 21.2% | 21.2% |
| Prestations services BNC | 21.1% | 24.6% | 25.6% |
| Professions libérales CIPAV | 21.2% | 23.2% | 23.2% |

Impact concret : un développeur web en BNC qui facture 60 000€/an paie 2 700€ de cotisations en plus par rapport à 2024.

## Les seuils de TVA chamboulés

Après le chaos de 2025 (seuil unique à 25 000€ annoncé puis suspendu), les seuils 2026 se stabilisent :

| Type | Seuil franchise TVA | Seuil majoré |
|------|-------------------|--------------|
| Prestations de services | 37 500€ | 41 250€ |
| Vente de marchandises | 85 000€ | 93 500€ |

Si vous dépassez le seuil majoré, vous devez facturer la TVA dès le 1er jour du mois de dépassement.

## L'ACRE en deux vitesses

Le dispositif ACRE (Aide à la Création ou Reprise d'Entreprise) évolue en 2026 :
- Création avant le 1er juillet 2026 : exonération de 50% sur les cotisations (4 trimestres)
- Création après le 1er juillet 2026 : exonération réduite à 25%

| Activité | Sans ACRE | ACRE avant 07/2026 | ACRE après 07/2026 |
|----------|----------|--------------------|--------------------|
| BNC | 25.6% | 12.8% | 19.2% |
| BIC services | 21.2% | 10.6% | 15.9% |
| Commerce | 12.3% | 6.2% | 9.3% |

## Les plafonds de CA (inchangés jusqu'en 2028)

Les seuils micro restent les mêmes :
- Vente de marchandises : 188 700€
- Prestations de services : 77 700€

## Ce qui va encore changer

- La facturation électronique obligatoire arrive pour les micro-entreprises courant 2026
- Le guichet unique INPI reste le passage obligé pour toutes les formalités
- Des discussions sont en cours sur une possible réforme de l'abattement fiscal micro

## Notre conseil

Si vous êtes en BNC et que vos cotisations vous semblent trop élevées, faites une simulation comparative avec la SASU ou l'EURL. À partir de 50 000€ de CA avec des frais réels supérieurs à 34%, le régime réel peut devenir plus avantageux.

---

**Sources officielles 2026 :**
- URSSAF - Taux micro-entrepreneur 2026 : autoentrepreneur.urssaf.fr
- DGFiP - Seuils TVA 2026 : impots.gouv.fr
- Loi de finances 2026 - Dispositions ACRE : legifrance.gouv.fr
- Service-public.fr - Régime micro-entreprise : entreprendre.service-public.fr
- Portail auto-entrepreneur : portail-autoentrepreneur.fr`
  },

  'tva-auto-entrepreneur-guide': {
    id: '14',
    title: 'TVA et auto-entrepreneur : le guide de survie 2026',
    slug: 'tva-auto-entrepreneur-guide',
    excerpt: 'Franchise TVA, dépassement de seuil, première déclaration : tout comprendre sur la TVA quand on est auto-entrepreneur.',
    category: 'Fiscalité',
    tags: ['TVA', 'auto-entrepreneur', 'seuils', 'franchise'],
    readTime: 10,
    date: '2026-01-15',
    author: 'Bozzam',
    popular: false,
    fullContent: `## TVA et auto-entrepreneur : le sujet qui fait peur

La TVA est le cauchemar de beaucoup d'auto-entrepreneurs. Franchise de base, seuils de dépassement, première déclaration, récupération : on démêle tout dans ce guide.

## La franchise en base de TVA : le principe

En tant qu'auto-entrepreneur, vous bénéficiez par défaut de la franchise en base de TVA. Cela signifie :
- Vous ne facturez PAS de TVA à vos clients
- Vous ne récupérez PAS la TVA sur vos achats
- Vous devez écrire sur vos factures : "TVA non applicable, article 293 B du CGI"

## Les seuils 2026

| Type d'activité | Seuil de franchise | Seuil majoré |
|----------------|-------------------|--------------|
| Prestations de services | 37 500€ | 41 250€ |
| Vente de marchandises | 85 000€ | 93 500€ |
| Activités mixtes | Chaque seuil s'applique par type | Idem |

## Que se passe-t-il quand on dépasse le seuil ?

Il y a deux cas de figure :

### Cas 1 : Dépassement du seuil de franchise (37 500€ pour les services)
- Vous restez en franchise tant que vous ne dépassez pas le seuil majoré
- Mais si vous dépassez 2 années consécutives, vous devenez redevable au 1er janvier de la 3ème année

### Cas 2 : Dépassement du seuil majoré (41 250€ pour les services)
- Vous devenez redevable de la TVA dès le 1er jour du mois de dépassement
- Vous devez immédiatement demander un numéro de TVA intracommunautaire
- Vos factures suivantes doivent inclure la TVA

## Devenir redevable de la TVA : mode d'emploi

1. Contactez votre SIE (Service des Impôts des Entreprises) pour obtenir votre numéro de TVA
2. Modifiez vos factures : ajoutez le montant HT, la TVA (20% en général) et le TTC
3. Choisissez votre régime de déclaration : simplifié (annuel) ou réel (mensuel/trimestriel)
4. Déclarez et payez la TVA collectée moins la TVA déductible

## TVA : ami ou ennemi ?

La TVA n'est pas forcément une mauvaise nouvelle :

| Avantage | Inconvénient |
|----------|-------------|
| Récupération TVA sur achats (ordinateur, logiciels, coworking) | Complexité administrative |
| Vos prix HT restent les mêmes pour les clients pro (ils déduisent la TVA) | Augmentation de 20% pour les clients particuliers |
| Meilleure image professionnelle | Déclarations régulières obligatoires |
| Possibilité de déduire la TVA sur investissements | Trésorerie à gérer (TVA collectée = argent de l'État) |

## Les erreurs fatales à éviter

1. Ne pas surveiller son CA et dépasser le seuil sans s'en rendre compte
2. Continuer à facturer sans TVA après dépassement du seuil majoré (redressement garanti)
3. Oublier de reverser la TVA collectée (c'est de l'argent qui ne vous appartient pas)
4. Ne pas récupérer la TVA sur les achats professionnels
5. Confondre seuil de franchise et seuil micro-entreprise (ce sont deux choses différentes)

## Le calendrier TVA

| Régime | Fréquence | Échéance |
|--------|-----------|----------|
| Simplifié | 2 acomptes + solde annuel | Juillet et décembre |
| Réel normal | Mensuel | Le 19 du mois suivant |
| Mini-réel | Mensuel (TVA) + simplifié (IS) | Le 19 du mois suivant |

---

**Sources officielles 2026 :**
- CGI - Art. 293 B (franchise TVA) : legifrance.gouv.fr
- DGFiP - Seuils TVA 2026 : impots.gouv.fr
- Service-public.fr - TVA micro-entrepreneur : entreprendre.service-public.fr
- URSSAF - Obligations déclaratives : autoentrepreneur.urssaf.fr
- Bofip - Régimes TVA : bofip.impots.gouv.fr`
  },

  'devenir-freelance-guide-2026': {
    id: '15',
    title: 'Devenir freelance en 2026 : le guide pas à pas',
    slug: 'devenir-freelance-guide-2026',
    excerpt: 'De la décision au premier client : les 10 étapes concrètes pour se lancer en freelance en 2026, avec les pièges à éviter.',
    category: 'Création',
    tags: ['freelance', 'guide', 'création', 'démarrage', '2026'],
    readTime: 14,
    date: '2026-01-10',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Devenir freelance en 2026 : les 10 étapes

En 2025, plus de 1,2 million d'entreprises ont été créées en France, dont 65% de micro-entreprises. Le freelancing n'a jamais été aussi accessible. Mais entre "créer son statut" et "vivre de son activité", il y a un gouffre. Ce guide vous donne les 10 étapes concrètes.

## Étape 1 : Validez votre offre AVANT de créer votre statut

L'erreur n°1 des freelances débutants : créer leur micro-entreprise avant d'avoir un seul client potentiel.

La bonne méthode :
1. Identifiez 3 à 5 entreprises qui pourraient avoir besoin de vous
2. Contactez-les et proposez une mission test (même gratuite)
3. Si au moins 2 sont intéressées, vous avez validé votre offre
4. Seulement alors, créez votre statut

## Étape 2 : Choisissez le bon statut

| Critère | Micro-entreprise | SASU | EURL |
|---------|-----------------|------|------|
| Création | 0€, 10 minutes | ~500€, 2-3 semaines | ~500€, 2-3 semaines |
| CA max | 77 700€ (services) | Illimité | Illimité |
| Charges sociales | 21-26% du CA | ~64% du salaire brut | ~45% de la rémunération |
| Comptabilité | Registre recettes | Expert-comptable | Expert-comptable |
| Protection sociale | Minimale | Maximale | Bonne |
| Idéal pour | Démarrer, tester | CA > 70 000€, protection | CA > 50 000€, optimisation |

Notre conseil : commencez en micro-entreprise. Vous pourrez toujours évoluer ensuite.

## Étape 3 : Créez votre statut en ligne

1. Rendez-vous sur formalites.entreprises.gouv.fr (guichet unique)
2. Créez votre compte et remplissez le formulaire
3. Choisissez votre code APE (activité principale)
4. Optez pour le versement libératoire de l'impôt si éligible
5. Recevez votre SIRET sous 1 à 2 semaines

## Étape 4 : Ouvrez un compte bancaire dédié

Même si ce n'est pas obligatoire en micro, séparez vos finances dès le jour 1. Les meilleures options en 2026 :
- Shine Free : 0€/mois, parfait pour démarrer
- Qonto Basic : 9€/mois, pour les plus ambitieux
- Un simple compte Boursorama séparé : gratuit

## Étape 5 : Fixez vos tarifs

La formule magique du TJM pour un freelance :

**TJM = (Salaire net souhaité x 12 + Charges + Frais + Congés) / Jours facturables**

Exemple pour un salaire net de 3 500€/mois :
- Salaire net annuel souhaité : 42 000€
- Charges sociales (BNC 25.6%) : +14 400€
- Frais pro (comptable, logiciels, coworking) : +4 000€
- Provision congés (8 semaines non facturées) : inclus dans le calcul
- Jours facturables : 218 jours - 40 jours (congés) - 20 jours (prospection) = 158 jours

**TJM = 60 400€ / 158 jours = 382€/jour minimum**

## Étape 6 : Créez vos outils de base

Les indispensables dès le jour 1 :
- Un modèle de facture conforme (14 mentions obligatoires)
- Un modèle de devis clair
- Des CGV adaptées à votre activité
- Un profil LinkedIn optimisé
- Un portfolio ou site vitrine simple

## Étape 7 : Trouvez vos premiers clients

Les 5 canaux les plus efficaces pour un freelance débutant :
1. Votre réseau personnel et professionnel (50% des premières missions)
2. LinkedIn (publication + prospection directe)
3. Les plateformes (Malt, Comet, Crème de la Crème)
4. Les meetups et événements de votre secteur
5. Le bouche-à-oreille (demandez des recommandations)

## Étape 8 : Sécurisez vos premiers revenus

- Signez toujours un devis ou contrat avant de commencer
- Demandez un acompte de 30 à 50% sur les grosses missions
- Facturez au fur et à mesure (pas tout à la fin)
- Gardez 30% de chaque encaissement pour les charges et impôts

## Étape 9 : Gérez votre admin comme un pro

Le rituel mensuel du freelance organisé :
1. Déclarez votre CA sur autoentrepreneur.urssaf.fr (mensuel ou trimestriel)
2. Émettez vos factures dans les 48h après la prestation
3. Relancez les factures impayées à J+7, J+15, J+30
4. Mettez de côté 25-30% du CA pour les charges
5. Suivez votre CA pour anticiper les seuils TVA

## Étape 10 : Planifiez la suite

Dès que votre activité décolle, pensez à :
1. Souscrire une prévoyance et une mutuelle
2. Ouvrir un PER pour préparer votre retraite
3. Évaluer le passage en société (au-delà de 50-60 000€ de CA)
4. Développer une offre récurrente (abonnement, retainer)
5. Construire votre marque personnelle

---

**Sources officielles 2026 :**
- Guichet unique - formalites.entreprises.gouv.fr
- URSSAF - autoentrepreneur.urssaf.fr
- Bpifrance Création - bpifrance-creation.fr
- Service-public.fr - entreprendre.service-public.fr
- INSEE - Statistiques création entreprises : insee.fr`
  },

  'cumul-salarie-micro-entreprise': {
    id: '16',
    title: 'Cumul emploi salarié + micro-entreprise : le mode d\'emploi complet',
    slug: 'cumul-salarie-micro-entreprise',
    excerpt: 'Peut-on être salarié et auto-entrepreneur en même temps ? Oui, mais attention aux pièges. Le guide complet du cumul en 2026.',
    category: 'Statuts',
    tags: ['cumul', 'salarié', 'micro-entreprise', 'side project'],
    readTime: 9,
    date: '2026-01-05',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Salarié + auto-entrepreneur : c'est possible (et de plus en plus courant)

En 2026, plus de 500 000 salariés cumulent leur emploi avec une micro-entreprise. C'est légal, encadré, et c'est souvent la meilleure façon de tester une activité freelance avant de se lancer à 100%.

## Les conditions à respecter

### Vis-à-vis de votre employeur

1. Vérifiez votre contrat de travail : recherchez une clause de non-concurrence ou d'exclusivité
2. Obligation de loyauté : votre activité ne doit pas concurrencer directement votre employeur
3. Pas d'utilisation des moyens de l'entreprise (ordinateur pro, fichiers clients)
4. Votre activité auto-entrepreneur ne doit pas empiéter sur vos horaires de travail

| Clause | Impact | Solution |
|--------|--------|----------|
| Non-concurrence | Interdit une activité similaire | Choisir un domaine différent |
| Exclusivité | Interdit toute autre activité | Négocier une levée ou attendre la fin |
| Loyauté | Obligation générale | Ne pas concurrencer, ne pas utiliser les ressources |
| Aucune clause | Liberté totale | Informer votre employeur par courtoisie |

### Vis-à-vis de l'URSSAF

Votre activité auto-entrepreneur génère des cotisations sociales séparées de votre activité salariée. Vous cotisez deux fois, mais vos droits retraite s'additionnent.

## L'impact sur vos impôts

Vos revenus de micro-entreprise s'ajoutent à votre salaire pour le calcul de l'impôt sur le revenu. Deux options :

### Option 1 : Barème progressif (par défaut)
- Votre CA micro (après abattement) s'ajoute à votre salaire imposable
- Vous montez potentiellement de tranche d'imposition

### Option 2 : Versement libératoire
- 1.7% du CA (BIC services) ou 2.2% (BNC)
- Condition : revenu fiscal de référence N-2 < 28 797€ (1 part)
- Avantageux si vos revenus cumulés vous placent dans la tranche 30%+

| Situation | Barème progressif | Versement libératoire |
|-----------|------------------|----------------------|
| Salaire 30 000€ + micro 15 000€ BNC | Tranche 30% sur le surplus | 2.2% = 330€ |
| Salaire 50 000€ + micro 20 000€ BNC | Tranche 30-41% | Non éligible (revenus trop élevés) |
| Salaire 20 000€ + micro 10 000€ BNC | Tranche 11% | 2.2% = 220€ |

## La protection sociale en cumul

| Prestation | Couverture |
|------------|-----------|
| Maladie / Remboursements | Via votre employeur (inchangé) |
| Arrêt maladie | Indemnités salarié uniquement |
| Retraite base | Trimestres cumulés (salarié + TNS) |
| Retraite complémentaire | Points AGIRC-ARRCO (salarié) + RCI (micro) |
| Chômage | Droits basés sur le salaire uniquement |

## Les 5 pièges à éviter

1. Travailler pour votre activité micro sur vos heures de bureau (faute professionnelle)
2. Utiliser les contacts ou outils de votre employeur pour votre micro (concurrence déloyale)
3. Oublier de déclarer vos revenus micro aux impôts (redressement)
4. Ne pas anticiper la hausse d'impôt liée au cumul des revenus
5. Penser que le chômage couvrira votre activité micro si vous êtes licencié (il ne couvre que le salaire)

## La stratégie optimale du cumul

1. Lancez votre micro-entreprise en parallèle de votre CDI
2. Testez votre activité pendant 6 à 12 mois
3. Constituez un matelas de trésorerie (6 mois de charges fixes minimum)
4. Quand votre CA micro atteint 70-80% de votre salaire net, envisagez la transition
5. Négociez une rupture conventionnelle pour bénéficier de l'ARE
6. Utilisez l'ARE pour sécuriser vos premiers mois à temps plein

## Faut-il prévenir son employeur ?

Légalement, non (sauf clause contractuelle). Mais en pratique :
- Si votre activité est dans un domaine totalement différent : pas nécessaire
- Si votre activité est dans le même secteur : informez par écrit pour vous protéger
- Si vous avez un doute : consultez le CSE ou un avocat en droit du travail

---

**Sources officielles 2026 :**
- Code du travail - Art. L1222-1 (obligation de loyauté) : legifrance.gouv.fr
- URSSAF - Cumul activité salariée et micro : urssaf.fr
- DGFiP - Versement libératoire : impots.gouv.fr
- Service-public.fr - Cumul emploi salarié et création : service-public.fr/particuliers/vosdroits/F31218
- France Travail - Droits ARE après rupture conventionnelle : francetravail.org`
  },

  'holding-freelance-guide-2026': {
    id: '17',
    title: 'Créer une holding en freelance : pourquoi et comment en 2026',
    slug: 'holding-freelance-guide-2026',
    excerpt: 'Holding, société mère, filiale : comment structurer votre activité freelance pour optimiser votre fiscalité et investir intelligemment.',
    category: 'Optimisation',
    tags: ['Holding', 'Fiscalité', 'Optimisation', 'SASU', 'Investissement'],
    readTime: 14,
    date: '2026-03-15',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Holding freelance : le levier fiscal que peu de freelances connaissent

Vous êtes freelance en SASU ou EURL, vous gagnez bien votre vie, et vous cherchez un moyen légal d'optimiser votre fiscalité tout en préparant l'avenir ? La holding est probablement la stratégie la plus puissante à votre disposition en 2026.

## Qu'est-ce qu'une holding exactement ?

Une holding (ou société mère) est une société qui détient des parts dans une ou plusieurs autres sociétés (les filiales). En tant que freelance, le schéma classique est :

1. Vous créez une **holding** (généralement une SASU ou SAS)
2. Cette holding détient 100% de votre **société opérationnelle** (votre SASU/EURL de freelance)
3. Les bénéfices remontent de la filiale vers la holding avec une fiscalité allégée

## Le régime mère-fille : le cœur de l'avantage

C'est LE mécanisme clé. Quand votre filiale verse des dividendes à la holding mère :
- **Exonération de 95%** des dividendes reçus (seule une quote-part de 5% pour frais et charges est imposée)
- Concrètement : sur 100 000€ de dividendes remontés, seuls 5 000€ sont soumis à l'IS

**Conditions du régime mère-fille (2026) :**
- La holding détient au moins 5% du capital de la filiale
- Les titres sont conservés au moins 2 ans
- Les deux sociétés sont soumises à l'IS

| Sans holding | Avec holding |
|-------------|-------------|
| Bénéfice filiale : 100 000€ | Bénéfice filiale : 100 000€ |
| IS filiale (15/25%) : ~19 375€ | IS filiale : ~19 375€ |
| Dividendes distribués : 80 625€ | Dividendes remontés : 80 625€ |
| Flat tax 30% : 24 188€ | IS holding (sur 5%) : ~201€ |
| **Net en poche : 56 437€** | **Trésorerie holding : ~80 424€** |

La différence est énorme : **24 000€ de plus** disponibles pour investir, avant toute distribution personnelle.

## Pourquoi créer une holding en 2026 ?

### 1. Optimiser la remontée de dividendes
Au lieu de payer la flat tax de 30% sur vos dividendes personnels, vous remontez les bénéfices dans la holding quasi-gratuitement.

### 2. Investir via la holding
La trésorerie disponible dans la holding peut être investie :
- **Immobilier** (via une SCI filiale)
- **Bourse** (compte-titres au nom de la holding)
- **Crypto-actifs** (actifs numériques en société)
- **Autres sociétés** (prises de participation)

### 3. Mutualiser les charges
La holding peut facturer des prestations de management (management fees) à ses filiales :
- Direction stratégique
- Services administratifs
- Comptabilité centralisée

### 4. Préparer la transmission
Une holding facilite la transmission de patrimoine professionnel grâce au Pacte Dutreil : abattement de **75% sur la valeur des titres transmis** pour les donations et successions.

## Les coûts réels d'une holding en 2026

| Poste | Coût estimé |
|-------|-------------|
| Création de la holding (SAS/SASU) | 300€ à 800€ |
| Capital social minimum | 1€ (mais 1 000€ recommandé) |
| Expert-comptable (holding) | 1 500€ à 3 000€/an |
| Expert-comptable (filiale) | 1 500€ à 3 000€/an |
| Greffe et formalités | 200€ à 400€/an |
| **Coût total annuel** | **3 500€ à 6 500€** |

## À partir de quel seuil c'est rentable ?

**Règle simple** : une holding devient intéressante à partir de **60 000€ à 80 000€ de bénéfice annuel** dans votre société opérationnelle, soit environ **120 000€ à 150 000€ de CA** pour un freelance en SASU.

| CA annuel | Bénéfice net estimé | Holding rentable ? |
|-----------|--------------------|--------------------|
| Moins de 80 000€ | < 40 000€ | Non — les frais dépassent l'économie |
| 80 000€ à 120 000€ | 40 000€ à 65 000€ | Limite — à calculer selon votre situation |
| 120 000€ à 200 000€ | 65 000€ à 120 000€ | Oui — économie significative |
| Plus de 200 000€ | > 120 000€ | Absolument — économie de 15 000€+/an |

## Les étapes pour créer votre holding

1. **Consulter un expert-comptable** spécialisé en holding (indispensable)
2. **Créer la holding** (SASU recommandée pour un freelance seul)
3. **Apporter vos parts** de société opérationnelle à la holding (apport de titres)
4. **Bénéficier du report d'imposition** sur la plus-value d'apport (article 150-0 B ter du CGI)
5. **Mettre en place la convention de management fees** entre holding et filiale
6. **Configurer le régime mère-fille** avec votre comptable

## Les pièges à éviter

1. **L'abus de droit** : si la holding n'a aucune substance économique réelle, l'administration fiscale peut la requalifier
2. **Management fees excessifs** : les honoraires doivent correspondre à des prestations réelles et être à prix de marché
3. **Oublier la TVA** : les management fees sont soumis à TVA (20%)
4. **Sortie précipitée** : vendre les titres apportés dans les 3 ans déclenche l'imposition de la plus-value en report
5. **Sous-estimer les coûts** : deux comptabilités, deux bilans, deux déclarations fiscales

## Holding + convention de trésorerie

La holding peut prêter de l'argent à ses filiales (et vice versa) via une convention de trésorerie. Cela permet de :
- Financer un investissement dans une filiale
- Équilibrer la trésorerie entre sociétés
- Éviter les découverts bancaires (prêt interne moins cher)

Le taux d'intérêt doit respecter le taux fiscal maximum déductible (TMP publié chaque trimestre par l'administration, environ 5.36% en 2026).

---

**Sources officielles 2026 :**
- Code général des impôts - Art. 145 et 216 (régime mère-fille) : legifrance.gouv.fr
- CGI - Art. 150-0 B ter (apport-cession en report) : legifrance.gouv.fr
- BOFiP - Management fees : bofip.impots.gouv.fr
- CGI - Art. 787 B (Pacte Dutreil transmission) : legifrance.gouv.fr
- DGFiP - Taux maximum des intérêts déductibles : impots.gouv.fr`
  },

  'sasu-vs-eurl-famille-marie': {
    id: '18',
    title: 'SASU vs EURL : quel statut quand on est marié avec enfants ?',
    slug: 'sasu-vs-eurl-famille-marie',
    excerpt: 'Marié, pacsé, avec enfants ? Le choix SASU ou EURL a un impact énorme sur votre famille. Comparatif complet 2026.',
    category: 'Statuts',
    tags: ['SASU', 'EURL', 'Famille', 'Mariage', 'Protection sociale'],
    readTime: 12,
    date: '2026-03-12',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Le choix du statut quand on a une famille : un enjeu crucial

Quand vous êtes célibataire, choisir entre SASU et EURL est surtout une question de chiffres. Mais dès que vous êtes marié(e) avec des enfants, d'autres paramètres entrent en jeu : protection du conjoint, régime matrimonial, couverture maladie de la famille, retraite, et transmission.

## Le point clé : régime social du dirigeant

La différence fondamentale entre SASU et EURL impacte directement votre famille :

| Critère | SASU (Président) | EURL (Gérant TNS) |
|---------|-----------------|-------------------|
| Régime social | Assimilé salarié (régime général) | Travailleur Non Salarié (SSI) |
| Cotisations sociales | ~45% du brut (élevées) | ~40-45% du revenu (moins élevées) |
| Bulletin de paie | Oui | Non |
| Couverture maladie famille | Régime général (identique salarié) | SSI (couverture de base similaire) |
| Indemnités journalières | Oui, dès le 4ème jour | Oui, mais avec un délai de carence de 3 jours |
| Congé maternité/paternité | Identique aux salariés | Droits TNS (montants légèrement inférieurs) |
| Retraite complémentaire | AGIRC-ARRCO | RCI (Retraite Complémentaire des Indépendants) |
| Assurance chômage | Non (sauf ATI sous conditions) | Non |

## L'impact du régime matrimonial

### Marié sous le régime de la communauté (défaut)

C'est le régime par défaut si vous n'avez pas fait de contrat de mariage.

**En SASU :**
- Les parts de la SASU acquises pendant le mariage sont un bien commun
- Votre conjoint a un droit sur la valeur des parts (pas sur la gestion)
- En cas de divorce, les parts entrent dans le partage

**En EURL :**
- Même principe, mais votre conjoint doit être informé de l'apport de biens communs
- Il/elle peut revendiquer la qualité d'associé pour la moitié des parts

| Situation | SASU | EURL |
|-----------|------|------|
| Biens communs | Parts = bien commun en valeur | Parts = bien commun, conjoint peut revendiquer |
| Consentement conjoint | Non requis pour la gestion | Requis pour l'apport de biens communs |
| Divorce | Partage de la valeur des parts | Partage + possible revendication d'associé |

### Marié sous le régime de la séparation de biens

- Chaque époux est propriétaire de ce qu'il crée
- Les parts de la société n'entrent pas dans un patrimoine commun
- Protection maximale du conjoint en cas de difficultés de l'entreprise

**Recommandation** : si vous créez une société avec un patrimoine familial à protéger, la séparation de biens est fortement recommandée.

## Protection sociale de la famille

### Couverture maladie du conjoint et des enfants

**En SASU** :
- Votre conjoint et vos enfants sont ayants droit du régime général
- Remboursements identiques à un salarié
- Complémentaire santé possible (Madelin)

**En EURL (TNS)** :
- Depuis 2020, tous les indépendants sont rattachés au régime général (CPAM)
- Les remboursements de base sont identiques
- La complémentaire santé Madelin est déductible fiscalement

**En pratique** : la différence de couverture maladie entre SASU et EURL est devenue minime depuis le rattachement des TNS au régime général.

### Congé maternité / paternité

| Prestation | SASU (assimilé salarié) | EURL (TNS) |
|-----------|------------------------|------------|
| Congé maternité (durée) | 16 semaines (identique salariée) | 16 semaines minimum |
| Indemnités journalières maternité | Basées sur le salaire, plafonnées à 100.36€/jour | Forfaitaire ~60.26€/jour |
| Congé paternité | 25 jours (comme salarié) | 25 jours |
| Indemnités paternité | Basées sur le salaire | Forfaitaire ~60.26€/jour |

**Avantage clair SASU** : les indemnités maternité/paternité sont plus élevées car calculées sur le salaire réel.

## Le quotient familial : l'impact sur vos impôts

Avec des enfants, le quotient familial réduit votre impôt. L'effet varie selon le statut :

### En SASU (salaire + dividendes)

- Le salaire est soumis au barème progressif → le quotient familial s'applique
- Les dividendes au PFU (flat tax 30%) → le quotient familial ne s'applique PAS
- Vous pouvez opter pour le barème progressif sur les dividendes (intéressant avec beaucoup d'enfants)

### En EURL à l'IS (rémunération TNS + dividendes)

- La rémunération TNS est soumise au barème progressif → quotient familial applicable
- Les dividendes au-delà de 10% du capital → soumis aux cotisations sociales TNS (pas de flat tax possible sur cette part)

| Situation familiale | Parts fiscales | Plafond avantage QF 2026 |
|--------------------|---------------|-------------------------|
| Marié, 0 enfant | 2 parts | — |
| Marié, 1 enfant | 2.5 parts | ~1 791€ |
| Marié, 2 enfants | 3 parts | ~3 582€ |
| Marié, 3 enfants | 4 parts | ~5 373€ + majoration |

**Exemple concret** : un couple marié avec 2 enfants (3 parts) et 80 000€ de revenu imposable paie environ 7 200€ d'IR. Le même revenu pour un célibataire (1 part) : environ 16 500€. Soit **9 300€ d'économie** grâce au quotient familial.

## Simulation comparative : freelance marié, 2 enfants, CA 120 000€

| Poste | SASU (salaire 4 000€ brut/mois) | EURL (rémunération 4 000€/mois) |
|-------|-------------------------------|-------------------------------|
| CA annuel | 120 000€ | 120 000€ |
| Charges déductibles | 10 000€ | 10 000€ |
| Rémunération brute annuelle | 48 000€ | 48 000€ |
| Cotisations sociales | ~21 600€ (45%) | ~19 200€ (40%) |
| Rémunération nette | ~26 400€ | ~28 800€ |
| Bénéfice société (avant IS) | 40 400€ | 42 800€ |
| IS (15% puis 25%) | ~6 060€ | ~6 420€ |
| Dividendes distribuables | ~34 340€ | ~36 380€ |
| Flat tax dividendes (30%) | ~10 302€ | Variable (cotisations TNS sur >10% capital) |
| **Revenu net total famille** | **~50 438€** | **~51 500€** |

**Résultat** : l'EURL est légèrement plus avantageuse en revenu net, mais la SASU offre une meilleure protection sociale (maternité, retraite complémentaire).

## Le statut du conjoint

Si votre conjoint travaille dans l'entreprise (comptabilité, secrétariat, etc.) :

| Statut du conjoint | SASU | EURL |
|-------------------|------|------|
| Conjoint salarié | Oui (contrat de travail, charges salariales) | Oui |
| Conjoint associé | Non (SASU = un seul associé, passer en SAS) | Non (EURL = un seul associé, passer en SARL) |
| Conjoint collaborateur | Non (réservé aux TNS) | Oui (gratuit, cotise pour la retraite) |

**Le statut de conjoint collaborateur** est exclusif à l'EURL/SARL : votre conjoint peut travailler dans l'entreprise sans salaire mais en cotisant pour sa retraite. C'est un avantage significatif de l'EURL.

## Notre recommandation selon votre profil familial

| Profil | Statut recommandé | Raison |
|--------|-------------------|--------|
| Marié, sans enfant, CA < 80k€ | EURL | Charges plus faibles, revenu net supérieur |
| Marié, 1-2 enfants, CA 80-150k€ | SASU | Meilleure protection maternité/paternité, retraite |
| Marié, 3+ enfants, revenus élevés | SASU + barème progressif | Quotient familial maximisé |
| Conjoint qui aide dans l'entreprise | EURL | Statut conjoint collaborateur possible |
| Projet de revente de société | SASU | Plus simple pour céder des actions |
| Protection patrimoine familial max | SASU + séparation de biens | Double protection |

---

**Sources officielles 2026 :**
- Code civil - Art. 1401 et suivants (régime matrimonial) : legifrance.gouv.fr
- URSSAF - Statut du conjoint collaborateur : urssaf.fr
- Ameli.fr - Congé maternité indépendantes : ameli.fr
- DGFiP - Quotient familial et plafonnement : impots.gouv.fr
- CGI - Art. 200 A (Prélèvement Forfaitaire Unique) : legifrance.gouv.fr
- Service-public.fr - Choix du régime matrimonial : service-public.fr/particuliers/vosdroits/F835`
  },

  'investir-bourse-crypto-societe': {
    id: '19',
    title: 'Investir en bourse et crypto via sa société en 2026',
    slug: 'investir-bourse-crypto-societe',
    excerpt: 'Compte-titres société, PEA, crypto-actifs : comment investir intelligemment via votre SASU, EURL ou holding en 2026.',
    category: 'Optimisation',
    tags: ['Bourse', 'Crypto', 'Investissement', 'Holding', 'Fiscalité'],
    readTime: 13,
    date: '2026-03-10',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Pourquoi investir via sa société plutôt qu'en personnel ?

Quand votre société génère des bénéfices que vous ne distribuez pas immédiatement, cet argent dort sur un compte courant à 0%. En l'investissant via la société (ou mieux, via une holding), vous faites travailler de l'argent qui n'a pas encore subi la flat tax de 30%.

**L'avantage clé** : vous investissez avec de l'argent brut (avant imposition personnelle), ce qui représente ~30% de capital supplémentaire par rapport à un investissement personnel après distribution.

## La fiscalité des investissements en société vs en personnel

| Type | En personnel | Via société (IS) | Via holding |
|------|-------------|-----------------|------------|
| Plus-values actions | Flat tax 30% | IS 15/25% sur PV | IS 15/25% + régime mère-fille si filiales |
| Dividendes reçus | Flat tax 30% | IS sur 100% | IS sur 5% seulement (régime mère-fille) |
| Plus-values crypto | Flat tax 30% (> 305€/an) | IS 15/25% | IS 15/25% |
| Intérêts (obligations, livrets) | Flat tax 30% | IS 15/25% | IS 15/25% |
| Moins-values | Imputables sur PV de même nature (10 ans) | Déductibles du résultat | Déductibles du résultat |

## Investir en bourse via sa société

### Le compte-titres société (CTO)

C'est l'outil principal pour investir en bourse via votre SASU, EURL ou holding.

**Comment ça marche :**
1. Vous ouvrez un compte-titres au nom de votre société
2. Vous transférez de la trésorerie excédentaire
3. Vous investissez en actions, ETF, obligations, etc.
4. Les plus-values et dividendes sont imposés à l'IS

**Banques proposant le CTO société en 2026 :**

| Banque/Courtier | Frais ouverture | Frais ordre | Particularités |
|----------------|----------------|-------------|----------------|
| Boursorama Pro | Gratuit | 1.99€ min | Interface simple |
| Saxo Banque | Gratuit | 2€ min | Large choix ETF |
| Interactive Brokers | Gratuit | 0.50€ min | Le moins cher, interface pro |
| Fortuneo Pro | Gratuit | 1.95€ min | Bonne interface mobile |

### PEA et société : attention !

**Un PEA ne peut PAS être ouvert au nom d'une société.** C'est un compte strictement personnel. Si vous investissez en bourse via votre société, ce sera obligatoirement via un CTO.

### La stratégie ETF en société

Pour une trésorerie d'entreprise, les ETF capitalisant sont préférables :
- Pas de dividendes distribués → pas d'IS annuel sur les revenus
- La plus-value n'est imposée qu'à la revente
- Diversification automatique

| Type d'ETF | Exemple | Avantage en société |
|-----------|---------|---------------------|
| ETF World capitalisant | MSCI World Acc (CW8) | Pas d'IS tant que non vendu |
| ETF S&P 500 capitalisant | Amundi S&P 500 Acc | Exposition US, pas d'IS intermédiaire |
| ETF Obligations | Amundi Euro Aggregate | Moins volatile, trésorerie court terme |
| ETF Immobilier (REIT) | iShares European Property | Dividendes réinvestis automatiquement |

### Comptabilisation des investissements

Les placements en bourse dans une société doivent être correctement comptabilisés :
- **Titres de placement** (court terme) : compte 503 — les moins-values sont provisionnées
- **Titres immobilisés** (long terme) : compte 271 — amortissement éventuel
- **Chaque opération** doit être tracée et déclarée dans les comptes annuels

## Investir en crypto via sa société

### Le cadre fiscal des crypto-actifs en société (2026)

Depuis 2023, les entreprises qui investissent en crypto-actifs sont soumises à un cadre fiscal précis :

**Régime fiscal :**
- Les plus-values sur actifs numériques sont soumises à l'IS (15% puis 25%)
- Les moins-values sont déductibles du résultat de l'exercice
- La valorisation en fin d'exercice suit la méthode du coût historique ou de la juste valeur (au choix, selon le PCG révisé)

| Aspect | En personnel | Via société |
|--------|-------------|-------------|
| Imposition PV | 30% (flat tax) dès 305€/an | IS 15% (< 42 500€) puis 25% |
| Moins-values | Non déductibles des autres revenus | Déductibles du résultat |
| Timing imposition | À chaque cession | À chaque cession + provision éventuelle |
| DeFi / Staking | Revenus imposables (BNC ou flat tax) | Produits imposables à l'IS |

### Plateformes acceptant les comptes société

| Plateforme | Compte société | KYB requis | Crypto disponibles |
|-----------|----------------|-----------|-------------------|
| Coinbase Institutional | Oui | Oui | 200+ |
| Kraken Business | Oui | Oui | 300+ |
| Bitstamp Corporate | Oui | Oui | 80+ |
| Binance Institutional | Oui (via filiale FR) | Oui | 400+ |

### La stratégie DCA en société

Le Dollar Cost Averaging (investissement régulier) fonctionne aussi en société :
1. Définissez un budget mensuel (ex : 10% de la trésorerie excédentaire)
2. Investissez de façon automatique chaque mois
3. Réduisez l'impact de la volatilité
4. Provisionnez les moins-values latentes en fin d'exercice

## Holding + investissements : la structure optimale

Le schéma le plus efficace pour un freelance investisseur :

1. **Société opérationnelle** (SASU/EURL) → génère le CA freelance
2. **Holding** (SASU) → reçoit les dividendes (régime mère-fille, quasi-exonérés)
3. **La holding investit** → CTO bourse, crypto, parts de SCI, etc.

**Avantages de cette structure :**
- Vous investissez avec ~95% des bénéfices (au lieu de ~70% après flat tax personnelle)
- Les revenus d'investissement restent dans la holding à l'IS (15/25%)
- Vous ne payez la flat tax que quand vous vous distribuez de l'argent personnellement
- Les pertes d'investissement sont déductibles du résultat de la holding

## Le piège du CGA / OGA

Si votre société n'adhère pas à un Centre de Gestion Agréé, certains avantages fiscaux peuvent être perdus. En société à l'IS, ce n'est pas obligatoire mais l'adhésion reste recommandée pour la crédibilité auprès de l'administration.

## Les 5 règles d'or de l'investissement en société

1. **Ne jamais investir la trésorerie nécessaire au fonctionnement** — gardez toujours 3 à 6 mois de charges
2. **Documenter chaque investissement** — PV d'AG autorisant l'investissement
3. **Tenir une comptabilité rigoureuse** — chaque trade, chaque plus/moins-value
4. **Provisionner les impôts** — l'IS sur les PV sera dû même si vous ne distribuez pas
5. **Consulter votre expert-comptable** — la comptabilisation des crypto peut être complexe

---

**Sources officielles 2026 :**
- CGI - Art. 38 et 39 (bénéfices imposables des sociétés) : legifrance.gouv.fr
- CGI - Art. 150 VH bis (fiscalité des actifs numériques) : legifrance.gouv.fr
- BOFiP - Régime des plus-values sur titres de placement : bofip.impots.gouv.fr
- AMF - Réglementation PSAN (Prestataires de Services sur Actifs Numériques) : amf-france.org
- PCG révisé 2025 - Comptabilisation des actifs numériques : anc.gouv.fr
- DGFiP - IS taux réduit et taux normal : impots.gouv.fr`
  },

  'sci-holding-immobilier-freelance': {
    id: '20',
    title: 'SCI et holding freelance : investir dans l\'immobilier intelligemment',
    slug: 'sci-holding-immobilier-freelance',
    excerpt: 'Créer une SCI via votre holding pour investir dans l\'immobilier locatif : montage, fiscalité et stratégies 2026.',
    category: 'Optimisation',
    tags: ['SCI', 'Immobilier', 'Holding', 'Investissement', 'Fiscalité'],
    readTime: 12,
    date: '2026-03-08',
    author: 'Bozzam',
    popular: true,
    fullContent: `## SCI + Holding : le combo gagnant des freelances investisseurs

Vous êtes freelance, votre activité tourne bien, et vous souhaitez investir dans l'immobilier locatif. Plutôt que d'acheter en nom propre et payer le maximum d'impôts, le montage SCI + holding vous permet d'investir avec la trésorerie de votre entreprise tout en optimisant la fiscalité.

## Qu'est-ce qu'une SCI ?

La Société Civile Immobilière (SCI) est une société dont l'objet est la détention et la gestion de biens immobiliers. Elle permet de :
- Acheter de l'immobilier à plusieurs
- Séparer patrimoine personnel et immobilier
- Choisir entre IR et IS pour la fiscalité
- Faciliter la transmission du patrimoine

## Le montage holding + SCI : comment ça marche ?

Le schéma optimal pour un freelance :

1. **Votre SASU/EURL freelance** génère des bénéfices
2. **Votre holding** reçoit les dividendes (régime mère-fille : quasi-exonérés)
3. **La holding détient des parts de la SCI** (associée majoritaire)
4. **La SCI achète le bien immobilier** avec un emprunt bancaire + apport de la holding

### Pourquoi ne pas acheter directement via la holding ?

| Critère | Achat via holding directe | Achat via SCI filiale |
|---------|--------------------------|----------------------|
| Séparation des risques | Non — le bien est dans la holding | Oui — responsabilité limitée à la SCI |
| Revente du bien | Cession d'actif (plus complexe) | Cession de parts de SCI (plus simple) |
| Gestion multi-biens | Tout mélangé | Une SCI par bien possible |
| Emprunt bancaire | Plus difficile (objet social inadapté) | Plus facile (objet social immobilier) |
| Transmission | Parts de holding (inclut tout) | Parts de SCI séparées |

## SCI à l'IR ou à l'IS ?

C'est LA question clé. La réponse dépend de votre stratégie :

### SCI à l'IR (transparence fiscale)

Les revenus fonciers remontent directement aux associés (vous ou votre holding) et sont imposés à leur niveau.

**Avantages :**
- Plus-value immobilière des particuliers (abattement pour durée de détention)
- Exonération totale de PV après 22 ans (IR) et 30 ans (prélèvements sociaux)
- Déficit foncier imputable sur le revenu global (jusqu'à 10 700€/an)

**Inconvénients :**
- Pas d'amortissement du bien
- Revenus fonciers imposés à votre TMI + 17.2% de PS

### SCI à l'IS (opaque)

La SCI est imposée comme une société classique à l'IS.

**Avantages :**
- **Amortissement du bien** (hors terrain) → réduction du bénéfice imposable
- IS à 15% jusqu'à 42 500€ de bénéfice
- Charges déductibles plus larges (travaux, intérêts d'emprunt, frais de gestion)
- Trésorerie qui reste dans la SCI

**Inconvénients :**
- Plus-value professionnelle à la revente (sur la valeur amortie → forte imposition)
- Double imposition si distribution (IS + flat tax ou IS holding)

| Critère | SCI à l'IR | SCI à l'IS |
|---------|-----------|-----------|
| Amortissement | Non | Oui (2 à 3% par an hors terrain) |
| IS sur loyers | Non (imposé aux associés) | 15% puis 25% |
| Plus-value revente | PV particuliers (abattement durée) | PV professionnelle (sur valeur nette comptable) |
| Exonération PV | Oui, après 22/30 ans | Non |
| Idéal pour | Patrimoine long terme, revente | Cash-flow optimisé, conservation longue |

## Simulation : achat d'un appartement via SCI + holding

**Hypothèses :**
- Bien immobilier : 200 000€ (dont 40 000€ terrain)
- Loyer mensuel : 900€ (10 800€/an)
- Charges annuelles : 3 000€ (copro, taxe foncière, assurance)
- Emprunt : 180 000€ sur 20 ans à 3.5%
- Annuité emprunt : ~12 500€ (dont ~6 000€ d'intérêts la 1ère année)

### En SCI à l'IS (via holding)

| Poste | Montant |
|-------|---------|
| Loyers annuels | 10 800€ |
| - Charges | -3 000€ |
| - Intérêts emprunt | -6 000€ |
| - Amortissement (160 000€ sur 30 ans) | -5 333€ |
| **Résultat fiscal** | **-3 533€** |
| **IS à payer** | **0€** |

Pendant les premières années, grâce à l'amortissement et aux intérêts d'emprunt, la SCI à l'IS ne paie **aucun impôt** tout en percevant des loyers.

### En SCI à l'IR (en nom propre)

| Poste | Montant |
|-------|---------|
| Loyers annuels | 10 800€ |
| - Charges | -3 000€ |
| - Intérêts emprunt | -6 000€ |
| **Revenu foncier net** | **1 800€** |
| IR (TMI 30%) + PS (17.2%) | ~850€ |

Pas d'amortissement → vous payez de l'impôt dès la première année.

## Le financement : comment la banque voit le montage

Les banques sont habituées au montage SCI + holding. Voici ce qu'elles demandent :

1. **Apport** : généralement 10 à 20% du prix (venant de la holding)
2. **Bilans** de votre société opérationnelle et de la holding (2-3 derniers exercices)
3. **Caution personnelle** : souvent exigée en complément
4. **Business plan** locatif : rendement, taux d'occupation prévu

**Astuce** : avoir une holding avec de la trésorerie et des bilans sains facilite considérablement l'obtention du prêt.

## La transmission du patrimoine immobilier

Le montage SCI + holding facilite la transmission grâce à plusieurs mécanismes :

### Donation de parts de SCI

- Vous pouvez donner progressivement des parts de SCI à vos enfants
- Abattement de 100 000€ par parent et par enfant tous les 15 ans
- Décote possible de 10 à 20% sur la valeur des parts (manque de liquidité)

### Démembrement de parts

- Vous donnez la nue-propriété des parts à vos enfants
- Vous conservez l'usufruit (et les revenus)
- Au décès, les enfants récupèrent la pleine propriété **sans droits de succession**

| Âge du donateur | Valeur usufruit | Valeur nue-propriété |
|----------------|----------------|---------------------|
| Moins de 51 ans | 60% | 40% |
| 51 à 60 ans | 50% | 50% |
| 61 à 70 ans | 40% | 60% |
| 71 à 80 ans | 30% | 70% |

**Exemple** : à 45 ans, vous donnez la nue-propriété de parts de SCI valant 300 000€. La valeur taxable est de 40% × 300 000€ = 120 000€. Avec l'abattement de 100 000€, les droits portent sur seulement 20 000€.

## Les erreurs à éviter

1. **Mélanger usage personnel et locatif** dans la SCI (risque de requalification)
2. **Oublier les formalités** (AG annuelles, comptes, déclarations fiscales)
3. **Sous-capitaliser la SCI** (1 000€ minimum recommandé)
4. **Ne pas faire de convention de trésorerie** entre holding et SCI
5. **Choisir l'IS sans réfléchir à la revente** (la plus-value sera calculée sur la valeur nette comptable amortie)
6. **Acheter sa résidence principale via la SCI** (avantage fiscal limité, risque de requalification)

---

**Sources officielles 2026 :**
- CGI - Art. 8 (transparence fiscale SCI à l'IR) : legifrance.gouv.fr
- CGI - Art. 206 (SCI à l'IS) : legifrance.gouv.fr
- CGI - Art. 150 U et suivants (plus-values immobilières) : legifrance.gouv.fr
- CGI - Art. 669 (barème usufruit/nue-propriété) : legifrance.gouv.fr
- BOFiP - Amortissement des immeubles : bofip.impots.gouv.fr
- DGFiP - Droits de donation : impots.gouv.fr`
  },

  'optimisation-fiscale-avancee-2026': {
    id: '21',
    title: 'Optimisation fiscale avancée pour freelances : les stratégies 2026',
    slug: 'optimisation-fiscale-avancee-2026',
    excerpt: 'PER, Madelin, holding, management fees, épargne salariale : toutes les stratégies légales pour réduire votre impôt en 2026.',
    category: 'Optimisation',
    tags: ['Optimisation', 'Fiscalité', 'PER', 'Madelin', 'Holding'],
    readTime: 15,
    date: '2026-03-05',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Au-delà des basiques : les stratégies que votre comptable ne vous dit pas toujours

Si vous gagnez plus de 60 000€ nets par an en freelance, les optimisations de base (choix du statut, ACRE, frais déductibles) ne suffisent plus. Voici les stratégies avancées que les freelances les plus avisés utilisent en 2026 pour réduire légalement leur charge fiscale.

## Stratégie 1 : Le PER (Plan d'Épargne Retraite)

Le PER est l'outil d'optimisation fiscale le plus accessible et le plus efficace pour un freelance.

**Principe** : vous versez de l'argent sur un PER → le montant est déduit de votre revenu imposable → vous économisez immédiatement de l'impôt.

### Plafonds de déduction 2026

| Statut | Plafond de déduction |
|--------|---------------------|
| TNS (EURL/micro) | 10% du bénéfice + 15% du bénéfice entre 1 et 8 PASS = jusqu'à ~81 384€ |
| Assimilé salarié (SASU) | 10% du salaire net imposable (plafond ~37 094€) |
| Cumul des 3 dernières années | Possible si non utilisé les années précédentes |

### Simulation PER

| TMI | Versement PER | Économie d'impôt immédiate | Coût réel |
|-----|--------------|---------------------------|-----------|
| 30% | 10 000€ | 3 000€ | 7 000€ |
| 30% | 20 000€ | 6 000€ | 14 000€ |
| 41% | 10 000€ | 4 100€ | 5 900€ |
| 41% | 30 000€ | 12 300€ | 17 700€ |

**Astuce** : si vous êtes en TNS (EURL) avec un bénéfice élevé, le plafond PER peut atteindre 81 384€/an. C'est un avantage majeur de l'EURL sur la SASU.

## Stratégie 2 : Le contrat Madelin (complémentaire santé et prévoyance)

En tant que TNS (gérant EURL, micro-entrepreneur), les cotisations à un contrat Madelin sont déductibles du bénéfice imposable.

| Type de contrat | Plafond déduction 2026 |
|----------------|----------------------|
| Santé (mutuelle) | 3.75% du bénéfice + 7% du PASS = ~7 009€ |
| Prévoyance (incapacité, invalidité) | 3.75% du bénéfice + 7% du PASS |
| Retraite Madelin | Remplacé par le PER depuis 2019 (contrats existants maintenus) |

**En SASU** : pas de Madelin, mais vous pouvez souscrire une mutuelle d'entreprise (déductible comme charge de la société).

## Stratégie 3 : L'épargne salariale en SASU

Peu de freelances le savent, mais un président de SASU **seul** peut mettre en place de l'épargne salariale :

### PEE (Plan d'Épargne Entreprise)
- Abondement de la société jusqu'à **3 709€/an** (brut)
- Exonéré d'IR pour le bénéficiaire
- Soumis uniquement à la CSG/CRDS (9.7%)
- Fonds bloqués 5 ans (sauf cas de déblocage anticipé)

### PERCO / PERE-CO (Épargne retraite collective)
- Abondement jusqu'à **7 418€/an**
- Exonéré d'IR
- Bloqué jusqu'à la retraite
- Cumulable avec le PEE

| Dispositif | Abondement max | Fiscalité | Disponibilité |
|-----------|---------------|-----------|---------------|
| PEE | 3 709€/an | Exonéré d'IR, CSG/CRDS | 5 ans |
| PERE-CO | 7 418€/an | Exonéré d'IR, CSG/CRDS | Retraite |
| **Total possible** | **11 127€/an** | **Net d'IR** | — |

**Économie réelle** : si votre TMI est à 30%, ces 11 127€ vous coûteraient ~7 789€ de plus en impôt si distribués en salaire ou dividendes.

## Stratégie 4 : Les management fees (holding)

Si vous avez une holding, elle peut facturer des prestations de management à votre société opérationnelle :

**Prestations facturables :**
- Direction stratégique et pilotage
- Services administratifs (comptabilité, juridique)
- Mise à disposition de locaux
- Recherche de missions / développement commercial

**Avantages :**
- Les management fees sont déductibles de la filiale (réduction du bénéfice imposable)
- Ils alimentent la holding en trésorerie (sans passer par des dividendes)
- Pas de flat tax (c'est du CA pour la holding)

**Attention** : les management fees doivent être justifiés par des prestations réelles, documentées, et facturées à prix de marché. Un montant excessif ou injustifié sera requalifié par l'administration fiscale.

| Poste | Montant raisonnable (fourchette) |
|-------|--------------------------------|
| Direction générale | 5 à 15% du CA de la filiale |
| Services administratifs | 2 à 5% du CA |
| Mise à disposition locaux | Loyer marché |
| **Total recommandé** | **10 à 20% du CA maximum** |

## Stratégie 5 : Le crédit d'impôt formation du dirigeant

Le saviez-vous ? En tant que dirigeant, vous bénéficiez d'un crédit d'impôt formation :

- **Montant** : nombre d'heures de formation × SMIC horaire (11.88€ en 2026)
- **Plafond** : 40 heures/an = ~475€
- **Doublé pour les TPE** (< 10 salariés) : ~950€
- **Cumulable** avec les charges de formation déductibles

Ce n'est pas énorme, mais c'est du crédit d'impôt (= argent remboursé), pas juste une déduction.

## Stratégie 6 : L'optimisation salaire / dividendes en SASU

La répartition optimale entre salaire et dividendes dépend de votre situation :

### Règle de base
- Salaire : meilleure protection sociale, cotisations élevées (~80% du net en charges)
- Dividendes : flat tax 30%, pas de cotisations sociales (en SASU)

### Le point d'équilibre

| Bénéfice société | Stratégie optimale | Explication |
|-----------------|-------------------|-------------|
| < 40 000€ | 100% salaire | Maximiser la protection sociale, remplir les tranches basses |
| 40 000€ - 80 000€ | Salaire ~3 000€ net/mois + dividendes | Équilibre cotisations/flat tax |
| 80 000€ - 150 000€ | Salaire ~4 000€ net/mois + dividendes | Flat tax plus avantageuse sur le surplus |
| > 150 000€ | Salaire minimum viable + dividendes + holding | Structure holding pour réinvestir |

### Cas spécial : option barème progressif sur les dividendes

Si vous avez un quotient familial élevé (marié + enfants), opter pour le barème progressif sur les dividendes peut être avantageux :
- Abattement de 40% sur les dividendes bruts
- Imposition au barème après abattement
- Intéressant si TMI ≤ 11% (rare avec des dividendes importants)

## Stratégie 7 : Les chèques-vacances et CESU

En SASU, même seul, vous pouvez bénéficier :

### Chèques-vacances
- Contribution de la société : jusqu'à ~530€/an par bénéficiaire
- Exonérée de charges sociales (sous conditions)
- Déductible du résultat de la société

### CESU préfinancé (services à la personne)
- Aide de la société : jusqu'à 2 421€/an
- Exonérée de charges sociales
- Crédit d'impôt de 25% pour la société (plafonné à 500 000€)

| Avantage | Montant max | Charges sociales | Impact fiscal |
|----------|-----------|-----------------|--------------|
| Chèques-vacances | ~530€/an | Exonéré | Charge déductible |
| CESU préfinancé | 2 421€/an | Exonéré | Crédit d'impôt 25% |
| **Total** | **~2 951€/an** | **0€** | **Double avantage** |

## Récapitulatif : le plan d'optimisation complet

| Stratégie | Économie annuelle estimée | Complexité |
|-----------|--------------------------|------------|
| PER (versement 15 000€, TMI 30%) | 4 500€ | Faible |
| Madelin santé/prévoyance (TNS) | 1 500-3 000€ | Faible |
| Épargne salariale PEE+PERE-CO (SASU) | 3 000-4 000€ | Moyenne |
| Holding + management fees | 5 000-15 000€ | Élevée |
| Optimisation salaire/dividendes | 2 000-8 000€ | Moyenne |
| Crédit formation dirigeant | 475-950€ | Faible |
| Chèques-vacances + CESU | 1 000-1 500€ | Faible |
| **Total potentiel** | **17 475€ - 35 950€/an** | — |

## Les limites à ne pas franchir

1. **Abus de droit** : toute opération sans justification économique réelle peut être requalifiée
2. **Acte anormal de gestion** : facturer des prestations fictives ou à prix excessif
3. **Confusion de patrimoine** : mélanger comptes personnels et professionnels
4. **Fraude fiscale** : ne pas déclarer des revenus, créer de fausses charges
5. **Sous-rémunération** : ne pas se verser un salaire "raisonnable" peut être requalifié

**La règle d'or** : chaque optimisation doit avoir une réalité économique et être correctement documentée.

---

**Sources officielles 2026 :**
- CGI - Art. 154 bis (déduction PER/Madelin TNS) : legifrance.gouv.fr
- Code du travail - Art. L3332-1 et suivants (PEE) : legifrance.gouv.fr
- CGI - Art. 244 quater M (crédit impôt formation dirigeant) : legifrance.gouv.fr
- URSSAF - Épargne salariale et exonérations : urssaf.fr
- DGFiP - PER et déductions fiscales : impots.gouv.fr
- BOFiP - Management fees et prix de transfert : bofip.impots.gouv.fr
- CGI - Art. 200 A (PFU / option barème progressif) : legifrance.gouv.fr`
  },

  'cdi-vs-portage-salarial': {
    id: '22',
    title: 'CDI vs Portage salarial : le comparatif complet 2026',
    slug: 'cdi-vs-portage-salarial',
    excerpt: 'Salaire, protection sociale, liberté, retraite : faut-il quitter son CDI pour le portage salarial ?',
    category: 'Statuts',
    tags: ['CDI', 'Portage salarial', 'Comparatif'],
    readTime: 11,
    date: '2026-03-20',
    author: 'Bozzam',
    popular: true,
    fullContent: `## CDI vs Portage salarial : deux mondes, un seul bulletin de paie

Le portage salarial séduit de plus en plus de cadres et consultants. Vous gardez le statut de salarié (bulletin de paie, chômage, retraite) tout en choisissant vos missions comme un freelance. Mais est-ce vraiment mieux qu'un CDI classique ?

## Comment fonctionne le portage salarial ?

Le portage salarial repose sur une relation tripartite :

1. **Vous** (le consultant porté) trouvez vos missions et négociez vos tarifs
2. **La société de portage** vous emploie en CDI ou CDD, gère la facturation et l'administratif
3. **Le client** vous confie la mission et paie la société de portage

La société de portage prélève des frais de gestion (entre 5% et 12% du CA HT), puis reverse le reste sous forme de salaire après déduction des charges.

## Comparatif chiffré : CDI vs Portage

Prenons l'exemple d'un consultant avec un TJM de 500€ en portage vs un salaire brut de 55 000€/an en CDI.

| Critère | CDI classique | Portage salarial |
|---------|--------------|-----------------|
| Revenu brut annuel | 55 000€ (fixe) | ~110 000€ CA (220 jours) |
| Frais de gestion (8%) | — | -8 800€ |
| CA net après gestion | — | 101 200€ |
| Charges patronales (~42%) | Payées par l'employeur | ~42 504€ |
| Charges salariales (~22%) | ~12 100€ | ~12 892€ |
| **Salaire net annuel** | **~42 900€** | **~45 804€** |
| Salaire net mensuel | ~3 575€ | ~3 817€ (si 220 jours facturés) |

**Attention** : en portage, vous n'êtes payé que si vous avez des missions. Pas de mission = pas de salaire.

## Protection sociale : match nul ?

| Protection | CDI | Portage salarial |
|-----------|-----|-----------------|
| Sécurité sociale | Régime général | Régime général |
| Mutuelle d'entreprise | Obligatoire (employeur) | Proposée par la société de portage |
| Prévoyance | Selon convention collective | Obligatoire (convention portage) |
| Retraite de base | Oui (CNAV) | Oui (CNAV) |
| Retraite complémentaire | AGIRC-ARRCO | AGIRC-ARRCO |
| Chômage | Oui | Oui (sous conditions) |
| Formation (CPF) | Oui | Oui |
| Congés payés | 25 jours | Provision ~10% du brut |

**Le portage offre la même protection qu'un CDI**, c'est son principal avantage par rapport au statut de freelance pur (micro, SASU, EURL).

## Le droit au chômage : la différence clé

**En CDI** : si vous êtes licencié ou en rupture conventionnelle, vous avez droit aux ARE. Démission = pas de chômage (sauf démission pour reconversion).

**En portage** : vous cotisez au chômage. Si votre mission se termine et que la société de portage met fin à votre contrat, vous pouvez toucher les ARE. C'est un filet de sécurité que n'ont pas les freelances en société.

## Quand choisir le portage plutôt que le CDI ?

| Profil | CDI recommandé | Portage recommandé |
|--------|---------------|-------------------|
| Besoin de stabilité absolue | Oui | Non |
| TJM potentiel > 400€ | — | Oui |
| Réseau professionnel solide | — | Oui |
| Aversion au risque élevée | Oui | — |
| Envie de choisir ses missions | Non | Oui |
| Transition vers le freelance | — | Oui (test avant de se lancer) |
| Prêt immobilier en cours | Oui (stabilité CDI) | Possible (CDI portage) |

## Les limites du portage salarial

1. **Les frais de gestion** (5-12%) réduisent votre revenu net
2. **Pas de déduction de frais** aussi large qu'en société (limité aux frais de mission)
3. **TJM minimum** : la convention collective impose un salaire minimum de 2 517€ brut/mois (soit ~300€/jour minimum)
4. **Pas de capitalisation** : vous ne pouvez pas constituer de trésorerie en société
5. **Dépendance** : vous dépendez de la société de portage pour la facturation et les paiements

## Portage vs SASU : lequel choisir ?

| Critère | Portage salarial | SASU |
|---------|-----------------|------|
| Bulletin de paie | Oui | Oui |
| Frais de gestion | 5-12% du CA | ~300-500€/an (comptable) |
| Chômage | Oui | Non (sauf ATI) |
| Déduction de frais | Limitée | Large (tous frais professionnels) |
| Trésorerie société | Non | Oui |
| Dividendes | Non | Oui (flat tax 30%) |
| Complexité administrative | Zéro | Moyenne (comptable obligatoire) |

**Conclusion** : le portage est idéal pour tester le freelance ou si vous tenez au filet de sécurité du chômage. La SASU est plus rentable à long terme si votre activité est stable.

---

**Sources officielles 2026 :**
- Convention collective du portage salarial (branche) : legifrance.gouv.fr
- Code du travail - Art. L1254-1 à L1254-31 (portage salarial) : legifrance.gouv.fr
- URSSAF - Cotisations portage salarial : urssaf.fr
- France Travail - ARE et portage salarial : francetravail.org
- Ministère du Travail - Portage salarial : travail-emploi.gouv.fr`
  },

  'seuils-depassement-micro-entreprise': {
    id: '23',
    title: 'Seuils micro-entreprise : que se passe-t-il quand on dépasse ?',
    slug: 'seuils-depassement-micro-entreprise',
    excerpt: 'Seuil de tolérance, franchise TVA, passage en société : tout ce qui change quand votre CA explose.',
    category: 'Fiscalité',
    tags: ['Micro-entreprise', 'Seuils', 'TVA'],
    readTime: 10,
    date: '2026-03-19',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Les 4 seuils que tout micro-entrepreneur doit connaître

En micro-entreprise, votre chiffre d'affaires est plafonné. Mais contrairement à ce que beaucoup pensent, il n'y a pas un seul seuil mais quatre, chacun avec des conséquences différentes.

## Seuil 1 : Le plafond de CA du régime micro (2026)

| Activité | Plafond annuel |
|----------|---------------|
| Vente de marchandises (BIC) | 188 700€ |
| Prestations de services (BIC) | 77 700€ |
| Professions libérales (BNC) | 77 700€ |

### La règle du seuil de tolérance

Vous ne perdez pas immédiatement le régime micro si vous dépassez le plafond une seule année. Le basculement se fait :
- **Dépassement 1 an** : vous restez en micro l'année suivante (seuil de tolérance)
- **Dépassement 2 ans consécutifs** : passage obligatoire au régime réel à partir du 1er janvier de l'année suivante

**Exemple** : vous êtes en BNC et facturez 85 000€ en 2025 et 90 000€ en 2026. Vous basculez au régime réel le 1er janvier 2027.

## Seuil 2 : La franchise en base de TVA (2026)

| Activité | Seuil de base | Seuil majoré |
|----------|--------------|-------------|
| Prestations de services | 36 800€ | 39 100€ |
| Vente de marchandises | 91 900€ | 101 000€ |

### Comment ça fonctionne ?

1. **En dessous du seuil de base** : pas de TVA à facturer ni à déclarer
2. **Entre le seuil de base et le seuil majoré** : TVA exigible à partir du 1er jour du mois de dépassement
3. **Au-dessus du seuil majoré** : TVA exigible immédiatement, rétroactivement au 1er jour du mois de dépassement

**Attention nouveau 2026** : depuis le 1er janvier 2026, le seuil unique européen de 25 000€ est entré en vigueur pour les livraisons de biens. Vérifiez si votre activité est concernée.

| Situation | Ce qui se passe |
|-----------|----------------|
| CA < 36 800€ (services) | Pas de TVA, mention "TVA non applicable - art. 293B du CGI" |
| CA entre 36 800€ et 39 100€ | TVA dès le mois de dépassement |
| CA > 39 100€ | TVA immédiate + régularisation possible |

## Seuil 3 : Le versement libératoire de l'IR

Pour bénéficier du versement libératoire (payer l'IR en même temps que les charges), votre revenu fiscal de référence N-2 ne doit pas dépasser :

- **27 478€ par part de quotient familial** (seuil 2026, basé sur revenus 2024)

Si vous dépassez ce seuil, vous perdez le droit au versement libératoire l'année suivante et devez déclarer vos revenus au barème progressif classique.

## Seuil 4 : La CFE (Cotisation Foncière des Entreprises)

La CFE est due par tous les micro-entrepreneurs, sauf :
- **Première année d'activité** : exonération totale
- **CA < 5 000€** : exonération totale

| CA annuel | CFE estimée (varie par commune) |
|-----------|-------------------------------|
| < 5 000€ | 0€ (exonéré) |
| 5 000€ à 10 000€ | 200€ à 500€ |
| 10 000€ à 32 600€ | 200€ à 1 000€ |
| 32 600€ à 100 000€ | 200€ à 2 000€ |

## Que faire quand on approche des seuils ?

### Option 1 : Rester en micro et surveiller

Si votre dépassement est ponctuel (un gros contrat exceptionnel), le seuil de tolérance vous protège. Continuez en micro.

### Option 2 : Passer en société

Si votre CA dépasse régulièrement les plafonds, il est temps de créer une SASU ou EURL :

| Critère | Micro-entreprise | SASU/EURL |
|---------|-----------------|-----------|
| Plafond de CA | 77 700€ ou 188 700€ | Aucun |
| Déduction des charges réelles | Non (abattement forfaitaire) | Oui |
| TVA | Franchise possible | Récupération possible |
| Crédibilité clients | Limitée | Forte |
| Optimisation fiscale | Très limitée | Large (salaire, dividendes, PER) |

### Le moment optimal pour basculer

La règle empirique : **quand vos charges réelles dépassent l'abattement forfaitaire** (34% en BNC, 50% en BIC services, 71% en BIC vente), la société devient plus avantageuse.

**Exemple BNC** : si vos charges réelles sont de 20 000€ sur 70 000€ de CA (soit 28.5%), restez en micro (abattement 34% = 23 800€ de charges fictives). Si vos charges réelles passent à 30 000€ (43%), la société est plus avantageuse.

---

**Sources officielles 2026 :**
- CGI - Art. 50-0 et 102 ter (seuils micro-entreprise) : legifrance.gouv.fr
- CGI - Art. 293 B (franchise en base de TVA) : legifrance.gouv.fr
- CGI - Art. 151-0 (versement libératoire IR) : legifrance.gouv.fr
- BOFiP - Seuils et tolérance micro-BIC/BNC : bofip.impots.gouv.fr
- DGFiP - CFE et exonérations : impots.gouv.fr
- Directive UE 2020/285 - Seuil TVA européen : eur-lex.europa.eu`
  },

  'micro-entreprise-guide-complet-2026': {
    id: '24',
    title: 'Micro-entreprise : le guide complet du statut en 2026',
    slug: 'micro-entreprise-guide-complet-2026',
    excerpt: 'Création, charges, plafonds, CFE, TVA, déclarations : tout savoir sur la micro-entreprise en 2026.',
    category: 'Guides',
    tags: ['Micro-entreprise', 'Création', 'Guide'],
    readTime: 14,
    date: '2026-03-18',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Micro-entreprise en 2026 : le statut préféré des freelances débutants

La micro-entreprise (ex auto-entreprise) reste le statut le plus simple et le plus rapide pour lancer une activité indépendante. Voici tout ce que vous devez savoir pour 2026.

## Création : 15 minutes et 0€

La création est gratuite et se fait en ligne :

1. Rendez-vous sur **formalites.entreprises.gouv.fr** (Guichet unique INPI)
2. Remplissez le formulaire de déclaration d'activité
3. Choisissez votre activité (code APE automatique)
4. Recevez votre numéro SIRET sous 1 à 4 semaines

**Documents nécessaires** : pièce d'identité, justificatif de domicile, déclaration de non-condamnation.

## Les charges sociales 2026

| Type d'activité | Taux URSSAF normal | Taux ACRE (1ère année) |
|----------------|-------------------|----------------------|
| Vente de marchandises (BIC) | 12.3% | 6.2% |
| Prestations BIC | 21.2% | 10.6% |
| Prestations BNC (hors CIPAV) | 23.1% | 11.6% |
| Professions libérales CIPAV | 23.2% | 11.6% |

### Contribution à la formation professionnelle (CFP)

En plus des charges URSSAF :
- BIC commerce : 0.1% du CA
- BIC services et BNC : 0.2% du CA
- Artisans : 0.3% du CA

## L'impôt sur le revenu : deux options

### Option 1 : Barème progressif classique

Votre CA est déclaré dans votre déclaration de revenus après un abattement forfaitaire :
- **71%** pour la vente de marchandises
- **50%** pour les prestations BIC
- **34%** pour les prestations BNC

**Exemple** : 60 000€ de CA en BNC → 60 000€ - 34% = 39 600€ de revenu imposable.

### Option 2 : Versement libératoire

Vous payez l'IR en même temps que les charges URSSAF :

| Activité | Taux versement libératoire |
|----------|---------------------------|
| Vente de marchandises | 1.0% |
| Prestations BIC | 1.7% |
| Prestations BNC | 2.2% |

**Condition** : revenu fiscal de référence N-2 < 27 478€ par part de quotient familial.

**Exemple** : 60 000€ de CA en BNC → VFL = 2.2% × 60 000€ = 1 320€ d'IR. Comparé au barème classique, c'est souvent plus avantageux dès que votre CA est élevé.

## La TVA en micro-entreprise

### Franchise en base de TVA

En dessous des seuils, vous ne facturez pas de TVA :

| Activité | Seuil franchise | Seuil majoré |
|----------|----------------|-------------|
| Services | 36 800€ | 39 100€ |
| Vente | 91 900€ | 101 000€ |

**Mention obligatoire sur vos factures** : "TVA non applicable, art. 293 B du CGI"

### Inconvénient : pas de récupération de TVA

Si vous achetez du matériel (ordinateur, logiciels, mobilier), vous payez la TVA sans pouvoir la récupérer. C'est un coût réel de 20% sur tous vos achats professionnels.

## Les déclarations obligatoires

| Déclaration | Fréquence | Où ? |
|-------------|-----------|------|
| CA URSSAF | Mensuelle ou trimestrielle | autoentrepreneur.urssaf.fr |
| Impôt sur le revenu | Annuelle (mai) | impots.gouv.fr |
| CFE | Annuelle (décembre) | impots.gouv.fr |
| TVA (si assujetti) | Mensuelle ou trimestrielle | impots.gouv.fr |

**Astuce** : même si votre CA est nul, vous devez déclarer 0€ à l'URSSAF. L'absence de déclaration entraîne une pénalité forfaitaire.

## Les avantages de la micro-entreprise

1. **Simplicité** : pas de comptabilité complexe, pas de bilan
2. **Charges prévisibles** : pourcentage fixe du CA
3. **Pas de CA = pas de charges** : idéal pour démarrer
4. **Cumul possible** : avec un CDI, la retraite, les études
5. **ACRE** : réduction de 50% des charges la première année

## Les limites

1. **Plafond de CA** : 77 700€ (services) ou 188 700€ (vente)
2. **Pas de déduction de charges réelles** : l'abattement forfaitaire peut être défavorable
3. **Pas de récupération de TVA** (sous franchise)
4. **Crédibilité limitée** : certains grands comptes refusent de travailler avec des micro-entrepreneurs
5. **Pas d'optimisation fiscale** : pas de PER déductible du bénéfice, pas de dividendes

---

**Sources officielles 2026 :**
- Guichet unique INPI - Création : formalites.entreprises.gouv.fr
- URSSAF - Taux de cotisations micro : autoentrepreneur.urssaf.fr
- CGI - Art. 50-0 (régime micro-BIC) : legifrance.gouv.fr
- CGI - Art. 102 ter (régime micro-BNC) : legifrance.gouv.fr
- CGI - Art. 151-0 (versement libératoire) : legifrance.gouv.fr
- DGFiP - Obligations déclaratives : impots.gouv.fr`
  },

  'sasu-is-vs-ir-regime-fiscal': {
    id: '25',
    title: 'SASU à l\'IS vs IR : quel régime fiscal choisir ?',
    slug: 'sasu-is-vs-ir-regime-fiscal',
    excerpt: 'IS ou IR en SASU ? Le choix a un impact majeur sur vos impôts, dividendes et charges.',
    category: 'Fiscalité',
    tags: ['SASU', 'IS', 'IR', 'Fiscalité'],
    readTime: 12,
    date: '2026-03-17',
    author: 'Bozzam',
    popular: true,
    fullContent: `## SASU à l'IS vs IR : un choix qui peut vous coûter (ou vous faire gagner) des milliers d'euros

Par défaut, une SASU est soumise à l'IS (Impôt sur les Sociétés). Mais vous pouvez opter pour l'IR (Impôt sur le Revenu) pendant les 5 premières années. Ce choix est stratégique et dépend entièrement de votre situation.

## Comment fonctionne la SASU à l'IS ?

1. La société paie l'IS sur son bénéfice
2. Vous vous versez un salaire (charges patronales + salariales)
3. Vous pouvez distribuer des dividendes (flat tax 30%)
4. La trésorerie reste dans la société

**Taux d'IS 2026 :**

| Bénéfice | Taux IS |
|----------|---------|
| 0 à 42 500€ | 15% |
| Au-delà de 42 500€ | 25% |

Condition pour le taux réduit : CA < 10M€ et capital détenu à 75%+ par des personnes physiques.

## Comment fonctionne la SASU à l'IR ?

1. Le bénéfice de la société est directement imposé à votre nom au barème progressif
2. Pas de distinction salaire / dividendes
3. L'intégralité du bénéfice est soumise aux cotisations sociales
4. Pas de trésorerie "protégée" dans la société

**Barème IR 2026 :**

| Tranche de revenu | Taux |
|-------------------|------|
| 0 à 11 497€ | 0% |
| 11 497€ à 29 315€ | 11% |
| 29 315€ à 83 823€ | 30% |
| 83 823€ à 180 294€ | 41% |
| Au-delà de 180 294€ | 45% |

## Simulation comparative : CA 100 000€, charges 15 000€

### SASU à l'IS

| Poste | Montant |
|-------|---------|
| CA | 100 000€ |
| Charges déductibles | -15 000€ |
| Salaire brut annuel (3 500€ net/mois) | -42 000€ |
| Charges patronales (~42%) | -17 640€ |
| Bénéfice avant IS | 25 360€ |
| IS (15%) | -3 804€ |
| Bénéfice après IS | 21 556€ |
| Dividendes distribués | 21 556€ |
| Flat tax (30%) | -6 467€ |
| **Revenu net total** | **~57 089€** |

### SASU à l'IR (célibataire sans enfant)

| Poste | Montant |
|-------|---------|
| CA | 100 000€ |
| Charges déductibles | -15 000€ |
| Bénéfice = revenu imposable | 85 000€ |
| Cotisations sociales (~45%) | -38 250€ |
| Revenu après cotisations | 46 750€ |
| IR (barème progressif, 1 part) | ~8 947€ |
| **Revenu net total** | **~37 803€** |

**Résultat : l'IS est nettement plus avantageux** dans ce cas (57 089€ vs 37 803€).

## Quand l'IR est-il intéressant en SASU ?

L'option IR peut être avantageuse dans des cas très spécifiques :

| Situation | IS ou IR ? |
|-----------|-----------|
| Déficit d'exploitation (1ères années) | IR — le déficit s'impute sur vos autres revenus |
| Revenus très faibles (< 15 000€ de bénéfice) | IR — TMI à 0% ou 11% |
| Investissements lourds la 1ère année | IR — déduction immédiate des charges |
| Bénéfices élevés et stables | IS — optimisation salaire/dividendes |
| Projet de holding future | IS — indispensable pour le régime mère-fille |

## Les pièges de l'option IR en SASU

1. **Option limitée à 5 ans** : après 5 exercices, retour obligatoire à l'IS
2. **Tout le bénéfice est imposé** même si vous ne le retirez pas de la société
3. **Cotisations sociales sur la totalité** du bénéfice (pas de split salaire/dividendes)
4. **Irrévocable** pendant la durée de l'option (sauf passage à l'IS anticipé)
5. **Pas de taux réduit** : le barème progressif peut être très élevé (41% ou 45%)

## Notre recommandation

**Dans 90% des cas, la SASU à l'IS est le meilleur choix.** L'option IR ne se justifie que si vous prévoyez des pertes les premières années ou si votre bénéfice est très faible.

---

**Sources officielles 2026 :**
- CGI - Art. 206 (IS) et Art. 239 bis AB (option IR) : legifrance.gouv.fr
- CGI - Art. 219 (taux d'IS) : legifrance.gouv.fr
- CGI - Art. 197 (barème IR 2026) : legifrance.gouv.fr
- BOFiP - Option IR pour les sociétés : bofip.impots.gouv.fr
- DGFiP - IS taux réduit conditions : impots.gouv.fr`
  },

  'eurl-is-ou-ir-comparatif': {
    id: '26',
    title: 'EURL : IS ou IR ? Le comparatif fiscal 2026',
    slug: 'eurl-is-ou-ir-comparatif',
    excerpt: 'Gérant majoritaire EURL : faut-il opter pour l\'IS ou rester à l\'IR ?',
    category: 'Fiscalité',
    tags: ['EURL', 'IS', 'IR', 'TNS'],
    readTime: 11,
    date: '2026-03-16',
    author: 'Bozzam',
    popular: true,
    fullContent: `## EURL : le seul statut qui vous laisse vraiment le choix

Contrairement à la SASU (IS par défaut), l'EURL est par défaut à l'IR (transparence fiscale). Vous pouvez ensuite opter pour l'IS de manière irrévocable. Ce choix est fondamental.

## EURL à l'IR : comment ça fonctionne ?

Le bénéfice de l'EURL est directement imposé à votre nom :

1. Le bénéfice est déterminé selon les règles BIC ou BNC
2. Il est ajouté à vos autres revenus du foyer fiscal
3. Il est soumis au barème progressif de l'IR
4. Les cotisations TNS sont calculées sur ce bénéfice

**Avantage clé** : vous pouvez déduire vos charges réelles (contrairement à la micro-entreprise).

## EURL à l'IS : comment ça fonctionne ?

1. La société paie l'IS sur son bénéfice (15% puis 25%)
2. Vous vous versez une rémunération de gérant (TNS)
3. Vous pouvez distribuer des dividendes (mais attention aux cotisations TNS sur la part > 10% du capital)

### La particularité EURL : dividendes et cotisations TNS

En EURL à l'IS, les dividendes dépassant 10% du capital social + primes d'émission + comptes courants d'associés sont soumis aux cotisations sociales TNS (~40%) au lieu de la flat tax.

| Part des dividendes | Traitement fiscal |
|--------------------|------------------|
| ≤ 10% du capital + primes + CCA | Flat tax 30% (ou barème + 17.2% PS) |
| > 10% du capital + primes + CCA | Cotisations TNS (~40%) + IR barème |

**Exemple** : capital EURL de 1 000€, pas de CCA. Si vous distribuez 30 000€ de dividendes, seuls 100€ (10% × 1 000€) sont à la flat tax. Les 29 900€ restants sont soumis aux cotisations TNS.

**Astuce** : augmenter le capital social ou alimenter le compte courant d'associé augmente le seuil de 10%.

## Simulation : CA 80 000€, charges 10 000€

### EURL à l'IR (célibataire)

| Poste | Montant |
|-------|---------|
| CA | 80 000€ |
| Charges déductibles | -10 000€ |
| Bénéfice | 70 000€ |
| Cotisations TNS (~40%) | -28 000€ |
| Revenu net avant IR | 42 000€ |
| IR (barème, 1 part) | ~5 847€ |
| **Revenu net total** | **~36 153€** |

### EURL à l'IS (rémunération 3 000€/mois)

| Poste | Montant |
|-------|---------|
| CA | 80 000€ |
| Charges déductibles | -10 000€ |
| Rémunération gérant | -36 000€ |
| Cotisations TNS (~40%) | -14 400€ |
| Bénéfice avant IS | 19 600€ |
| IS (15%) | -2 940€ |
| Bénéfice après IS | 16 660€ |
| Dividendes (majorité > 10% capital) | 16 660€ |
| Cotisations TNS sur dividendes (~40%) | -6 664€ |
| **Revenu net total** | **~39 256€** |

L'IS est légèrement plus avantageux, mais la différence est moins marquée qu'en SASU à cause des cotisations TNS sur les dividendes.

## Quand choisir l'IR ? Quand choisir l'IS ?

| Situation | IR recommandé | IS recommandé |
|-----------|--------------|--------------|
| Bénéfice < 30 000€ | Oui (TMI faible) | Non |
| Bénéfice 30 000€ - 60 000€ | À calculer selon situation familiale | À calculer |
| Bénéfice > 60 000€ | Non | Oui |
| Déficit prévu (lancement) | Oui (imputation sur autres revenus) | Non |
| Projet d'investissement / holding | Non | Oui |
| Conjoint avec gros revenus | Non (TMI élevée) | Oui |

---

**Sources officielles 2026 :**
- CGI - Art. 8 (EURL à l'IR, transparence fiscale) : legifrance.gouv.fr
- CGI - Art. 206 (option IS EURL) : legifrance.gouv.fr
- CSS - Art. L131-6 (assiette cotisations TNS) : legifrance.gouv.fr
- CGI - Art. 62 (rémunération gérant majoritaire) : legifrance.gouv.fr
- BOFiP - Dividendes EURL et cotisations sociales : bofip.impots.gouv.fr`
  },

  'bareme-progressif-vs-flat-tax': {
    id: '27',
    title: 'Barème progressif vs flat tax : quand opter pour le barème ?',
    slug: 'bareme-progressif-vs-flat-tax',
    excerpt: 'Flat tax 30% ou barème progressif avec abattement 40% ? Calculs 2026.',
    category: 'Fiscalité',
    tags: ['Flat tax', 'Barème', 'Dividendes', 'IR'],
    readTime: 10,
    date: '2026-03-15',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Flat tax vs barème progressif : ne laissez pas de l'argent sur la table

Depuis 2018, les revenus du capital (dividendes, plus-values, intérêts) sont soumis au PFU (Prélèvement Forfaitaire Unique) de 30%, dit "flat tax". Mais vous pouvez opter pour le barème progressif de l'IR. Dans certains cas, c'est bien plus avantageux.

## Comment fonctionne la flat tax ?

Le PFU de 30% se décompose en :
- **12.8%** d'impôt sur le revenu
- **17.2%** de prélèvements sociaux (CSG-CRDS)

C'est simple, forfaitaire, et ne dépend pas de vos autres revenus.

## Comment fonctionne l'option barème progressif ?

Si vous optez pour le barème :
1. Les dividendes bénéficient d'un **abattement de 40%** avant imposition
2. Le montant après abattement est ajouté à vos autres revenus
3. L'ensemble est soumis au barème progressif de l'IR
4. Les prélèvements sociaux restent à 17.2% (sur le montant brut)
5. Une partie de la CSG (6.8%) est déductible du revenu imposable

**Attention** : l'option barème s'applique à TOUS vos revenus du capital (dividendes + plus-values + intérêts). Vous ne pouvez pas choisir le barème pour les dividendes et la flat tax pour les plus-values.

## La comparaison chiffrée

### Exemple : 30 000€ de dividendes bruts

**Flat tax :**
- IR : 30 000€ × 12.8% = 3 840€
- PS : 30 000€ × 17.2% = 5 160€
- **Total : 9 000€** (30%)

**Barème progressif (TMI 11%) :**
- Abattement 40% : 30 000€ × 60% = 18 000€ imposable
- CSG déductible : 30 000€ × 6.8% = 2 040€
- Base imposable : 18 000€ - 2 040€ = 15 960€
- IR (TMI 11%) : ~1 756€
- PS : 30 000€ × 17.2% = 5 160€
- **Total : ~6 916€** (23%)

**Économie avec le barème : 2 084€ !**

## Le tableau de décision selon votre TMI

| TMI | Taux effectif flat tax | Taux effectif barème (avec abattement 40% + CSG déductible) | Meilleur choix |
|-----|----------------------|-----------------------------------------------------------|---------------|
| 0% | 30% | 17.2% | Barème |
| 11% | 30% | ~23% | Barème |
| 30% | 30% | ~29.2% | Barème (légèrement) |
| 41% | 30% | ~36.2% | Flat tax |
| 45% | 30% | ~39% | Flat tax |

**Règle simple** : si votre TMI est ≤ 30%, optez pour le barème progressif. Au-delà, la flat tax est plus avantageuse.

## Cas pratiques

### Cas 1 : Freelance SASU, célibataire, salaire 36 000€ net + 20 000€ de dividendes

- Revenu imposable hors dividendes : 36 000€ → TMI = 30%
- Avec 20 000€ de dividendes au barème : 36 000€ + (20 000€ × 60% - 1 360€ CSG) = 46 640€
- La dernière tranche reste à 30% → **barème légèrement avantageux**

### Cas 2 : Couple marié (2 parts), un seul revenu 50 000€ + 40 000€ de dividendes

- Revenu imposable : 50 000€ / 2 parts = 25 000€ par part → TMI = 11%
- Avec dividendes au barème : (50 000€ + 40 000€ × 60% - 2 720€) / 2 = 37 640€ par part → TMI = 30%
- **Barème nettement avantageux** grâce au quotient familial

### Cas 3 : Freelance seul avec 100 000€ de salaire + 50 000€ de dividendes

- TMI déjà à 41% → **flat tax clairement meilleure**

## Comment opter pour le barème ?

L'option se fait lors de votre déclaration de revenus annuelle (formulaire 2042) :
- Cochez la case **2OP** "Vous optez pour l'imposition au barème progressif"
- L'option s'applique pour l'année en question uniquement
- Vous pouvez changer chaque année

---

**Sources officielles 2026 :**
- CGI - Art. 200 A (PFU flat tax) : legifrance.gouv.fr
- CGI - Art. 158-3 2° (abattement 40% dividendes) : legifrance.gouv.fr
- CGI - Art. 197 (barème IR 2026) : legifrance.gouv.fr
- BOFiP - Option pour le barème progressif : bofip.impots.gouv.fr
- DGFiP - Déclaration 2042 case 2OP : impots.gouv.fr`
  },

  'freelance-vs-cdi-pourquoi': {
    id: '28',
    title: 'Freelance vs CDI : pourquoi franchir le pas en 2026 ?',
    slug: 'freelance-vs-cdi-pourquoi',
    excerpt: 'Revenus, liberté, sécurité, retraite : le comparatif honnête entre salariat et freelance.',
    category: 'Freelance pratique',
    tags: ['Freelance', 'CDI', 'Comparatif'],
    readTime: 12,
    date: '2026-03-14',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Le vrai comparatif que personne ne vous fait

Beaucoup d'articles sur le freelance sont écrits par des freelances enthousiastes ou des recruteurs. Ici, on compare froidement les chiffres, les avantages et les risques réels.

## Le nerf de la guerre : combien gagne-t-on vraiment ?

### Salarié CDI : cadre IT à Paris

| Poste | Montant |
|-------|---------|
| Salaire brut annuel | 55 000€ |
| Charges salariales (~22%) | -12 100€ |
| Salaire net avant IR | 42 900€ |
| IR (célibataire, 1 part) | -5 375€ |
| **Net après IR** | **37 525€** |

### Freelance SASU : même profil, TJM 500€

| Poste | Montant |
|-------|---------|
| CA annuel (218 jours facturés) | 109 000€ |
| Frais pro (comptable, matériel, etc.) | -8 000€ |
| Salaire net (3 500€/mois × 12) | 42 000€ |
| Charges patronales (~42%) | -17 640€ |
| Bénéfice avant IS | 41 360€ |
| IS (15%) | -6 204€ |
| Dividendes (flat tax 30%) | 35 156€ - 10 547€ = 24 609€ |
| **Revenu net total** | **~56 062€** |

**Différence : +18 537€/an en freelance**, soit +49% de revenus nets.

## Mais le CDI a des avantages cachés

| Avantage CDI | Valeur estimée/an |
|-------------|-------------------|
| Congés payés (5 semaines) | Inclus dans le salaire |
| Mutuelle (50% employeur) | ~600€ |
| Tickets restaurant | ~1 200€ |
| Intéressement / participation | 0 à 5 000€ |
| Formation payée par l'employeur | Variable |
| Chômage en cas de licenciement | Filet de sécurité |
| Cotisations retraite employeur | ~8% du brut |

**Total avantages cachés : 3 000€ à 10 000€/an** selon l'entreprise.

Même en ajoutant ces avantages, le freelance reste plus rémunérateur dès que le TJM dépasse ~400€.

## La liberté : le vrai moteur

| Aspect | CDI | Freelance |
|--------|-----|-----------|
| Choix des missions | Non (affecté par l'employeur) | Oui |
| Choix des horaires | Limité (35-39h) | Total |
| Choix du lieu de travail | Selon politique entreprise | Libre |
| Vacances | 5 semaines imposées | Illimitées (mais non payées) |
| Refuser une mission | Difficile | Facile |
| Travailler avec plusieurs clients | Interdit (sauf accord) | Normal |

## Les risques réels du freelance

### 1. L'intermission (pas de mission)

En moyenne, un freelance IT en France connaît 1 à 2 mois d'intermission par an. C'est le risque numéro 1.

**Solution** : constituez une trésorerie de 3 à 6 mois de charges dans votre société.

### 2. Pas de chômage

Si votre activité s'arrête, pas d'ARE (sauf si vous venez de quitter un CDI et créez dans les conditions ARE + création).

### 3. La solitude

Plus de collègues quotidiens, plus de machine à café partagée. C'est un aspect sous-estimé.

**Solution** : coworking, communautés de freelances, meetups.

### 4. La gestion administrative

Facturation, comptabilité, relances clients, URSSAF, TVA... C'est du temps non facturable.

**Solution** : un bon comptable + des outils comme Bozzam qui automatisent tout.

## À partir de quel TJM le freelance est-il rentable ?

| TJM | CA estimé (218j) | Net freelance SASU | Équivalent salaire brut CDI |
|-----|------------------|-------------------|-----------------------------|
| 300€ | 65 400€ | ~35 000€ | ~45 000€ brut |
| 400€ | 87 200€ | ~46 000€ | ~60 000€ brut |
| 500€ | 109 000€ | ~56 000€ | ~73 000€ brut |
| 600€ | 130 800€ | ~68 000€ | ~88 000€ brut |
| 700€ | 152 600€ | ~80 000€ | ~104 000€ brut |

## Notre verdict honnête

**Passez freelance si** :
- Votre TJM potentiel est > 400€
- Vous avez un réseau ou des compétences recherchées
- Vous supportez l'incertitude
- Vous voulez gagner plus et choisir vos projets

**Restez en CDI si** :
- Vous avez un prêt immobilier en cours de négociation
- Vous tenez à la stabilité absolue
- Votre secteur a peu d'offres freelance
- Vous n'aimez pas la gestion administrative

---

**Sources officielles 2026 :**
- INSEE - Revenus des indépendants : insee.fr
- URSSAF - Cotisations et contributions : urssaf.fr
- DGFiP - Barème IR 2026 : impots.gouv.fr
- France Travail - Cumul ARE et création : francetravail.org
- DARES - Statistiques emploi indépendant : dares.travail-emploi.gouv.fr`
  },

  'aides-creation-entreprise-2026': {
    id: '29',
    title: 'Aides à la création d\'entreprise : le guide complet 2026',
    slug: 'aides-creation-entreprise-2026',
    excerpt: 'ACRE, ARE, ARCE, NACRE, prêt d\'honneur, garantie BPI : toutes les aides disponibles.',
    category: 'Guides',
    tags: ['Aides', 'Création', 'ACRE', 'ARE'],
    readTime: 13,
    date: '2026-03-13',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Toutes les aides pour créer votre entreprise en 2026

Créer une entreprise coûte de l'argent. Mais l'État, les régions et les organismes publics proposent de nombreuses aides pour réduire la facture. Voici le guide exhaustif.

## Aide 1 : L'ACRE (Aide aux Créateurs et Repreneurs d'Entreprise)

L'ACRE est l'aide la plus connue : une réduction de 50% des cotisations sociales pendant 12 mois.

### Conditions 2026

- Être demandeur d'emploi, bénéficiaire RSA/ASS, avoir moins de 26 ans, ou créer en zone prioritaire
- Ne pas avoir bénéficié de l'ACRE dans les 3 dernières années
- En micro-entreprise : demande automatique pour les demandeurs d'emploi

### Montant de l'économie

| Statut | Cotisations normales | Avec ACRE | Économie |
|--------|---------------------|-----------|----------|
| Micro BNC (CA 50 000€) | 11 550€ | 5 775€ | 5 775€ |
| Micro BIC services (CA 50 000€) | 10 600€ | 5 300€ | 5 300€ |
| SASU (salaire 36 000€ brut) | ~15 120€ | ~7 560€ | 7 560€ |

**Attention 2026** : depuis le décret n°2026-69, le taux ACRE passe de 50% à 25% pour les créations après le 1er juillet 2026.

## Aide 2 : Le maintien des ARE (Allocation Retour à l'Emploi)

Si vous étiez salarié et avez droit au chômage, vous pouvez cumuler ARE + revenus d'activité :

### Les règles du cumul ARE + création

1. Déclarez vos revenus d'activité chaque mois à France Travail
2. Vos ARE sont recalculées : ARE versées = ARE normales - 70% de vos revenus d'activité
3. Les jours non indemnisés prolongent vos droits

**Exemple** : ARE de 2 000€/mois, revenus d'activité de 1 000€/mois → ARE versées = 2 000€ - 700€ = 1 300€/mois.

**Astuce** : ne vous versez pas de rémunération en société les premiers mois pour toucher 100% de vos ARE.

## Aide 3 : L'ARCE (Aide à la Reprise ou Création d'Entreprise)

Alternative au maintien des ARE : vous touchez 60% de vos droits restants en capital, en deux versements.

| Versement | Quand | Montant |
|-----------|-------|---------|
| 1er versement | À la création | 50% de l'ARCE (soit 30% des droits totaux) |
| 2ème versement | 6 mois après | 50% restants (si l'entreprise existe toujours) |

**Exemple** : droits ARE restants de 30 000€ → ARCE = 18 000€ (9 000€ + 9 000€ 6 mois après).

### ARE maintenues vs ARCE : le comparatif

| Critère | Maintien ARE | ARCE |
|---------|-------------|------|
| Montant total | 100% des droits (étalés) | 60% des droits (en capital) |
| Versement | Mensuel, dégressif | 2 versements en capital |
| Idéal pour | Revenus complémentaires réguliers | Investissement initial (matériel, stock) |
| Retour au chômage possible | Oui (droits restants) | Possible (droits restants = 40%) |

## Aide 4 : Le NACRE (Nouvel Accompagnement pour la Création et la Reprise d'Entreprise)

Le NACRE offre un accompagnement en 3 phases :
1. **Aide au montage du projet** (4 mois)
2. **Aide au financement** : prêt à taux zéro de 1 000€ à 10 000€
3. **Accompagnement au développement** (3 ans)

Conditions : demandeur d'emploi, bénéficiaire RSA, jeune de 18-25 ans.

## Aide 5 : Le prêt d'honneur

Prêt personnel à taux zéro, sans garantie, accordé par des réseaux d'accompagnement :

| Réseau | Montant | Durée |
|--------|---------|-------|
| Initiative France | 3 000€ à 50 000€ | 2 à 5 ans |
| Réseau Entreprendre | 15 000€ à 50 000€ | 2 à 5 ans |
| France Active | Variable | Variable |

**Avantage bonus** : un prêt d'honneur facilite l'obtention d'un prêt bancaire (effet levier × 3 à 7).

## Aide 6 : La garantie BPI France

BPI France peut garantir jusqu'à 60% de votre prêt bancaire, ce qui rassure la banque :

| Type de projet | Garantie maximale |
|---------------|------------------|
| Création d'entreprise | 60% du prêt |
| Reprise d'entreprise | 50% du prêt |
| Innovation | 60% du prêt |

## Aide 7 : Les aides régionales

Chaque région propose ses propres dispositifs. Exemples :

| Région | Aide | Montant |
|--------|------|---------|
| Île-de-France | Aide à la création PM'up | Jusqu'à 250 000€ |
| Auvergne-Rhône-Alpes | CRÉA | 2 000€ à 5 000€ |
| Occitanie | Prêt CRÉA | Jusqu'à 10 000€ |
| Nouvelle-Aquitaine | Créateur | Variable |

Consultez le site de votre CCI ou de votre Conseil Régional.

## Récapitulatif : les aides cumulables

| Aide | Cumulable avec ACRE | Cumulable avec ARE | Cumulable avec ARCE |
|------|--------------------|--------------------|---------------------|
| ACRE | — | Oui | Oui |
| Maintien ARE | Oui | — | Non (choix ARE ou ARCE) |
| ARCE | Oui | Non | — |
| NACRE | Oui | Oui | Oui |
| Prêt d'honneur | Oui | Oui | Oui |
| Garantie BPI | Oui | Oui | Oui |

**Meilleur combo** : ACRE + maintien ARE + prêt d'honneur + garantie BPI.

---

**Sources officielles 2026 :**
- URSSAF - ACRE conditions et montants : urssaf.fr
- France Travail - ARE et création d'entreprise : francetravail.org
- France Travail - ARCE : francetravail.org
- BPI France - Garanties : bpifrance.fr
- Initiative France - Prêts d'honneur : initiative-france.fr
- Décret n°2026-69 - Modification ACRE : legifrance.gouv.fr`
  },

  'gerer-tresorerie-freelance': {
    id: '30',
    title: 'Gérer sa trésorerie quand on est freelance',
    slug: 'gerer-tresorerie-freelance',
    excerpt: 'Provisions, acomptes IS, URSSAF trimestriel, TVA : comment ne jamais être pris au dépourvu.',
    category: 'Gestion',
    tags: ['Trésorerie', 'Gestion', 'Freelance'],
    readTime: 10,
    date: '2026-03-12',
    author: 'Bozzam',
    popular: true,
    fullContent: `## La règle d'or : ce qui est sur votre compte pro n'est pas à vous

L'erreur numéro 1 des freelances : confondre le solde du compte bancaire avec l'argent disponible. Une grande partie de ce solde est déjà réservée (charges, TVA, IS, IR).

## Combien provisionner : la règle des pourcentages

Pour chaque euro facturé, voici ce que vous devez mettre de côté :

### En micro-entreprise (BNC)

| Poste | % du CA à provisionner |
|-------|----------------------|
| Charges URSSAF | 23.1% |
| IR (VFL) ou provision IR | 2.2% (VFL) ou 10-15% |
| CFE | 1-2% |
| **Total à provisionner** | **~26-40%** |

### En SASU à l'IS

| Poste | % du CA à provisionner |
|-------|----------------------|
| Salaire + charges sociales | ~35-45% |
| IS (acomptes) | 5-10% |
| TVA à reverser | ~10-15% (si applicable) |
| Comptable | 2-3% |
| **Total à provisionner** | **~55-70%** |

**Ce qui reste (30-45%) = votre marge réelle.**

## Le calendrier des paiements

| Mois | URSSAF | TVA | IS | CFE | IR |
|------|--------|-----|----|----|-----|
| Janvier | Acompte T4 (15/01) | — | — | — | — |
| Février | — | Déclaration annuelle (CA12) | — | — | — |
| Mars | — | — | Acompte 1 (15/03) | — | — |
| Avril | Acompte T1 (15/04) | — | — | — | — |
| Mai | — | — | — | — | Déclaration revenus |
| Juin | — | — | Acompte 2 (15/06) | — | — |
| Juillet | Acompte T2 (15/07) | — | — | — | — |
| Septembre | — | — | Acompte 3 (15/09) | — | — |
| Octobre | Acompte T3 (15/10) | — | — | — | — |
| Décembre | — | — | Acompte 4 (15/12) | Paiement | — |

## La méthode des 3 comptes

La méthode la plus simple pour ne jamais être pris au dépourvu :

1. **Compte courant pro** : le compte principal (encaissements clients)
2. **Compte épargne "Charges"** : transférez immédiatement 40-50% de chaque encaissement
3. **Compte épargne "Trésorerie"** : votre matelas de sécurité (3-6 mois de charges)

### Exemple concret

Vous encaissez 8 000€ HT d'un client :

| Transfert | Montant | Destination |
|-----------|---------|-------------|
| TVA collectée (20%) | 1 600€ | Compte Charges |
| Provision charges (40%) | 3 200€ | Compte Charges |
| Rémunération nette | 2 500€ | Votre compte perso |
| Marge / trésorerie | 700€ | Compte Trésorerie |

## Gérer les retards de paiement

Les retards de paiement sont le fléau des freelances. En moyenne, un freelance attend 45 jours après facturation pour être payé.

### Les bonnes pratiques

1. **Facturez immédiatement** à la fin de la mission
2. **Délai de paiement** : 30 jours maximum (légal)
3. **Acompte** : demandez 30% à la signature
4. **Relance automatique** : J+7 (rappel), J+15 (relance formelle), J+30 (mise en demeure)
5. **Pénalités de retard** : obligatoires sur vos factures (taux BCE + 10 points, soit ~14.25% en 2026)

## Le matelas de sécurité

| Situation | Trésorerie recommandée |
|-----------|----------------------|
| Freelance solo, missions courtes | 3 mois de charges |
| Freelance solo, missions longues | 6 mois de charges |
| Avec charges fixes (bureau, salarié) | 6 à 9 mois |
| Activité saisonnière | 6 à 12 mois |

**Charges mensuelles typiques d'un freelance SASU** :
- Salaire net : 3 500€
- Charges sociales : ~2 900€
- Comptable : ~250€
- Outils/logiciels : ~150€
- Assurance RC Pro : ~50€
- **Total : ~6 850€/mois → trésorerie recommandée : 20 000€ à 41 000€**

---

**Sources officielles 2026 :**
- Code de commerce - Art. L441-10 (délais de paiement) : legifrance.gouv.fr
- DGFiP - Acomptes IS : impots.gouv.fr
- URSSAF - Calendrier des échéances : urssaf.fr
- DGFiP - TVA déclarations : impots.gouv.fr
- BCE - Taux directeur (pénalités de retard) : ecb.europa.eu`
  },

  'assurance-rc-pro-freelance': {
    id: '31',
    title: 'Assurance RC Pro freelance : obligatoire ou pas ?',
    slug: 'assurance-rc-pro-freelance',
    excerpt: 'Responsabilité civile professionnelle : qui est obligé, combien ça coûte, que couvre-t-elle ?',
    category: 'Protection sociale',
    tags: ['Assurance', 'RC Pro', 'Freelance'],
    readTime: 8,
    date: '2026-03-11',
    author: 'Bozzam',
    popular: true,
    fullContent: `## RC Pro : ne jouez pas à la roulette russe

Une erreur dans un livrable, un retard qui coûte cher au client, une perte de données... Sans RC Pro, c'est votre patrimoine personnel qui est en jeu.

## Qui est obligé de souscrire ?

La RC Pro est **obligatoire** pour certaines professions réglementées :
- Professionnels du bâtiment (décennale)
- Professionnels de santé
- Avocats, experts-comptables, notaires
- Agents immobiliers
- Courtiers en assurance

**Pour les freelances IT, consultants, graphistes, rédacteurs** : la RC Pro n'est pas légalement obligatoire mais fortement recommandée. Certains clients l'exigent contractuellement.

## Que couvre la RC Pro ?

| Garantie | Ce qu'elle couvre |
|----------|------------------|
| Dommages matériels | Vous cassez le matériel du client |
| Dommages immatériels | Erreur dans un livrable, perte de données, bug coûteux |
| Dommages corporels | Blessure d'un tiers sur votre lieu de travail |
| Frais de défense | Honoraires d'avocat si mise en cause |
| Perte de données | Fuite ou perte de données client |

### Exemple concret

Vous développez un site e-commerce. Un bug bloque les ventes pendant 3 jours. Le client réclame 15 000€ de manque à gagner. Sans RC Pro, vous payez de votre poche. Avec RC Pro, l'assurance couvre.

## Combien ça coûte ?

| Profil freelance | CA annuel | Prime RC Pro annuelle |
|-----------------|-----------|----------------------|
| Consultant IT | < 50 000€ | 200€ à 400€ |
| Consultant IT | 50 000€ à 150 000€ | 400€ à 800€ |
| Développeur web | < 100 000€ | 300€ à 600€ |
| Graphiste / Designer | < 50 000€ | 150€ à 350€ |
| Consultant management | < 100 000€ | 300€ à 700€ |

**Coût moyen : 30€ à 60€/mois**. C'est une charge déductible de votre résultat.

## Comment choisir sa RC Pro ?

### Les critères importants

1. **Plafond de garantie** : minimum 100 000€, idéalement 300 000€ à 500 000€
2. **Franchise** : la somme qui reste à votre charge (500€ à 1 500€ en général)
3. **Couverture mondiale** : si vous avez des clients étrangers
4. **Cyber-risques** : couverture des piratages et fuites de données
5. **Protection juridique** : prise en charge des frais d'avocat

### Les assureurs recommandés pour freelances

| Assureur | Spécificité | Tarif indicatif |
|----------|-------------|----------------|
| Hiscox | Spécialiste tech/IT | 250€ à 600€/an |
| AXA Freelance | Offre dédiée | 300€ à 700€/an |
| Simplis (MMA) | 100% en ligne | 200€ à 500€/an |
| Coover | Comparateur | Variable |

## RC Pro et charges déductibles

La prime d'assurance RC Pro est **100% déductible** de votre résultat imposable, que vous soyez en micro (non, pas de déduction possible), en SASU ou en EURL.

| Statut | Déductibilité RC Pro |
|--------|---------------------|
| Micro-entreprise | Non (charges forfaitaires) |
| SASU | Oui (charge de la société) |
| EURL | Oui (charge de la société) |

---

**Sources officielles 2026 :**
- Code des assurances - Art. L124-1 et suivants (RC Pro) : legifrance.gouv.fr
- Loi Spinetta - Assurance décennale obligatoire : legifrance.gouv.fr
- ORIAS - Registre des intermédiaires : orias.fr
- ACPR - Réglementation assurance : acpr.banque-france.fr`
  },

  'per-freelance-optimisation-fiscale': {
    id: '32',
    title: 'Le PER pour freelance : l\'arme anti-impôts en 2026',
    slug: 'per-freelance-optimisation-fiscale',
    excerpt: 'Plan d\'Épargne Retraite : plafonds, stratégies et simulation d\'économie d\'impôt.',
    category: 'Optimisation',
    tags: ['PER', 'Retraite', 'Optimisation', 'Fiscalité'],
    readTime: 11,
    date: '2026-03-10',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Le PER : l'outil d'optimisation préféré des freelances

Le PER individuel permet de déduire vos versements de votre revenu imposable. Pour un freelance avec une TMI élevée, c'est l'optimisation la plus simple et la plus efficace.

## Comment ça fonctionne ?

1. Vous versez de l'argent sur un PER
2. Le montant versé est déduit de votre revenu imposable
3. Vous économisez immédiatement de l'impôt
4. L'argent est bloqué jusqu'à la retraite (sauf cas de déblocage anticipé)
5. À la sortie, le capital est imposé (mais souvent à un taux inférieur)

## Les plafonds de déduction 2026

### Si vous êtes TNS (EURL, micro-entreprise)

Le plafond est beaucoup plus élevé que pour les salariés :

| Composante | Calcul | Montant max |
|-----------|--------|-------------|
| 10% du bénéfice (plafonné à 8 PASS) | 10% × 370 944€ | 37 094€ |
| + 15% du bénéfice entre 1 et 8 PASS | 15% × (370 944€ - 46 368€) | 48 686€ |
| **Plafond total TNS** | | **~81 384€/an** |

### Si vous êtes assimilé salarié (SASU)

| Composante | Calcul | Montant max |
|-----------|--------|-------------|
| 10% des revenus d'activité professionnelle | Plafonné à 10% de 8 PASS | 37 094€ |
| Ou minimum | 10% du PASS | 4 637€ |

### La récupération des plafonds non utilisés

Vous pouvez cumuler les plafonds non utilisés des 3 dernières années. Si vous n'avez jamais versé sur un PER, vous pouvez potentiellement déduire 3 à 4 ans de plafonds d'un coup.

## Simulation d'économie d'impôt

| TMI | Versement PER | Économie immédiate | Coût réel |
|-----|--------------|-------------------|-----------|
| 11% | 10 000€ | 1 100€ | 8 900€ |
| 30% | 10 000€ | 3 000€ | 7 000€ |
| 30% | 30 000€ | 9 000€ | 21 000€ |
| 41% | 10 000€ | 4 100€ | 5 900€ |
| 41% | 50 000€ | 20 500€ | 29 500€ |

## Les cas de déblocage anticipé

Contrairement aux idées reçues, le PER n'est pas totalement bloqué :

| Cas de déblocage | Conditions |
|-----------------|-----------|
| Achat résidence principale | Sans condition de durée |
| Décès du conjoint | Justificatif décès |
| Invalidité | Vous, conjoint ou enfant |
| Surendettement | Commission de surendettement |
| Fin de droits chômage | Attestation France Travail |
| Liquidation judiciaire | Jugement du tribunal |

**L'achat de la résidence principale** est le cas le plus utilisé : vous déduisez les versements de votre IR, puis débloquez le capital pour acheter votre logement.

## PER en TNS vs PER en SASU : quel avantage ?

| Critère | TNS (EURL) | Assimilé salarié (SASU) |
|---------|-----------|------------------------|
| Plafond PER | ~81 384€/an | ~37 094€/an |
| Déductibilité | Du bénéfice BIC/BNC | Du revenu salarial |
| Impact cotisations | Réduit les cotisations TNS | Pas d'impact sur cotisations |
| Avantage | Double économie (IR + cotisations) | Économie IR uniquement |

**C'est un avantage significatif de l'EURL** : le PER réduit à la fois votre IR et vos cotisations sociales TNS.

## La stratégie de sortie optimale

À la retraite, vous pouvez sortir en :
- **Capital** : imposé au barème progressif (sans abattement)
- **Rente viagère** : imposée au titre des pensions (abattement 10%)
- **Mix capital + rente** : combiner les deux

**La stratégie optimale** : sortir en capital l'année où vos revenus sont les plus faibles (première année de retraite = souvent TMI plus basse qu'en activité).

---

**Sources officielles 2026 :**
- CGI - Art. 154 bis (déduction PER TNS) : legifrance.gouv.fr
- CGI - Art. 163 quatervicies (déduction PER salarié) : legifrance.gouv.fr
- Code monétaire - Art. L224-1 à L224-40 (PER) : legifrance.gouv.fr
- DGFiP - PER et déductions : impots.gouv.fr
- BOFiP - Cas de déblocage anticipé : bofip.impots.gouv.fr`
  },

  'tva-intracommunautaire-freelance': {
    id: '33',
    title: 'TVA intracommunautaire pour freelances : guide pratique',
    slug: 'tva-intracommunautaire-freelance',
    excerpt: 'Clients en Europe, numéro de TVA intracommunautaire, autoliquidation : le guide complet.',
    category: 'Fiscalité',
    tags: ['TVA', 'Intracommunautaire', 'Europe'],
    readTime: 9,
    date: '2026-03-09',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Vous facturez un client en Allemagne ou en Belgique ? Attention à la TVA

Dès que vous travaillez avec un client dans l'Union Européenne, les règles de TVA changent. Si vous ne les respectez pas, vous risquez de payer la TVA deux fois ou de recevoir un redressement.

## Le numéro de TVA intracommunautaire

### Qui doit l'avoir ?

Tout professionnel qui réalise des échanges commerciaux avec des entreprises d'autres pays de l'UE. Même en franchise de TVA (micro-entreprise), vous pouvez en avoir besoin.

### Comment l'obtenir ?

- **SASU/EURL** : attribué automatiquement par le SIE (Service des Impôts des Entreprises) lors de l'inscription à la TVA
- **Micro-entreprise** : faites une demande auprès de votre SIE (formulaire libre ou via votre espace impots.gouv.fr)

Format : FR + 2 chiffres + votre SIREN (ex : FR12345678901)

## La règle de base : l'autoliquidation

Pour les prestations de services B2B (entre entreprises), la TVA est due dans le pays du client. C'est le mécanisme d'autoliquidation :

1. Vous facturez HT (sans TVA française)
2. Vous mentionnez "Autoliquidation - TVA due par le preneur, art. 283-2 du CGI"
3. Le client déclare et paie la TVA dans son pays
4. Vous déclarez l'opération dans votre DES (Déclaration Européenne de Services)

### Les mentions obligatoires sur la facture

| Mention | Obligatoire ? |
|---------|--------------|
| Votre n° de TVA intracommunautaire | Oui |
| Le n° de TVA du client | Oui |
| Mention "Autoliquidation" | Oui |
| Article de loi (art. 283-2 CGI) | Recommandé |
| Montant HT | Oui |
| Montant TVA = 0€ | Oui |

## Cas particulier : micro-entrepreneur en franchise

Si vous êtes en micro-entreprise sous franchise de TVA et facturez un client UE :

### Prestation de services

- Demandez un n° de TVA intracommunautaire à votre SIE
- Facturez HT avec mention autoliquidation
- Remplissez une DES (Déclaration Européenne de Services) chaque mois
- Vous restez en franchise de TVA en France (pas de changement sur vos ventes françaises)

### Vente de biens

- Les règles sont différentes et plus complexes
- Depuis 2021 : guichet unique OSS (One-Stop Shop) pour les ventes à des particuliers UE
- Pour les ventes B2B : autoliquidation similaire

## La DES (Déclaration Européenne de Services)

| Détail | Information |
|--------|-----------|
| Quoi ? | Déclarer les prestations de services rendues à des clients UE |
| Quand ? | Chaque mois (avant le 10 du mois suivant) |
| Où ? | douane.gouv.fr (portail EMEBI/DES) |
| Pénalité en cas d'oubli | 750€ par déclaration manquante |

## Les erreurs fréquentes

1. **Facturer avec TVA à un client UE B2B** → le client paie la TVA dans son pays ET vous la reversez en France = double imposition
2. **Oublier de vérifier le n° de TVA du client** → si le numéro est invalide, la TVA française s'applique. Vérifiez sur le site VIES de la Commission Européenne
3. **Confondre B2B et B2C** → si votre client est un particulier (pas d'entreprise), c'est la TVA française qui s'applique (sauf si vente de biens avec OSS)
4. **Oublier la DES** → amende de 750€ par déclaration manquante

## Vérifier un numéro de TVA intracommunautaire

Avant chaque première facture à un nouveau client UE, vérifiez son numéro de TVA sur le système VIES : ec.europa.eu/taxation_customs/vies

Si le numéro n'est pas valide, facturez avec TVA française.

---

**Sources officielles 2026 :**
- CGI - Art. 259 et suivants (lieu de prestation de services) : legifrance.gouv.fr
- CGI - Art. 283-2 (autoliquidation) : legifrance.gouv.fr
- Douanes - DES et EMEBI : douane.gouv.fr
- Commission Européenne - VIES : ec.europa.eu/taxation_customs/vies
- BOFiP - TVA et prestations intracommunautaires : bofip.impots.gouv.fr`
  },

  'freelance-pret-immobilier-banque': {
    id: '34',
    title: 'Freelance et prêt immobilier : comment convaincre la banque',
    slug: 'freelance-pret-immobilier-banque',
    excerpt: 'Bilan, ancienneté, apport, courtier : les clés pour obtenir un prêt quand on est indépendant.',
    category: 'Freelance pratique',
    tags: ['Prêt immobilier', 'Banque', 'Freelance'],
    readTime: 10,
    date: '2026-03-08',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Freelance et prêt immobilier : c'est possible (mais il faut préparer son dossier)

Les banques n'aiment pas l'incertitude. Et être freelance, pour un banquier, c'est de l'incertitude. Pourtant, des milliers de freelances obtiennent un prêt immobilier chaque année. Voici comment.

## Ce que la banque regarde

### Les 5 critères clés

| Critère | Ce que la banque veut | Poids |
|---------|----------------------|-------|
| Ancienneté | 3 ans minimum d'activité | Très fort |
| Revenus stables | CA et bénéfice en croissance ou stable | Très fort |
| Taux d'endettement | < 35% des revenus | Fort |
| Apport personnel | 10 à 20% du prix du bien | Fort |
| Gestion bancaire | Pas de découvert, épargne régulière | Moyen |

### Comment la banque calcule vos revenus ?

| Statut | Revenus pris en compte |
|--------|----------------------|
| Micro-entreprise | Moyenne des 3 derniers CA × abattement (34%, 50% ou 71%) |
| SASU (salaire) | Moyenne des 3 derniers salaires nets imposables |
| SASU (salaire + dividendes) | Salaire seul (les dividendes sont rarement pris en compte à 100%) |
| EURL (TNS) | Moyenne des 3 dernières rémunérations de gérant |

**Le piège** : si vous optimisez votre rémunération (petit salaire + gros dividendes en SASU), la banque ne voit qu'un petit salaire. Anticipez 2-3 ans avant votre projet immobilier.

## Préparer son dossier : la checklist

| Document | Obligatoire |
|----------|------------|
| 3 derniers bilans comptables | Oui |
| 3 derniers avis d'imposition | Oui |
| Kbis de moins de 3 mois | Oui |
| Relevés bancaires pro (6 derniers mois) | Oui |
| Relevés bancaires perso (3 derniers mois) | Oui |
| Justificatif d'apport | Oui |
| Compromis de vente | Oui (au moment de la demande) |
| Attestation comptable de revenus prévisionnels | Recommandé |

## Les stratégies pour maximiser vos chances

### 1. Augmentez votre salaire 2-3 ans avant

Si vous êtes en SASU, augmentez progressivement votre salaire net pour montrer des revenus stables et croissants. Les dividendes ne comptent souvent que pour 30-50%.

### 2. Constituez un apport solide

| Apport | Impact sur le dossier |
|--------|----------------------|
| 0% | Très difficile (quasi impossible en freelance) |
| 10% | Minimum acceptable |
| 15% | Bon dossier |
| 20%+ | Excellent — taux préférentiels |

### 3. Utilisez un courtier

Un courtier spécialisé en indépendants sait :
- Quelles banques acceptent les freelances
- Comment présenter vos revenus au mieux
- Négocier les taux et conditions

**Coût** : 0 à 1% du montant emprunté (souvent offert car payé par la banque).

### 4. Visez les bonnes banques

| Banque | Profil accepté | Particularité |
|--------|---------------|---------------|
| Crédit Mutuel | Indépendants avec 2+ ans | Souple sur les revenus mixtes |
| Banque Populaire | TNS et dirigeants | Compte pro + perso = meilleur taux |
| Caisse d'Épargne | Tous profils | Souple avec courtier |
| LCL | Cadres et consultants | Exige 3 ans de bilan |
| Banques en ligne | Variable | Souvent plus strictes |

## Simulation : capacité d'emprunt d'un freelance

| Revenu net mensuel retenu | Taux d'endettement 35% | Mensualité max | Montant empruntable (25 ans, 3.5%) |
|--------------------------|----------------------|---------------|----------------------------------|
| 3 000€ | 1 050€ | 1 050€ | ~210 000€ |
| 4 000€ | 1 400€ | 1 400€ | ~280 000€ |
| 5 000€ | 1 750€ | 1 750€ | ~350 000€ |
| 6 000€ | 2 100€ | 2 100€ | ~420 000€ |

---

**Sources officielles 2026 :**
- HCSF - Normes d'octroi des crédits immobiliers (taux d'endettement 35%) : economie.gouv.fr
- Banque de France - Taux d'usure : banque-france.fr
- Code de la consommation - Art. L313-1 et suivants : legifrance.gouv.fr
- ACPR - Recommandation sur le crédit immobilier : acpr.banque-france.fr`
  },

  'frais-deductibles-societe-sasu-eurl': {
    id: '35',
    title: 'Frais déductibles en société : le guide SASU et EURL',
    slug: 'frais-deductibles-societe-sasu-eurl',
    excerpt: 'Repas, transport, bureau, téléphone, formation : tout ce que vous pouvez déduire en 2026.',
    category: 'Fiscalité',
    tags: ['Frais', 'Déductibles', 'SASU', 'EURL'],
    readTime: 12,
    date: '2026-03-07',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Déduire ses frais : le levier fiscal que tout freelance en société doit maîtriser

En micro-entreprise, vous ne pouvez pas déduire vos charges réelles. En SASU ou EURL, chaque euro de frais déductible réduit votre bénéfice imposable. C'est un avantage majeur.

## La règle de base : qu'est-ce qui est déductible ?

Pour être déductible, une charge doit respecter 3 conditions :
1. **Engagée dans l'intérêt de l'entreprise** (pas personnelle)
2. **Justifiée par une facture** ou un reçu
3. **Comptabilisée dans l'exercice** concerné

## Les frais les plus courants

### Repas et restauration

| Type de repas | Déductible ? | Plafond 2026 |
|---------------|-------------|-------------|
| Repas seul (midi, jours travaillés) | Oui, partiellement | Différence entre 20.70€ et 5.35€ = 15.35€/repas |
| Restaurant avec client | Oui, 100% | Raisonnable (pas de plafond fixe) |
| Restaurant avec prospect | Oui, 100% | Raisonnable |
| Courses alimentaires personnelles | Non | — |

**Exemple** : vous déjeunez seul pour 15€. Déductible : 15€ - 5.35€ (forfait repas domicile) = 9.65€.

### Bureau et espace de travail

| Poste | Déductible ? | Condition |
|-------|-------------|-----------|
| Loyer bureau dédié | Oui, 100% | Bail commercial ou professionnel |
| Coworking | Oui, 100% | Facture au nom de la société |
| Bureau à domicile | Oui, partiellement | Au prorata de la surface (ex: 20% du loyer) |
| Mobilier de bureau | Oui | Facture au nom de la société |
| Électricité, internet (domicile) | Oui, partiellement | Au prorata usage pro |

### Matériel informatique et logiciels

| Poste | Déductible ? | Seuil |
|-------|-------------|-------|
| Ordinateur | Oui | < 500€ HT = charge directe, > 500€ = amortissement |
| Téléphone portable | Oui | Idem |
| Logiciels et abonnements SaaS | Oui, 100% | Facture au nom de la société |
| Hébergement web | Oui, 100% | Facture au nom de la société |
| Matériel périphérique | Oui | Souris, clavier, écran, etc. |

### Déplacements et transport

| Type | Déductible ? | Condition |
|------|-------------|-----------|
| Train/Avion (déplacement pro) | Oui, 100% | Justificatif + motif professionnel |
| Indemnités kilométriques | Oui | Barème fiscal (ex: 0.603€/km pour 7CV, < 5 000 km) |
| Location véhicule | Oui, 100% | Usage professionnel uniquement |
| Taxi/VTC | Oui | Déplacement professionnel |
| Abonnement transport en commun | Oui, 50% minimum | Pris en charge par la société |
| Péages et parking | Oui | Déplacement professionnel |

### Formation et développement

| Poste | Déductible ? |
|-------|-------------|
| Formations professionnelles | Oui + crédit d'impôt formation dirigeant |
| Livres et documentation pro | Oui |
| Conférences et séminaires | Oui |
| Abonnements presse professionnelle | Oui |

### Communication et marketing

| Poste | Déductible ? |
|-------|-------------|
| Création de site web | Oui |
| Publicité (Google Ads, LinkedIn) | Oui |
| Cartes de visite | Oui |
| Cadeaux clients | Oui (< 73€ TTC/personne/an pour récupérer la TVA) |

### Assurances et cotisations

| Poste | Déductible ? |
|-------|-------------|
| RC Pro | Oui |
| Mutuelle santé (entreprise) | Oui |
| Prévoyance | Oui (dans les limites légales) |
| Cotisations syndicales ou professionnelles | Oui |

## Les frais NON déductibles

| Frais | Pourquoi ? |
|-------|-----------|
| Vêtements courants | Usage personnel (sauf vêtements de travail spécifiques) |
| Amendes et contraventions | Sanctions personnelles |
| Impôt sur le revenu personnel | Impôt personnel |
| Dépenses somptuaires | Luxe excessif (yacht, résidence secondaire) |
| Charges personnelles | Courses alimentaires, loisirs |

## Récapitulatif des économies potentielles

| Poste | Montant annuel estimé | Économie IS (25%) |
|-------|----------------------|-------------------|
| Repas (220 jours × 10€) | 2 200€ | 550€ |
| Bureau domicile (20% de 800€/mois) | 1 920€ | 480€ |
| Matériel informatique | 1 500€ | 375€ |
| Logiciels et abonnements | 2 000€ | 500€ |
| Déplacements | 3 000€ | 750€ |
| Formation | 1 500€ | 375€ |
| Assurances | 1 200€ | 300€ |
| **Total** | **~13 320€** | **~3 330€** |

---

**Sources officielles 2026 :**
- CGI - Art. 39 (charges déductibles des BIC) : legifrance.gouv.fr
- BOFiP - Frais de repas déductibles : bofip.impots.gouv.fr
- BOFiP - Indemnités kilométriques : bofip.impots.gouv.fr
- CGI - Art. 39-4 (dépenses somptuaires) : legifrance.gouv.fr
- DGFiP - Seuil d'immobilisation 500€ : impots.gouv.fr
- BOFiP - Cadeaux clients et TVA : bofip.impots.gouv.fr`
  },

  'cotisations-retraite-freelance-2026': {
    id: '36',
    title: 'Cotisations retraite freelance : tout comprendre en 2026',
    slug: 'cotisations-retraite-freelance-2026',
    excerpt: 'Trimestres, points, CIPAV, SSI, AGIRC-ARRCO : combien cotisez-vous et quelle retraite espérer ?',
    category: 'Protection sociale',
    tags: ['Retraite', 'Cotisations', 'Freelance'],
    readTime: 11,
    date: '2026-03-06',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Retraite et freelance : le sujet que tout le monde repousse

La retraite, c'est loin. Mais les décisions que vous prenez aujourd'hui (statut, niveau de cotisations, PER) déterminent ce que vous toucherez dans 20, 30 ou 40 ans.

## Comment fonctionne la retraite en France ?

Le système repose sur 2 piliers :
1. **Retraite de base** : calculée en trimestres (vous en avez besoin de 172 pour le taux plein si né après 1973)
2. **Retraite complémentaire** : calculée en points

## Les régimes selon votre statut

| Statut | Retraite de base | Complémentaire |
|--------|-----------------|----------------|
| SASU (président assimilé salarié) | CNAV (régime général) | AGIRC-ARRCO |
| EURL (gérant TNS) | SSI (ex-RSI) | RCI |
| Micro-entreprise (hors CIPAV) | SSI | RCI |
| Micro-entreprise (CIPAV) | CIPAV | CIPAV |
| Portage salarial | CNAV | AGIRC-ARRCO |

## Combien de trimestres validez-vous ?

### En micro-entreprise

Le nombre de trimestres dépend de votre CA :

| Activité | CA pour 1 trimestre | CA pour 4 trimestres |
|----------|--------------------|--------------------|
| Vente (BIC) | 6 090€ | 24 360€ |
| Prestation BIC | 3 530€ | 14 120€ |
| Prestation BNC (hors CIPAV) | 2 880€ | 11 520€ |
| Prestation BNC (CIPAV) | 2 880€ | 11 520€ |

**Attention** : avec un CA annuel de 10 000€ en BNC, vous ne validez que 3 trimestres. Il faut un minimum de CA pour valider vos 4 trimestres par an.

### En SASU

Les trimestres sont validés si votre salaire brut atteint :
- **1 trimestre** : 1 747€ brut (150 × SMIC horaire)
- **4 trimestres** : 6 990€ brut/an

Avec un salaire brut de 3 000€/mois (36 000€/an), vous validez facilement 4 trimestres.

### En EURL (TNS)

La validation dépend du revenu cotisé :
- **1 trimestre** : ~1 747€ de revenu
- **4 trimestres** : ~6 990€/an

## La retraite complémentaire : les points

### AGIRC-ARRCO (SASU, portage)

| Élément | Valeur 2026 |
|---------|-------------|
| Prix d'achat d'un point | ~19.63€ |
| Valeur de service d'un point | ~1.4159€/an |
| Taux d'appel | 127% (vous cotisez 27% de plus que ce qui donne des points) |
| Cotisation tranche 1 (< 1 PASS) | 7.87% (part salariale) + 12.95% (part patronale) |
| Cotisation tranche 2 (1-8 PASS) | 8.64% + 21.59% |

**Calcul simplifié** : avec un salaire brut de 48 000€/an, vous accumulez environ 300-350 points AGIRC-ARRCO par an, soit ~500€/an de pension complémentaire.

### RCI (EURL TNS, micro hors CIPAV)

La retraite complémentaire des indépendants est moins généreuse :

| Revenu | Taux cotisation | Points acquis |
|--------|----------------|---------------|
| 0 à 40 784€ | 7% | Variable |
| 40 784€ à 4 PASS | 8% | Variable |

## Simulation : quelle retraite à 67 ans ?

### Scénario 1 : Freelance SASU, 25 ans de cotisations, salaire moyen 48 000€ brut/an

| Composante | Montant mensuel estimé |
|-----------|----------------------|
| Retraite de base (CNAV) | ~1 200€ |
| Complémentaire AGIRC-ARRCO | ~900€ |
| **Total** | **~2 100€/mois** |

### Scénario 2 : Freelance EURL (TNS), 25 ans, revenu moyen 45 000€/an

| Composante | Montant mensuel estimé |
|-----------|----------------------|
| Retraite de base (SSI) | ~1 100€ |
| Complémentaire RCI | ~350€ |
| **Total** | **~1 450€/mois** |

### Scénario 3 : Micro-entrepreneur BNC, 25 ans, CA moyen 50 000€/an

| Composante | Montant mensuel estimé |
|-----------|----------------------|
| Retraite de base (SSI) | ~800€ |
| Complémentaire RCI | ~250€ |
| **Total** | **~1 050€/mois** |

**Constat** : la SASU offre une retraite significativement plus élevée grâce à AGIRC-ARRCO, mais les cotisations sont aussi plus élevées pendant la vie active.

## Comment améliorer sa retraite en freelance ?

| Stratégie | Impact |
|-----------|--------|
| PER individuel | Capital ou rente supplémentaire à la retraite |
| Assurance vie | Complément de revenus (rachat partiel) |
| Investissement immobilier (SCI) | Revenus locatifs |
| Augmenter son salaire SASU | Plus de points AGIRC-ARRCO |
| Racheter des trimestres | 1 trimestre = 1 000€ à 6 000€ selon revenus |

---

**Sources officielles 2026 :**
- CNAV - Retraite du régime général : lassuranceretraite.fr
- AGIRC-ARRCO - Retraite complémentaire : agirc-arrco.fr
- SSI - Retraite des indépendants : secu-independants.fr
- CIPAV - Retraite professions libérales : lacipav.fr
- Info-retraite - Simulateur officiel : info-retraite.fr
- CGI - Art. 154 bis (rachat de trimestres) : legifrance.gouv.fr`
  },

  'frais-km-notes-frais-freelance-portage': {
    id: '37',
    title: 'Frais kilométriques et notes de frais : le guide freelance et portage 2026',
    slug: 'frais-km-notes-frais-freelance-portage',
    excerpt: 'IK, repas, hôtel, matériel : tout ce que vous pouvez vous faire rembourser pour augmenter votre net.',
    category: 'Optimisation',
    tags: ['Frais kilométriques', 'Notes de frais', 'Portage', 'Freelance'],
    readTime: 13,
    date: '2026-03-21',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Les frais récupérables : le levier caché pour augmenter votre revenu net

Que vous soyez en portage salarial, en SASU ou en EURL, une partie de vos dépenses professionnelles peut être remboursée ou déduite. Bien gérés, ces frais peuvent représenter **3 000€ à 10 000€ de gains nets par an**. Voici le guide complet.

## Partie 1 : Les indemnités kilométriques (IK)

### Le barème fiscal 2026

Si vous utilisez votre véhicule personnel pour des déplacements professionnels, vous pouvez appliquer le barème kilométrique officiel au lieu de déduire les frais réels (essence, assurance, entretien).

**Barème IK 2026 — Voitures :**

| Puissance fiscale | Jusqu'à 5 000 km | De 5 001 à 20 000 km | Au-delà de 20 000 km |
|------------------|------------------|---------------------|---------------------|
| 3 CV et moins | d × 0.529 | (d × 0.316) + 1 065 | d × 0.370 |
| 4 CV | d × 0.606 | (d × 0.340) + 1 330 | d × 0.407 |
| 5 CV | d × 0.636 | (d × 0.357) + 1 395 | d × 0.427 |
| 6 CV | d × 0.665 | (d × 0.374) + 1 457 | d × 0.447 |
| 7 CV et plus | d × 0.697 | (d × 0.394) + 1 515 | d × 0.470 |

*(d = distance parcourue en km)*

**Barème IK 2026 — Deux-roues (motos > 50 cm³) :**

| Puissance | Jusqu'à 3 000 km | De 3 001 à 6 000 km | Au-delà de 6 000 km |
|-----------|-----------------|--------------------|--------------------|
| 1-2 CV | d × 0.395 | (d × 0.099) + 891 | d × 0.248 |
| 3-5 CV | d × 0.468 | (d × 0.082) + 1 158 | d × 0.275 |
| > 5 CV | d × 0.606 | (d × 0.079) + 1 583 | d × 0.343 |

### Exemple concret

Vous avez une voiture 6 CV et parcourez 12 000 km professionnels par an :
- Calcul : (12 000 × 0.374) + 1 457 = **5 945€ d'IK déductibles**
- Si votre taux d'IS est à 25% : économie de **1 486€**
- En portage salarial : **5 945€ remboursés hors bulletin = net d'impôt**

### Majoration véhicule électrique

Depuis 2021, les IK pour les véhicules 100% électriques bénéficient d'une **majoration de 20%** sur le barème.

| Véhicule | IK pour 12 000 km (6 CV) | IK véhicule électrique |
|----------|-------------------------|----------------------|
| Thermique | 5 945€ | — |
| Électrique | — | 7 134€ (+20%) |

**Soit 1 189€ d'économie supplémentaire** en roulant en électrique.

### Ce qui compte comme déplacement professionnel

| Type de trajet | IK déductibles ? |
|---------------|-----------------|
| Domicile → client (mission) | Oui |
| Domicile → bureau habituel | Non (trajet domicile-travail, sauf > 40 km justifié) |
| Client A → Client B | Oui |
| Déplacement pour prospection | Oui |
| Déplacement pour formation | Oui |
| Trajet personnel | Non |

**Obligation** : tenir un relevé kilométrique (date, trajet, motif, km) pour chaque déplacement.

## Partie 2 : Frais récupérables en portage salarial

En portage, les frais professionnels sont remboursés **hors bulletin de paie** : ils ne sont pas soumis aux charges sociales ni à l'impôt. C'est un levier puissant pour augmenter votre net.

### Les frais de mission (refacturables au client)

| Frais | Conditions | Impact |
|-------|-----------|--------|
| Transport (train, avion) | Justificatif + lien avec la mission | 100% remboursé hors bulletin |
| Hébergement (hôtel) | Si déplacement > 50 km du domicile | 100% remboursé hors bulletin |
| Repas en déplacement | Barème URSSAF (max 20.70€/repas en 2026) | Remboursé hors bulletin |
| Location de véhicule | Pour la mission | 100% remboursé hors bulletin |
| Taxi / VTC | Justificatif | 100% remboursé hors bulletin |

### Les frais de fonctionnement (non refacturables)

| Frais | Plafond URSSAF 2026 | Comment |
|-------|--------------------|---------|
| Indemnités kilométriques | Barème fiscal (voir ci-dessus) | Relevé kilométrique |
| Repas hors déplacement (midi) | 7.30€/repas (forfait URSSAF) | Ou réel plafonné à 20.70€ avec justificatif |
| Abonnement internet (% pro) | 50% du forfait | Facture au nom du salarié porté |
| Téléphone (% pro) | 50% du forfait | Facture |
| Fournitures de bureau | Justificatif | Facture |

### Simulation : impact des frais sur votre net en portage

**Sans optimisation frais :**

| Poste | Montant |
|-------|---------|
| CA mensuel HT | 10 000€ |
| Frais de gestion (8%) | -800€ |
| Charges patronales (~42%) | -3 864€ |
| Charges salariales (~22%) | -1 174€ |
| **Salaire net** | **~4 162€** |

**Avec optimisation frais (500€/mois de frais pro) :**

| Poste | Montant |
|-------|---------|
| CA mensuel HT | 10 000€ |
| Frais de gestion (8%) | -800€ |
| Remboursement frais (hors bulletin) | -500€ |
| Base charges patronales | 8 700€ |
| Charges patronales (~42%) | -3 654€ |
| Charges salariales (~22%) | -1 110€ |
| Salaire net (bulletin) | ~3 936€ |
| + Remboursement frais | +500€ |
| **Total net en poche** | **~4 436€** |

**Gain : 274€/mois soit 3 288€/an** de revenu net supplémentaire.

## Partie 3 : Frais récupérables en SASU / EURL

En société, les frais professionnels sont des charges déductibles du résultat imposable.

### Les indemnités kilométriques en société

Deux options :

**Option 1 : Le véhicule est au nom personnel**
- La société vous rembourse les IK au barème fiscal
- Le remboursement est exonéré de charges sociales et d'IR
- Vous tenez un relevé kilométrique

**Option 2 : Le véhicule est au nom de la société**
- Tous les frais réels sont déduits (carburant, assurance, entretien, amortissement)
- Attention : l'avantage en nature pour l'usage personnel est soumis à charges

| Critère | IK (véhicule personnel) | Véhicule société |
|---------|------------------------|-----------------|
| Simplicité | Très simple (barème) | Complexe (comptabilité détaillée) |
| Coût pour la société | IK uniquement | Tous les frais + TVS |
| Charges sociales | Exonéré | Avantage en nature imposable |
| TVA récupérable | Non | Partiellement (carburant) |
| Recommandé si | < 20 000 km/an | > 20 000 km/an |

### Les petits frais souvent oubliés

| Frais | Déductible ? | Comment récupérer |
|-------|-------------|-------------------|
| Café / boissons en rendez-vous | Oui | Note de frais + ticket |
| Parking professionnel | Oui | Ticket + motif |
| Péages | Oui | Badge télépéage (relevé mensuel) |
| Timbres et envois postaux | Oui | Reçu |
| Petit matériel (< 500€ HT) | Oui, charge directe | Facture au nom de la société |
| Abonnement coworking | Oui, 100% | Facture mensuelle |
| Nettoyage bureau | Oui | Facture |
| Cadeaux clients (< 73€ TTC) | Oui + TVA récupérable | Facture |
| Cotisation réseau pro | Oui | Facture ou reçu |
| Livres et formation | Oui | Facture |

### Les frais de repas : les règles exactes

**Repas seul (jours travaillés) :**
- Déductible entre le forfait repas domicile (5.35€) et le plafond (20.70€)
- Si votre repas coûte 14€ : déductible = 14€ - 5.35€ = **8.65€**
- Sur 220 jours : 220 × 8.65€ = **1 903€ de charges déductibles**

**Repas d'affaires (avec client, prospect, partenaire) :**
- 100% déductible
- TVA récupérable (10% ou 20%)
- Obligation : noter le nom de l'invité et le motif au dos du ticket

| Type de repas | Limite | TVA récupérable |
|---------------|--------|----------------|
| Repas seul | 20.70€ max (- forfait 5.35€) | Non |
| Repas d'affaires | Raisonnable (pas de limite fixe) | Oui (10% restaurant) |
| Traiteur bureau | 100% si pro | Oui (20%) |

## Partie 4 : Les frais de télétravail / bureau à domicile

Si vous travaillez depuis chez vous :

### En société (SASU/EURL)

La société peut vous verser une indemnité d'occupation du domicile :
- Calculée au prorata de la surface utilisée (ex : 15 m² sur 70 m² = 21%)
- Applicable sur : loyer/intérêts emprunt, charges, électricité, internet, assurance habitation
- **Exonérée de charges sociales** dans la limite du raisonnable

| Poste | Montant mensuel | Prorata 21% |
|-------|----------------|-------------|
| Loyer (ou intérêts emprunt) | 1 000€ | 210€ |
| Charges copro | 150€ | 31€ |
| Électricité | 100€ | 21€ |
| Internet | 40€ | 21€ (50% pro) |
| Assurance habitation | 30€ | 6€ |
| **Total indemnité mensuelle** | | **~289€** |
| **Soit par an** | | **~3 468€ net d'impôt** |

### En portage salarial

L'indemnité de télétravail est généralement forfaitaire :
- **10.70€/jour** de télétravail (barème URSSAF 2026)
- Ou forfait mensuel : **59.40€** (1 jour/semaine) à **253.90€** (5 jours/semaine)
- Exonéré de charges et d'impôt

## Récapitulatif : combien pouvez-vous récupérer par an ?

| Poste de frais | Montant annuel estimé | En société (économie IS 25%) | En portage (net en poche) |
|---------------|----------------------|----------------------------|--------------------------|
| IK (12 000 km, 6 CV) | 5 945€ | 1 486€ | 5 945€ |
| Repas (220 jours) | 1 903€ | 476€ | 1 606€ (barème URSSAF) |
| Bureau domicile | 3 468€ | 867€ | 2 543€ (forfait télétravail) |
| Téléphone + internet | 600€ | 150€ | 300€ |
| Petit matériel + fournitures | 800€ | 200€ | 800€ |
| Formations | 1 500€ | 375€ + crédit impôt | 1 500€ |
| **Total** | **~14 216€** | **~3 554€** | **~12 694€** |

**En portage**, les frais sont remboursés net d'impôt et de charges. C'est l'avantage majeur du portage pour les frais.

**En société**, les frais réduisent le bénéfice imposable. L'économie est de 15 à 25% du montant des frais.

## Les erreurs à éviter

1. **Pas de justificatif = pas de déduction** : gardez TOUS vos tickets et factures
2. **Frais personnels déguisés** : un contrôle URSSAF ou fiscal peut requalifier des frais personnels
3. **IK sans relevé kilométrique** : en cas de contrôle, l'intégralité des IK peut être redressée
4. **Frais excessifs** : des notes de restaurant à 200€/personne seront questionnées
5. **Double déduction** : ne déduisez pas un même frais en IK ET en frais réels
6. **Oublier la TVA** : sur les frais de repas d'affaires, la TVA est récupérable (ne l'oubliez pas !)

---

**Sources officielles 2026 :**
- DGFiP - Barème kilométrique 2026 : impots.gouv.fr
- BOFiP - Indemnités kilométriques : bofip.impots.gouv.fr
- URSSAF - Frais professionnels et limites d'exonération : urssaf.fr
- URSSAF - Indemnité de télétravail : urssaf.fr
- CGI - Art. 83-3 (frais professionnels déductibles) : legifrance.gouv.fr
- BOFiP - Frais de repas : bofip.impots.gouv.fr
- Convention collective portage salarial - Frais de mission : legifrance.gouv.fr`
  },

  'epargne-salariale-sasu-pee-pereco': {
    id: '38',
    title: 'Épargne salariale en SASU : PEE et PERE-CO même seul',
    slug: 'epargne-salariale-sasu-pee-pereco',
    excerpt: 'Jusqu\'à 11 000€/an exonérés d\'IR et de charges : l\'épargne salariale est accessible même au président SASU seul.',
    category: 'Optimisation',
    tags: ['Épargne salariale', 'PEE', 'PERE-CO', 'SASU'],
    readTime: 12,
    date: '2026-03-24',
    author: 'Bozzam',
    popular: true,
    fullContent: `## L'épargne salariale : le secret le mieux gardé des présidents de SASU

La plupart des freelances en SASU ignorent qu'ils peuvent mettre en place un PEE et un PERE-CO même s'ils sont seuls dans leur société. C'est pourtant l'un des leviers d'optimisation les plus puissants : jusqu'à **11 127€/an** exonérés d'impôt sur le revenu et de charges sociales.

## Le PEE (Plan d'Épargne Entreprise)

### Principe

Le PEE est un dispositif d'épargne collective. La société verse un abondement (une sorte de bonus) sur le plan de son dirigeant. Cet abondement est :
- **Exonéré d'impôt sur le revenu** pour le bénéficiaire
- **Exonéré de cotisations sociales** (soumis uniquement à la CSG/CRDS de 9.7%)
- **Déductible du résultat** de la société (charge)

### Plafonds 2026

| Élément | Montant |
|---------|---------|
| Abondement maximum annuel | 3 709.44€ (8% du PASS) |
| Versement volontaire maximum | 25% de la rémunération annuelle brute |
| Durée de blocage | 5 ans |

### Cas de déblocage anticipé du PEE

| Cas | Condition |
|-----|----------|
| Mariage / PACS | Dans les 6 mois suivant l'événement |
| Naissance du 3ème enfant | Dans les 6 mois |
| Divorce avec garde d'enfant | Jugement |
| Invalidité | Vous, conjoint ou enfant |
| Décès du conjoint | Justificatif |
| Création ou reprise d'entreprise | Par vous ou votre conjoint |
| Acquisition résidence principale | Compromis de vente |
| Surendettement | Commission |
| Rupture contrat de travail | Fin de mandat social |

**Le cas le plus utile** : la rupture du contrat de travail (fin de mandat de président) permet de débloquer immédiatement les fonds.

## Le PERE-CO (Plan d'Épargne Retraite d'Entreprise Collectif)

### Principe

Le PERE-CO est l'équivalent retraite du PEE. La société verse un abondement sur un plan bloqué jusqu'à la retraite (sauf cas de déblocage anticipé, similaires au PER individuel + achat résidence principale).

### Plafonds 2026

| Élément | Montant |
|---------|---------|
| Abondement maximum annuel | 7 418.88€ (16% du PASS) |
| Versement volontaire maximum | 25% de la rémunération brute |
| Disponibilité | Retraite (ou déblocage anticipé) |

## PEE + PERE-CO : le combo gagnant

| Dispositif | Abondement max | IR | Charges sociales | CSG/CRDS |
|-----------|---------------|-----|-----------------|----------|
| PEE | 3 709€ | Exonéré | Exonéré | 9.7% |
| PERE-CO | 7 418€ | Exonéré | Exonéré | 9.7% |
| **Total** | **11 127€** | **Exonéré** | **Exonéré** | **~1 079€** |

**Net réel reçu** : 11 127€ - 1 079€ (CSG/CRDS) = **10 048€** net d'impôt.

**Comparaison** : si ces 11 127€ avaient été versés en salaire (TMI 30%) :
- Charges patronales (~42%) : +4 673€ de coût
- Charges salariales (~22%) : -2 448€
- IR (30%) : -2 604€
- **Net en poche : ~6 075€**

**Gain de l'épargne salariale : ~3 973€/an** par rapport au salaire.

## Comment mettre en place l'épargne salariale en SASU ?

### Étapes

1. **Choisir un gestionnaire** : banque ou courtier spécialisé (Epsens, Natixis Interépargne, Amundi, BNP)
2. **Rédiger le règlement du plan** : le gestionnaire fournit un modèle
3. **Déposer le règlement** auprès de la DREETS (ex-DIRECCTE)
4. **Prendre une décision unilatérale** du président (pas besoin d'accord collectif si vous êtes seul)
5. **Effectuer les versements** et abondements

### Coûts de mise en place

| Poste | Coût estimé |
|-------|-------------|
| Ouverture du plan | 0 à 300€ |
| Frais de gestion annuels | 0.2% à 0.8% de l'encours |
| Frais de tenue de compte | 15 à 30€/an |
| Comptable (formalités) | 200 à 500€ (première année) |

### Les supports d'investissement

L'argent du PEE/PERE-CO est investi dans des FCPE (Fonds Communs de Placement d'Entreprise) :

| Profil | Type de FCPE | Risque | Rendement attendu |
|--------|-------------|--------|-------------------|
| Prudent | Monétaire / Obligataire | Faible | 2-4%/an |
| Équilibré | Diversifié | Moyen | 4-7%/an |
| Dynamique | Actions | Élevé | 6-10%/an |
| ISR | Investissement responsable | Variable | Variable |

## Épargne salariale et EURL : c'est possible aussi

Le gérant majoritaire d'EURL peut aussi mettre en place un PEE et un PERE-CO, avec les mêmes plafonds et avantages. La seule différence :

| Critère | SASU | EURL |
|---------|------|------|
| Qui met en place ? | Président (décision unilatérale) | Gérant (décision unilatérale) |
| Abondement déductible | Du résultat IS | Du résultat (IS ou IR) |
| Impact cotisations TNS | Non applicable | L'abondement ne rentre pas dans l'assiette TNS |

## Épargne salariale + PER individuel : cumulable ?

Oui ! Les deux dispositifs sont indépendants et cumulables :

| Dispositif | Plafond | Fiscalité |
|-----------|---------|-----------|
| PER individuel (TNS) | ~81 384€/an | Déductible du revenu |
| PER individuel (SASU) | ~37 094€/an | Déductible du revenu |
| PEE (abondement) | 3 709€/an | Exonéré d'IR |
| PERE-CO (abondement) | 7 418€/an | Exonéré d'IR |

**Attention** : les versements volontaires sur le PERE-CO réduisent le plafond PER individuel. Seuls les abondements de la société sont en plus.

## La stratégie optimale par niveau de revenu

| Bénéfice société | Stratégie recommandée |
|-----------------|----------------------|
| < 50 000€ | Salaire uniquement (pas assez pour optimiser) |
| 50 000€ - 80 000€ | Salaire + PER individuel |
| 80 000€ - 120 000€ | Salaire + dividendes + PEE |
| 120 000€ - 200 000€ | Salaire + dividendes + PEE + PERE-CO + PER |
| > 200 000€ | Holding + Salaire + PEE + PERE-CO + PER + investissements |

---

**Sources officielles 2026 :**
- Code du travail - Art. L3332-1 à L3332-28 (PEE) : legifrance.gouv.fr
- Code du travail - Art. L3334-1 à L3334-16 (PERE-CO) : legifrance.gouv.fr
- URSSAF - Épargne salariale et exonérations : urssaf.fr
- CGI - Art. 163 bis AA (régime fiscal PEE) : legifrance.gouv.fr
- CSS - Art. L242-1 (assiette cotisations, exclusion épargne salariale) : legifrance.gouv.fr
- DGFiP - Abondement et plafonds : impots.gouv.fr`
  },

  'apport-cession-150-0-b-ter-report': {
    id: '39',
    title: 'Apport-cession (150-0 B ter) : reporter l\'impôt sur la plus-value',
    slug: 'apport-cession-150-0-b-ter-report',
    excerpt: 'Vous vendez votre société ? Le mécanisme d\'apport-cession permet de réinvestir sans payer immédiatement la plus-value.',
    category: 'Optimisation',
    tags: ['Plus-value', 'Apport-cession', 'Holding', 'Cession'],
    readTime: 13,
    date: '2026-03-23',
    author: 'Bozzam',
    popular: true,
    fullContent: `## L'apport-cession : le mécanisme qui permet de vendre sa société sans payer d'impôt (immédiatement)

Quand un freelance vend sa société, la plus-value peut être taxée à 30% (flat tax) voire plus. Le mécanisme de l'article 150-0 B ter du CGI permet de reporter cette imposition en passant par une holding. C'est l'outil clé des entrepreneurs qui veulent réinvestir.

## Le problème : vendre = payer (cher)

**Exemple sans holding** : vous avez créé votre SASU avec 1 000€ de capital. Après 5 ans, un acheteur vous propose 200 000€.

| Poste | Montant |
|-------|---------|
| Prix de vente | 200 000€ |
| Prix d'acquisition | 1 000€ |
| Plus-value | 199 000€ |
| Flat tax 30% | 59 700€ |
| **Net en poche** | **140 300€** |

Vous perdez **59 700€** d'impôt. Si vous souhaitez réinvestir (immobilier, nouvelle société, bourse), vous investissez avec 140 000€ au lieu de 200 000€.

## La solution : l'apport-cession via holding

### Le schéma en 3 étapes

1. **Apport** : avant la vente, vous apportez vos parts de société opérationnelle à une holding que vous détenez
2. **Report d'imposition** : la plus-value d'apport est constatée mais son imposition est reportée (art. 150-0 B ter)
3. **Cession** : la holding vend les parts de la société opérationnelle. Le prix de vente arrive dans la holding

**Résultat** : les 200 000€ sont dans la holding, sans avoir payé les 59 700€ de flat tax.

### Conditions du report d'imposition

| Condition | Détail |
|-----------|--------|
| Contrôle de la holding | Vous devez détenir le contrôle de la holding (> 33.33% des droits de vote) |
| Holding soumise à l'IS | La holding doit être à l'IS |
| Apport avant la cession | L'apport doit être fait avant la signature de la vente |
| Chronologie respectée | Pas de cession "concomitante" (simultanée à l'apport) |

## Les obligations de réinvestissement

Depuis 2012, si la holding cède les titres apportés dans les **3 ans** suivant l'apport, elle doit réinvestir au moins **60% du prix de cession** dans une activité économique dans les **2 ans** suivant la cession.

### Qu'est-ce qui compte comme réinvestissement ?

| Type d'investissement | Éligible ? | Conditions |
|----------------------|-----------|-----------|
| Création d'une nouvelle société | Oui | Activité commerciale, artisanale, libérale, agricole ou industrielle |
| Acquisition de parts de société | Oui | Obtenir le contrôle ou renforcer le contrôle |
| Souscription au capital d'une société | Oui | Société exerçant une activité éligible |
| Acquisition de fonds de commerce | Oui | Exploitation directe |
| Investissement immobilier locatif nu | Non | Considéré comme patrimonial |
| Investissement en bourse (actions cotées) | Non | Sauf prise de contrôle (> 33%) |
| SCI à l'IS avec activité de gestion | Incertain | Jurisprudence en évolution |

### Le calendrier à respecter

| Événement | Délai |
|-----------|-------|
| Apport des titres à la holding | Avant la cession |
| Cession par la holding | Libre (mais si < 3 ans → obligation de remploi) |
| Réinvestissement (si cession < 3 ans) | 60% dans les 2 ans suivant la cession |
| Conservation du réinvestissement | 12 mois minimum |

## Simulation complète

### Sans apport-cession

| Poste | Montant |
|-------|---------|
| Vente directe | 200 000€ |
| Flat tax 30% | -59 700€ |
| Capital disponible pour investir | 140 300€ |
| Investissement (rendement 7%/an, 10 ans) | 275 878€ |

### Avec apport-cession

| Poste | Montant |
|-------|---------|
| Vente via holding | 200 000€ |
| Flat tax | 0€ (reportée) |
| Capital disponible dans la holding | 200 000€ |
| Réinvestissement 60% obligatoire (120 000€) | Nouvelle activité |
| Investissement libre (80 000€) | CTO bourse, SCI, trésorerie |
| Valeur totale à 10 ans (7%/an sur 200 000€) | 393 430€ |

**Différence : +117 552€** grâce au report d'imposition, simplement parce que vous investissez avec 200 000€ au lieu de 140 300€.

## Les cas de fin du report (= imposition déclenchée)

| Événement | Conséquence |
|-----------|------------|
| Cession des parts de la holding | Imposition immédiate de la PV en report |
| Transfert du domicile fiscal hors de France | Imposition (exit tax), sursis possible dans l'UE |
| Non-respect du remploi (60% dans 2 ans) | Imposition de la PV initiale + intérêts de retard |
| Donation des parts de holding | **Purge du report !** La PV n'est jamais imposée |
| Décès | Purge du report (les héritiers ne paient pas la PV) |

### Le coup de maître : la donation-purge

Si vous donnez les parts de la holding à vos enfants :
1. Le report d'imposition est **définitivement purgé** (la PV n'est plus jamais imposée)
2. Vous payez uniquement les droits de donation (avec abattement de 100 000€ par enfant)
3. Si donation en nue-propriété : décote supplémentaire

**Exemple** : PV en report de 199 000€ → flat tax normalement due = 59 700€. Donation de la holding (valeur 200 000€) à 2 enfants → abattement 200 000€ → droits de donation = 0€. Économie : **59 700€**.

## Les erreurs à ne pas commettre

1. **Cession concomitante** : si l'apport et la vente sont simultanés ou quasi-simultanés, l'administration peut requalifier en abus de droit
2. **Holding sans substance** : la holding doit avoir une réalité (bureau, décisions stratégiques, gestion active)
3. **Réinvestissement trop tardif** : si cession < 3 ans et remploi pas fait sous 2 ans = imposition + intérêts de retard (0.2%/mois)
4. **Réinvestissement dans du patrimonial** : l'immobilier locatif nu ou la bourse ne comptent pas comme remploi
5. **Oublier la déclaration** : le report doit être déclaré chaque année sur la 2042-C (formulaire complémentaire)

---

**Sources officielles 2026 :**
- CGI - Art. 150-0 B ter (report d'imposition apport-cession) : legifrance.gouv.fr
- BOFiP - Apport de titres et report d'imposition : bofip.impots.gouv.fr
- CGI - Art. 150-0 A (plus-values sur valeurs mobilières) : legifrance.gouv.fr
- CGI - Art. 150-0 D ter (abattement cession dirigeant retraite) : legifrance.gouv.fr
- Conseil d'État - Jurisprudence abus de droit apport-cession : conseil-etat.fr
- DGFiP - Formulaire 2042-C (déclaration du report) : impots.gouv.fr`
  },

  'remuneration-optimale-dirigeant-2026': {
    id: '40',
    title: 'Rémunération optimale du dirigeant : le calcul exact en 2026',
    slug: 'remuneration-optimale-dirigeant-2026',
    excerpt: 'Salaire, dividendes, PER, épargne salariale : la méthode pas à pas pour maximiser votre net.',
    category: 'Optimisation',
    tags: ['Rémunération', 'Dividendes', 'Optimisation', 'SASU', 'EURL'],
    readTime: 14,
    date: '2026-03-22',
    author: 'Bozzam',
    popular: true,
    fullContent: `## La rémunération du dirigeant : un puzzle fiscal à optimiser

Vous êtes freelance en société et vous vous demandez combien vous verser en salaire, combien en dividendes, et comment utiliser les autres leviers pour maximiser ce qui arrive sur votre compte en banque ? Voici la méthode complète.

## Étape 1 : Comprendre les flux d'argent

Pour chaque euro de CA, voici les chemins possibles :

| Canal de sortie | Charges sociales | IR | Avantage |
|----------------|-----------------|-----|----------|
| Salaire (SASU) | ~80% du net en charges patronales+salariales | Barème progressif | Retraite, protection sociale |
| Rémunération TNS (EURL) | ~45% du net | Barème progressif | Cotisations plus faibles |
| Dividendes SASU | 0% charges sociales | Flat tax 30% | Simple, prévisible |
| Dividendes EURL (>10% capital) | ~45% cotisations TNS | Barème progressif | Complique l'EURL |
| PEE (abondement) | CSG/CRDS 9.7% | Exonéré | Très avantageux |
| PERE-CO (abondement) | CSG/CRDS 9.7% | Exonéré | Bloqué retraite |
| PER individuel | Aucune | Déductible du revenu | Bloqué retraite |
| Frais professionnels | Aucune | Déductible du résultat | Réel uniquement |
| Bureau domicile | Aucune | Exonéré (indemnité) | Limité au prorata |

## Étape 2 : Le salaire optimal en SASU

### Le raisonnement

Le salaire en SASU sert à :
- Valider vos trimestres retraite (minimum ~7 000€ brut/an pour 4 trimestres)
- Cotiser pour la retraite complémentaire AGIRC-ARRCO
- Bénéficier des indemnités journalières maladie/maternité
- Remplir les tranches basses d'IR (0% et 11%)

### Le calcul du salaire optimal

**Objectif** : maximiser le salaire dans les tranches IR à 0% et 11%, puis basculer en dividendes pour la tranche à 30%+.

| Tranche IR | Seuil (célibataire) | Remplir en salaire ? |
|-----------|--------------------|--------------------|
| 0% | 0 à 11 497€ | Oui — c'est gratuit en IR |
| 11% | 11 497€ à 29 315€ | Oui — 11% < flat tax 12.8% |
| 30% | 29 315€ à 83 823€ | Non — dividendes plus avantageux |

**Salaire net optimal (célibataire)** : ~29 315€/an soit **~2 443€ net/mois** (pour rester dans la tranche à 11%).

**En couple (2 parts)** : ~58 630€/an soit **~4 886€ net/mois**.

### Au-delà : dividendes

Tout bénéfice supplémentaire est distribué en dividendes à la flat tax (30%), ce qui est plus avantageux que le salaire dès que votre TMI dépasse 11%.

## Étape 3 : Le salaire optimal en EURL

En EURL, le raisonnement est différent car les cotisations TNS sont plus faibles :

| Poste | SASU | EURL |
|-------|------|------|
| Coût total pour 1€ net | ~1.82€ | ~1.47€ |
| Charges sur dividendes | 0% (flat tax seule) | ~45% sur la part > 10% capital |
| Split optimal | Salaire modéré + dividendes | Rémunération élevée (dividendes pénalisés) |

**En EURL** : prenez le maximum en rémunération TNS (pas de split salaire/dividendes avantageux à cause des cotisations sur dividendes > 10% capital).

## Étape 4 : Ajouter l'épargne salariale (SASU)

Après le salaire et les dividendes, ajoutez l'épargne salariale :

| Dispositif | Montant max | Économie vs salaire (TMI 30%) |
|-----------|-------------|-------------------------------|
| PEE abondement | 3 709€ | ~1 500€ |
| PERE-CO abondement | 7 418€ | ~3 000€ |
| **Total** | **11 127€** | **~4 500€** |

## Étape 5 : Ajouter le PER individuel

Le PER individuel est le dernier étage de la fusée :

| Statut | Versement recommandé | Économie IR (TMI 30%) |
|--------|---------------------|----------------------|
| SASU (TMI 30%) | 10 000€ à 15 000€ | 3 000€ à 4 500€ |
| EURL TNS (TMI 30%) | 15 000€ à 30 000€ | 4 500€ à 9 000€ |
| EURL TNS (TMI 41%) | 30 000€ à 50 000€ | 12 300€ à 20 500€ |

## Simulation complète : bénéfice 150 000€

### SASU — stratégie optimale

| Poste | Montant |
|-------|---------|
| Bénéfice société | 150 000€ |
| Salaire brut (2 800€ net/mois × 12) | 33 600€ net → coût société ~61 150€ |
| PEE + PERE-CO abondement | 11 127€ → coût société ~12 207€ (+ CSG/CRDS) |
| Frais pro (bureau, repas, IK, etc.) | 12 000€ |
| Bénéfice avant IS | 150 000€ - 61 150€ - 12 207€ - 12 000€ = 64 643€ |
| IS (15% sur 42 500€ + 25% au-delà) | 11 911€ |
| Dividendes distribuables | 52 732€ |
| Flat tax (30%) | 15 820€ |
| PER individuel (15 000€) → économie IR | -4 500€ |
| | |
| **Revenus nets :** | |
| Salaire net après IR | ~30 100€ |
| Dividendes nets | ~36 912€ |
| PEE + PERE-CO (net de CSG) | ~10 048€ |
| Indemnité bureau domicile | ~3 468€ |
| Économie IR via PER | ~4 500€ |
| **Total net disponible** | **~85 028€** |

### Sans optimisation (tout en salaire)

| Poste | Montant |
|-------|---------|
| Bénéfice société | 150 000€ |
| Frais pro | 12 000€ |
| Salaire brut max | ~138 000€ |
| Charges patronales (~42%) | ~57 960€ |
| Salaire net | ~80 040€ |
| IR (célibataire, TMI 41%) | ~16 837€ |
| **Total net disponible** | **~63 203€** |

**Gain de l'optimisation : ~21 825€/an** soit +34% de revenu net.

## Étape 6 : Les frais à ne pas oublier

| Frais récupérable | Montant annuel estimé |
|-------------------|----------------------|
| IK (10 000 km, 6 CV) | ~5 200€ |
| Repas (220 jours × 9€) | ~1 980€ |
| Bureau domicile (prorata) | ~3 468€ |
| Téléphone + internet (50%) | ~480€ |
| Logiciels et abonnements | ~1 500€ |
| Formation + livres | ~1 000€ |
| **Total** | **~13 628€** |

Ces frais réduisent le bénéfice imposable et augmentent votre net disponible sans aucune charge sociale.

## Le tableau récapitulatif : quelle stratégie selon votre bénéfice

| Bénéfice société | Salaire net/mois | Dividendes/an | PEE+PERE-CO | PER | Net total estimé |
|-----------------|-----------------|---------------|-------------|-----|-----------------|
| 40 000€ | 2 500€ | 0€ | Non | Non | ~30 000€ |
| 60 000€ | 2 800€ | 5 000€ | Non | 5 000€ | ~38 000€ |
| 80 000€ | 2 800€ | 15 000€ | PEE seul | 10 000€ | ~50 000€ |
| 100 000€ | 3 000€ | 25 000€ | PEE+PERE-CO | 15 000€ | ~65 000€ |
| 150 000€ | 3 000€ | 50 000€ | PEE+PERE-CO | 15 000€ | ~85 000€ |
| 200 000€+ | 3 500€ | Holding | PEE+PERE-CO | 20 000€+ | ~110 000€+ |

## Les erreurs classiques

1. **Tout prendre en salaire** : vous payez 2× plus de charges que nécessaire
2. **Tout prendre en dividendes** : vous ne cotisez pas pour la retraite et la protection sociale
3. **Ignorer l'épargne salariale** : c'est de l'argent exonéré que vous laissez sur la table
4. **Oublier les frais** : chaque euro de frais déductible réduit votre IS
5. **Ne pas anticiper** : l'optimisation se planifie en début d'exercice, pas en décembre
6. **Confondre SASU et EURL** : la stratégie dividendes est très différente (flat tax en SASU vs cotisations TNS en EURL)

---

**Sources officielles 2026 :**
- CGI - Art. 200 A (PFU/flat tax) : legifrance.gouv.fr
- CGI - Art. 197 (barème IR 2026) : legifrance.gouv.fr
- CGI - Art. 219 (taux IS 2026) : legifrance.gouv.fr
- URSSAF - Cotisations dirigeants (assimilé salarié et TNS) : urssaf.fr
- Code du travail - Art. L3332-1 (PEE) : legifrance.gouv.fr
- CGI - Art. 154 bis (déduction PER TNS) : legifrance.gouv.fr
- BOFiP - Rémunération du dirigeant et IS : bofip.impots.gouv.fr`
  },

  'compte-courant-associe-remuneration-tresorerie': {
    id: '41',
    title: 'Compte courant d\'associé : rémunérer sa trésorerie personnelle en 2026',
    slug: 'compte-courant-associe-remuneration-tresorerie',
    excerpt: 'Prêter de l\'argent à sa société et toucher des intérêts déductibles : le levier discret pour sortir de la trésorerie sans flat tax.',
    category: 'Optimisation',
    tags: ['Compte courant', 'Trésorerie', 'SASU', 'EURL', 'Optimisation'],
    readTime: 12,
    date: '2026-03-26',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Le compte courant d'associé : le levier oublié des dirigeants

Vous avez de la trésorerie personnelle dormante sur un Livret A à 1,7% ? Et votre société a besoin de financer son BFR ou un investissement ? Le compte courant d'associé (CCA) vous permet de prêter votre argent à votre société **et de toucher des intérêts déductibles** du résultat, plafonnés par un taux légal publié chaque trimestre par l'administration fiscale.

C'est l'un des rares outils qui permet de faire sortir de l'argent de la société **sans flat tax de 30%**, tout en diminuant l'impôt sur les sociétés (IS).

## Qu'est-ce qu'un compte courant d'associé ?

Le CCA est un prêt de l'associé (vous) à la société. Juridiquement, c'est une avance en compte courant inscrite au passif du bilan. Concrètement :

- Vous virez 30 000€ de votre compte personnel vers le compte pro de la société
- La société vous doit cette somme et peut l'utiliser comme trésorerie
- La société vous verse des intérêts (facultatifs mais recommandés)
- Vous pouvez retirer votre argent à tout moment (sauf clause de blocage)

| Mouvement | Impact société | Impact associé |
|-----------|---------------|----------------|
| Apport en CCA | Trésorerie +, dette au passif | Créance sur la société |
| Intérêts versés | Charge déductible (IS) | Revenu imposable (flat tax 30%) |
| Remboursement | Trésorerie - | Récupération du capital (non taxée) |

## Le taux maximum déductible en 2026

Les intérêts versés à l'associé sont déductibles du résultat **dans la limite du taux moyen des prêts à taux variable aux entreprises** publié chaque trimestre par Bercy.

| Période de clôture 12 mois | Taux maximum déductible estimé 2026 |
|----------------------------|-------------------------------------|
| Clôture 31/12/2026 | ~5,80% (à confirmer trimestriellement) |
| Clôture 30/06/2026 | ~5,95% |
| Clôture 31/03/2026 | ~6,00% |

> Source officielle : BOFiP - BOI-BIC-CHG-50-50-30. Le taux exact est publié au Journal Officiel chaque trimestre.

**Tout intérêt au-delà de ce taux est réintégré fiscalement** (et requalifié en revenu distribué, avec flat tax).

## Le match : CCA rémunéré vs dividendes vs Livret A

Prenons 30 000€ de trésorerie personnelle à placer. Comparaison sur 1 an :

| Option | Rendement brut | Fiscalité | Net pour l'associé | Coût net société |
|--------|---------------|-----------|-------------------|------------------|
| Livret A (1,7%) | 510€ | Exonéré | 510€ | — |
| LEP (3,5% si éligible) | 1 050€ | Exonéré | 1 050€ | — |
| CCA à 5,80% | 1 740€ | Flat tax 30% → 1 218€ | **1 218€** | -435€ d'IS (25%) → **coût net 1 305€** |
| Dividendes 1 740€ | — | Flat tax 30% → 1 218€ | 1 218€ | Après IS déjà payé : coût 2 320€ de bénéfice |

**Conclusion** : le CCA rémunéré à 5,80% surpasse largement le Livret A (1 218€ vs 510€) **et** coûte moins cher à la société que la distribution d'un dividende équivalent (car déductible de l'IS).

## Cas concret : 50 000€ de trésorerie personnelle placée en CCA

Contexte : Marc, président SASU, a 50 000€ d'épargne personnelle et sa société a besoin de financer son BFR.

| Année | Intérêts CCA (5,80%) | IS économisé (25%) | Flat tax (30%) | Gain net Marc |
|-------|---------------------|-------------------|----------------|---------------|
| Année 1 | 2 900€ | -725€ | -870€ | **2 030€** |
| Année 2 | 2 900€ | -725€ | -870€ | 2 030€ |
| Année 3 | 2 900€ | -725€ | -870€ | 2 030€ |
| **Total 3 ans** | 8 700€ | -2 175€ | -2 610€ | **6 090€** |

**Vs placement Livret A 3 ans : ~1 580€**. Gain net : **+4 510€**.

## Les 4 conditions pour que ça marche

### 1. Capital social intégralement libéré
Le capital social doit être **entièrement appelé et libéré** avant de pouvoir verser des intérêts déductibles sur le CCA (article 39-1-3° du CGI).

### 2. Convention écrite
Rédigez une **convention de compte courant d'associé** précisant :
- Le montant de l'apport
- Le taux d'intérêt (ne pas dépasser le taux légal)
- Les conditions de remboursement
- Les modalités de blocage (optionnel)

### 3. Décision d'AG ou du président (SASU/EURL)
- En SASU : décision du président
- En EURL : décision de l'associé unique
- En SAS/SARL : convention réglementée à approuver en AG

### 4. Déclaration en convention réglementée
Le CCA est une convention réglementée à mentionner dans le rapport de gestion et à approuver par l'assemblée.

## Les pièges à éviter

### Piège n°1 : Dépasser le taux légal
Les intérêts au-delà du taux légal **ne sont pas déductibles**. Exemple : vous versez 7% d'intérêts, seuls ~5,80% sont déductibles. Le reste est réintégré + majoration de 40% pour manquement délibéré possible.

### Piège n°2 : Capital non libéré
Si votre capital social (1€ symbolique par exemple) n'est pas intégralement libéré, **aucun intérêt n'est déductible**. Libérez votre capital avant.

### Piège n°3 : Pas de convention écrite
En cas de contrôle fiscal, l'absence de convention peut faire requalifier les intérêts en distribution déguisée (flat tax + pas de déduction).

### Piège n°4 : Prélèvements à la source
Les intérêts de CCA versés à un particulier font l'objet d'un **prélèvement forfaitaire unique (PFU) de 30%** retenu à la source par la société (CSG + CRDS 17,2% + IR 12,8%). La société doit faire le reversement via la déclaration 2777.

### Piège n°5 : Comptes courants débiteurs
L'associé ne peut **pas avoir un compte courant débiteur** (société qui prête à l'associé). C'est interdit en SASU/SARL sous peine de sanctions pénales (art. L241-3 du Code de commerce).

## CCA vs apport en capital : quelle différence ?

| Critère | Apport en capital | Compte courant d'associé |
|---------|------------------|-------------------------|
| Récupération | Liquidation ou réduction de capital (lourd) | À tout moment (sauf blocage) |
| Rémunération | Dividendes (flat tax) | Intérêts (flat tax) mais **déductibles** |
| Formalisme | AG + statuts + K-bis | Simple convention |
| Risque | Perte en cas de liquidation (dernier rang) | Créancier classique (rang supérieur) |
| Droit de vote | Oui | Non |

**Conclusion** : le CCA est **plus souple** et **plus avantageux fiscalement** qu'un apport en capital pour financer une activité au quotidien.

## Le cas spécial du blocage du CCA

Vous pouvez **bloquer** votre CCA pendant 3 à 5 ans. Avantage :
- Meilleure image auprès des banques (capitaux propres élargis)
- Permet d'augmenter le fonds de roulement sans augmenter le capital social
- Rassure les partenaires

En contrepartie, vous ne pouvez pas retirer les fonds pendant la période de blocage.

## Tableau récapitulatif : quand utiliser le CCA ?

| Situation | Recommandation |
|-----------|---------------|
| Trésorerie perso dormante sur Livret A | ✅ CCA à taux légal |
| Société en croissance, besoin BFR | ✅ CCA bloqué 3 ans |
| Très forte trésorerie société | ❌ Pas besoin de CCA |
| Capital social non libéré | ❌ Libérer le capital avant |
| Vous êtes dans la TMI 45% | ⚠️ Arbitrer avec barème progressif |

## FAQ

**Le CCA doit-il être rémunéré ?**
Non, la rémunération est facultative. Mais sans rémunération, vous perdez le levier fiscal.

**Puis-je faire un CCA en EURL ?**
Oui, mêmes règles. Attention : en EURL à l'IR, les intérêts du CCA sont réintégrés car l'associé et la société sont confondus fiscalement.

**Que se passe-t-il en cas de liquidation ?**
Vous êtes créancier chirographaire (rang inférieur aux créanciers privilégiés mais supérieur aux associés). Vos chances de récupération sont meilleures qu'un apport en capital.

**Le CCA entre-t-il dans l'assiette de l'IFI ?**
Le CCA prêté à une société opérationnelle est en principe exonéré d'IFI (bien professionnel). Attention en holding passive.

---

**Sources officielles 2026 :**
- CGI - Art. 39-1-3° (déductibilité des intérêts de CCA) : legifrance.gouv.fr
- BOFiP - BOI-BIC-CHG-50-50-30 (taux maximum déductible) : bofip.impots.gouv.fr
- CGI - Art. 212 (lutte contre la sous-capitalisation) : legifrance.gouv.fr
- Code de commerce - Art. L241-3 (interdiction des comptes courants débiteurs) : legifrance.gouv.fr
- CGI - Art. 200 A (PFU sur les intérêts) : legifrance.gouv.fr`
  },

  'voiture-societe-vs-ik-vs-loa-2026': {
    id: '42',
    title: 'Voiture de société vs IK vs LOA : le comparatif fiscal 2026',
    slug: 'voiture-societe-vs-ik-vs-loa-2026',
    excerpt: 'Achat, location longue durée, leasing ou indemnités kilométriques : le match chiffré pour choisir la bonne stratégie véhicule en SASU/EURL.',
    category: 'Optimisation',
    tags: ['Voiture', 'LOA', 'IK', 'SASU', 'EURL', 'Déduction'],
    readTime: 13,
    date: '2026-03-27',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Voiture et freelance : 4 stratégies, une seule optimale

Vous êtes freelance en SASU ou EURL et vous vous demandez s'il vaut mieux acheter la voiture au nom de la société, la louer en LOA, la prendre en LLD ou utiliser votre voiture personnelle avec des IK ? Voici le match chiffré en 2026.

## Les 4 stratégies possibles

| Stratégie | Propriétaire | Déduction société | Inconvénient |
|-----------|-------------|-------------------|--------------|
| 1. Achat par la société | Société | Amortissement plafonné + frais | Plafonds stricts, AEN dirigeant |
| 2. LOA (leasing) | Crédit-bailleur | Loyers plafonnés + frais | AEN dirigeant |
| 3. LLD (location longue durée) | Loueur | Loyers plafonnés + frais | AEN dirigeant |
| 4. Véhicule perso + IK | Vous | IK kilométriques | Pas de TVA récupérable |

## Le plafond d'amortissement en 2026

Pour tous les véhicules de tourisme (M1), l'administration fiscale plafonne la déduction de l'amortissement ou des loyers en fonction des émissions CO₂ (article 39-4 du CGI) :

| Émissions CO₂ (NEDC / WLTP) | Plafond de déduction 2026 |
|------------------------------|--------------------------|
| ≤ 20 g/km (électrique, hydrogène) | **30 000€** |
| 21 à 49 g/km (hybride rechargeable) | **20 300€** |
| 50 à 159 g/km (thermique standard) | **18 300€** |
| ≥ 160 g/km (grosse cylindrée) | **9 900€** |

> **Traduction** : si vous achetez un véhicule thermique à 45 000€, seuls **18 300€** seront amortis fiscalement sur 5 ans (soit ~3 660€/an). Le reste (26 700€) est perdu fiscalement.

## Stratégie 1 : Achat par la société — Le match thermique vs électrique

### Véhicule thermique à 45 000€ (CO₂ > 50 g)

| Poste | Année 1-5 | Commentaire |
|-------|-----------|-------------|
| Prix d'achat | 45 000€ | — |
| Amortissement déductible/an | 3 660€ | Plafonné à 18 300€/5 ans |
| Amortissement non déductible/an | 5 340€ | Réintégré au résultat fiscal |
| Frais annuels (assurance, entretien, carburant) | 6 000€ | Déductibles à 100% |
| TVA déductible sur achat | **0€** | Non récupérable sur VP |
| TVA déductible sur entretien | ~600€ | À 100% |
| AEN (avantage en nature) dirigeant | Oui | ~4 500€/an à déclarer en salaire |

### Véhicule électrique à 45 000€ (CO₂ ≤ 20 g)

| Poste | Année 1-5 | Commentaire |
|-------|-----------|-------------|
| Prix d'achat | 45 000€ | Bonus écologique ≤ 4 000€ possible |
| Amortissement déductible/an | 6 000€ | Plafonné à 30 000€/5 ans |
| Amortissement non déductible/an | 3 000€ | Réintégré |
| Frais annuels | 3 000€ | Électricité bien moins chère |
| TVA déductible sur achat | **0€** | Non récupérable sur VP électrique |
| TVA déductible sur borne | 100% | Si installation au bureau |
| AEN dirigeant | Exonération partielle | Abattement 50% pour VE jusqu'en 2027 |

**Conclusion achat** : l'électrique est nettement plus rentable fiscalement et socialement (AEN réduit) en 2026.

## Stratégie 2 : LOA / LLD — L'avantage de la flexibilité

### Comparatif LOA/LLD vs achat direct (thermique 45 000€)

| Critère | Achat | LOA/LLD 48 mois (~750€/mois) |
|---------|-------|------------------------------|
| Sortie trésorerie | 45 000€ d'un coup | 750€ × 48 = 36 000€ |
| Déduction | Amortissement plafonné | Loyer plafonné (même règle 18 300€) |
| Bilan | Immobilisation au bilan | Hors bilan (charge simple) |
| Fin de contrat | Véhicule à vous | Option d'achat ou restitution |
| Revente | À votre charge | Pas de risque de revente |

**La LOA/LLD est préférable si** :
- Vous voulez préserver votre trésorerie
- Vous voulez changer de véhicule tous les 3-4 ans
- Vous voulez éviter l'aléa de la revente

**L'achat est préférable si** :
- Vous avez la trésorerie disponible
- Vous gardez longtemps vos voitures (7-10 ans)
- Véhicule utilitaire (N1) avec TVA récupérable

## Stratégie 3 : IK — Le calcul 2026

Les indemnités kilométriques (IK) sont le régime par défaut si vous utilisez votre véhicule personnel pour des trajets pro.

### Barème IK 2026 (véhicule thermique)

| Puissance fiscale | Jusqu'à 5 000 km | De 5 001 à 20 000 km | Au-delà de 20 000 km |
|-------------------|------------------|---------------------|---------------------|
| 3 CV et moins | d × 0,529 | (d × 0,316) + 1 065 | d × 0,370 |
| 4 CV | d × 0,606 | (d × 0,340) + 1 330 | d × 0,407 |
| 5 CV | d × 0,636 | (d × 0,357) + 1 395 | d × 0,427 |
| 6 CV | d × 0,666 | (d × 0,374) + 1 460 | d × 0,447 |
| 7 CV et plus | d × 0,699 | (d × 0,392) + 1 535 | d × 0,469 |

> **Bonus véhicule électrique** : majoration de **+20%** sur toutes les tranches.

### Exemple : 15 000 km/an, 6 CV thermique
IK = (15 000 × 0,374) + 1 460 = **7 070€/an** de remboursement exonéré de charges et d'IR.

### Exemple : 15 000 km/an, 6 CV électrique
IK = 7 070€ × 1,20 = **8 484€/an**.

## Le match complet : 15 000 km/an sur 5 ans

Véhicule thermique acheté neuf 45 000€, revendu 15 000€ après 5 ans :

| Stratégie | Déduction totale 5 ans | Coût net après IS 25% | AEN dirigeant 5 ans |
|-----------|------------------------|----------------------|--------------------|
| 1. Achat société | 48 300€ (amort. + frais) | -33 075€ net | +22 500€ AEN (salaire) |
| 2. LOA 48 mois | 46 000€ (loyers + frais) | -34 500€ net | +22 500€ AEN |
| 3. LLD 48 mois | 44 000€ (loyers) | -33 000€ net | +22 500€ AEN |
| 4. **IK perso (6 CV)** | 35 350€ (7 070€ × 5) | -26 513€ net | **0€ AEN** |

**Gagnant pour un thermique à usage mixte** : **IK perso** (car pas d'AEN, simple, pas de gestion).

**Gagnant pour un électrique à usage 100% pro** : achat société (amortissement 30 000€, AEN à -50%).

## L'AEN dirigeant : le piège caché

Si la société met un véhicule à votre disposition pour usage privé, vous devez déclarer un **avantage en nature (AEN)** en salaire :

| Méthode | Calcul AEN |
|---------|-----------|
| Forfait achat (>5 ans) | 6% du prix d'achat TTC/an |
| Forfait achat (<5 ans) | 9% du prix d'achat TTC/an |
| Forfait location (>5 ans) | 6% du prix neuf TTC/an |
| Forfait location (<5 ans) | 9% du prix neuf TTC/an |

**Exonération partielle VE** : jusqu'au 31/12/2027, les véhicules électriques bénéficient d'un **abattement de 50% sur l'AEN** (plafonné à 2 000€/an) et l'électricité fournie n'est pas comptée.

### Exemple AEN thermique 45 000€ neuf
AEN = 45 000€ × 9% = **4 050€/an** à déclarer en salaire (+ charges sociales + IR).

**Coût réel** : pour le président SASU en TMI 30%, cet AEN représente ~2 800€ de charges sociales et ~1 215€ d'IR supplémentaires, soit ~4 000€/an de coûts cachés.

## TVA : la subtilité qui change tout

| Type de véhicule | TVA récupérable à l'achat |
|------------------|--------------------------|
| Voiture particulière (VP / M1) | **Non** (0%) |
| Véhicule utilitaire (N1, 2 places) | **Oui** (100%) |
| Pick-up 5 places (double cabine) | Non (requalifié VP) |
| Camping-car N1 | Oui |
| Moto, scooter | Oui si N1 |

**Astuce** : certains modèles "dérivés VP" (Dacia Dokker Van, Renault Kangoo VU, Peugeot Partner VU) sont homologués N1 → TVA récupérable à 100% sur l'achat.

**Carburant** :
- Gazole : TVA récupérable à 80%
- Essence : TVA récupérable à 80% (depuis 2022)
- Électricité : TVA récupérable à 100%

## Arbre de décision : quelle stratégie choisir ?

1. **Moins de 10 000 km pro/an ?** → IK véhicule perso (simple, pas d'AEN)
2. **Plus de 15 000 km pro/an ?** → Véhicule société, étudier LOA/LLD
3. **Véhicule électrique et >10 000 km ?** → Achat société (plafond 30 000€ + AEN -50%)
4. **Utilitaire 2 places ?** → Achat société (TVA 100% récupérable)
5. **Gros SUV thermique ?** → Perso + IK (fiscalement sanctionné en société)

## Tableau récapitulatif

| Profil | Stratégie optimale | Gain annuel estimé |
|--------|-------------------|---------------------|
| Consultant 8 000 km/an | IK 6 CV perso | ~3 600€ exonérés |
| Commercial 25 000 km/an | LLD 3 ans + carte carburant | ~12 000€ déduits |
| Artisan avec matériel | Achat N1 utilitaire | TVA 100% + amort. libre |
| Dirigeant image + luxe | LOA VE haut de gamme | Amort. 30 000€ + AEN -50% |
| Freelance occasionnel | Pas de véhicule société | Pure IK si besoin |

## Les erreurs classiques

1. **Acheter un gros thermique en société** : la moitié de la voiture n'est pas amortissable
2. **Oublier l'AEN** : l'URSSAF redresse 3 ans en arrière avec majorations
3. **Mettre des IK sur une voiture société** : non-cumulable (erreur fréquente)
4. **Choisir le VP au lieu du N1** : perdre 20% de TVA sur 45 000€ = 7 500€
5. **Ignorer le bonus VE** : jusqu'à 4 000€ d'aide + AEN divisé par 2

---

**Sources officielles 2026 :**
- CGI - Art. 39-4 (plafond d'amortissement véhicules) : legifrance.gouv.fr
- Barème kilométrique 2026 : impots.gouv.fr
- BOFiP - BOI-BIC-AMT-20-40-50 (amortissements véhicules) : bofip.impots.gouv.fr
- URSSAF - AEN véhicule : urssaf.fr
- CGI - Art. 298-4 (TVA véhicules) : legifrance.gouv.fr
- Décret 2020-1726 (abattement AEN véhicules électriques) : legifrance.gouv.fr`
  },

  'pacte-dutreil-transmission-societe-2026': {
    id: '43',
    title: 'Pacte Dutreil : transmettre sa société avec 75% d\'abattement',
    slug: 'pacte-dutreil-transmission-societe-2026',
    excerpt: 'Donation ou succession de votre société avec 75% d\'abattement sur les droits : les 5 conditions et la marche à suivre en 2026.',
    category: 'Optimisation',
    tags: ['Transmission', 'Dutreil', 'Donation', 'Holding', 'Succession'],
    readTime: 14,
    date: '2026-03-28',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Le pacte Dutreil : le dispositif qui divise les droits de succession par 4

Vous êtes freelance en société (SASU, EURL, SAS) et vous avez construit une belle valeur ? Bonne nouvelle : le **pacte Dutreil** (article 787 B du CGI) vous permet de **transmettre votre société** (donation ou succession) avec un **abattement de 75% sur la valeur** taxable aux droits de mutation.

Concrètement, sur une société valorisée 2 000 000€, seuls **500 000€** sont soumis aux droits de donation/succession. Associé à l'abattement en ligne directe (100 000€/enfant/parent tous les 15 ans), c'est **le dispositif n°1 de transmission patrimoniale** pour les dirigeants.

## Le gain fiscal : l'ampleur du levier Dutreil

Prenons le cas de Sophie, présidente SASU, société valorisée **1 500 000€**, transmise à ses 2 enfants.

### Sans pacte Dutreil

| Poste | Montant |
|-------|---------|
| Valeur transmise/enfant | 750 000€ |
| Abattement ligne directe | -100 000€ |
| Base taxable/enfant | 650 000€ |
| Droits de donation (barème progressif) | **~144 000€ par enfant** |
| **Total droits pour 2 enfants** | **~288 000€** |

### Avec pacte Dutreil (abattement 75%)

| Poste | Montant |
|-------|---------|
| Valeur transmise/enfant | 750 000€ |
| Abattement Dutreil (75%) | -562 500€ |
| Base après Dutreil | 187 500€ |
| Abattement ligne directe | -100 000€ |
| Base taxable/enfant | 87 500€ |
| Droits de donation | **~14 200€ par enfant** |
| **Total droits pour 2 enfants** | **~28 400€** |

**Économie : ~260 000€ soit -90% de droits.**

## Le pacte Dutreil, comment ça marche ?

C'est un contrat en 3 étages :

1. **Engagement collectif** de conservation des titres pendant **au moins 2 ans** (signé avant la donation/succession)
2. **Engagement individuel** de chaque donataire/héritier de conserver les titres pendant **4 ans supplémentaires** après la fin de l'engagement collectif
3. **Un des signataires exerce une fonction de direction** dans la société pendant l'engagement collectif + 3 ans après la transmission

> Au total, les titres doivent être conservés **pendant au moins 6 ans** (2 ans collectif + 4 ans individuel).

## Les 5 conditions à respecter (2026)

### Condition 1 — Société éligible

La société doit exercer une **activité opérationnelle** : industrielle, commerciale, artisanale, agricole ou libérale. Sont **exclues** :
- Les sociétés de gestion de patrimoine (SCI non exploitante, holding passive pure)
- Les sociétés qui détiennent principalement des placements financiers passifs

**Cas des holdings** : une holding animatrice (qui pilote activement ses filiales) est éligible. Une holding simplement patrimoniale ne l'est pas.

### Condition 2 — Engagement collectif de 2 ans

Un ou plusieurs associés signent un engagement de conservation portant sur :
- **Au moins 17% des droits financiers et 34% des droits de vote** pour une société non cotée (seuils 2026)
- Durée minimale : 2 ans, tacitement reconduite

> **Nouveauté 2026** : l'engagement collectif "réputé acquis" est possible si le dirigeant détenait seul ces seuils depuis 2 ans et exerçait une fonction de direction.

### Condition 3 — Engagement individuel de 4 ans

Chaque donataire/héritier s'engage à **conserver les titres pendant 4 ans après la fin de l'engagement collectif**.

### Condition 4 — Fonction de direction

**Pendant toute la durée de l'engagement collectif + 3 ans après la transmission**, **au moins un des signataires** doit exercer :
- Une fonction de direction (président, gérant, directeur général)
- Une activité professionnelle principale (pour les sociétés IR)

### Condition 5 — Déclaration en annexe de la déclaration de succession/donation

Le notaire doit joindre le pacte Dutreil et attester chaque année pendant 6 ans du respect des engagements.

## Pacte Dutreil + donation en pleine propriété avant 70 ans = combo gagnant

Si vous donnez **avant vos 70 ans**, vous cumulez :

| Avantage | Effet |
|----------|-------|
| Abattement Dutreil | -75% sur la valeur taxable |
| Réduction de droits -50% | Sur les droits restants (donation en pleine propriété avant 70 ans) |
| Abattement ligne directe | -100 000€/enfant (tous les 15 ans) |

### Simulation : société 2M€, donation à 2 enfants à 65 ans

| Poste | Montant |
|-------|---------|
| Valeur transmise/enfant | 1 000 000€ |
| Après Dutreil 75% | 250 000€ |
| Après abattement 100 000€ | 150 000€ |
| Droits bruts | ~28 200€ |
| Réduction -50% avant 70 ans | -14 100€ |
| **Droits nets/enfant** | **~14 100€** |
| **Total pour 2 enfants** | **~28 200€** |

**Vs sans Dutreil et après 70 ans : ~380 000€**. Gain : **~350 000€**.

## Démembrement : transmettre la nue-propriété en gardant l'usufruit

Stratégie avancée : donner la **nue-propriété** aux enfants en gardant l'**usufruit**.

### Barème fiscal 2026 (art. 669 CGI)

| Âge usufruitier | Valeur usufruit | Valeur nue-propriété |
|-----------------|-----------------|---------------------|
| Moins de 21 ans | 90% | 10% |
| 21 à 30 ans | 80% | 20% |
| 31 à 40 ans | 70% | 30% |
| 41 à 50 ans | 60% | 40% |
| 51 à 60 ans | 50% | 50% |
| 61 à 70 ans | 40% | 60% |
| 71 à 80 ans | 30% | 70% |
| 81 à 90 ans | 20% | 80% |

### Combiné Dutreil + démembrement à 61-70 ans

Société 1M€ → nue-propriété = 600 000€

| Poste | Montant |
|-------|---------|
| Valeur nue-propriété/enfant | 300 000€ |
| Après Dutreil 75% | 75 000€ |
| Après abattement 100 000€ | 0€ |
| **Droits nets/enfant** | **0€** |

**Vous avez transmis 1M€ sans aucun droit à payer**, et gardez l'usufruit (dividendes, pouvoir de vote).

## Les pièges fatals qui font perdre Dutreil

### Piège n°1 : Cession des titres avant 6 ans
Si un enfant vend ses titres avant la fin de l'engagement individuel, **le Dutreil est rétroactivement perdu**. Les droits dus sont recalculés + intérêts de retard.

### Piège n°2 : Transformation ou fusion
Certaines opérations (fusion, scission, apport à une holding non signataire) **rompent** l'engagement. Il faut prévoir une clause de continuation.

### Piège n°3 : Arrêt de l'activité opérationnelle
Si la société bascule en simple gestion de patrimoine pendant les 6 ans, **Dutreil tombe**.

### Piège n°4 : Absence de fonction de direction
Si aucun signataire n'exerce plus de fonction de direction pendant les 3 ans post-transmission, **Dutreil tombe**.

### Piège n°5 : Seuils de détention non respectés
Le seuil de 17% / 34% (sociétés non cotées) doit être **maintenu pendant l'engagement collectif**.

## Apport à une holding : le mécanisme intercalaire

Vous pouvez **apporter** les titres Dutreil à une **holding** détenue par les enfants pendant l'engagement individuel, sous conditions strictes :

- La holding doit avoir pour objet exclusif la gestion des titres apportés
- Les titres apportés doivent représenter **> 50% de l'actif** de la holding
- La fonction de direction doit être exercée dans la holding ou dans la société apportée
- Les parts de la holding doivent être conservées jusqu'à la fin de l'engagement

**Intérêt** : permet de mutualiser plusieurs participations familiales ou de préparer une cession future.

## Dutreil et freelance : c'est pertinent à partir de quand ?

| Valorisation société | Pertinence Dutreil |
|---------------------|-------------------|
| < 200 000€ | ⚠️ Peu utile (abattement 100k€ suffit) |
| 200 000€ à 500 000€ | ✅ Économie 20-50k€ |
| 500 000€ à 1M€ | ✅✅ Économie 50-150k€ |
| 1M€ à 3M€ | ✅✅✅ Économie 150-600k€ |
| > 3M€ | ✅✅✅✅ Indispensable + holding |

## Les étapes concrètes pour mettre en place Dutreil

1. **Évaluer la société** : expert-comptable ou commissaire aux apports (méthode mixte)
2. **Vérifier l'éligibilité** : activité opérationnelle, seuils de détention
3. **Rédiger le pacte collectif** : notaire + avocat fiscaliste (compter 2 000 à 5 000€ HT)
4. **Enregistrer auprès des impôts** : SIE dont dépend la société
5. **Attendre 2 ans** (engagement collectif minimum)
6. **Réaliser la donation** chez le notaire
7. **Souscrire les attestations annuelles** pendant 6 ans

**Coût moyen d'une opération Dutreil** : 3 000 à 15 000€ d'honoraires (notaire + fiscaliste) selon la complexité.

## FAQ

**Puis-je faire Dutreil sur une SASU unipersonnelle ?**
Oui. Le "pacte collectif" peut être signé par vous seul sous forme d'**engagement réputé acquis** si vous détenez les seuils (17% / 34%) depuis 2 ans et exercez la direction.

**Faut-il un notaire obligatoirement ?**
Pour la donation oui. Pour le pacte initial, un avocat fiscaliste suffit (acte sous seing privé enregistré).

**Que se passe-t-il en cas de décès avant les 2 ans collectif ?**
Si le décès survient avant la fin de l'engagement collectif, les héritiers peuvent poursuivre l'engagement. Dutreil s'applique.

**Puis-je cumuler Dutreil avec assurance-vie ?**
Oui. Dutreil s'applique aux titres de société, l'assurance-vie a son propre régime (abattement 152 500€/bénéficiaire).

**Dutreil et pacte Dutreil-transmission (2003) : quelle différence ?**
C'est le même dispositif. "Pacte Dutreil" est le nom courant. "Article 787 B" est la référence juridique.

---

**Sources officielles 2026 :**
- CGI - Art. 787 B (abattement 75% Dutreil) : legifrance.gouv.fr
- CGI - Art. 779 (abattement ligne directe 100 000€) : legifrance.gouv.fr
- CGI - Art. 790 (réduction de 50% avant 70 ans) : legifrance.gouv.fr
- CGI - Art. 669 (barème usufruit/nue-propriété) : legifrance.gouv.fr
- BOFiP - BOI-ENR-DMTG-10-20-40 (pacte Dutreil) : bofip.impots.gouv.fr
- Loi n°2019-1479 de finances 2020 (assouplissement Dutreil) : legifrance.gouv.fr`
  },

  'holding-freelance-strategie-complete-2026': {
    id: '44',
    title: 'Holding pour freelance : le guide stratégique complet 2026',
    slug: 'holding-freelance-strategie-complete-2026',
    excerpt: 'Pourquoi et comment monter une holding quand on est freelance : régime mère-fille, apport de titres, filiales, SCI, crédit bancaire. Le playbook complet avec bilans chiffrés.',
    category: 'Optimisation',
    tags: ['Holding', 'Mère-fille', 'SCI', 'SASU', 'EURL', 'Stratégie'],
    readTime: 18,
    date: '2026-04-02',
    author: 'Bozzam',
    popular: true,
    fullContent: `## La holding : l'outil stratégique du freelance confirmé

Quand votre SASU ou EURL commence à générer une trésorerie qui dépasse vos besoins personnels, la question de la **holding** devient centrale. Ce n'est pas un outil pour "gros patrons" : **dès 60 000€ de dividendes distribuables par an**, une holding devient rentable pour un freelance.

Ce guide est le playbook complet : à quoi sert la holding, comment apporter votre société existante, le régime mère-fille, la création de filiales, la SCI financée par la holding, et 3 bilans chiffrés pour visualiser l'ensemble.

## 1. Les 5 usages concrets d'une holding

| Usage | Bénéfice principal |
|-------|-------------------|
| Remonter les dividendes sans flat tax | Économie 28,9% sur les dividendes |
| Créer plusieurs filiales (sociétés filles) | Mutualisation des risques + équilibrage IS |
| Acheter de l'immobilier via SCI | Financement bancaire à effet de levier |
| Investir en bourse (CTO société) | Report d'imposition des plus-values |
| Préparer la transmission (Dutreil) | Abattement 75% sur les droits |

## 2. Le régime mère-fille : le mécanisme fiscal central

C'est **la clé** de la holding. Quand votre SASU (fille) distribue 100 000€ de dividendes à votre holding (mère), au lieu de payer la flat tax de 30% (30 000€), **seuls 5% sont réintégrés au résultat de la holding** pour "quote-part de frais et charges".

### Le calcul exact

| Poste | Sans holding | Avec holding (mère-fille) |
|-------|-------------|--------------------------|
| Dividende distribué par la fille | 100 000€ | 100 000€ |
| Flat tax (30%) | -30 000€ | 0€ (exonération) |
| Quote-part 5% imposable à l'IS (25%) | — | 100 000€ × 5% × 25% = -1 250€ |
| **Montant disponible dans la holding** | 70 000€ (sur compte perso) | **98 750€ (dans la holding)** |

**Économie immédiate** : **28 750€ disponibles pour réinvestir** dans la holding (filiales, SCI, bourse), au lieu de sortir sur votre compte personnel.

### Les 3 conditions du régime mère-fille (art. 145 CGI)

1. La holding détient **au moins 5% du capital** de la fille
2. Les titres sont détenus **pendant au moins 2 ans**
3. La fille est une société soumise à l'IS

> **Important** : la holding doit opter pour le régime mère-fille sur déclaration. Ce n'est pas automatique.

## 3. Comment faire rentrer votre SASU existante dans une holding

Deux mécanismes possibles :

### Option A — Apport de titres (apport-cession 150-0 B ter)

Vous apportez vos actions SASU à la holding. Avantages :
- **Report d'imposition** sur la plus-value d'apport
- La holding devient propriétaire de la SASU sans sortie de cash
- Si vous cédez plus tard la SASU, la holding reçoit le prix de vente (pas vous personnellement)

### Option B — Cession à la holding (achat)

La holding achète vos titres avec de la trésorerie (ou un crédit bancaire). Avantages :
- Vous récupérez du cash sur votre compte perso **au prix de la plus-value imposée**
- Le prix de cession peut servir à rembourser votre résidence principale
- La holding rembourse le crédit avec les dividendes de la fille (mère-fille = pas de flat tax)

### Cas pratique : SASU valorisée 300 000€

**Scénario** : vous avez créé votre SASU il y a 5 ans avec un capital de 1 000€. Elle est aujourd'hui valorisée à 300 000€.

| Étape | Action | Montant |
|-------|--------|---------|
| 1 | Évaluation par expert-comptable (méthode EBE + actif net) | ~500 à 2 500€ HT |
| 2 | Rapport du commissaire aux apports (obligatoire > 30 000€) | ~1 500 à 4 000€ HT |
| 3 | Création de la holding (SASU ou SARL) | ~200 à 1 500€ |
| 4 | Apport des titres SASU → holding (capital holding = 300 000€) | 0€ cash |
| 5 | Plus-value d'apport (299 000€) placée en report d'imposition (150-0 B ter) | Impôt reporté |
| 6 | Délai de détention 3 ans obligatoire avant cession de la fille sans remettre en cause le report | — |

> **Note** : les tarifs dépendent du professionnel (Paris vs province, cabinet vs plateforme en ligne). Comptez minimum **2 000 à 3 500€ tout compris** pour une opération propre.

### Après l'opération, votre patrimoine se présente ainsi :

| Détention | Valeur | Type |
|-----------|--------|------|
| Vous (personne physique) | 100% holding | Parts sociales |
| Holding | 100% SASU (fille) + trésorerie | Actions |

## 4. Créer plusieurs filiales : l'intégration fiscale

Une fois la holding en place, vous pouvez créer des **sociétés filles** supplémentaires (nouvelle activité, acquisition, test d'un projet).

### Avantage n°1 : Isolation des risques
Si la fille n°2 fait faillite, cela n'impacte pas la fille n°1 ni la holding.

### Avantage n°2 : Intégration fiscale (IS)
Si la holding détient **≥ 95% de chaque fille**, elle peut opter pour l'**intégration fiscale** : les résultats de toutes les sociétés sont **agrégés** pour le calcul de l'IS.

| Société | Résultat fiscal |
|---------|----------------|
| Holding | +10 000€ |
| Fille 1 (activité principale) | +80 000€ |
| Fille 2 (nouvelle activité qui démarre) | -30 000€ |
| Fille 3 (SCI à l'IS) | -5 000€ |
| **Résultat d'intégration** | **+55 000€** |
| IS sans intégration | (10 000 × 15%) + (80 000 × 15% + 37 500 × 25%) = 22 875€ |
| IS avec intégration | 42 500 × 15% + 12 500 × 25% = 9 500€ |
| **Économie annuelle** | **~13 375€** |

### Avantage n°3 : Mutualisation de la trésorerie
La holding peut prêter aux filiales via des comptes courants ou des conventions de cash pooling.

## 5. La SCI financée par la holding : le levier immobilier

Voici le schéma le plus puissant pour un freelance qui veut acheter de l'immobilier.

### Le schéma en 4 étapes

1. **Création d'une SCI à l'IS** détenue à 99% par la holding et 1% par vous (pour éviter la SCI unipersonnelle)
2. La holding **apporte du cash** à la SCI (CCA ou capital)
3. La banque **prête à la SCI** en se basant sur les **3 bilans** (holding + SASU fille + SCI)
4. La SCI achète un bien immobilier (résidence pro, locatif, local commercial)

### Pourquoi la banque prête ?

La banque analyse :
- **Bilan de la SASU (fille)** : chiffre d'affaires, EBE, capacité à verser des dividendes
- **Bilan de la holding** : capacité à remonter les dividendes vers la SCI
- **Bilan de la SCI** : projet immobilier, loyers prévisionnels, VRD

Le levier bancaire est **bien plus fort** qu'en nom propre : la capacité d'emprunt de la SCI peut atteindre **5 à 10× les loyers nets annuels** (selon les banques).

### Exemple chiffré : achat d'un local pro à 400 000€

| Poste | Montant |
|-------|---------|
| Prix du bien (local commercial loué à la SASU fille) | 400 000€ |
| Apport holding → SCI (CCA) | 80 000€ (20%) |
| Crédit SCI sur 20 ans (4,2%) | 320 000€ |
| Mensualité crédit | ~1 980€ |
| Loyer payé par la SASU à la SCI (déductible) | 2 400€/mois |
| Cash-flow mensuel net SCI | ~+420€ |

**Cerise sur le gâteau** : la SASU déduit son loyer (économie IS), et au terme du crédit, la SCI possède un actif de 400 000€+.

## 6. Bilans chiffrés : avant vs après montage holding

### Bilan A — Avant (SASU seule)

| Actif | Passif |
|-------|--------|
| Trésorerie : 80 000€ | Capital : 1 000€ |
| Matériel : 5 000€ | Réserves : 70 000€ |
| Clients : 15 000€ | Dettes : 29 000€ |
| **Total : 100 000€** | **Total : 100 000€** |

**Situation** : vous êtes contraint de sortir 50 000€ en dividendes → flat tax 15 000€. Coût fiscal annuel récurrent.

### Bilan B — Après (Holding + SASU fille + SCI)

**Holding**

| Actif | Passif |
|-------|--------|
| Titres SASU : 300 000€ | Capital : 300 000€ |
| Titres SCI : 99€ | Résultat : 80 000€ |
| CCA en SCI : 80 000€ | Dettes : 0€ |
| Trésorerie : 0€ | |
| **Total : 380 099€** | **Total : 380 099€** |

**SASU (fille)**

| Actif | Passif |
|-------|--------|
| Trésorerie : 20 000€ | Capital : 1 000€ |
| Matériel : 5 000€ | Réserves : 10 000€ |
| Clients : 15 000€ | Dettes : 29 000€ |
| **Total : 40 000€** | **Total : 40 000€** |

**SCI (à l'IS)**

| Actif | Passif |
|-------|--------|
| Immobilier (local) : 400 000€ | Capital : 1 000€ |
| Trésorerie : 5 000€ | CCA holding : 80 000€ |
| | Emprunt banque : 320 000€ |
| | Résultat : 4 000€ |
| **Total : 405 000€** | **Total : 405 000€** |

### Gain global annuel estimé

| Poste | Montant annuel |
|-------|---------------|
| Remontée dividendes mère-fille (vs flat tax) | +14 500€ (sur 50 000€ de dividendes) |
| Loyer SASU → SCI (déductible de l'IS SASU) | +7 200€ d'économie IS |
| Capitalisation immobilière (amortissement crédit) | +~12 000€/an de patrimoine |
| Intégration fiscale (si filiales multiples) | +5 000 à 15 000€ |
| **Total impact positif annuel** | **~40 000€+** |

## 7. Les coûts et contraintes à ne pas sous-estimer

| Poste | Coût annuel |
|-------|-------------|
| Comptabilité holding | ~1 200 à 2 400€ HT |
| Comptabilité SCI à l'IS | ~800 à 1 500€ HT |
| CFE holding | ~400 à 800€ |
| CFE SCI | ~0 à 500€ (activité patrimoniale) |
| Avocat/fiscaliste ponctuel | ~500 à 2 000€ |
| **Coût récurrent total** | **~3 000 à 7 000€/an** |

> **Seuil de rentabilité** : la holding devient rentable à partir de ~60 000 à 80 000€ de dividendes distribuables par an (ou si projet immobilier).

## 8. Les erreurs classiques

1. **Monter la holding trop tôt** : coûts récurrents > gains fiscaux
2. **Oublier le délai de 2 ans** (mère-fille) : si vous distribuez dans les 2 ans, l'exonération est perdue rétroactivement
3. **Violer le délai de 3 ans** post-apport (150-0 B ter) : remise en cause du report d'imposition si cession trop rapide sans réemploi
4. **SCI à l'IR** au lieu de l'IS quand la holding détient : l'IR reporte les bénéfices sur vous personnellement (flat tax + IR)
5. **Absence de substance économique** : holding sans bureau, sans activité réelle → risque d'abus de droit fiscal
6. **Mauvaise valorisation** : sur-valoriser la SASU pour augmenter le capital holding peut entraîner une requalification

## 9. Checklist de démarrage

| Étape | Acteur | Délai |
|-------|--------|-------|
| Évaluation SASU | Expert-comptable | 2 à 4 semaines |
| Rapport commissaire aux apports | Commissaire (CAC) | 3 à 6 semaines |
| Statuts holding | Avocat ou plateforme | 1 semaine |
| Création holding (RCS) | Greffe | 1 semaine |
| Apport des titres SASU | Notaire/avocat | 1 semaine |
| Option régime mère-fille | Option fiscale (SIE) | Immédiat |
| (Optionnel) Création SCI | Notaire | 2 semaines |
| (Optionnel) Dossier crédit | Banque | 4 à 8 semaines |

**Durée totale pour un montage complet** : 2 à 4 mois.

---

**Sources officielles 2026 :**
- CGI - Art. 145 (régime mère-fille) : legifrance.gouv.fr
- CGI - Art. 216 (quote-part de frais et charges 5%) : legifrance.gouv.fr
- CGI - Art. 150-0 B ter (apport-cession, report d'imposition) : legifrance.gouv.fr
- CGI - Art. 223 A et suivants (intégration fiscale) : legifrance.gouv.fr
- Code de commerce - Art. L225-8 (commissaire aux apports) : legifrance.gouv.fr
- BOFiP - BOI-IS-BASE-10-10 (régime mère-fille) : bofip.impots.gouv.fr`
  },

  'salaire-smic-dividendes-vs-salaire-plein-2026': {
    id: '45',
    title: 'Salaire SMIC + dividendes vs salaire plein : la bonne stratégie 2026',
    slug: 'salaire-smic-dividendes-vs-salaire-plein-2026',
    excerpt: 'Faut-il se verser le SMIC + dividendes ou un salaire plein en SASU/EURL ? Le comparatif chiffré selon votre statut en 2026.',
    category: 'Optimisation',
    tags: ['Salaire', 'Dividendes', 'SMIC', 'SASU', 'EURL', 'Stratégie'],
    readTime: 13,
    date: '2026-04-01',
    author: 'Bozzam',
    popular: true,
    fullContent: `## La question du "combien me payer" en 3 scénarios

Vous êtes dirigeant de SASU ou EURL et vous vous demandez : faut-il se verser **uniquement le SMIC** et compléter avec des dividendes ? Ou **un salaire plein** de 3 000 à 4 000€ net/mois ? Ou bien encore **tout en rémunération TNS** ? La réponse dépend radicalement de votre statut.

## Les 3 scénarios à comparer

| Scénario | Statut optimal | Principe |
|----------|---------------|----------|
| A. Salaire SMIC + dividendes | SASU | Valider les trimestres, tout le reste en dividendes |
| B. Salaire plein + peu de dividendes | SASU avec TMI basse ou besoin de cotisations retraite élevées | Maximiser la protection sociale |
| C. Tout en rémunération TNS | EURL à l'IS | Cotisations plus faibles, dividendes pénalisés > 10% capital |

## Le SMIC 2026 : les chiffres clés

- **SMIC horaire brut** : 12,10€ (revalorisation janvier 2026)
- **SMIC mensuel brut (35h)** : 1 835,35€
- **SMIC mensuel net** : ~1 450€
- **SMIC annuel brut** : 22 024€
- **Coût employeur (après réductions)** : ~26 000€/an

## Scénario A — SASU au SMIC + dividendes (TMI 30%)

### Les paramètres

- Bénéfice avant rémunération : 100 000€
- Statut : Président SASU célibataire, 1 part
- Rémunération : SMIC brut 22 024€/an

### Le calcul

| Poste | Montant |
|-------|---------|
| Bénéfice avant rém. | 100 000€ |
| Salaire brut SMIC | 22 024€ |
| Charges patronales SASU (~42% après réductions) | ~9 250€ |
| Coût total salaire pour la société | ~31 274€ |
| Bénéfice avant IS | 100 000€ - 31 274€ = 68 726€ |
| IS (15% jusqu'à 42 500€ + 25% au-delà) | 6 375€ + 6 557€ = 12 932€ |
| Dividende distribuable | 55 794€ |
| Flat tax 30% | -16 738€ |
| Dividende net | **39 056€** |
| Salaire net après charges salariales | ~17 240€ |
| IR sur salaire (taux moyen ~5,5%) | -949€ |
| **Net total disponible** | **~55 347€** |

### Validations sociales

| Critère | Résultat |
|---------|----------|
| Trimestres retraite | 4/4 (validés avec SMIC) |
| Indemnités journalières maladie | Oui (IJ faibles ~25€/j) |
| Retraite complémentaire AGIRC-ARRCO | Cotisation minimale |
| Assurance chômage | Non (dirigeant SASU) |

## Scénario B — SASU avec salaire plein 3 500€ net/mois

### Les paramètres

- Même bénéfice : 100 000€
- Salaire brut : ~54 000€/an (~3 500€ net/mois)

### Le calcul

| Poste | Montant |
|-------|---------|
| Salaire brut | 54 000€ |
| Charges patronales (~42%) | ~22 680€ |
| Coût total salaire | ~76 680€ |
| Bénéfice avant IS | 23 320€ |
| IS (15%) | -3 498€ |
| Dividende distribuable | 19 822€ |
| Flat tax 30% | -5 947€ |
| Dividende net | **13 875€** |
| Salaire net après charges salariales | ~42 120€ |
| IR sur salaire (taux moyen ~13%) | -5 476€ |
| **Net total disponible** | **~50 519€** |

### Validations sociales

| Critère | Résultat |
|---------|----------|
| Trimestres retraite | 4/4 |
| Indemnités journalières maladie | **Oui (IJ ~82€/j)** |
| Retraite complémentaire | Cotisations élevées |
| Protection maternité/invalidité | Meilleure |

## Scénario C — EURL tout en rémunération TNS

### Les paramètres

- Même bénéfice : 100 000€
- Rémunération TNS : 60 000€

### Le calcul

| Poste | Montant |
|-------|---------|
| Rémunération TNS | 60 000€ |
| Cotisations sociales TNS (~45%) | -27 000€ |
| Net avant IR | 33 000€ |
| Bénéfice avant IS | 100 000€ - 60 000€ = 40 000€ |
| IS (15%) | -6 000€ |
| Résultat distribuable | 34 000€ |
| **Piège EURL** : dividendes > 10% capital → cotisations TNS (~45%) | |
| Dividende net (hyp. 10 000€ dans les 10% capital → flat tax ; le reste 24 000€ cotisé) | |
| Dividende 10 000€ - flat tax 30% = 7 000€ | 7 000€ |
| Dividende 24 000€ - cotis TNS 45% - IR 11% = 11 760€ | 11 760€ |
| IR sur rémunération TNS (TMI 30%) | ~5 500€ |
| **Net total disponible** | **~46 260€** |

## Le match : comparatif final

| Scénario | Net total disponible | Protection sociale | Retraite future |
|----------|---------------------|-------------------|-----------------|
| A. SASU SMIC + dividendes | **55 347€** ✅ | ⚠️ Faible | ⚠️ Faible |
| B. SASU salaire plein | 50 519€ | ✅ Forte | ✅ Forte |
| C. EURL tout TNS | 46 260€ | ⚠️ Moyenne | ⚠️ Moyenne |

## Comment choisir : la règle d'or

| Votre situation | Scénario recommandé |
|-----------------|---------------------|
| Vous avez < 35 ans, en bonne santé, pas d'enfants | **A — SASU SMIC + dividendes** (+5 000 à 10 000€/an) |
| Vous avez une famille, souhaitez IJ maladie élevées | **B — SASU salaire plein** (protection sociale) |
| Vous êtes en EURL et votre TMI dépasse 30% | **C — EURL avec optimisation PER** |
| Vous préparez un prêt immobilier | **B — Salaire plein** (meilleure lecture bancaire) |
| Vous anticipez une cession dans 3 ans | **A — SMIC + dividendes** (accumulation holding) |

## Le piège du SMIC : ce que personne ne vous dit

### Piège n°1 : Les IJ maladie quasi-nulles
Au SMIC, vos indemnités journalières en cas d'arrêt maladie plafonnent à ~25€/jour. Une assurance prévoyance **indispensable** (comptez 80 à 200€/mois).

### Piège n°2 : La retraite de base
Au SMIC, vous cotisez pour **4 trimestres validés** mais avec un **salaire de référence faible**. Votre retraite sera ~60% inférieure à un salaire plein sur 25 ans.

> **Compensation** : versez 10 000 à 15 000€/an sur un **PER individuel** (déductible des dividendes via IR de la société si TMI > 30%).

### Piège n°3 : Pas d'assurance chômage
Le président SASU au SMIC ne cotise pas à l'assurance chômage. En cas de perte d'activité, aucun droit.

## Cas particulier : le SMIC "plancher" pour valider la retraite

Le minimum absolu pour valider **4 trimestres** en 2026 est de :
- **6 990€ brut/an** (150× SMIC horaire × 4)

Vous pouvez donc théoriquement vous verser **moins que le SMIC** (~583€ brut/mois) et valider vos 4 trimestres. Attention : cela ne valide pas les points AGIRC-ARRCO.

## Tableau final : quelle stratégie selon le bénéfice

| Bénéfice société | Stratégie SASU | Stratégie EURL |
|-----------------|---------------|----------------|
| < 40 000€ | Salaire plein (pas de div.) | Rém. TNS plein |
| 40 000 à 80 000€ | Salaire 2 500€ net + dividendes | Rém. TNS 40 000€ |
| 80 000 à 150 000€ | Salaire 2 500 à 3 000€ net + dividendes + PER | Rém. TNS 50-60 000€ + PER |
| > 150 000€ | SMIC + dividendes + PEE/PERE-CO + holding | Rém. TNS 70 000€ + holding |

---

**Sources officielles 2026 :**
- Code du travail - Art. L3231-1 (SMIC) : legifrance.gouv.fr
- URSSAF - Barème cotisations SASU et EURL 2026 : urssaf.fr
- CGI - Art. 200 A (flat tax 30%) : legifrance.gouv.fr
- CGI - Art. 219 (IS 2026) : legifrance.gouv.fr
- Code sécu. sociale - Art. L131-6 (cotisations TNS dividendes > 10% capital) : legifrance.gouv.fr`
  },

  'passer-sasu-a-eurl-quand-pourquoi-2026': {
    id: '46',
    title: 'Passer de SASU à EURL : quand et pourquoi en 2026',
    slug: 'passer-sasu-a-eurl-quand-pourquoi-2026',
    excerpt: 'À partir de quel niveau de bénéfice l\'EURL devient plus rentable que la SASU ? Procédure de transformation, gains annuels, cas où il ne faut pas le faire.',
    category: 'Optimisation',
    tags: ['SASU', 'EURL', 'Transformation', 'Fiscalité', 'Stratégie'],
    readTime: 12,
    date: '2026-03-31',
    author: 'Bozzam',
    popular: true,
    fullContent: `## SASU → EURL : pourquoi y penser en 2026

Vous avez créé une SASU parce que c'était "plus simple" et parce que vous ne vouliez pas des cotisations TNS. Quelques années plus tard, votre activité génère 80 000 à 150 000€ de bénéfice/an et vous découvrez que votre voisin en EURL paie **30% de charges en moins**. C'est normal : **la SASU est plus chère que l'EURL dès que le bénéfice dépasse un certain seuil**.

## Le différentiel de cotisations : le chiffre qui change tout

| Poste | SASU (président) | EURL (gérant majoritaire) |
|-------|-----------------|--------------------------|
| Régime | Assimilé salarié | TNS (indépendant) |
| Taux de cotisations sur rémunération | ~80% (patronales + salariales) | ~45% |
| Cotisations minimales (hors rém.) | 0€ | ~1 100€/an (CIPAV ou SSI) |
| Dividendes | Flat tax 30% | Cotisations TNS sur part > 10% capital |
| Protection sociale | Forte (équivalent salarié) | Moyenne (pas de chômage, IJ plafonnées) |

**Traduction** : pour 60 000€ de rémunération nette, la SASU coûte **~108 000€** à la société. L'EURL coûte **~87 000€**. Différence : **21 000€/an**.

## Le seuil de bascule : à partir de quand l'EURL devient rentable

### Pour un célibataire sans enfant

| Bénéfice annuel | SASU net disponible | EURL net disponible | Gagnant |
|-----------------|---------------------|---------------------|---------|
| 40 000€ | 26 800€ | 25 400€ | SASU (léger) |
| 60 000€ | 39 200€ | 38 500€ | SASU (léger) |
| **80 000€** | **49 500€** | **52 000€** | **EURL +2 500€** |
| 100 000€ | 58 200€ | 63 000€ | EURL +4 800€ |
| 150 000€ | 78 500€ | 89 000€ | EURL +10 500€ |
| 200 000€ | 98 000€ | 114 500€ | EURL +16 500€ |

**Seuil de bascule** : **~75 000€ de bénéfice annuel** pour un célibataire.

### Pour un couple (2 parts)

Le seuil monte à **~95 000€** (l'IR progressif est moins pénalisant en couple, ce qui avantage la SASU avec sa flat tax sur dividendes plus longtemps).

## Les 5 vraies raisons de passer en EURL

### Raison 1 : Le bénéfice annuel dépasse 80 000€
Gain potentiel : 2 500 à 20 000€/an.

### Raison 2 : Vous ne comptez pas distribuer beaucoup de dividendes
Si vous réinvestissez l'essentiel du bénéfice dans la société, la flat tax SASU n'est pas un avantage. L'EURL est moins chère sur la partie rémunération.

### Raison 3 : Vous voulez maximiser votre retraite via Madelin/PER TNS
Le PER TNS permet de déduire **jusqu'à 10% du bénéfice + 15% sur la tranche > 1 PASS**, soit ~85 000€ déductibles/an en 2026. Beaucoup plus qu'en SASU.

### Raison 4 : Vous êtes profession libérale CIPAV favorable
Certaines professions libérales (CIPAV) bénéficient de taux avantageux.

### Raison 5 : Vous préparez un rachat ou une holding
L'EURL est plus flexible pour certaines opérations patrimoniales (apport à holding avec option IR).

## Les 4 cas où il NE FAUT PAS passer en EURL

### Cas 1 : Vous prévoyez d'embaucher votre conjoint
En SASU, votre conjoint peut être salarié avec chômage. En EURL (TNS), le conjoint collaborateur n'a pas de chômage.

### Cas 2 : Vous avez besoin d'indemnités journalières maladie élevées
En SASU, IJ ~82€/jour. En EURL, IJ plafonnées à ~55€/jour et délai de carence plus long.

### Cas 3 : Vous préparez un prêt immobilier à court terme
Les banques lisent mieux les fiches de paie SASU que les bénéfices EURL.

### Cas 4 : Vous êtes en grossesse ou planifiez un enfant
Indemnités maternité SASU ~3 800€/mois (plafond sécu). En EURL TNS : forfait ~4 000€ sur 16 semaines = ~250€/semaine.

## La procédure de transformation SASU → EURL (2026)

Bonne nouvelle : **pas besoin de dissoudre et recréer**. C'est une **simple transformation** qui conserve la personnalité morale, le numéro SIRET, les contrats clients, les comptes bancaires.

### Les 7 étapes

| Étape | Délai | Coût estimé |
|-------|-------|-------------|
| 1. Décision de transformation (associé unique) | 1 jour | — |
| 2. Rapport d'un commissaire à la transformation (obligatoire si pas de CAC) | 2 à 4 semaines | 1 500 à 3 000€ HT |
| 3. Modification des statuts (SASU → SARL à associé unique = EURL) | 1 semaine | 200 à 800€ |
| 4. Publication annonce légale | 1 semaine | ~200€ |
| 5. Dépôt au greffe + CFE | 2 semaines | ~200€ |
| 6. Radiation président auprès URSSAF, inscription gérant TNS | 1 à 2 mois | — |
| 7. Option fiscale (IS ou IR) | Immédiat | — |
| **Total** | **2 à 3 mois** | **~2 500 à 4 500€** |

## Les conséquences fiscales immédiates

### 1. Pas de cession, pas de plus-value
Comme c'est une transformation, **aucune plus-value** n'est taxée. Les réserves et le report à nouveau restent intacts.

### 2. Changement de régime social du dirigeant
Dès la transformation, vous basculez d'assimilé salarié → TNS. Attention aux chevauchements de cotisations sur 6 à 12 mois.

### 3. Maintien de l'IS (par défaut)
L'EURL reste à l'IS si la SASU était à l'IS. Vous pouvez opter pour l'IR dans les 3 premières années.

### 4. Les dividendes ultérieurs
Dès la transformation, les dividendes distribués sont soumis au régime EURL : **cotisations TNS sur la part > 10% du capital social libéré + primes d'émission**.

> **Astuce** : pour optimiser, augmentez le capital social avant transformation (ex. incorporation de réserves) pour relever le seuil de 10%.

## Cas pratique : Julien, consultant SASU à 120 000€ de bénéfice

### Avant (SASU)

| Poste | Montant |
|-------|---------|
| Salaire net annuel (3 000€/mois) | 36 000€ |
| Coût social SASU | ~65 000€ |
| Bénéfice distribuable | 55 000€ |
| IS (15% + 25%) | 10 500€ |
| Dividende brut | 44 500€ |
| Flat tax | 13 350€ |
| Dividende net | 31 150€ |
| IR sur salaire (TMI 30%) | 4 000€ |
| **Net total** | **~63 150€** |

### Après (EURL)

| Poste | Montant |
|-------|---------|
| Rémunération TNS | 65 000€ |
| Cotisations TNS (~45%) | 29 250€ |
| Net avant IR | 35 750€ |
| Bénéfice distribuable | 55 000€ |
| IS (15% + 25%) | 10 500€ |
| Dividende distribuable | 44 500€ |
| Dividende dans les 10% capital (flat tax) : 2 000€ | 1 400€ |
| Dividende au-delà (cotis + IR) : 42 500€ - 19 125€ cotis - IR = ~19 000€ | 19 000€ |
| IR sur rémunération (TMI 30%) | ~8 000€ |
| **Net total** | **~48 150€** |

**Julien** : gain de **15 000€/an** en passant en EURL ? Pas vraiment. L'EURL est pénalisée par les cotisations TNS sur dividendes.

**Correction** : avec optimisation via PER TNS (15 000€ déductibles) + rémunération à 70 000€ et réinvestissement, on atteint **~66 000€ net**, soit +3 000€/an. Faible gain en pratique.

## La vérité de terrain : quand la bascule est vraiment rentable

En pratique, la bascule SASU → EURL est **vraiment rentable** quand :

1. **Vous ne distribuez jamais de dividendes** (tout reste en société ou en rémunération TNS)
2. **Vous avez un TMI à 41 ou 45%** et maximisez le PER TNS
3. **Vous êtes profession libérale CIPAV** avec taux favorables
4. **Vous anticipez une grosse opération** (cession, holding, réinvestissement massif)

Sinon, le gain est souvent **< 5 000€/an** pour des contraintes sociales importantes.

## Le cas inverse : EURL → SASU

Si vous êtes en EURL et que vous voulez :
- Embaucher votre conjoint avec chômage
- Revendre la société facilement (SASU plus liquide)
- Prendre un arrêt maladie / maternité

La bascule EURL → SASU suit la même procédure (~2 500 à 4 000€).

## Checklist avant de transformer

- [ ] Simuler avec un expert-comptable (coût 300 à 800€)
- [ ] Vérifier l'impact sur retraite / prévoyance (demander un RIS)
- [ ] Vérifier l'impact sur dossier bancaire en cours
- [ ] Anticiper la double cotisation pendant 6 à 12 mois
- [ ] Augmenter le capital si gros dividendes prévus
- [ ] Souscrire une prévoyance TNS complémentaire

---

**Sources officielles 2026 :**
- Code de commerce - Art. L227-3 (transformation SASU) : legifrance.gouv.fr
- Code de commerce - Art. L223-43 (commissaire à la transformation) : legifrance.gouv.fr
- CGI - Art. 219 (IS) : legifrance.gouv.fr
- URSSAF - Comparatif TNS vs assimilé salarié 2026 : urssaf.fr
- Code sécu. sociale - Art. L131-6 (dividendes TNS > 10% capital) : legifrance.gouv.fr`
  },

  'compte-titres-holding-bourse-actions-2026': {
    id: '47',
    title: 'Compte-titres via holding : investir en bourse avec sa société en 2026',
    slug: 'compte-titres-holding-bourse-actions-2026',
    excerpt: 'Ouvrir un compte-titres au nom de sa holding pour investir en bourse : fiscalité IS, régime mère-fille sur dividendes, brokers compatibles, exemples chiffrés.',
    category: 'Optimisation',
    tags: ['Compte-titres', 'Bourse', 'Holding', 'Actions', 'Dividendes'],
    readTime: 14,
    date: '2026-03-30',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Investir en bourse via sa société : le levier oublié des dirigeants

Vous avez accumulé 100 000 à 500 000€ de trésorerie dans votre SASU ou votre holding, et vous vous dites : "sortir en dividendes → flat tax 30% → placer en PEA, c'est cher." La solution : **ouvrir un compte-titres au nom de votre holding** et investir directement **en bourse avec l'argent de la société**, sans jamais passer par la case flat tax.

## Pourquoi investir via la holding et pas en perso

### Comparatif investissement 100 000€ en actions cotées

| Critère | CTO personnel | CTO holding |
|---------|--------------|-------------|
| Étape préalable | Flat tax 30% (100 000€ → 70 000€ disponibles) | 0€ (argent dans la holding) |
| Montant investi | **70 000€** | **100 000€** |
| Imposition plus-values | Flat tax 30% à la sortie | IS (15% jusqu'à 42 500€, 25% au-delà) |
| Imposition dividendes actions | Flat tax 30% | **Régime mère-fille (1,25% réel si > 5%)** ou IS 25% |
| Report d'imposition si pas de sortie | Non | **Oui** (capitalisation 100% dans la société) |
| Succession | Droits de succession (barème progressif) | Droits sur titres holding (avec pacte Dutreil possible) |

> **Effet de levier** : en investissant via la holding, vous investissez **~43% de plus** pour le même effort fiscal de départ.

## Le fonctionnement concret

### Étape 1 — Ouvrir un CTO au nom de la holding

Votre holding (SASU, SAS ou SARL) ouvre un compte-titres ordinaire (CTO) chez un broker **acceptant les personnes morales**.

| Broker | Personne morale | Frais | Plateforme |
|--------|-----------------|-------|------------|
| Interactive Brokers | ✅ Oui (IBKR Pro entreprise) | Très bas (0,05%) | Avancée |
| Saxo Banque | ✅ Oui (CTO entreprise) | Moyens | Complète |
| Degiro | ❌ Non (pas de compte société) | — | — |
| BoursoBank (pour pro) | ✅ Oui (Boursorama Pro) | Moyens | Simple |
| CIC / BNP / SG | ✅ Oui (CTO pro) | Élevés | Classique |
| Trade Republic | ⚠️ Depuis 2025 : oui pour sociétés | Bas | Mobile |

> **Important** : PEA et PEA-PME **ne sont pas accessibles aux sociétés**. Uniquement personnes physiques.

### Étape 2 — Virer la trésorerie

Vous virez la trésorerie du compte pro de la holding vers le CTO. Aucun impact fiscal (transfert interne).

### Étape 3 — Investir

Vous achetez des actions, ETF, obligations, OPCVM. Les dividendes et plus-values sont imposés à l'IS.

## La fiscalité détaillée

### 1. Les plus-values sur cession d'actions cotées

| Type d'actif | Fiscalité société |
|-------------|-------------------|
| Actions cotées court terme (< 2 ans) | IS au taux plein (25%) |
| Actions cotées long terme (> 2 ans, détention < 5%) | IS au taux plein (25%) |
| Actions cotées participation > 5% détenues > 2 ans | **Exonération à 88%** (régime titres de participation) |
| ETF actions | IS 25% |
| Obligations | IS 25% sur coupons et plus-values |
| SICAV actions (coupon capitalisant) | IS 25% en "sortie" uniquement |

### 2. Les dividendes reçus par la société

| Situation | Régime fiscal |
|-----------|--------------|
| Dividende d'action cotée < 5% du capital | IS 25% sur 100% du dividende |
| Dividende action cotée ≥ 5% détenue > 2 ans | **Régime mère-fille : exonération à 95%** (imposé à 25% sur 5%) → **1,25% réel** |
| Dividende SICAV capitalisant | Imposé uniquement à la sortie |
| Dividende ETF synthétique | Imposé uniquement à la sortie (comme SICAV) |

## Exemple 1 : Investir 100 000€ en ETF World

### Paramètres
- Holding : 100 000€ de trésorerie disponible
- ETF MSCI World capitalisant (ex. Amundi MWRD, Lyxor WLD)
- Performance annuelle hypothétique : 8%/an
- Durée : 10 ans

### Résultats après 10 ans

| Scénario | Valeur finale | Impôt final | Net disponible |
|----------|--------------|-------------|----------------|
| CTO holding (ETF capitalisant) | 215 892€ (100k € × 1,08^10) | 115 892€ × 25% = 28 973€ à la vente | **186 919€ dans la holding** |
| CTO perso (après flat tax initial 70k€ investis) | 151 124€ (70k × 1,08^10) | 81 124€ × 30% = 24 337€ | **126 787€ net personnel** |

**Gain via holding** : **+60 132€** après 10 ans, soit **+47%** vs détention personnelle.

> **Note** : ETF capitalisant > ETF distribuant pour la holding (pas de friction fiscale annuelle sur les dividendes).

## Exemple 2 : Investir dans des actions à dividendes (TotalEnergies, LVMH, etc.)

### Paramètres
- Holding achète pour 200 000€ d'actions TotalEnergies
- Rendement dividende : 5% brut/an = 10 000€/an
- Participation < 5% du capital TotalEnergies → **pas de régime mère-fille**

### Résultat annuel

| Poste | Montant |
|-------|---------|
| Dividende brut reçu | 10 000€ |
| IS 25% | -2 500€ |
| **Dividende net dans la holding** | **7 500€** |

**Vs CTO perso** : 10 000€ - 3 000€ flat tax = 7 000€ net. **Avantage holding : 500€/an**.

**Effet cumulé sur 10 ans** : +5 000€ de plus + capitalisation (car tout reste dans la holding).

## Exemple 3 : Participation qualifiante > 5% (régime mère-fille)

### Paramètres
- Holding détient 6% du capital d'une SAS non cotée depuis > 2 ans
- Dividende annuel : 50 000€

### Résultat

| Poste | Montant |
|-------|---------|
| Dividende reçu | 50 000€ |
| Quote-part 5% imposable à l'IS 25% | 50 000€ × 5% × 25% = -625€ |
| **Dividende net dans la holding** | **49 375€** |

**Vs CTO perso (flat tax 30%)** : 50 000€ - 15 000€ = 35 000€.

**Gain holding** : **+14 375€/an** via mère-fille.

## Les 3 stratégies gagnantes

### Stratégie 1 — ETF capitalisants mondiaux
Pour un freelance en phase d'accumulation : 80% ETF World + 20% ETF émergents. Pas de friction fiscale annuelle, capitalisation optimale.

### Stratégie 2 — Portefeuille dividendes haut rendement
TotalEnergies, Orange, Danone, Veolia : rendement 4 à 6%. Attention : IS 25% chaque année si participation < 5%.

### Stratégie 3 — Private equity / actions non cotées > 5%
Via clubs d'investissement ou levée Angel : si participation > 5%, régime mère-fille = fiscalité quasi-nulle.

## Les pièges à éviter

### Piège n°1 : Confondre CTO société et CTO personnel
Un CTO société nécessite un K-bis, des statuts et un RIB pro. L'ouverture est plus longue (2 à 4 semaines).

### Piège n°2 : Oublier la comptabilité
Les titres du CTO doivent être comptabilisés en **immobilisations financières** (compte 271 ou 273). L'expert-comptable doit les évaluer chaque année (principe de prudence).

### Piège n°3 : La CSG-CRDS sur dividendes de source étrangère
Les dividendes d'actions US (Apple, Microsoft) subissent une retenue à la source de 15% (crédit d'impôt récupérable sur l'IS).

### Piège n°4 : Le trading fréquent en société
Si la holding fait du trading actif (>20 opérations/an), l'administration peut requalifier en activité commerciale → impact sur le régime mère-fille.

### Piège n°5 : Les actifs volatils et la situation nette
Si vos actions perdent 40% et que votre holding se retrouve avec des capitaux propres négatifs, vous devez régulariser dans les 2 ans.

## Les brokers recommandés en 2026

| Broker | Avantages | Inconvénients |
|--------|-----------|---------------|
| **Interactive Brokers Pro** | Frais ultra-bas, tous marchés, outils avancés | Interface technique, K-bis + statuts obligatoires |
| **Saxo Banque Entreprise** | Plateforme française, support FR | Frais plus élevés, minimum 20 000€ |
| **BoursoBank Pro** | Simple, intégré au compte pro | Offre titres limitée, frais moyens |
| **Trade Republic société** | App mobile, fractions d'actions | Nouveau, offre société encore limitée |
| **Banque privée (CIC, SG, BNP)** | Conseil personnalisé | Frais élevés (1-2%) + rétrocessions |

## Checklist pour démarrer

- [ ] Holding créée et immatriculée (K-bis disponible)
- [ ] Trésorerie disponible ≥ 20 000€ (seuil minimum raisonnable)
- [ ] Choix du broker (Interactive Brokers recommandé pour < 500k€)
- [ ] Ouverture du compte (K-bis + statuts + RIB + pièce d'identité associés)
- [ ] Virement initial depuis compte pro holding
- [ ] Définition allocation (ETF, actions, obligations)
- [ ] Information de l'expert-comptable (comptabilisation)
- [ ] Stratégie de sortie (réinvestissement vs distribution)

## Tableau récapitulatif : quelle stratégie d'investissement selon votre profil

| Trésorerie holding | Stratégie recommandée | Gain annuel estimé vs perso |
|-------------------|----------------------|----------------------------|
| 20k à 50k€ | ETF World capitalisant | +1 à 3% fiscal |
| 50k à 200k€ | 70% ETF + 30% actions div. | +2 à 5% fiscal |
| 200k à 500k€ | ETF + actions + obligations | +3 à 6% fiscal |
| 500k€ à 1M€ | Portefeuille diversifié + SCPI | +4 à 8% fiscal |
| > 1M€ | Conseiller en gestion de patrimoine | +5 à 10% fiscal |

## FAQ

**Puis-je ouvrir un PEA au nom de ma holding ?**
Non. Le PEA est strictement réservé aux personnes physiques.

**Les dividendes d'actions françaises reçus par ma holding bénéficient-ils du régime mère-fille ?**
Oui, **si** la holding détient ≥ 5% du capital depuis ≥ 2 ans. Sinon, imposition IS classique.

**Dois-je déclarer mes comptes-titres à l'étranger (IBKR Irlande) ?**
Oui, via le formulaire 3916-bis chaque année, sous peine d'amende 1 500€/compte.

**Puis-je faire du trading court terme avec ma holding ?**
Techniquement oui, mais attention à la requalification en activité commerciale si volume élevé. Préférez l'investissement long terme.

**Et la SCPI via holding ?**
Oui, les SCPI achetées via holding génèrent des revenus fonciers imposés à l'IS 25% (plus avantageux que l'IR + prélèvements sociaux 47,2% en perso).

---

**Sources officielles 2026 :**
- CGI - Art. 145 (régime mère-fille) : legifrance.gouv.fr
- CGI - Art. 216 (quote-part 5%) : legifrance.gouv.fr
- CGI - Art. 219 (IS + titres de participation) : legifrance.gouv.fr
- CGI - Art. 219 I-a quinquies (long terme titres de participation 12%) : legifrance.gouv.fr
- BOFiP - BOI-IS-BASE-20-20-10 (titres de participation) : bofip.impots.gouv.fr
- PCG - Plan comptable général (immobilisations financières 271, 273) : anc.gouv.fr`
  },

  'facturation-electronique-obligatoire-2026': {
    id: '48',
    title: 'Facturation électronique obligatoire 2026 : PDP, Chorus Pro et calendrier',
    slug: 'facturation-electronique-obligatoire-2026',
    excerpt: 'Toutes les entreprises passent à la facture électronique en 2026 : plateformes agréées (PDP), calendrier, sanctions, comment se préparer.',
    category: 'Gestion',
    tags: ['Facturation électronique', 'PDP', 'Chorus Pro', 'Obligation', '2026'],
    readTime: 13,
    date: '2026-04-14',
    author: 'Bozzam',
    popular: true,
    fullContent: `## La révolution facturation électronique arrive

À partir du **1er septembre 2026**, toutes les entreprises françaises (y compris freelances, micro-entrepreneurs et TPE) doivent **recevoir** les factures au format électronique. À partir du **1er septembre 2027**, elles doivent aussi les **émettre** dans ce format. Cette réforme (article 153 de la loi de finances 2020, actualisée en 2024) transforme radicalement la gestion administrative.

## Le calendrier officiel 2026-2027

| Date | Obligation | Concernés |
|------|-----------|-----------|
| **1er sept. 2026** | Réception obligatoire des factures électroniques | **Toutes** les entreprises (y compris micro) |
| **1er sept. 2026** | Émission obligatoire | Grandes entreprises et ETI |
| **1er sept. 2027** | Émission obligatoire | TPE, PME, freelances, micro-entrepreneurs |

## Qu'est-ce qu'une facture électronique légale ?

Une facture PDF envoyée par email **n'est PAS** une facture électronique au sens légal. La facture électronique doit :

1. Être émise dans un **format structuré** : Factur-X, UBL ou CII
2. Contenir des **données lisibles par machine**
3. Transiter par une **Plateforme de Dématérialisation Partenaire (PDP)** ou le **Portail Public de Facturation (PPF)**
4. Être conservée **10 ans** au format électronique

## Les 3 acteurs du nouveau système

### 1. Le PPF (Portail Public de Facturation)
Portail gratuit géré par l'État (via Chorus Pro). Accessible à tous. Fonctionnalités limitées mais gratuites.

### 2. Les PDP (Plateformes de Dématérialisation Partenaires)
Plateformes privées agréées par la DGFiP. Offrent des services enrichis : intégration comptable, workflow, archivage, alertes.

### Liste PDP agréées 2026 (extrait)

| PDP | Prix indicatif | Points forts |
|-----|---------------|--------------|
| Pennylane | 29€/mois | Compta intégrée |
| Qonto Invoice | Inclus dans plan Pro (29€/mois) | Banque + factures |
| Sage Facture Élec | 15€/mois | Écosystème Sage |
| Cegid | 25€/mois | Grandes structures |
| Docaposte (La Poste) | 12€/mois | Fiable, français |

### 3. Les OD (Opérateurs de Dématérialisation)
Simples relais techniques (pas d'agrément). Ne peuvent pas seuls faire transiter la facture — doivent passer par PDP/PPF.

## Ce que doit contenir une facture électronique en 2026

En plus des mentions habituelles, la facture doit inclure :

| Mention nouvelle | Détail |
|-----------------|--------|
| **SIREN client** | Identifiant obligatoire |
| **Adresse de livraison** | Si différente de facturation |
| **Type d'opération** | Biens/services/mixte |
| **Paiement de la TVA** | Sur débit ou encaissement |
| **Options TVA** | Régime réel/franchise/etc. |

## L'e-reporting : la déclaration automatique

Parallèlement à la facturation électronique, l'**e-reporting** oblige à transmettre à l'administration fiscale certaines informations en temps réel :

| Type d'opération | E-reporting requis ? |
|------------------|---------------------|
| Vente B2B France | Non (déjà via facture élec.) |
| Vente B2B UE | Oui |
| Vente B2C | Oui |
| Vente hors UE | Oui |
| Encaissements | Oui (statut paiement) |

## Les sanctions en cas de non-respect

| Manquement | Sanction |
|-----------|----------|
| Facture non transmise via PDP/PPF | 15€ par facture (plafond 15 000€/an) |
| Défaut d'e-reporting | 250€ par transmission (plafond 15 000€/an) |
| Récidive | Doublement des amendes |

## Comment se préparer en 3 étapes (freelance / TPE)

### Étape 1 — Choisir sa PDP (avant juin 2026)
Comparez selon vos besoins : intégration comptable, tarif, ergonomie mobile. Un abonnement moyen = 15-30€/mois.

### Étape 2 — Mettre à jour son logiciel de facturation
- Si vous utilisez Pennylane, Qonto, Sage, Freebe : mise à jour automatique
- Si vous utilisez Word/Excel : basculer sur un outil dédié
- Formats acceptés : Factur-X (PDF/A-3 avec XML embarqué), UBL, CII

### Étape 3 — Récupérer le SIREN de vos clients
Enregistrer le SIREN de chaque client B2B dans votre outil. Vérifiable gratuitement sur societe.com ou l'annuaire INSEE.

## Le cas de la micro-entreprise en franchise de TVA

Vous êtes en franchise de TVA ? **Vous êtes concerné**. Vos factures doivent mentionner "TVA non applicable, art. 293 B du CGI" ET transiter par PDP/PPF à partir de septembre 2027 (ou 2026 pour la réception).

## Coût annuel estimé pour un freelance

| Poste | Coût |
|-------|------|
| Abonnement PDP (moyen) | 180 à 360€/an |
| Formation initiale (facultative) | 200 à 500€ (une fois) |
| Migration de l'historique | 0 à 200€ (une fois) |
| **Total récurrent** | **~250€/an** |

**Vs coût gagné** : suppression des rappels client (jusqu'à 50% de gain de temps), paiement plus rapide (~7 à 15 jours en moyenne), moins d'erreurs comptables.

## FAQ

**Puis-je continuer à envoyer un PDF par email à mes clients ?**
À partir de septembre 2027 : **non** pour les factures B2B en France. Il faudra passer par une PDP.

**Que se passe-t-il pour les factures à des particuliers (B2C) ?**
Les factures B2C **ne sont pas concernées** par la facturation électronique. Mais elles font l'objet d'un e-reporting (transmission des données de vente).

**Mon expert-comptable s'en occupe-t-il ?**
Renseignez-vous rapidement. Beaucoup d'experts-comptables proposent leur PDP maison.

**Puis-je choisir plusieurs PDP ?**
Oui, mais dans les faits, une seule suffit (émission + réception).

---

**Sources officielles 2026 :**
- Article 153 - Loi de finances 2020 (article 289 bis du CGI) : legifrance.gouv.fr
- Ordonnance 2021-1190 (facturation électronique) : legifrance.gouv.fr
- Décret 2022-1299 (généralisation) : legifrance.gouv.fr
- DGFiP - Liste PDP agréées : impots.gouv.fr
- Portail public : chorus-pro.gouv.fr
- Norme européenne EN 16931 (facture électronique)`
  },

  'contrat-freelance-clauses-indispensables-2026': {
    id: '49',
    title: 'Contrat freelance : les 10 clauses indispensables en 2026',
    slug: 'contrat-freelance-clauses-indispensables-2026',
    excerpt: 'Propriété intellectuelle, RGPD, pénalités, résiliation, non-concurrence : les clauses juridiques à ne jamais oublier dans vos contrats de mission.',
    category: 'Freelance pratique',
    tags: ['Contrat', 'Juridique', 'Freelance', 'Clauses', 'RGPD'],
    readTime: 12,
    date: '2026-04-13',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Le contrat freelance, votre meilleure protection

70% des litiges entre freelances et clients viennent d'un contrat mal rédigé ou inexistant. Voici les **10 clauses juridiques indispensables** à intégrer dans chaque mission en 2026.

## 1. Objet précis de la mission

**Erreur classique** : "Prestations de développement web".
**Bonne rédaction** : "Développement d'une application mobile iOS/Android composée de X écrans, avec les fonctionnalités A, B, C, livrable au format APK/IPA, incluant Y jours de recette et Z jours de support post-livraison."

**Pourquoi c'est critique** : sans objet précis, le client peut vous demander sans cesse "juste une petite modification en plus".

## 2. Prix et modalités de paiement

| Élément | Recommandation |
|---------|---------------|
| Montant | HT + TTC (si TVA) |
| Modalités | 30% acompte, 40% mi-parcours, 30% à la livraison |
| Délai de paiement | 30 jours max (loi LME 2008) |
| Retard | Pénalités = **3× le taux d'intérêt légal** minimum (5,07% en 2026 → 15,21%) + indemnité forfaitaire **40€** |

## 3. Délais et livrables

**Structure recommandée** :
- Phase 1 : Cadrage → J+7
- Phase 2 : Développement → J+30
- Phase 3 : Livraison → J+45

**Cause exonératoire** : "Les délais sont indicatifs et peuvent être ajustés en cas de retard imputable au client (validation, retour d'information, contenus)."

## 4. Propriété intellectuelle (PI)

C'est **la** clause la plus importante. Deux modèles :

### Modèle A — Cession totale
"Le prestataire cède au client, à titre exclusif et pour toute la durée de protection légale des droits d'auteur, les droits patrimoniaux (reproduction, représentation, adaptation) sur les livrables, moyennant paiement intégral du prix."

### Modèle B — Licence limitée
"Le prestataire accorde au client une licence non-exclusive, transférable, pour un usage interne uniquement. Les droits patrimoniaux restent la propriété du prestataire, qui conserve la possibilité de réutiliser certaines briques techniques."

**Attention** : la cession n'est **effective** qu'au paiement intégral (art. L131-4 CPI).

## 5. RGPD et protection des données (mission critique en 2026)

Si vous traitez des données personnelles pour le compte du client, la clause RGPD est **obligatoire** :

- Statut : sous-traitant au sens de l'art. 28 RGPD
- Instructions documentées du responsable de traitement (le client)
- Durée de conservation
- Sous-traitance ultérieure autorisée ou non
- Notification de failles sous 72h
- Sanctions RGPD : **jusqu'à 20 M€ ou 4% du CA mondial**

Modèle de clause : "Le prestataire agit en qualité de sous-traitant conformément à l'article 28 du RGPD. Un accord de traitement de données (DPA) sera signé en annexe."

## 6. Confidentialité (NDA)

Durée typique : **3 à 5 ans après la fin de la mission**.

"Le prestataire s'engage à ne pas divulguer les informations confidentielles du client (business plan, code source, contacts, tarifs) pendant la mission et pendant [X] ans après sa fin. Une pénalité forfaitaire de [10 000€ à 50 000€] est prévue en cas de violation."

## 7. Résiliation et rupture

| Cas | Préavis | Conséquence |
|-----|---------|-------------|
| Résiliation à convenance | 30 jours | Paiement du travail effectué + indemnité (10 à 20% du reste) |
| Résiliation pour faute | Immédiate | Après mise en demeure restée sans effet 15 jours |
| Force majeure | Immédiate | Pas d'indemnité |
| Insolvabilité | Immédiate | Résiliation de plein droit |

## 8. Non-concurrence / Non-sollicitation

**Clause valable si** :
- Limitée dans le temps (max 2 ans post-mission)
- Limitée géographiquement (secteur d'activité + territoire)
- Compensation financière (pour un freelance, généralement pas exigée)
- Non-sollicitation des salariés : durée 12-24 mois, généralement gratuite

## 9. Responsabilité et assurance

**Plafond de responsabilité** : "La responsabilité du prestataire est plafonnée au montant total facturé sur les 12 derniers mois."

**Exclusions** : "Sont exclues de la responsabilité les pertes indirectes (chiffre d'affaires, perte de clientèle, atteinte à l'image)."

**Assurance RC Pro** : mentionner votre assureur et le montant de couverture (obligatoire pour certaines missions IT, conseil, santé).

## 10. Loi applicable et juridiction compétente

**Modèle** : "Le présent contrat est soumis au droit français. Tout litige relèvera de la compétence exclusive du Tribunal de commerce de [Ville du siège du prestataire]."

**Astuce** : choisissez votre ville pour éviter d'avoir à vous déplacer en cas de litige.

## Bonus — 3 clauses à négocier fermement

### A. Clause de revalorisation annuelle
"Les tarifs seront révisés chaque année selon l'indice Syntec ou +2% minimum."

### B. Clause de modification (avenant)
"Toute demande de modification hors périmètre initial fera l'objet d'un avenant écrit avec devis complémentaire."

### C. Facturation d'acompte
"Un acompte de 30% est dû à la signature. La mission ne débute qu'après réception du paiement."

## Erreurs fatales à éviter

1. **Contrat verbal** : aucune preuve, litige perdu d'avance
2. **PDF non signé** : signature électronique obligatoire (DocuSign, Yousign)
3. **Devis sans conditions générales** : ajoutez toujours vos CGV
4. **Oublier le RGPD** : sanctions civiles ET pénales
5. **Ne pas plafonner la responsabilité** : risque d'être ruiné sur une seule erreur

## Modèle de contrat 2026 (structure)

1. Préambule et parties
2. Objet
3. Prix et modalités de paiement
4. Délais et planning
5. Livrables
6. Propriété intellectuelle
7. Confidentialité
8. RGPD (annexe DPA)
9. Résiliation
10. Non-concurrence / non-sollicitation
11. Responsabilité et assurances
12. Force majeure
13. Loi et juridiction
14. Signature électronique

---

**Sources officielles 2026 :**
- Code civil - Art. 1101 à 1231-7 (droit des contrats) : legifrance.gouv.fr
- Code de la propriété intellectuelle - Art. L131-4 : legifrance.gouv.fr
- Loi LME 2008 (délais de paiement 30 jours) : legifrance.gouv.fr
- RGPD - Art. 28 (sous-traitance) : cnil.fr
- Décret 2013-306 (indemnité forfaitaire 40€) : legifrance.gouv.fr`
  },

  'fiscalite-crypto-freelance-2026': {
    id: '50',
    title: 'Fiscalité crypto pour freelance et entrepreneur en 2026',
    slug: 'fiscalite-crypto-freelance-2026',
    excerpt: 'Bitcoin, Ethereum, staking, DeFi : comment déclarer et optimiser vos gains crypto en tant que freelance ou dirigeant en 2026.',
    category: 'Fiscalité',
    tags: ['Crypto', 'Bitcoin', 'Fiscalité', 'DeFi', 'Déclaration'],
    readTime: 14,
    date: '2026-04-12',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Crypto en 2026 : ce que doit savoir un freelance

Depuis 2019, la fiscalité crypto en France est encadrée par l'article 150 VH bis du CGI. En 2026, plusieurs évolutions importantes s'appliquent, notamment sur le staking, les airdrops, et la déclaration des comptes à l'étranger.

## Le régime fiscal des plus-values crypto en 2026

### Pour un particulier occasionnel (règle par défaut)

| Poste | Traitement fiscal |
|-------|-------------------|
| Plus-value nette annuelle | Flat tax **30%** (12,8% IR + 17,2% prélèvements sociaux) |
| Option barème progressif | Possible (article 200 A CGI) |
| Seuil de déclenchement | Dès le 1er euro (seuil des 305€ supprimé en 2023) |
| Moins-values | Reportables 10 ans sur plus-values de même nature |

### Pour un professionnel (activité habituelle)

Depuis la loi de finances 2022, la fréquence des transactions ne suffit plus. Pour être qualifié de **professionnel** en crypto, il faut :
- Utiliser des outils **similaires aux traders professionnels** (bots, API, algorithmes)
- Réaliser un nombre significatif d'opérations sur des **cryptos exotiques** ou en **DeFi complexe**

Dans ce cas : imposition BIC (barème progressif + prélèvements sociaux TNS ~45%).

## Le calcul de la plus-value en 2026

La formule **spéciale crypto** est différente d'une plus-value classique :

$$Plus-value = Prix de cession × [1 - (Prix total d'acquisition ÷ Valeur globale du portefeuille)]$$

### Exemple chiffré

- Vous avez acheté 1 BTC à 30 000€ et 1 BTC à 50 000€ (total investi : 80 000€)
- Votre portefeuille total vaut aujourd'hui : 200 000€
- Vous vendez 0,5 BTC pour 40 000€

Plus-value = 40 000 × [1 - (80 000 ÷ 200 000)] = 40 000 × 0,6 = **24 000€**

Flat tax = 24 000 × 30% = **7 200€ d'impôt**.

## Staking, airdrops, DeFi : le traitement en 2026

### Staking (ETH, ADA, SOL, etc.)

| Situation | Fiscalité |
|-----------|-----------|
| Récompenses reçues (staking centralisé) | BNC ou BIC selon activité (barème IR) |
| Récompenses reçues (staking on-chain) | Non imposable à la réception (BOFiP 2024) |
| Revente des tokens stakés | Plus-value classique (formule ci-dessus) |

### Airdrops
- **Non imposables à la réception** (jurisprudence 2023)
- **Imposables à la revente** (plus-value classique)

### DeFi (yield farming, liquidity pools)
- **Rémunérations en cryptos** : BNC/BIC si régulier (barème progressif)
- Sortie de pool : plus-value crypto classique

### NFT
- **Cession de NFT artistique** : plus-value au barème (art. 150 UA CGI)
- **NFT utilitaire ou gaming** : plus-value crypto (art. 150 VH bis)

## L'article crucial pour freelances : le compte-titres crypto en société

Une SASU ou EURL peut détenir des cryptos. La fiscalité change complètement :

| Situation | Traitement |
|-----------|-----------|
| Achat de crypto par la société | Immobilisation financière |
| Plus-value à la cession | Imposée à l'**IS (25%)** |
| Moins-value | Déductible du résultat |
| Distribution des gains | Flat tax 30% lors de la sortie en dividendes |

**Comparaison rendement 100 000€ investis en BTC, sortie après plus-value 100% (200 000€)** :

| Détention | Impôt à la cession | Net final |
|-----------|-------------------|-----------|
| Personnel (flat tax) | 100 000€ × 30% = 30 000€ | 170 000€ |
| Société SASU (IS + flat tax dividende) | 100 000€ × 25% = 25 000€ IS, puis dividende 75 000€ × 30% = 22 500€ | **152 500€** |
| Société SASU (réinvesti, sans distribution) | 25 000€ IS | **175 000€** dans la société |

**Gagnant si vous ne sortez pas** : la société.
**Gagnant si vous sortez tout de suite** : le compte personnel.

## Les 4 obligations déclaratives en 2026

### 1. Comptes crypto à l'étranger — formulaire **3916-bis**
Obligatoire pour tout compte hors France (Binance, Coinbase, Kraken, Bitpanda). **Amende : 750€ par compte non déclaré** (1 500€ si solde > 50 000€).

### 2. Plus-values annuelles — formulaire **2086**
Détail de chaque cession réalisée dans l'année.

### 3. Report au 2042-C — case 3AN (plus-value) ou 3BN (moins-value)

### 4. IFI si patrimoine immobilier via crypto
Certaines cryptos immobilières (RealT tokens) sont soumises à l'IFI si le patrimoine immobilier dépasse 1,3 M€.

## Les 3 stratégies d'optimisation

### Stratégie 1 — Utiliser les moins-values latentes
Vendez vos positions en moins-value en fin d'année pour compenser vos plus-values. La règle des moins-values reportables 10 ans s'applique.

### Stratégie 2 — Détenir via société uniquement si vous ne sortez pas
Si vous voulez capitaliser sans sortir sur 5-10 ans, la société est plus avantageuse (IS 25% < flat tax 30%). Sinon, restez en perso.

### Stratégie 3 — Expatriation avant cession
Si vous prévoyez une grosse cession, une expatriation peut être fiscalement pertinente (attention à l'**exit tax** française si patrimoine crypto > 800 000€).

## Erreurs à ne pas commettre

1. **Ne pas déclarer** : Bercy croise les données avec les exchanges européens (MiCA + DAC8)
2. **Confondre plus-value crypto et compte-titres** : formules totalement différentes
3. **Oublier le formulaire 3916-bis** : 750€ à 1 500€ par compte
4. **Requalifier en pro sans le vouloir** : gains BIC/BNC bien plus taxés
5. **Investir crypto en société sans documentation** : requalification en distribution occulte possible

## FAQ

**Le mining est-il imposé en 2026 ?**
Oui. Les gains sont imposés en BNC (barème IR + prélèvements sociaux).

**Puis-je payer en crypto ?**
Vous pouvez accepter des cryptos comme moyen de paiement B2B. La transaction est traitée comme une opération de troc, avec plus-value crypto au moment de la conversion.

**Bitcoin en salaire dirigeant ?**
Non recommandé. L'URSSAF exigera cotisations en euros sur la contrevaleur du BTC.

**MiCA et DAC8 : impact en 2026 ?**
Les exchanges européens partagent automatiquement vos données avec Bercy. Fini le "je n'ai jamais déclaré" — Bercy sait.

---

**Sources officielles 2026 :**
- CGI - Art. 150 VH bis (plus-values crypto) : legifrance.gouv.fr
- CGI - Art. 200 A (flat tax + option barème) : legifrance.gouv.fr
- BOFiP - BOI-RPPM-PVBMC-30 (cryptomonnaies) : bofip.impots.gouv.fr
- Règlement MiCA (UE) 2023/1114 : eur-lex.europa.eu
- Directive DAC8 (échange automatique) : eur-lex.europa.eu
- Formulaires 2086 et 3916-bis : impots.gouv.fr`
  },

  'recruter-premier-salarie-sasu-eurl-2026': {
    id: '51',
    title: 'Recruter son premier salarié en SASU/EURL : le guide complet 2026',
    slug: 'recruter-premier-salarie-sasu-eurl-2026',
    excerpt: 'Passer de freelance seul à employeur : coût réel d\'un salarié, aides à l\'embauche, obligations, contrats, DPAE. Le playbook chiffré.',
    category: 'Gestion',
    tags: ['Recrutement', 'Salarié', 'SASU', 'EURL', 'Employeur'],
    readTime: 15,
    date: '2026-04-11',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Passer de freelance seul à employeur : le grand saut

Vous avez atteint le plafond de votre temps et vous voulez déléguer ? Recruter est **la** décision qui change tout : gain de temps, croissance, mais aussi obligations administratives, charges, et responsabilités. Voici le playbook complet.

## Le coût réel d'un salarié en 2026

### SMIC 2026 (1 835€ brut/mois)

| Poste | Montant mensuel | Annuel |
|-------|----------------|--------|
| Salaire brut | 1 835€ | 22 024€ |
| Charges patronales (~28% après réductions Fillon) | 514€ | 6 167€ |
| **Coût total employeur** | **~2 349€** | **~28 190€** |

### Cadre 3 500€ brut/mois

| Poste | Mensuel | Annuel |
|-------|---------|--------|
| Salaire brut | 3 500€ | 42 000€ |
| Charges patronales (~42%) | 1 470€ | 17 640€ |
| **Coût total employeur** | **~4 970€** | **~59 640€** |

### Règle rapide : "coût = brut × 1,42" (cadre), "brut × 1,28" (SMIC après aides)

## Les 8 aides à l'embauche 2026

### 1. Réduction générale de cotisations (ex-Fillon)
Applicable jusqu'à **1,6 SMIC** (2 936€ brut). Réduction maximale au SMIC (~26% du brut).

### 2. Aide à la première embauche
- **4 000€** (versés en 2 fois) pour la première embauche en CDI ou CDD ≥ 6 mois
- Conditions : entreprise < 250 salariés, embauche via Pôle Emploi (France Travail)

### 3. Aide à l'embauche d'un jeune (moins de 26 ans)
- **1 000€ à 4 000€** selon le contrat (apprentissage, CDI, CDD)

### 4. Aide à l'embauche d'un demandeur d'emploi longue durée
- **2 000€ à 6 000€** sur 2 ans

### 5. Aide à l'apprentissage
- **6 000€ pour la première année** (mineur ou majeur, jusqu'à Bac+5)

### 6. Contrat de professionnalisation
- Aide de **6 000€** + réduction des charges patronales

### 7. Emploi Franc (zones défavorisées)
- **5 000€ sur 3 ans** (CDI) si le salarié réside en QPV (Quartier Prioritaire de la Ville)

### 8. Aide au maintien dans l'emploi (senior 55+)
- Jusqu'à **4 000€/an** pendant 2 ans

## Les 7 étapes pour recruter en 2026

### Étape 1 — Définir le poste
- Fiche de poste : missions, compétences, résultats attendus
- Type de contrat : CDI ou CDD (justification obligatoire)
- Temps de travail : 35h, 39h (heures sup), forfait jour cadre

### Étape 2 — Publier l'annonce
- Gratuit : Pôle Emploi (France Travail), LinkedIn, Indeed
- Payant : Welcome to the Jungle (300-500€/mois), APEC (cadres), spécialisés

### Étape 3 — Sélection et entretiens
- Filtres CV → entretiens (2-3 rounds)
- Test technique (facultatif mais recommandé)
- Vérification des références

### Étape 4 — Proposition d'embauche
- Proposition écrite (email) : poste, salaire, avantages, date de démarrage
- Négociation possible avant signature du contrat

### Étape 5 — DPAE (Déclaration Préalable À l'Embauche)
Obligatoire **jusqu'à 8 jours avant** l'embauche, via **URSSAF.fr** ou **net-entreprises.fr**.

### Étape 6 — Contrat de travail (à signer avant démarrage)
Mentions obligatoires : identités, poste, lieu, temps de travail, rémunération, convention collective, période d'essai.

**Durée période d'essai** :
| Statut | CDI | CDD |
|--------|-----|-----|
| Ouvrier/employé | 2 mois | 1 jour par semaine (max 1 mois) |
| Technicien/agent maîtrise | 3 mois | Idem |
| Cadre | 4 mois | Idem |

### Étape 7 — Visite d'information et de prévention (VIP)
Obligatoire dans les **3 mois** suivant l'embauche (médecine du travail).

## Les obligations récurrentes d'un employeur

### Mensuelles
- **DSN** (Déclaration Sociale Nominative) : avant le 5 ou le 15 du mois (selon taille)
- **Fiche de paie** : à remettre au salarié
- **Paiement des charges** : URSSAF, retraite, prévoyance

### Annuelles
- **Bilan social** (> 300 salariés seulement, pas pour vous)
- **Entretien professionnel** : tous les 2 ans
- **Formation** : plan de développement des compétences
- **Congés payés** : décompte 2,5 jours ouvrables/mois

### Ponctuelles
- **Contrat de mutuelle** : obligatoire (financée à 50% minimum par l'employeur)
- **Complémentaire prévoyance** : obligatoire pour les cadres (loi 78-49)
- **DUERP** (Document Unique d'Évaluation des Risques Professionnels)
- **Affichages obligatoires** : convention collective, horaires, sécurité

## Le coût caché : outils de gestion RH

| Outil | Coût mensuel |
|-------|-------------|
| Logiciel de paie (Silae, PayFit, Sage) | 20 à 40€/salarié |
| Mutuelle collective (part employeur) | 40 à 80€/mois |
| Prévoyance (cadres) | 10 à 25€/mois |
| Comptable (traitement paie) | 30 à 60€/salarié |
| Assurance responsabilité employeur (RC) | Inclus dans RC Pro |

**Total moyen** : ~100 à 200€/mois en frais annexes par salarié.

## SASU vs EURL : impact du recrutement

| Critère | SASU (assimilé salarié) | EURL (TNS) |
|---------|-------------------------|-----------|
| Complexité RH | Élevée (URSSAF, DSN, cotisations lourdes) | Idem (le dirigeant TNS reste distinct) |
| Coût dirigeant | 80% brut | 45% brut |
| Coût salarié | 42% brut cadre | 42% brut cadre |
| **Impact recrutement sur cash** | Fort (dirigeant + salarié = 2 fiches paie assimilé salarié+cadre) | Moins fort (dirigeant TNS) |

## Simulation : recruter un dev 3 500€ brut

| Poste | Coût annuel |
|-------|-------------|
| Salaire brut | 42 000€ |
| Charges patronales | 17 640€ |
| Aide embauche jeune (une fois) | -4 000€ |
| Mutuelle employeur (50%) | 480€ |
| Prévoyance | 240€ |
| DUERP + affichages | 200€ (une fois) |
| Logiciel paie | 240€ |
| **Coût total année 1** | **~56 800€** |
| **Coût récurrent** | **~60 600€/an** |

**Seuil pour recruter** : minimum **80 000€ de CA supplémentaire** grâce au salarié pour être rentable.

## Alternatives au CDI (à considérer)

### Sous-traitance freelance
- Pas d'engagement long terme
- Pas de charges sociales employeur
- Facturation à la mission

### Portage salarial
- Vous "payez" le portage, la personne est salariée du portage
- Coût total ~30% de plus qu'un CDI direct
- Zéro admin pour vous

### Apprentissage
- Coût = 27% du SMIC pour un mineur, jusqu'à 100% du SMIC pour 26+ ans
- Aide unique de **6 000€**
- Idéal pour former à sa méthode

### Stage
- Gratification minimale : **~4,50€/h** (2026)
- Max 6 mois
- Formation obligatoire post-stage si embauche

## Checklist embauche 2026

- [ ] Fiche de poste finalisée
- [ ] Convention collective identifiée
- [ ] Grille salariale conforme
- [ ] Contrat de travail rédigé (modèle à jour)
- [ ] DPAE effectuée
- [ ] Adhésion mutuelle collective
- [ ] Adhésion prévoyance (cadres)
- [ ] Logiciel de paie configuré
- [ ] DUERP rédigé
- [ ] Affichages obligatoires en place
- [ ] Visite médicale planifiée

---

**Sources officielles 2026 :**
- Code du travail : legifrance.gouv.fr
- URSSAF - Barème charges patronales 2026 : urssaf.fr
- Pôle Emploi (France Travail) - Aides à l'embauche : francetravail.fr
- Ministère du Travail - Réduction générale : travail-emploi.gouv.fr
- Code de la sécurité sociale - Art. L241-13 (réduction Fillon)`
  },

  'freelance-impayes-recouvrement-2026': {
    id: '52',
    title: 'Freelance et impayés : recouvrement amiable et judiciaire en 2026',
    slug: 'freelance-impayes-recouvrement-2026',
    excerpt: 'Client qui ne paie pas ? Relances, mise en demeure, injonction de payer, procédure européenne : toutes les armes du freelance en 2026.',
    category: 'Gestion',
    tags: ['Impayés', 'Recouvrement', 'Freelance', 'Juridique', 'Facture'],
    readTime: 12,
    date: '2026-04-10',
    author: 'Bozzam',
    popular: true,
    fullContent: `## L'impayé, cauchemar n°1 du freelance

25% des freelances ont subi au moins un impayé de plus de 3 000€ dans leur carrière. La bonne nouvelle : la loi française est **très** favorable au créancier. Voici les 5 armes légales pour récupérer votre argent en 2026.

## Le calendrier de la relance : la méthode CRAM

**C**ourtoise → **R**appel ferme → **A**vis de recouvrement → **M**ise en demeure

| Étape | Délai | Contenu |
|-------|-------|---------|
| J+0 | Facture envoyée | Facture claire avec échéance J+30 |
| J+31 | Relance courtoise | Email amical rappelant la facture |
| J+45 | Rappel ferme | Email + mention "risque de pénalités" |
| J+60 | Avis de recouvrement | Lettre recommandée avec AR + pénalités calculées |
| J+75 | Mise en demeure | LRAR avec délai final 8 jours |
| J+90 | Injonction de payer | Requête au tribunal de commerce |

## Étape 1 — La relance amiable (60% des cas résolus ici)

### Modèle de relance courtoise (J+31)

> *"Bonjour [Nom], Sauf erreur de ma part, la facture n°[X] du [date] pour un montant de [Y]€ TTC n'a pas encore été réglée. Je vous serais reconnaissant de bien vouloir procéder au règlement dans les meilleurs délais. Cordialement."*

### Modèle de rappel ferme (J+45)

> *"Bonjour [Nom], Ma facture n°[X] du [date] est en retard de X jours. Conformément à mes CGV, un retard de paiement entraîne l'application de pénalités calculées au taux de [3× taux légal] soit [15,21%] par an, ainsi qu'une indemnité forfaitaire de 40€ (article D441-5 du Code de commerce). Merci de régulariser sous 8 jours."*

## Étape 2 — La mise en demeure formelle (J+75)

**Format obligatoire** :
- Lettre **recommandée avec accusé de réception**
- Mention explicite "MISE EN DEMEURE"
- Montant total réclamé (facture + pénalités + 40€ + éventuels frais)
- Délai final (8 à 15 jours)
- Menace d'action judiciaire

**Effet juridique** : point de départ officiel des intérêts moratoires si non déjà appliqués. Preuve indispensable pour toute procédure future.

**Coût** : ~7€ (LRAR classique) ou 3,50€ (LRAR électronique via lettre-recommandee.fr).

## Étape 3 — L'injonction de payer (procédure simple et efficace)

C'est **LA** procédure adaptée aux freelances.

### Comment ça marche

1. Vous remplissez le formulaire **Cerfa 12946** (Tribunal de commerce si votre débiteur est une entreprise)
2. Vous joignez : facture(s), mise en demeure, CGV, contrat
3. Vous déposez au **tribunal de commerce du siège social du débiteur**
4. Frais de greffe : **35,21€**
5. Délai : le juge statue en **1 à 3 semaines**, sans audience
6. Si acceptée, une **ordonnance d'injonction de payer** est délivrée

### Notification au débiteur

L'ordonnance doit être **notifiée par un commissaire de justice** (ex-huissier) sous 6 mois. Coût : ~60 à 100€.

### Contestation
Le débiteur a **1 mois** pour faire opposition. S'il ne le fait pas, l'ordonnance devient définitive → exécution forcée possible.

### Exécution
- Commissaire de justice : saisie sur compte bancaire, salaire, ou biens
- Frais mis à la charge du débiteur

**Efficacité** : 70% des impayés sont réglés à ce stade.

## Étape 4 — Le référé provision (si urgence)

Si le montant est important et l'obligation manifeste, le **référé provision** permet d'obtenir un jugement en **15 jours à 1 mois** au tribunal de commerce.

**Coût** : ~2 000 à 5 000€ (avocat non obligatoire mais recommandé, frais judiciaires).

## Étape 5 — La procédure européenne (client UE)

Depuis 2008, la **procédure européenne d'injonction de payer** (règlement CE n°1896/2006) permet de récupérer une créance dans toute l'UE.

- Formulaire A (demande) : à remplir sur e-justice.europa.eu
- Créance transfrontalière incontestée
- Traduction dans la langue du débiteur nécessaire
- Coût : 30 à 100€ selon le pays
- Délai : 1 à 3 mois

## Les 5 armes légales à connaître

### 1. Pénalités de retard automatiques
Depuis 2013, elles s'appliquent **automatiquement** sans mention dans le contrat. Taux minimum : **BCE + 10 points** (soit ~14,25% en 2026). Vos CGV peuvent stipuler un taux plus élevé.

### 2. Indemnité forfaitaire de 40€
Due de plein droit pour chaque facture en retard (art. D441-5).

### 3. Frais de recouvrement
Vos honoraires d'avocat / commissaire de justice sont à la charge du débiteur (art. 700 du CPC).

### 4. Droit de rétention
Vous pouvez **retenir un livrable** tant que le paiement n'est pas effectué (à condition que ce soit prévu au contrat).

### 5. Compensation
Si vous devez de l'argent au client (avoir, TVA récupérée), compensez avec sa dette (art. 1289 Code civil).

## Cas particulier : le client insolvable

Si le client est en **procédure collective** (sauvegarde, redressement, liquidation) :

1. **Déclarez votre créance** dans les 2 mois au mandataire judiciaire (obligatoire)
2. Vous devenez **créancier chirographaire** (rang faible)
3. Vous serez payé après les créanciers privilégiés (URSSAF, salariés, banques)
4. Taux de récupération moyen : **5 à 15%** pour un chirographaire

## Coûts et rendement des procédures

| Procédure | Coût | Délai | Taux de récupération |
|-----------|------|-------|---------------------|
| Relance amiable | 0€ | 15-30j | 40% |
| Mise en demeure LRAR | 7€ | 15j | 20% supplémentaires |
| Injonction de payer | 35 + 100€ (huissier) | 30-60j | 70% des cas restants |
| Référé provision | 2 000-5 000€ | 15-30j | 90% (dossier solide) |
| Procédure européenne | 30-100€ | 30-90j | 60% |

## Prévention : 5 clauses à mettre dans vos CGV

1. **Acompte de 30%** à la signature
2. **Facturation mensuelle** (pas à la livraison uniquement)
3. **Retard de paiement** : pénalité = **taux BCE + 15 points** (soit ~19% en 2026)
4. **Indemnité forfaitaire** : 40€ + frais de recouvrement
5. **Clause d'interruption** : possibilité de suspendre la mission après 15 jours de retard

## Outils digitaux 2026

| Outil | Fonction | Prix |
|-------|----------|------|
| Bill.com | Relances auto | 25€/mois |
| Rubypayeur | Recouvrement | 12% des créances récupérées |
| Deep Legal | Injonction de payer online | 89€/dossier |
| Kompany | Vérification solvabilité pré-mission | 15€/vérification |
| BODACC | Alertes procédures collectives | Gratuit |

## Erreurs fatales à éviter

1. **Ne pas envoyer de LRAR** : preuve manquante en procédure
2. **Attendre 6 mois pour agir** : prescription commerciale = **5 ans**, mais plus vous attendez, moins vous récupérez
3. **Négliger le contrat** : sans contrat écrit, il faudra prouver l'existence de la créance
4. **Se braquer** : parfois un plan de paiement en 3-6 mois vaut mieux qu'un procès
5. **Ne pas facturer les pénalités** : vous êtes en droit ! Facturez-les.

---

**Sources officielles 2026 :**
- Code de commerce - Art. L441-10 et L441-11 (pénalités de retard) : legifrance.gouv.fr
- Code de commerce - Art. D441-5 (indemnité forfaitaire 40€) : legifrance.gouv.fr
- Code de procédure civile - Art. 1405 et suivants (injonction de payer) : legifrance.gouv.fr
- Règlement (CE) n°1896/2006 (procédure européenne)
- Cerfa 12946 (formulaire injonction de payer) : service-public.fr`
  },

  'ceder-clientele-freelance-2026': {
    id: '53',
    title: 'Céder sa clientèle freelance : évaluer et vendre son activité en 2026',
    slug: 'ceder-clientele-freelance-2026',
    excerpt: 'Vendre son portefeuille clients, son site, sa société : méthodes d\'évaluation, fiscalité de la plus-value, garantie de passif. Le guide de la sortie freelance.',
    category: 'Optimisation',
    tags: ['Cession', 'Vente', 'Clientèle', 'Plus-value', 'Exit'],
    readTime: 14,
    date: '2026-04-09',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Vendre son activité : sortir gagnant en 2026

Vous avez construit votre activité de freelance ou d'entrepreneur depuis 5, 10, 15 ans. À un moment, la question de la sortie se pose : reconversion, retraite, projet de vie. Voici comment valoriser et céder votre activité en 2026.

## Ce que vous vendez réellement

| Type d'actif | Valorisation typique |
|-------------|---------------------|
| Fonds de commerce (clientèle + matériel + baux) | 30-70% du CA annuel |
| Portefeuille clients récurrents | 6 à 24 mois de MRR |
| Site web / e-commerce | 24-48× EBITDA mensuel |
| Marque et propriété intellectuelle | Variable (~1-3× CA de licence) |
| Titres de société (parts sociales) | Multiples EBITDA (voir tableau) |

## Les 4 méthodes d'évaluation en 2026

### 1. Méthode des barèmes de la profession (fonds de commerce artisan/commerçant)

Chaque profession a un **barème INSEE / Notaires** :
- Coiffeur : 60 à 100% du CA
- Restaurant : 60 à 100% du CA
- Freelance IT / conseil : rarement applicable (activité personnelle)

### 2. Méthode des multiples d'EBITDA (société)

| Type d'activité | Multiple EBITDA |
|-----------------|----------------|
| Consulting IT / conseil | 3× à 5× |
| Agence web / marketing | 4× à 6× |
| SaaS B2B (récurrent) | 6× à 10× |
| E-commerce | 3× à 5× |
| Formation en ligne | 4× à 7× |
| Freelance seul (bench de tête sans salariés) | 1× à 2× (peu de valeur transférable) |

### 3. Méthode DCF (Discounted Cash Flow)

Actualisation des flux de trésorerie futurs à un taux (généralement 10-15% pour une TPE).

### 4. Méthode comparative (transactions similaires)

Recherchez les transactions publiques sur BODACC, Fusac, ou plateformes comme Fusacq et Cession-commerce.

## Cas pratique — Freelance consultant IT

**Situation** : consultant seul, CA 150 000€, marge nette 60 000€ après charges, 3 clients récurrents.

| Méthode | Valorisation |
|---------|-------------|
| Multiple EBITDA (3×) | 180 000€ |
| Portefeuille récurrent (12 mois MRR) | 90 000€ |
| Barème CA (50%) | 75 000€ |
| **Valeur négociable** | **80 000 à 150 000€** |

**Difficulté** : le "goodwill" (relation personnelle) est peu transférable. La valorisation est donc moindre qu'une agence structurée.

## Cas pratique — Agence 5 personnes

**Situation** : agence web, CA 800 000€, EBITDA 200 000€, équipe de 5.

| Méthode | Valorisation |
|---------|-------------|
| Multiple EBITDA (5×) | 1 000 000€ |
| Actif net comptable + goodwill | 900 000€ |
| **Valeur négociable** | **900 000 à 1 100 000€** |

## La fiscalité de la cession en 2026

### Cession de parts sociales (SASU/EURL/SARL)

**Régime standard** :
- Plus-value = Prix de cession - Prix d'acquisition
- Imposition : **flat tax 30%** ou barème progressif + prélèvements sociaux 17,2%

**Abattement pour durée de détention** (si option barème progressif uniquement) :
| Durée détention | Abattement |
|-----------------|-----------|
| 2-8 ans | 50% |
| 8+ ans | 65% |

### Régime "dirigeant partant à la retraite" (art. 150-0 D ter)

Si le cédant part à la retraite :
- **Abattement fixe de 500 000€** sur la plus-value (avant flat tax)
- Conditions : 5 ans d'exercice + départ retraite dans les 2 ans de la cession

**Exemple** : plus-value de 800 000€ → base imposable réduite à 300 000€ → flat tax 90 000€ au lieu de 240 000€. **Économie : 150 000€**.

### Régime Dutreil (transmission familiale)

Voir notre article dédié — abattement de **75%** sur la valeur transmise.

### Régime apport-cession 150-0 B ter (report d'imposition)

Si vous apportez vos titres à une holding avant la cession → report de la plus-value. Voir article dédié.

## Les étapes d'une cession réussie

### Étape 1 — Préparer l'activité (12-24 mois avant)

- Formalisez les process (documentation)
- Diversifiez les clients (< 30% de dépendance à un seul client)
- Recrutez / responsabilisez pour montrer que ça tourne sans vous
- Clean-up comptable : payez vos dettes, à jour URSSAF, TVA

### Étape 2 — Évaluer et positionner

- Faire évaluer par un expert-comptable ou société d'évaluation (500 à 3 000€)
- Rédiger un teaser (2 pages anonymisées)
- Rédiger un mémorandum d'information (10-20 pages détaillées)

### Étape 3 — Chercher des acquéreurs

| Canal | Coût | Efficacité |
|-------|------|-----------|
| Réseau personnel | 0€ | Bonne (30% des deals) |
| Fusacq / Cession-commerce | ~100€/mois | Moyenne |
| Cabinet fusac (M&A) | 5-10% du deal | Meilleure (structuré) |
| Concurrent direct (approche discrète) | 0€ | Excellente si acheteur stratégique |

### Étape 4 — Négociation et LOI (Letter of Intent)

- Accord de confidentialité (NDA)
- Lettre d'intention non-engageante
- Prix, modalités de paiement, calendrier

### Étape 5 — Due diligence (30 à 90 jours)

L'acquéreur audite votre entreprise :
- Comptabilité (3-5 dernières années)
- Contrats clients, fournisseurs, salariés
- Juridique (contentieux, PI)
- Fiscal

### Étape 6 — Contrat de cession + garantie de passif

**Garantie d'actif et de passif (GAP)** : vous garantissez à l'acquéreur qu'aucun passif caché n'existe. Durée typique : 2 à 5 ans. Plafond : 10 à 30% du prix.

**Séquestre** : 10 à 20% du prix conservé pendant la durée de la GAP.

### Étape 7 — Signature et closing

- Signature devant notaire ou sous seing privé
- Paiement du prix (immédiat, échelonné, ou earn-out)
- Formalités : modification RCS, transfert des contrats

## Les 5 pièges de la cession

### Piège 1 — Sur-évaluation
Un prix trop élevé fait fuir les acquéreurs. Restez dans les multiples de votre secteur.

### Piège 2 — Cession trop rapide
Sans préparation, l'acquéreur vous propose 40% de la valeur réelle. Prévoyez 12-24 mois de préparation.

### Piège 3 — Dépendance client
Si un client fait > 40% du CA, l'acquéreur négociera fort à la baisse.

### Piège 4 — Absence de contrat
Vos accords clients doivent être **écrits et transférables**. Sinon, aucune garantie de continuité pour l'acquéreur.

### Piège 5 — Fiscalité mal anticipée
Sans structure (holding), vous pouvez perdre 30% de plus-value en flat tax. Anticipez 2 ans avant.

## Alternative : le rachat par les salariés (RES / LBO)

Si vous n'avez pas d'acquéreur externe, vos salariés peuvent racheter via un **LBO** :
- Création d'une holding par les salariés
- Emprunt bancaire (levier 3× à 5×)
- Remboursement par les dividendes de la société cible
- **Vous êtes payé sur 5 à 7 ans** (durée du crédit)

## Simulation : cession 500 000€ après 10 ans de SASU

| Stratégie | Impôt | Net |
|-----------|-------|-----|
| Cession directe (flat tax 30%) | 150 000€ | **350 000€** |
| Cession avec option barème + abattement 65% | ~55 000€ | **445 000€** |
| Cession "dirigeant partant en retraite" (abattement 500k€) | 0€ | **500 000€** |
| Cession via holding (apport-cession, réinvestissement) | 0€ (reporté) | 500 000€ dans holding |
| Transmission Dutreil aux enfants | ~15 000€ | 485 000€ transmis |

**Différence** : **jusqu'à 150 000€ selon la stratégie**.

## Checklist cession

- [ ] Comptabilité à jour (3 derniers exercices audités)
- [ ] Contrats clients formalisés et transférables
- [ ] Process documentés (SOP)
- [ ] Dépendance client < 30%
- [ ] Salariés en CDI (pas de dépendance à des freelances)
- [ ] Fiscalité anticipée (holding, Dutreil, retraite)
- [ ] Expert-comptable ET avocat spécialisé cession
- [ ] NDA signés avant partage d'infos

---

**Sources officielles 2026 :**
- CGI - Art. 150-0 A à 150-0 D ter (plus-value de cession) : legifrance.gouv.fr
- CGI - Art. 787 B (Dutreil transmission) : legifrance.gouv.fr
- CGI - Art. 150-0 B ter (apport-cession) : legifrance.gouv.fr
- Code de commerce - Art. L141-1 à L141-22 (cession fonds de commerce) : legifrance.gouv.fr
- BODACC (annonces cessions) : bodacc.fr`
  },

  'freelance-expatrie-portugal-dubai-estonie-2026': {
    id: '54',
    title: 'Freelance expatrié : Portugal, Dubaï, Estonie — fiscalité 2026',
    slug: 'freelance-expatrie-portugal-dubai-estonie-2026',
    excerpt: 'S\'expatrier pour optimiser sa fiscalité : NHR Portugal, résidence Dubaï, e-Residency Estonie. Conditions, pièges, exit tax français en 2026.',
    category: 'Optimisation',
    tags: ['Expatriation', 'Portugal', 'Dubaï', 'Estonie', 'Fiscalité internationale'],
    readTime: 15,
    date: '2026-04-08',
    author: 'Bozzam',
    popular: true,
    fullContent: `## L'expatriation fiscale : mythe ou réalité en 2026 ?

Beaucoup de freelances rêvent d'optimiser leur fiscalité en quittant la France. **Attention** : en 2026, les règles sont bien plus strictes qu'il y a 10 ans. Voici la réalité de trois destinations phares : Portugal, Dubaï, Estonie.

## La règle d'or : la résidence fiscale

Vous n'êtes pas imposé en France selon votre nationalité, mais selon votre **résidence fiscale** (art. 4 B du CGI). Vous êtes résident fiscal français si :

1. Votre **foyer** (famille) est en France, OU
2. Vous séjournez en France **plus de 183 jours/an**, OU
3. Votre **activité professionnelle principale** est en France, OU
4. Le **centre de vos intérêts économiques** est en France

**Un seul critère suffit**. Si vous voulez expatrier fiscalement, il faut sortir des 4 en même temps.

## Destination 1 — Portugal (NHR / RNH)

### Le régime Non-Habitual Resident (NHR) en 2026

Depuis 2024, le NHR **classique a été supprimé** pour les nouveaux arrivants. Un nouveau régime "**NHR 2.0**" existe mais est réservé aux :
- Enseignants-chercheurs
- Scientifiques
- Startups technologiques (avec statut IFICI)

**Pour un freelance IT classique** : plus d'accès au NHR.

### Fiscalité IRS "standard" au Portugal 2026

| Tranche annuelle | Taux |
|-----------------|------|
| 0 - 8 059€ | 13% |
| 8 059 - 12 160€ | 16,5% |
| 12 160 - 17 233€ | 22% |
| 17 233 - 22 306€ | 25% |
| 22 306 - 28 400€ | 32% |
| 28 400 - 41 629€ | 35,5% |
| 41 629 - 44 987€ | 43,5% |
| 44 987 - 83 696€ | 45% |
| > 83 696€ | 48% |

**Constat** : le Portugal en régime standard est **plus taxé** que la France pour les hauts revenus.

### Cotisations sociales freelance au Portugal

- **21,4% du CA** (Segurança Social)
- 2 premiers exercices : réduction à 0% possible
- **Total charges** : ~15-25% du CA selon activité

### Avantage encore valide : les **résidents non-domiciliés** (via société ADS)

Créer une société Madère IBC (International Business Center) — 5% IRC (IS) jusqu'en 2027.

## Destination 2 — Dubaï / Émirats Arabes Unis (UAE)

### Fiscalité en 2026

| Impôt | Taux |
|-------|------|
| IR (personne physique) | **0%** |
| IS (société) < 375 000 AED de bénéfice | **0%** |
| IS (société) > 375 000 AED (~90 000€) | **9%** |
| TVA | 5% |
| Cotisations sociales | **0%** (pas de sécurité sociale) |

### Créer une société à Dubaï

**Free Zone** (recommandé pour freelances) :
- IFZA, DMCC, RAKEZ : 100% étranger, 0% IS jusqu'à seuil
- Coût création : **7 000 à 15 000€** (première année)
- Renouvellement : **4 000 à 8 000€/an**
- Visa Golden Visa : 10 ans (si investissement > 500 000 AED)

**Mainland** :
- IS 9%
- Sponsor local nécessaire (parfois)

### Vraie question : le retour France

Si vous rentrez en France (ou y séjournez > 183j) : imposition rétroactive française possible **si vous êtes qualifié de résident fictif** (art. 4 B CGI).

### Convention fiscale France-UAE 1989

- Pas de double imposition
- Mais Bercy considère depuis 2020 que **si votre activité est réalisée depuis la France, vous êtes imposable en France** malgré la société UAE.

**Conclusion Dubaï** : très intéressant SI vous vivez réellement là-bas (école des enfants, médecin, vie quotidienne). Sinon, risque très élevé de redressement.

## Destination 3 — Estonie (e-Residency)

### L'e-Residency : la carte de résident numérique

- Coût : **100€ + 60€ carte à puce**
- Permet de créer une société estonienne en ligne
- Signature numérique reconnue dans toute l'UE
- **N'est PAS une résidence fiscale** (attention à la confusion)

### Fiscalité société estonienne (OÜ)

| Poste | Traitement |
|-------|-----------|
| IS sur bénéfices retenus | **0%** (déclaration annuelle simple) |
| IS sur bénéfices distribués (dividendes) | **20% flat** (2026) |
| IS sur bénéfices distribués régulièrement | **14%** (taux réduit dès la 3ème année) |
| Coût comptable annuel | 500 à 2 000€ |
| TVA UE | 20% (seuil 40 000€) |

### Combinaison typique freelance IT français

1. E-Residency Estonie → société OÜ
2. Client français facture la société OÜ
3. Bénéfices non distribués → 0% IS (capitalisation)
4. Distribution ponctuelle → 20% IS

**MAIS** : si vous êtes résident fiscal français, la **règle CFC (Controlled Foreign Company)** requalifie les bénéfices en revenus français (art. 209 B CGI). L'Estonie fiscalement est **inefficace pour un résident français**.

### Cas où l'Estonie fonctionne
- Nomade numérique **sans résidence fiscale claire**
- Résident dans un pays avec convention favorable
- Structure de holding pour freelances européens

## Comparatif 3 destinations pour 100 000€ de bénéfice

| Destination | IR/IS | Cotisations | Net freelance | Facilité |
|-------------|-------|-------------|---------------|----------|
| **France (SASU)** | ~35% globale | Inclus | ~55 000€ | ✅✅✅ |
| **Portugal NHR (si éligible)** | 20% | 21% CA | ~65 000€ | ✅✅ |
| **Portugal standard** | ~40% | 21% CA | ~50 000€ | ✅✅ |
| **Dubaï (résident effectif)** | 0-9% | 0% | ~91 000€ | ⚠️ Vie sur place |
| **Estonie (résident FR)** | Requalifié France | Inclus | ~55 000€ | ⚠️ CFC |

## L'exit tax : le piège français

Si vous quittez la France en emportant plus de **800 000€ de titres de société** (ou 50% détention), vous êtes soumis à l'**exit tax** (art. 167 bis CGI) :

- Plus-value latente taxée à **flat tax 30%** au départ
- Paiement immédiat sauf **sursis** (garanties bancaires)
- Sursis "automatique" si départ vers **UE/EEE**
- Après **5 ans** hors de France : dégrèvement total si vous ne vendez pas

## La checklist du départ (12 mois avant)

1. **J-12 mois** : décider la destination, valider la convention fiscale
2. **J-9 mois** : audit patrimonial (Exit tax ? Assurance-vie ? Immobilier ?)
3. **J-6 mois** : couper les liens administratifs France (bail, EDF, portable, SIRET si nécessaire)
4. **J-3 mois** : ouvrir compte bancaire et société dans le pays cible
5. **J-1 mois** : déclaration de départ à France Impôts (formulaire 2042-NR)
6. **J-0** : effectuer le déménagement physique (baux, vols, école)
7. **J+3 mois** : première déclaration fiscale dans le pays d'accueil

## Les erreurs qui coûtent cher

1. **Résidence "fictive"** : garder un logement, une carte Sécu, un médecin en France → redressement possible
2. **Aller-retours fréquents** : > 90 jours cumulés/an peut suffire à requalifier
3. **Compte français principal** : payer ses courses avec CB française = présence en France
4. **Oublier l'exit tax** : sanction 40-80% de la plus-value
5. **Non-déclaration du compte étranger** : 1 500€/an d'amende par compte

## FAQ

**Puis-je "essayer" 6 mois sans engagement fiscal ?**
Oui, tant que vous ne franchissez pas 183 jours et que vous conservez votre résidence FR.

**La convention fiscale France-Portugal existe-t-elle encore ?**
Oui, mais elle a été révisée en 2016 pour limiter les optimisations abusives.

**Puis-je facturer à un client français depuis Dubaï ?**
Techniquement oui. Fiscalement, si vous êtes vraiment résident UAE, c'est OK. Si Bercy considère votre activité comme française, redressement.

**Combien coûte un cabinet d'expatriation fiscale ?**
5 000 à 30 000€ selon complexité. Recommandé pour patrimoine > 300 000€.

---

**Sources officielles 2026 :**
- CGI - Art. 4 B (résidence fiscale) : legifrance.gouv.fr
- CGI - Art. 167 bis (exit tax) : legifrance.gouv.fr
- CGI - Art. 209 B (CFC - sociétés étrangères contrôlées) : legifrance.gouv.fr
- Portugal - Régime NHR : portaldasfinancas.gov.pt
- UAE Federal Tax Authority : tax.gov.ae
- Estonie - e-Residency : e-resident.gov.ee`
  },

  'conjoint-collaborateur-couple-entrepreneur-2026': {
    id: '55',
    title: 'Conjoint collaborateur : le statut pour couples entrepreneurs en 2026',
    slug: 'conjoint-collaborateur-couple-entrepreneur-2026',
    excerpt: 'Votre conjoint(e) participe à votre activité freelance ? Découvrez les 3 statuts (collaborateur, salarié, associé), leurs coûts et leur protection sociale.',
    category: 'Protection sociale',
    tags: ['Conjoint', 'Couple', 'Statut', 'Protection sociale', 'Famille'],
    readTime: 11,
    date: '2026-04-07',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Votre conjoint(e) travaille avec vous ? Il/elle doit avoir un statut

Depuis 2019, il est **obligatoire** de déclarer le conjoint qui participe régulièrement à l'activité (loi PACTE, art. L121-4 du Code de commerce). Trois statuts existent : conjoint collaborateur, conjoint salarié, ou conjoint associé.

## Les 3 statuts en un coup d'œil

| Statut | Rémunération | Cotisations sociales | Protection sociale | Autonomie |
|--------|-------------|---------------------|-------------------|-----------|
| **Collaborateur** | Aucune (règle légale) | Forfaitaires (SSI/CIPAV) | Retraite + IJ maladie | Faible |
| **Salarié** | Salaire normal | Charges classiques (~82% du brut) | Complète (chômage + retraite + IJ) | Élevée |
| **Associé** | Dividendes + rémunération | Selon statut société | Variable | Maximale |

## Statut 1 — Conjoint collaborateur

### Conditions
- Marié, pacsé, ou concubin déclaré
- Participation **régulière** à l'activité
- **Pas de rémunération** directe
- **Pas de rôle de dirigeant** (le conjoint collaborateur n'est pas gérant)

### Cotisations 2026 (5 assiettes au choix)

| Base de cotisation | Cotisation annuelle estimée | Trimestres validés |
|-------------------|----------------------------|-------------------|
| **1/3 du PASS** (15 456€) | ~4 400€ | 4/4 |
| **1/2 du PASS** (23 184€) | ~6 500€ | 4/4 |
| **1 PASS** (46 368€) | ~13 000€ | 4/4 |
| **1/3 rev. du chef** | Variable | Selon montant |
| **1/2 rev. du chef** | Variable | Selon montant |

### Ce qui est couvert
- ✅ Retraite de base + complémentaire
- ✅ Indemnités journalières maladie (~30€/j)
- ✅ Assurance maternité (16 semaines)
- ✅ Invalidité / décès

### Ce qui n'est PAS couvert
- ❌ Assurance chômage
- ❌ Accidents du travail (sauf option payante)
- ❌ Formation professionnelle (partiellement)

### Limite temporelle 2026
Le conjoint collaborateur ne peut cumuler ce statut que **5 ans maximum**. Au-delà, il doit basculer en salarié ou associé (loi PACTE 2019).

## Statut 2 — Conjoint salarié

### Conditions
- Contrat de travail écrit
- Rémunération minimum : **SMIC** (1 835€ brut/mois en 2026)
- Lien de subordination effectif

### Coût employeur

| Poste | SMIC brut | 3 000€ brut |
|-------|-----------|-------------|
| Salaire brut annuel | 22 024€ | 36 000€ |
| Charges patronales (~42%) | 9 250€ | 15 120€ |
| **Coût total** | **~31 274€** | **~51 120€** |

### Avantages
- ✅ Protection sociale **maximale** (chômage inclus)
- ✅ Congés payés
- ✅ Retraite au régime général
- ✅ Cotisation à l'AGIRC-ARRCO

### Attention : requalification possible
Si le conjoint salarié n'exerce pas de vrai travail, l'URSSAF peut requalifier en **rémunération fictive** (redressement + pénalités).

## Statut 3 — Conjoint associé

### Conditions
- Détient des parts sociales de la société (SASU/EURL → SAS/SARL)
- Pas nécessairement salarié
- Peut être dirigeant

### Fiscalité en SARL
- Le conjoint associé travaillant dans la société est **TNS** si détention ≥ 50%
- Cotisations forfaitaires minimum : ~1 100€/an

### Fiscalité en SAS
- Dividendes en flat tax 30%
- Salaire (si président ou directeur général) : régime assimilé salarié

### Avantages
- ✅ Autonomie et pouvoir de décision
- ✅ Dividendes possibles
- ✅ Transmission facilitée (part successorale)

### Inconvénients
- ⚠️ Responsabilité (illimitée en SNC, limitée en SAS/SARL)
- ⚠️ Complexité administrative

## Comparatif chiffré : couple entrepreneur 80 000€ de bénéfice

### Hypothèse 1 — Conjoint collaborateur (1/2 PASS)

| Poste | Montant |
|-------|---------|
| Bénéfice société | 80 000€ |
| Cotisation conjoint | -6 500€ |
| Salaire chef d'entreprise | -30 000€ + charges 12 600€ |
| **Bénéfice IS** | 30 900€ |
| IS 15% | -4 635€ |
| **Distributable dividendes** | **26 265€** |

Net total foyer : ~50 000€.

### Hypothèse 2 — Conjoint salarié SMIC

| Poste | Montant |
|-------|---------|
| Bénéfice société | 80 000€ |
| Salaire conjoint SMIC | -31 274€ |
| Salaire chef | -30 000€ + charges |
| **Bénéfice IS** | -6 000€ (perte) |
| Dividendes | 0€ |

Net total foyer : ~52 000€ (salaires nets combinés).

### Hypothèse 3 — Conjoint associé + dividendes

| Poste | Montant |
|-------|---------|
| Bénéfice société | 80 000€ |
| Salaire chef | -30 000€ + charges |
| **Bénéfice IS** | 37 400€ |
| IS 15% | -5 610€ |
| Dividendes à répartir | 31 790€ |
| Flat tax 30% | -9 537€ |
| **Dividendes nets pour le couple** | **22 253€** |

Net total foyer : ~48 000€, mais avec autonomie décisionnelle et transmission.

## Impact retraite (30 ans de cotisation)

| Statut | Retraite mensuelle nette estimée |
|--------|--------------------------------|
| Conjoint collaborateur (1/2 PASS) | ~600-800€ |
| Conjoint salarié SMIC | ~1 000-1 200€ |
| Conjoint salarié 3 000€ brut | ~1 800-2 200€ |
| Conjoint associé (dividendes) | Variable (retraite personnelle uniquement si activité) |

## FAQ

**Le conjoint collaborateur peut-il signer des contrats ?**
Oui, avec autorisation du chef d'entreprise (mandat explicite ou tacite).

**En cas de divorce, quels sont ses droits ?**
- Conjoint collaborateur : droits sur le fonds selon régime matrimonial + créance de participation possible
- Conjoint salarié : indemnités de licenciement possibles
- Conjoint associé : conserve ses parts sociales

**Puis-je changer de statut en cours d'année ?**
Oui, avec modification au Registre des Bénéficiaires Effectifs (RBE) et déclaration URSSAF.

**Le concubin peut-il être conjoint collaborateur ?**
Oui depuis 2007, à condition de justifier d'une communauté de vie stable.

**Combien coûte le statut de collaborateur si le chef d'entreprise est en micro ?**
En micro-entreprise, le conjoint collaborateur paie **58% ou 46% des cotisations du chef** (au choix).

## Checklist pour un couple entrepreneur

- [ ] Déterminer si le conjoint participe régulièrement (> 8h/semaine)
- [ ] Évaluer le régime matrimonial (communauté ? séparation ?)
- [ ] Comparer les 3 statuts sur cotisations ET protection
- [ ] Anticiper la retraite (RIS actuel et projection)
- [ ] Déclarer au CFE (Guichet Unique)
- [ ] Mettre à jour le K-bis + Kbis+
- [ ] Ouvrir compte bancaire pro dédié
- [ ] Contrat écrit si salarié

---

**Sources officielles 2026 :**
- Code de commerce - Art. L121-4 à L121-8 (conjoint collaborateur) : legifrance.gouv.fr
- Loi PACTE n°2019-486 (obligation de déclaration + limite 5 ans) : legifrance.gouv.fr
- URSSAF - Conjoint collaborateur : urssaf.fr
- SSI - Barème cotisations 2026 : secu-independants.fr`
  },

  'formation-professionnelle-freelance-cpf-fifpl-2026': {
    id: '56',
    title: 'Formation professionnelle freelance : CPF, FIF-PL et OPCO en 2026',
    slug: 'formation-professionnelle-freelance-cpf-fifpl-2026',
    excerpt: 'Utilisez votre budget formation : CPF, FIF-PL pour libéraux, AGEFICE pour commerçants, OPCO pour dirigeants. Comment financer 100% de votre montée en compétence.',
    category: 'Bons plans/Conseils',
    tags: ['Formation', 'CPF', 'FIF-PL', 'OPCO', 'AGEFICE'],
    readTime: 11,
    date: '2026-04-06',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Vous cotisez pour la formation — utilisez-la !

Chaque freelance cotise **0,25% de son CA** (ou 0,10% selon activité) pour la formation professionnelle continue. En 2026, cela représente en moyenne **200 à 600€/an** de droits à formation. La plupart des freelances ne les utilisent jamais. Voici comment récupérer 100% de votre budget.

## Les 4 dispositifs à connaître selon votre statut

| Statut | Organisme | Budget annuel typique | Formations éligibles |
|--------|-----------|----------------------|---------------------|
| **Micro-entrepreneur BIC** | AGEFICE | 100 à 500€ | Compétences métier + gestion |
| **Micro-entrepreneur BNC / libéral** | FIF-PL | 500 à 1 500€ | Compétences métier |
| **Micro-entrepreneur artisan** | Conseil de la formation CMA | 100 à 500€ | Métiers artisanaux |
| **Dirigeant SASU/EURL** | OPCO EP / AKTO / Atlas | Variable | Tous types |

## CPF (Compte Personnel de Formation)

Le CPF est **cumulé au long de votre carrière** et suit la personne, pas le statut.

### Alimentation en 2026

| Statut | Alimentation annuelle |
|--------|----------------------|
| Salarié temps plein | 500€/an (plafond 5 000€) |
| Salarié à mi-temps | 250€/an |
| Freelance / micro | **0€** (pas d'alimentation nouvelle mais crédit historique conservé) |

**Important 2026** : depuis la réforme 2024, le freelance n'alimente **plus** son CPF (seul le salariat cotise). Mais le **crédit historique** reste utilisable.

### Formations éligibles CPF

- Certifications RS/RNCP inscrites au répertoire
- Bilan de compétences
- VAE (Validation des Acquis de l'Expérience)
- Permis (B, C, poids lourds)
- Création/reprise d'entreprise

### Comment utiliser son CPF ?
1. Se connecter sur **moncompteformation.gouv.fr**
2. Vérifier son solde
3. Choisir une formation certifiée
4. Reste à charge : **100€** (participation obligatoire depuis 2024)

## FIF-PL (Fonds Interprofessionnel de Formation - Professions Libérales)

**Pour qui ?** Micro-entrepreneurs libéraux BNC (consultants, coachs, avocats, médecins, architectes, informaticiens libéraux non-cadres, etc.).

### Budget 2026

| Type de formation | Plafond annuel | Plafond journalier |
|-------------------|----------------|-------------------|
| Formation thématique prioritaire | 750€ | 250€ |
| Formation générale non prioritaire | 300€ | 150€ |
| Bilan de compétences | 1 500€ | — |
| E-learning long | 750€ | 40€/h |

### Formations "prioritaires" 2026
Chaque année, le FIF-PL définit des thèmes prioritaires par profession. Exemples 2026 :
- Cybersécurité (IT, avocats)
- IA générative et outils numériques
- Gestion de crise et bien-être
- Développement durable

### Comment faire ?
1. Choisir une formation dispensée par un organisme certifié Qualiopi
2. Faire la demande de prise en charge **AVANT** le début de formation (via FIF-PL.fr)
3. Réponse sous 30-45 jours
4. Suivre la formation → obtenir attestation
5. Faire la demande de remboursement (justificatifs)

## AGEFICE (Association de Gestion du Financement de la Formation des Chefs d'Entreprise)

**Pour qui ?** Micro-entrepreneurs et dirigeants **commerçants** (BIC).

### Budget 2026

| Type | Plafond annuel | Détail |
|------|---------------|--------|
| Formations "métier" | 2 500€ | 21€/h max |
| Bilan de compétences | 1 400€ | — |
| VAE | 800€ | — |
| Formation "informatique et bureautique" | 3 500€ | 40€/h max |

### Formation gratuite dispensée par la CCI
Attention : les CCI proposent des formations "Mallette du dirigeant" **100% prises en charge par l'AGEFICE**. Coût pour vous : **0€**.

## OPCO EP (Opérateurs de Compétences)

**Pour qui ?** Salariés d'entreprises de moins de 50 salariés, dirigeants SASU/EURL.

### En pratique pour un dirigeant SASU seul
- Verser 0,55% de la masse salariale (soi-même) à l'OPCO
- Accès à un budget de formation de **plusieurs milliers d'euros**
- Prise en charge : 100% des coûts (jusqu'à plafond OPCO)

### Formations éligibles
- Toutes formations liées au poste
- Certifications professionnelles
- Coaching de dirigeant
- Formations "compétences transverses" (langues, IA, management)

## Les 10 meilleures formations freelance en 2026

### Compétences techniques
1. **Certification IA générative** (OpenAI, Anthropic, Google) — 800 à 2 000€
2. **Cybersécurité (ISO 27001, RGPD)** — 1 500 à 3 000€
3. **Data / SQL / Python** — 500 à 1 500€
4. **Développement mobile Flutter/React Native** — 800 à 2 500€

### Compétences business
5. **Formation vente / négociation** — 1 000 à 3 000€
6. **Marketing digital (SEO, Ads)** — 800 à 2 000€
7. **Prise de parole publique** — 1 200 à 2 500€
8. **Comptabilité et fiscalité TPE** — 500 à 1 500€

### Compétences transverses
9. **Anglais professionnel (TOEIC, IELTS)** — 800 à 2 500€
10. **Bilan de compétences complet** — 1 500 à 2 500€

## Simulation : freelance IT BNC en 2026

**Situation** : Consultant IT freelance BNC, CA 90 000€, cotise 0,25% = 225€/an au FIF-PL

**Formations réalisées en 2026** :
- Certification IA générative : 1 500€ (prise en charge FIF-PL 750€)
- Anglais professionnel : 2 000€ (CPF crédit historique 1 500€)
- E-learning cybersécurité : 600€ (prise en charge FIF-PL 600€)

**Coût réel pour le freelance** : (1500-750) + (2000-1500) + (600-600) = **1 250€**
**Valeur totale des formations** : **4 100€**
**Reste à charge net après IR** : 1 250€ × (1 - 0,3) = **~875€** (car frais professionnels déductibles)

**ROI temps investi** : 3-5× le tarif journalier suivant les compétences acquises.

## Les 5 erreurs qui font perdre le budget

1. **Ne pas demander avant la formation** : la plupart des OPCA exigent une demande PRÉALABLE
2. **Choisir un organisme non-Qualiopi** : pas de remboursement possible
3. **Attendre la fin d'année** : les budgets sont limités et attribués au fil de l'eau
4. **Ne pas justifier le lien avec l'activité** : refus quasi-automatique
5. **Oublier de facturer le temps de formation à ses clients** : perte d'opportunité

## Plateformes de formations éligibles en 2026

| Plateforme | Éligible CPF | Éligible FIF-PL |
|-----------|--------------|-----------------|
| OpenClassrooms | ✅ | ✅ |
| Le Wagon | ✅ | ✅ |
| Ironhack | ✅ | ✅ |
| Simplon | ✅ | ✅ |
| Coursera (certif.) | ⚠️ Partiel | ⚠️ Sur dossier |
| LinkedIn Learning | ❌ | ⚠️ |
| Udemy | ❌ | ❌ |

## FAQ

**Puis-je cumuler CPF + FIF-PL sur la même formation ?**
Oui, si les coûts dépassent un seul plafond. Attention à ne pas double-financer la même ligne.

**Puis-je former mon conjoint(e) sur mon budget ?**
Non, votre budget est nominatif.

**Une formation en présentiel est-elle mieux prise en charge que le e-learning ?**
Non, mêmes règles.

**Puis-je passer un master en formation continue ?**
Oui, mais souvent CPF seul ne suffit pas. Complément possible via prêt étudiant "conversion pro" ou financement personnel.

---

**Sources officielles 2026 :**
- Code du travail - Art. L6323-1 (CPF) : legifrance.gouv.fr
- Code du travail - Art. L6331-48 (contribution formation freelance) : legifrance.gouv.fr
- FIF-PL - Barème 2026 : fifpl.fr
- AGEFICE - Prises en charge : agefice.fr
- Portail public : moncompteformation.gouv.fr
- OPCO EP : opcoep.fr`
  },

  'freelance-ia-fiscalite-rc-droits-auteur-2026': {
    id: '57',
    title: 'Freelance et IA : fiscalité, RC pro et droits d\'auteur en 2026',
    slug: 'freelance-ia-fiscalite-rc-droits-auteur-2026',
    excerpt: 'ChatGPT, Midjourney, Claude : utiliser l\'IA en freelance en 2026. Statut fiscal des livrables IA, responsabilité, propriété intellectuelle, AI Act européen.',
    category: 'Bons plans/Conseils',
    tags: ['IA', 'ChatGPT', 'AI Act', 'Droits d\'auteur', 'RC Pro'],
    readTime: 13,
    date: '2026-04-05',
    author: 'Bozzam',
    popular: true,
    fullContent: `## L'IA dans le quotidien du freelance : opportunité et pièges

En 2026, **78% des freelances IT et créatifs utilisent au moins un outil d'IA** (ChatGPT, Midjourney, Claude, GitHub Copilot). Mais l'usage professionnel de l'IA soulève des questions fiscales, juridiques et de responsabilité que peu maîtrisent. Voici le guide complet.

## L'AI Act européen : le cadre 2026

Depuis février 2025, le **Règlement européen sur l'IA (AI Act)** classifie les systèmes IA en 4 niveaux de risque :

| Niveau | Exemples | Obligations 2026 |
|--------|---------|-----------------|
| **Inacceptable** | Social scoring, manipulation subliminale | Interdit |
| **Haut risque** | RH, éducation, justice, biométrie | Marquage CE + audit + registre |
| **Risque limité** | Chatbots, deepfakes | Transparence + information utilisateur |
| **Risque minimal** | Filtres photo, jeux | Aucune obligation |

### Obligations pour freelances utilisant l'IA (Risque limité et Haut risque)

- ✅ **Transparence** : informer le client si le livrable est généré ou assisté par IA
- ✅ **Marquage** : les contenus deepfake doivent être identifiés comme tels
- ✅ **Registre** (si IA haut risque) : traçabilité des inputs/outputs
- ✅ **Formation continue** : compétences numériques attestées
- ⚠️ **Sanctions** : jusqu'à **35 M€ ou 7% CA mondial** (usage interdit), **15 M€ ou 3% CA** (défaut de conformité)

## Le statut fiscal des livrables IA

### Question 1 : dois-je facturer différemment un livrable généré par IA ?

**Non** — la fiscalité est identique. Le CA reste imposable normalement en BIC/BNC. Aucune TVA spécifique.

### Question 2 : les abonnements IA sont-ils déductibles ?

**Oui**, ce sont des frais professionnels classiques.

| Outil | Coût mensuel 2026 | Déductibilité |
|-------|------------------|---------------|
| ChatGPT Plus / Pro | 20€ à 200€ | 100% |
| Claude Pro / Team | 20€ à 60€ | 100% |
| Midjourney Standard/Pro | 30€ à 60€ | 100% |
| GitHub Copilot | 10€ à 39€ | 100% |
| Perplexity Pro | 20€ | 100% |
| Notion AI | 8€ | 100% |
| Zapier / Make (workflows IA) | 20€ à 50€ | 100% |
| Cursor / VS Code AI | 20€ | 100% |
| **Total moyen d'un stack IA freelance** | **~150 à 400€/mois** | **100% déductible** |

### TVA sur les abonnements IA

- OpenAI (Irlande) : facture avec TVA locale → **auto-liquidation** requise
- Anthropic (USA/UK/EU) : selon entité → généralement HT + auto-liquidation
- Midjourney (USA) : HT + auto-liquidation

**Impact** : vous devez mentionner ces achats en **acquisitions intra-communautaires** ou en **prestations extra-UE** sur votre déclaration de TVA.

## La propriété intellectuelle des livrables IA

C'est **la** grande question de 2026. Voici la position juridique claire :

### Jurisprudence 2024-2026

- **US Copyright Office** (2023) : une œuvre 100% générée par IA n'est **PAS** protégeable par copyright
- **Cour de justice UE** (2024) : "l'auteur d'une œuvre est une personne physique". L'IA n'est pas auteur.
- **CPI français Art. L111-1** : "L'auteur d'une œuvre de l'esprit jouit d'un droit... personnel."

### Conséquences pour votre client

| Type de livrable | Protection PI |
|-----------------|--------------|
| Texte 100% écrit par ChatGPT | **Aucune** (domaine public de fait) |
| Texte réécrit / structuré par vous | Oui (votre "empreinte" créative) |
| Image 100% Midjourney sans retouche | **Aucune** |
| Image Midjourney + retouche Photoshop créative | Partielle (sur les retouches) |
| Code généré par Copilot | **Aucune protection sur le code brut, protection sur l'architecture originale** |

### Ce que vous devez communiquer au client

Si vous vendez un livrable "100% IA" sans retouche significative, **le client ne peut pas empêcher un concurrent de générer le même**. Cette information est **critique** à négocier au contrat.

## Clause contractuelle IA à intégrer (modèle 2026)

> *"Le prestataire déclare utiliser des outils d'intelligence artificielle pour tout ou partie des livrables. Il s'engage à :
> 1. Informer le client de l'usage significatif de l'IA
> 2. Réaliser une supervision humaine (vérification, correction, personnalisation)
> 3. Garantir l'absence de contenu illicite, discriminatoire ou hallucinatoire dans les livrables finaux
> 4. Ne pas divulguer les données confidentielles du client à des modèles publics
> 
> Le client reconnaît que la protection au titre du droit d'auteur peut être limitée sur les livrables entièrement générés par IA."*

## Le risque RGPD : ne JAMAIS envoyer de données personnelles à un LLM public

### Cas concrets à risque
- Envoyer un CV pour "reformuler" → RGPD violé
- Partager une base client Excel pour "analyser" → RGPD violé
- Coller un mail privé pour "traduire" → RGPD violé
- Uploader un contrat signé pour "résumer" → confidentialité + RGPD

### Solutions conformes en 2026
- **LLM en local** : Ollama, LM Studio, Mistral 7B → aucune donnée sortante
- **API entreprise** : OpenAI Enterprise, Claude for Business, Azure OpenAI (contrats DPA signés)
- **Anonymisation** systématique avant envoi

### Sanctions CNIL 2024-2025
- Cabinet d'avocats : 30 000€ pour usage ChatGPT public avec données clients
- Startup RH : 80 000€ pour tri de CV via GPT sans consentement

## Responsabilité civile professionnelle (RC Pro) et IA

Votre RC Pro couvre-t-elle les erreurs générées par l'IA ?

### État 2026 des assureurs
- **Hiscox, AXA Pro, MAIF Pro** : oui pour "usage IA classique" (livrable revu humainement)
- **Exclusion générale** : livrable non-revu avec "hallucination" causant préjudice
- **Nouvelle option** : **RC Pro IA renforcée** (+30% de prime, couvre hallucinations)

### Cas de responsabilité pénale
- **Deepfake sans consentement** : art. 226-8 CP (1 an + 15 000€)
- **Contenus discriminatoires générés par IA** : responsabilité du prestataire qui publie
- **Contrefaçon** : le livrable IA basé sur œuvre protégée = contrefaçon possible

## Cas pratique — Consultant freelance qui utilise ChatGPT

**Situation** : consultant marketing, CA 90 000€, utilise ChatGPT Plus (200€/mois) pour rédiger contenus.

### Fiscalité
- Abonnements IA déductibles : **2 400€/an**
- Économie d'impôt (TMI 30%) : **~720€/an**
- Gain de temps : 40% → ROI énorme

### Facturation
- Aucun changement de facturation
- Clause IA à ajouter au contrat (transparence)

### Sécurité
- Utilise **Claude for Business** pour données sensibles
- **ChatGPT Enterprise** ou API pour missions clients premium
- Chiffrement des sauvegardes

## Les 3 stratégies gagnantes freelance + IA en 2026

### Stratégie 1 — Freelance "orchestrateur IA"
Vous ne faites plus le livrable seul, vous orchestrez l'IA. Facturation identique, **temps divisé par 3-5**. Marge en hausse de 200%.

### Stratégie 2 — Freelance "AI-augmented" (super-humain)
Vous facturez au forfait avec un livrable haute qualité IA + supervision humaine. Positionnement premium.

### Stratégie 3 — Freelance "IA expert"
Vous devenez consultant en IA pour PME (implémentation, formation, gouvernance). Tarif : 800 à 1 500€/j.

## Le référentiel Qualiopi + certifications IA (2026)

Pour vous former (ou vous certifier) :
- **OpenAI Certified Prompt Engineer**
- **Google AI Fundamentals** (Coursera)
- **AWS Certified AI Practitioner**
- **Microsoft Azure AI-900**
- **Anthropic Claude Certification**

Toutes éligibles CPF/FIF-PL (voir article dédié formation).

## FAQ

**L'IA peut-elle remplacer mon expert-comptable ?**
Non. La comptabilité en France exige un professionnel inscrit à l'Ordre pour valider les comptes annuels. L'IA peut aider à catégoriser, pas à certifier.

**Puis-je vendre un livrable "100% IA" sans mentionner l'IA ?**
Techniquement oui pour l'instant, mais l'AI Act 2026 vous oblige à informer le client si l'IA est utilisée de façon significative.

**Puis-je facturer plus cher un livrable "sans IA" ?**
Oui, positionnement possible. Mais soyez transparent : "livrable 100% créé par humain, sans assistance IA".

**Les modèles IA génèrent du contenu contrefait — je risque quoi ?**
La responsabilité vous incombe (vous êtes celui qui vend le livrable). Vérifiez toujours l'originalité (Copyscape, Turnitin).

---

**Sources officielles 2026 :**
- Règlement (UE) 2024/1689 - AI Act : eur-lex.europa.eu
- CPI - Art. L111-1 (droit d'auteur) : legifrance.gouv.fr
- RGPD - Règlement (UE) 2016/679 : cnil.fr
- CNIL - IA et RGPD (recommandations 2024) : cnil.fr
- Code pénal - Art. 226-8 (deepfake) : legifrance.gouv.fr
- US Copyright Office - Guidance on AI-generated works (2023)`
  },

  'immobilier-sasu-eurl-vs-sci-vs-holding-sci-2026': {
    id: '58',
    title: 'Acheter un bien immobilier : SASU/EURL directe vs SCI vs holding+SCI en 2026',
    slug: 'immobilier-sasu-eurl-vs-sci-vs-holding-sci-2026',
    excerpt: 'Le comparatif chiffré des 3 stratégies pour acheter un bien immobilier en tant que freelance : achat direct par la société, SCI patrimoniale, ou holding + SCI.',
    category: 'Optimisation',
    tags: ['Immobilier', 'SCI', 'Holding', 'SASU', 'EURL', 'Comparatif'],
    readTime: 16,
    date: '2026-04-24',
    author: 'Bozzam',
    popular: true,
    fullContent: `## L'immobilier via société : 3 stratégies, un seul gagnant

Vous êtes freelance en SASU ou EURL et vous voulez acheter un bien immobilier (bureau, local, résidence secondaire, locatif) avec l'argent de votre société ? Trois structures sont possibles. Chacune a un impact fiscal et patrimonial radicalement différent.

## Les 3 stratégies en un coup d'œil

| Stratégie | Structure | Complexité | Coût création |
|-----------|-----------|-----------|---------------|
| **1. Achat direct SASU/EURL** | Bien inscrit à l'actif de la société d'exploitation | Simple | 0€ |
| **2. SCI seule** | SCI propriétaire, loue au dirigeant ou tiers | Moyenne | ~1 500€ |
| **3. Holding + SCI** | Holding détient SCI, qui détient l'immobilier | Complexe | ~3 500€ |

## Cas de référence : achat d'un local pro à 400 000€

Reprenons ce scénario pour comparer les 3 stratégies :
- Prix du bien : 400 000€
- Apport : 80 000€ (20%)
- Crédit sur 20 ans à 4,2% : 320 000€
- Loyer prévisionnel : 2 400€/mois si loué à votre SASU
- Revente estimée dans 15 ans : 500 000€ (plus-value 100 000€)

## Stratégie 1 — Achat direct par la SASU/EURL

### Fonctionnement
La SASU/EURL achète le bien avec sa trésorerie + un crédit à son nom. Le bien est inscrit à l'actif du bilan.

### Avantages
- ✅ **Simplicité** : pas de structure supplémentaire
- ✅ **Amortissement du bien** : ~2,5%/an du prix hors terrain → 8 000€/an de charge fiscale
- ✅ **Frais professionnels** : entretien, taxes, intérêts d'emprunt déductibles

### Inconvénients (majeurs)
- ❌ **Confusion actifs pro / patrimoine** : si votre activité fait faillite, vous perdez aussi l'immobilier
- ❌ **Plus-value professionnelle à la revente** : imposée à l'IS (25%) PLUS flat tax sur dividendes (30%) = **~47,5% total**
- ❌ **Amortissements récupérés à la revente** : intégralement réintégrés dans le résultat imposable
- ❌ **Vente = distribution en dividendes** : soumise à flat tax pour sortir l'argent

### Fiscalité à la revente (bien 400k€ → 500k€ après 15 ans)

| Poste | Montant |
|-------|---------|
| Prix de vente | 500 000€ |
| Valeur nette comptable (400k - amort. 120k) | 280 000€ |
| Plus-value professionnelle | 220 000€ |
| IS (25%) | -55 000€ |
| Réserves distribuables | 165 000€ |
| Flat tax dividendes (30%) | -49 500€ |
| **Net perçu par le dirigeant** | **115 500€** |

**Fiscalité totale : 47,5% de la plus-value**. Très pénalisant.

## Stratégie 2 — SCI seule (avec ou sans société d'exploitation)

### Fonctionnement
Vous créez une SCI dont vous êtes associé (99%) + votre conjoint ou associé (1%). La SCI achète le bien avec un crédit à son nom. La SCI loue le bien à votre SASU/EURL (bail commercial ou professionnel).

### Choix crucial : IR ou IS ?
Voir article dédié. Pour un freelance qui a une **activité pro dans le local** : SCI à l'IS quasi obligatoire (sinon revenus fonciers réintégrés à votre IR + prélèvements sociaux 47,2%).

### Avantages
- ✅ **Isolation patrimoniale** : la SASU peut couler, la SCI garde le bien
- ✅ **Loyer déductible** pour la SASU (économie IS ~600€/mois soit 7 200€/an)
- ✅ **Plus-value à la revente** : régime plus-value immobilière particulier (SCI IR) ou IS + amortissement (SCI IS)
- ✅ **Transmission facilitée** : donation de parts sociales possible tous les 15 ans (100k€/parent/enfant)

### Inconvénients
- ⚠️ **Financement bancaire** : la banque peut être méfiante si SCI toute jeune. Solution : caution personnelle du dirigeant
- ⚠️ **Comptabilité SCI** obligatoire (500 à 1 500€/an)
- ⚠️ **Nécessite un tiers associé** (1% minimum)

### Fiscalité à la revente (SCI à l'IS)

| Poste | Montant |
|-------|---------|
| Prix de vente | 500 000€ |
| VNC (400k - amort. 120k) | 280 000€ |
| Plus-value | 220 000€ |
| IS 25% (SCI à l'IS) | -55 000€ |
| Résultat disponible en SCI | 165 000€ |
| Flat tax dividendes SCI | -49 500€ |
| **Net perçu par l'associé** | **115 500€** |

**Fiscalité totale : 47,5%** (identique à SASU directe pour la revente, mais avec **isolation patrimoniale** et **loyer déductible pendant 15 ans** = économie de ~54 000€ d'IS pendant l'exploitation).

**Avantage net Stratégie 2 sur Stratégie 1 = ~54 000€ sur 15 ans**.

## Stratégie 3 — Holding + SCI (le montage optimal)

### Fonctionnement
1. Vous créez une **holding** (SASU ou SARL)
2. La holding détient la **SASU/EURL d'exploitation** (activité freelance)
3. La holding détient aussi la **SCI** qui achète l'immobilier
4. Loyer SCI → SASU ⇐ prestations
5. Bénéfices SASU → Holding (régime mère-fille = **95% exonération**)
6. Dividendes SCI → Holding (régime mère-fille aussi)
7. La holding réinvestit ou distribue au dirigeant

### Structure visuelle

Vous → Holding → détient à la fois → SASU (exploitation) et SCI (immobilier).
Loyer versé par SASU à SCI. Dividendes SCI et SASU remontent vers Holding.

### Avantages massifs
- ✅ **Régime mère-fille** : dividendes de la SCI vers la holding exonérés à 95% (soit **~1,25% d'IS réel**)
- ✅ **Réinvestissement facile** dans une autre SCI ou activité via la holding (sans flat tax)
- ✅ **Financement bancaire amélioré** : 3 bilans (holding + SASU + SCI) → capacité d'emprunt supérieure
- ✅ **Transmission Dutreil** possible sur les titres holding (75% d'abattement)
- ✅ **Apport-cession 150-0 B ter** : report d'imposition si cession
- ✅ **Multiplication des SCI** : ajouter une SCI locative, une SCI résidence secondaire, etc.

### Inconvénients
- ❌ **Complexité** : 3 sociétés à gérer, 3 comptabilités
- ❌ **Coûts** : ~3 500€ à la création + ~3 000 à 5 000€/an de comptabilité récurrente
- ❌ **Seuil de rentabilité** : à partir de ~60 000€ de dividendes/an, ou projet immobilier ≥ 300 000€

### Fiscalité à la revente (Holding + SCI IS)

| Poste | Montant |
|-------|---------|
| Prix de vente immobilier | 500 000€ |
| Plus-value SCI (après amort.) | 220 000€ |
| IS SCI 25% | -55 000€ |
| Résultat SCI distribuable | 165 000€ |
| Remontée vers holding (mère-fille 95% exo) | 165 000€ - (5% × 25%) = -2 063€ |
| **Net dans la holding** | **~162 937€** |

**Fiscalité totale : ~26% de la plus-value** (uniquement l'IS SCI + petite quote-part), l'argent reste dans la holding pour réinvestir.

**Vs Stratégie 1 (SASU directe)** : gain de **~47 500€** à la seule revente + économies annuelles d'IS via loyer déductible.

## Le match complet sur 15 ans

| Critère | 1. SASU directe | 2. SCI seule | 3. Holding + SCI |
|---------|----------------|--------------|------------------|
| Coût création | 0€ | 1 500€ | 3 500€ |
| Coût récurrent/an | 0€ | 1 000€ | 3 500€ |
| Loyer déductible SASU/15 ans | Non | +54 000€ | +54 000€ |
| Isolation patrimoniale | ❌ Non | ✅ Oui | ✅ Oui |
| Fiscalité plus-value | 47,5% | 47,5% (IS + flat tax) | **~26%** (mère-fille) |
| Transmission Dutreil | Non | Partielle | ✅ Optimal |
| Multiplication de biens | Difficile | Moyenne | ✅ Facile |
| **Bilan net 15 ans (revente 500k€)** | **~62 000€** | **~116 000€** | **~205 000€** |

**Gagnant net : Stratégie 3 (Holding + SCI)**, avec un gain de **~143 000€** vs Stratégie 1 sur 15 ans.

## Le seuil de rentabilité de chaque stratégie

| Situation | Stratégie recommandée |
|-----------|----------------------|
| Bien < 100 000€, freelance débutant | Achat perso ou SCI simple |
| Bien 100 à 300 000€, freelance stable | SCI à l'IS |
| Bien > 300 000€, activité mature | Holding + SCI |
| Multi-biens (locatif + pro) | Holding + plusieurs SCI |
| Anticipation transmission | Holding + SCI + Dutreil |

## Les erreurs classiques

1. **Achat direct en SASU pour "faire simple"** : vous perdez 30% du gain à la revente
2. **SCI à l'IR quand on est freelance à TMI 41%** : revenus fonciers = IR + prélèvements sociaux (47,2%) → catastrophe
3. **Ne pas anticiper la transmission** : sans holding + Dutreil, vos héritiers paient 30-40% de droits
4. **Sous-évaluer le coût comptable** : la comptabilité SCI + holding = ~5 000€/an récurrents
5. **Absence de bail commercial** entre SCI et SASU : requalification fiscale possible

## FAQ

**Peut-on créer une SCI unipersonnelle ?**
Non. Une SCI doit avoir minimum 2 associés (le conjoint, un ami, un parent).

**Que se passe-t-il en cas de divorce pour la SCI en couple ?**
Selon le régime matrimonial. Voir notre article dédié SCI en couple.

**Peut-on faire un montage holding + SCI en cours de route ?**
Oui, via un apport de parts SCI à la holding (ou vente). Prévoir 1 500 à 3 500€ de frais + délai 3 mois.

**Est-ce que la banque prête plus facilement à une SCI qu'à une SASU ?**
En général oui, car la SCI est spécifiquement dédiée à l'immobilier et rassure la banque (activité claire, garanties possibles).

---

**Sources officielles 2026 :**
- CGI - Art. 145 (régime mère-fille) : legifrance.gouv.fr
- CGI - Art. 150 U et 150 V (plus-values immobilières) : legifrance.gouv.fr
- CGI - Art. 219 (IS) : legifrance.gouv.fr
- CGI - Art. 787 B (Dutreil transmission) : legifrance.gouv.fr
- Code civil - Art. 1832 à 1873 (sociétés civiles) : legifrance.gouv.fr
- BOFiP - BOI-BIC-AMT-20-40 (amortissement immobilier)`
  },

  'sci-ir-vs-sci-is-freelance-2026': {
    id: '59',
    title: 'SCI à l\'IR vs SCI à l\'IS pour freelance : le match 2026',
    slug: 'sci-ir-vs-sci-is-freelance-2026',
    excerpt: 'Louer, revendre, transmettre : quel régime fiscal choisir pour votre SCI en tant que freelance ? Le comparatif complet des impacts en 2026.',
    category: 'Optimisation',
    tags: ['SCI', 'IR', 'IS', 'Immobilier', 'Fiscalité'],
    readTime: 13,
    date: '2026-04-23',
    author: 'Bozzam',
    popular: true,
    fullContent: `## SCI à l'IR ou SCI à l'IS : le choix décisif

Une SCI est par défaut à l'**IR** (revenus fonciers), mais peut opter pour l'**IS**. Ce choix, souvent fait à la légère, détermine votre fiscalité pour les 20 ans à venir. Voici le match complet en 2026.

## Les différences fondamentales

| Critère | SCI à l'IR | SCI à l'IS |
|---------|-----------|-----------|
| Imposition | Chez les associés (revenus fonciers) | Chez la SCI (IS 15%/25%) |
| Taux d'imposition | Barème IR + PS 17,2% | 15% jusqu'à 42 500€, puis 25% |
| Amortissement du bien | ❌ Non | ✅ Oui (2 à 4%/an) |
| Déficit foncier | Reportable 10 ans (sur revenus fonciers) | Reportable dans la SCI |
| Location meublée | ❌ Impossible (perte SCI IR) | ✅ Possible |
| Plus-value à la revente | Régime particulier avec abattements durée | Plus-value pro (fort taux) |
| Dividendes | N/A (transparence fiscale) | Flat tax 30% |

## Simulation 1 — Location nue à 24 000€/an de loyer

### SCI à l'IR (freelance TMI 30%)

| Poste | Montant |
|-------|---------|
| Loyer brut | 24 000€ |
| Charges déductibles (taxe foncière, entretien) | -3 000€ |
| Intérêts d'emprunt | -8 000€ |
| **Revenu foncier imposable** | **13 000€** |
| IR (TMI 30%) | -3 900€ |
| Prélèvements sociaux (17,2%) | -2 236€ |
| **Net perçu** | **~6 864€/an** |

### SCI à l'IS

| Poste | Montant |
|-------|---------|
| Loyer brut | 24 000€ |
| Charges + intérêts + amortissement bien (2,5% de 300k) | -18 500€ |
| **Résultat imposable IS** | **5 500€** |
| IS 15% | -825€ |
| Résultat après IS | 4 675€ |
| Si distribution : flat tax 30% | -1 403€ |
| **Net perçu (si distribution)** | **~3 272€/an** |
| **Ou capitalisé dans la SCI** | **4 675€** |

**Constat** : à court terme, la SCI à l'IR verse plus de cash au propriétaire. Mais elle taxe plus fort.

## Simulation 2 — Revente après 15 ans (bien acheté 300k€, revendu 400k€)

### SCI à l'IR — régime plus-value immobilière particulier

Abattements pour durée de détention (art. 150 VC du CGI) :

| Durée | Abattement IR | Abattement PS |
|-------|--------------|---------------|
| 0-5 ans | 0% | 0% |
| 6-21 ans | 6%/an | 1,65%/an |
| 22 ans | 4% | 1,60% |
| 23-30 ans | Exonération totale IR | 9%/an |
| 30 ans | Exonération totale | Exonération totale |

**Après 15 ans (10 ans après la 5e année)** :
- Abattement IR : 60% → taxable sur 40k€
- Abattement PS : 16,5% → taxable sur 83k€

| Poste | Montant |
|-------|---------|
| Plus-value brute | 100 000€ |
| IR après abattement (40k × 19%) | -7 600€ |
| PS après abattement (83k × 17,2%) | -14 276€ |
| **Net après revente** | **~78 124€** |

### SCI à l'IS — plus-value professionnelle

| Poste | Montant |
|-------|---------|
| Prix de vente | 400 000€ |
| Valeur nette comptable (300k - amortiss. cumulés 100k) | 200 000€ |
| Plus-value comptable | 200 000€ |
| IS 25% | -50 000€ |
| Résultat SCI disponible | 150 000€ |
| Flat tax si distribution | -45 000€ |
| **Net perçu** | **~105 000€** |

**Après 15 ans : la SCI à l'IS gagne 27 000€** grâce à l'amortissement, malgré une fiscalité plus lourde à la revente.

## L'irréversibilité de l'option IS

**⚠️ Danger majeur** : une SCI à l'IR peut opter pour l'IS, mais **c'est irréversible depuis 2019** (art. 239-2 CGI). Vous ne pouvez plus revenir à l'IR.

**Depuis 2019**, une SCI à l'IR peut opter pour l'IS pendant les **5 premières années** d'existence, mais avec un droit de renonciation possible avant la fin de la 5ème année (revenir à l'IR).

## Les 6 cas d'usage typiques

### Cas 1 — Freelance qui loue à sa société : IS obligatoire
Si votre SCI loue à votre SASU/EURL, le régime IR entraîne les revenus fonciers dans votre déclaration → imposition IR + prélèvements sociaux (47,2% pour TMI 30%). Choisissez l'IS.

### Cas 2 — Location nue à des tiers, TMI < 30% : IR possible
Si vous êtes en TMI 11% ou 30%, l'IR peut être avantageux (surtout avec des travaux qui créent du déficit foncier).

### Cas 3 — Location meublée (LMNP/LMP) : IS obligatoire
Une SCI à l'IR ne peut pas faire de location meublée (art. 34 CGI). Passer en IS ou créer une autre structure (SARL de famille).

### Cas 4 — Objectif transmission : IR privilégié
Le régime IR + abattements progressifs permet de vendre après 22 ans sans quasiment aucune plus-value taxable. Mieux pour l'héritage.

### Cas 5 — Objectif capitalisation long terme : IS
L'IS avec amortissement = moins d'impôt annuel + réinvestissement possible dans la SCI (nouveaux biens).

### Cas 6 — Freelance avec holding : IS obligatoire
Si votre holding détient la SCI, elle doit être à l'IS pour bénéficier du régime mère-fille sur les dividendes.

## Tableau de décision

| Votre profil | Régime SCI |
|--------------|-----------|
| Freelance qui loue à sa SASU/EURL | ✅ IS |
| Freelance avec holding | ✅ IS |
| Investisseur locatif nu, TMI 11-30% | ⚠️ IR (souvent) |
| Investisseur locatif meublé | ✅ IS |
| Objectif transmission après 22 ans | ⚠️ IR |
| Multi-biens et croissance | ✅ IS |

## Erreurs classiques

1. **Choisir IR par défaut sans réfléchir** : perte fiscale sur 15-20 ans
2. **Faire de la location meublée en SCI IR** : requalification et pénalités
3. **Opter pour l'IS après 5 ans** : impossible depuis 2019
4. **Ne pas amortir le bien en SCI IS** : perte massive de charge fiscale (~10 000€/an sur un bien 300k€)
5. **Confondre plus-value particulière (IR) et pro (IS)** : régimes totalement différents

## FAQ

**Une SCI à l'IR est-elle obligée d'établir des comptes annuels ?**
Non, comptabilité simplifiée (recettes/dépenses). C'est un avantage de l'IR.

**Puis-je créer 2 SCI : une IR et une IS ?**
Oui, c'est même recommandé pour diversifier les stratégies.

**Que se passe-t-il si ma SCI IR fait de la location meublée par erreur ?**
Elle est requalifiée en société commerciale à l'IS **rétroactivement**, avec pénalités.

**Le passage de l'IR à l'IS entraîne-t-il des taxes ?**
Oui, généralement une plus-value latente est calculée et taxée (art. 202 ter CGI), sauf option de report.

---

**Sources officielles 2026 :**
- CGI - Art. 8, 34, 206 (SCI IR/IS) : legifrance.gouv.fr
- CGI - Art. 239-2 (option IS irrévocable) : legifrance.gouv.fr
- CGI - Art. 150 U, 150 VC (plus-values immo IR) : legifrance.gouv.fr
- CGI - Art. 219 (IS) : legifrance.gouv.fr
- BOFiP - BOI-IS-CHAMP-40 (option IS des SCI)`
  },

  'lmnp-lmp-freelance-cumul-2026': {
    id: '60',
    title: 'LMNP/LMP en tant que freelance : cumul optimal en 2026',
    slug: 'lmnp-lmp-freelance-cumul-2026',
    excerpt: 'Cumuler votre activité freelance avec un statut de loueur meublé (LMNP ou LMP) : plafonds, amortissements, exonérations, calculs 2026.',
    category: 'Optimisation',
    tags: ['LMNP', 'LMP', 'Immobilier', 'Location meublée', 'Cumul'],
    readTime: 13,
    date: '2026-04-22',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Louer en meublé en plus d'être freelance : la bonne équation ?

Le statut de **Loueur en Meublé Non Professionnel (LMNP)** ou **Professionnel (LMP)** permet à un freelance de générer des revenus locatifs en meublé tout en cumulant l'amortissement du bien. Voici comment ça marche en 2026.

## LMNP vs LMP : la différence en un tableau

| Critère | LMNP | LMP |
|---------|------|-----|
| Recettes annuelles | < 23 000€ ET < 50% des revenus foyer | Sinon LMP |
| Régime social | Aucune cotisation URSSAF si non-pro | Cotisations SSI (~35% du bénéfice) |
| Régime fiscal | BIC (micro-BIC ou réel) | BIC (obligatoire régime réel) |
| Déficit imputable | Sur revenus BIC seulement | Sur revenu global (illimité) |
| Plus-value à la revente | Régime particulier (abattement durée) | Plus-value professionnelle |
| Statut | Non professionnel | Professionnel |

## Le régime fiscal LMNP en 2026

### Régime micro-BIC (par défaut)

- Recettes < **77 700€** en 2026
- Abattement forfaitaire de **50%** (71% pour meublé de tourisme classé)
- Pas de comptabilité, pas de charges déductibles
- Imposé au barème IR + prélèvements sociaux 17,2%

### Régime réel simplifié

- Applicable dès le 1er euro sur option
- Charges déductibles : intérêts, taxe foncière, entretien, syndic, assurance
- **AMORTISSEMENT du bien immobilier** (2,5-3%/an) + du mobilier (10-20%/an)
- Excellent pour ramener le résultat à 0€ voire créer un déficit

## Simulation LMNP réel — location meublée à 1 200€/mois

### Hypothèses
- Bien acheté 200 000€ (dont 20 000€ terrain)
- Loyer annuel : 14 400€
- Charges annuelles (taxe, syndic, entretien) : 3 500€
- Intérêts d'emprunt : 5 000€
- Amortissement bien (2,5% sur 180 000€) : 4 500€
- Amortissement mobilier (10% sur 15 000€) : 1 500€

### Résultat fiscal LMNP réel

| Poste | Montant |
|-------|---------|
| Recettes locatives | 14 400€ |
| Charges déductibles | -3 500€ |
| Intérêts | -5 000€ |
| Amortissement bien | -4 500€ |
| Amortissement mobilier | -1 500€ |
| **Résultat imposable** | **-100€ (déficit)** |
| IR + prélèvements sociaux | **0€** |

**Bilan** : vous encaissez 14 400€ de loyer, vous ne payez **quasiment rien d'impôt**. Le déficit est reportable sur les revenus BIC des 10 années suivantes.

### Vs micro-BIC (par défaut)
- Abattement 50% sur 14 400€ = 7 200€ imposables
- IR (TMI 30%) : 2 160€
- PS 17,2% : 1 238€
- **Total impôt** : 3 398€

**Économie du régime réel : ~3 400€/an**.

## Le statut LMP : pour qui ?

Le statut LMP est déclenché **automatiquement** si :

1. Recettes annuelles > 23 000€
2. **ET** recettes > 50% des revenus totaux du foyer

**Exemple** : freelance CA 40 000€ + LMP 25 000€. Total revenus = 65 000€. LMP représente 38% < 50% → reste LMNP.

**Autre exemple** : freelance CA 20 000€ + LMP 25 000€. Total = 45 000€. LMP représente 55% > 50% → **bascule automatique en LMP**.

## Avantages du LMP

- ✅ **Déficit imputable sur le revenu global** (illimité vs 10 700€/an en foncier)
- ✅ **Exonération plus-value** après 5 ans + recettes < 90 000€ (art. 151 septies)
- ✅ **Exonération IFI** (bien professionnel)
- ✅ **Régime PACTE Dutreil** possible sur transmission

## Inconvénients du LMP

- ❌ **Cotisations SSI** (~35% du bénéfice net)
- ❌ **Complexité** : comptabilité obligatoire, expert-comptable recommandé (1 500 à 3 000€/an)
- ❌ **Non-affiliation à Pôle Emploi** en cas de cessation
- ❌ **Impact sur la retraite** : cotisations basses = retraite faible

## Cumul LMNP + Freelance SASU : le combo gagnant

**Scénario type** : freelance IT en SASU, cherche à investir sa trésorerie en LMNP.

### Étape 1 — Sortir de la trésorerie de la SASU
Option A : Dividendes → flat tax 30% (30 000€ sortis = 21 000€ nets)
Option B : Rémunération → charges + IR (30 000€ = ~17 000€ nets)
Option C : Compte courant d'associé → 0€ d'impôt (voir article dédié)

### Étape 2 — Utiliser les fonds pour un LMNP
Apport 40 000€ + emprunt 160 000€ pour un bien à 200 000€.

### Étape 3 — Loyer LMNP quasi-défiscalisé
Grâce à l'amortissement, résultat proche de 0 pendant 15-20 ans.

### Bilan sur 15 ans

| Poste | Montant |
|-------|---------|
| Cash-flow LMNP net annuel | ~4 000€ |
| Cumul 15 ans | 60 000€ |
| Impôt total payé sur 15 ans | ~0€ |
| Plus-value revente (200k → 280k) | 80 000€ |
| Impôt plus-value (après 15 ans abattement) | ~15 000€ |
| **Net final** | **~125 000€ + capital remboursé** |

## Les meublés de tourisme (Airbnb, Booking)

### Régime spécial 2026
- Abattement micro-BIC : **50%** (meublé classique), **71%** (meublé classé)
- Attention **Loi Le Meur 2024** : nouvelles restrictions en zones tendues (Paris, Lyon, Marseille)

### Cotisations sociales
- Si recettes < 23 000€ : aucune cotisation
- Si > 23 000€ : cotisation SSI ou régime général selon activité principale

## Erreurs classiques

1. **Ne pas déclarer le meublé** : redressement + pénalités
2. **Choisir le régime micro-BIC quand le réel est plus avantageux** : perte fiscale de 2 000-5 000€/an
3. **Location meublée en SCI IR** : requalification en société commerciale
4. **Oublier l'amortissement du mobilier** : perte annuelle
5. **Sous-estimer le passage automatique en LMP** : cotisations sociales imprévues

## Checklist LMNP freelance

- [ ] Numéro SIRET auprès du greffe (formulaire P0i, gratuit)
- [ ] Adhésion à un CGA (Centre de Gestion Agréé) pour éviter majoration 25% du résultat
- [ ] Comptabilité en règle (expert-comptable)
- [ ] Amortissement du bien calculé correctement
- [ ] Inventaire du mobilier (indispensable pour le régime meublé)
- [ ] Bail meublé conforme (durée 1 an min, 9 mois pour étudiants)
- [ ] Assurance PNO (propriétaire non occupant)

---

**Sources officielles 2026 :**
- CGI - Art. 155 IV (statut LMP) : legifrance.gouv.fr
- CGI - Art. 50-0 (micro-BIC) : legifrance.gouv.fr
- CGI - Art. 151 septies (exonération plus-value LMP) : legifrance.gouv.fr
- BOFiP - BOI-BIC-CHAMP-40 (locations meublées) : bofip.impots.gouv.fr
- Loi Le Meur 2024 (meublés de tourisme) : legifrance.gouv.fr`
  },

  'demembrement-propriete-dirigeant-2026': {
    id: '61',
    title: 'Démembrement de propriété pour dirigeant : usufruit et nue-propriété en 2026',
    slug: 'demembrement-propriete-dirigeant-2026',
    excerpt: 'Séparer usufruit et nue-propriété pour transmettre en optimisant : mécanismes, barème fiscal 2026, cas concrets pour freelance et dirigeant.',
    category: 'Optimisation',
    tags: ['Démembrement', 'Usufruit', 'Nue-propriété', 'Transmission', 'Immobilier'],
    readTime: 12,
    date: '2026-04-21',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Démembrement : diviser la propriété pour mieux régner (fiscalement)

Le **démembrement de propriété** consiste à séparer un bien en deux droits : l'**usufruit** (jouir du bien et percevoir les revenus) et la **nue-propriété** (posséder le bien mais sans usage). C'est **le levier n°1 de transmission** patrimoniale pour dirigeants et freelances.

## La distinction usufruit / nue-propriété

| Droit | Ce qui appartient |
|-------|------------------|
| **Usufruit** | Utiliser le bien (habiter, louer, encaisser les loyers, dividendes) |
| **Nue-propriété** | Devenir propriétaire pleinement à l'extinction de l'usufruit (décès ou fin du terme) |

## Le barème fiscal 2026 (art. 669 CGI)

Le barème détermine la répartition fiscale de la valeur du bien :

| Âge de l'usufruitier | Valeur usufruit | Valeur nue-propriété |
|---------------------|-----------------|---------------------|
| Moins de 21 ans | 90% | 10% |
| 21 à 30 ans | 80% | 20% |
| 31 à 40 ans | 70% | 30% |
| 41 à 50 ans | 60% | 40% |
| 51 à 60 ans | 50% | 50% |
| 61 à 70 ans | 40% | 60% |
| 71 à 80 ans | 30% | 70% |
| 81 à 90 ans | 20% | 80% |
| Plus de 91 ans | 10% | 90% |

## Cas 1 — Transmettre à ses enfants avec économie d'impôts

### Situation
- Vous avez 55 ans
- Bien immobilier valorisé 500 000€
- 2 enfants majeurs à qui vous voulez le transmettre

### Sans démembrement (donation pleine propriété)
- Donation 250 000€/enfant
- Abattement 100 000€/enfant
- Base taxable : 150 000€/enfant
- Droits : ~28 200€/enfant → **56 400€ au total**

### Avec démembrement (donation de nue-propriété uniquement)
- Valeur nue-propriété à 55 ans : 50% du bien = 250 000€
- Donation 125 000€/enfant
- Abattement 100 000€/enfant
- Base taxable : 25 000€/enfant
- Droits : ~4 000€/enfant → **8 000€ au total**

**Économie immédiate : 48 400€**.

### Cerise sur le gâteau
À votre décès, l'usufruit s'éteint automatiquement. Vos enfants deviennent **pleins propriétaires SANS aucun impôt supplémentaire**. La transmission finale se fait "gratuitement".

## Cas 2 — Optimiser l'IS d'une holding via démembrement

Vous voulez qu'une **holding possède l'usufruit** d'un bien immobilier (pour percevoir les loyers déductibles), et vous-même personnellement la nue-propriété.

### Avantages
- La holding déduit les loyers du bien de son résultat IS
- Vous récupérez le bien en pleine propriété à la fin du démembrement (généralement 10-20 ans)
- Aucune fiscalité à l'extinction de l'usufruit (art. 1133 CGI)

### Exemple chiffré
- Bien 500 000€, démembrement temporaire de 15 ans
- Valeur usufruit temporaire : 23% du bien × 15 ans = ~345 000€
- Holding achète l'usufruit pour 345 000€ (déductible fiscalement sur 15 ans)
- Vous achetez la nue-propriété pour 155 000€ (pas d'IR car achat, pas donation)

**Impact fiscal** : la holding amortit 345 000€ sur 15 ans (~23 000€/an de charge). Économie IS : ~5 750€/an × 15 = **86 250€**.

## Cas 3 — Démembrement croisé entre conjoints

Utilisé pour protéger le conjoint survivant en cas de décès.

- Vous achetez ensemble un bien de 400 000€
- Chacun apporte 50%
- **Vous** : nue-propriété sur la moitié de votre conjoint
- **Conjoint** : nue-propriété sur votre moitié

**Résultat au premier décès** : chacun récupère l'usufruit du bien du défunt sans aucune fiscalité de succession (car il a déjà la nue-propriété).

## Cas 4 — Démembrement de parts de SCPI

Vous achetez pour 100 000€ de SCPI Pierval Santé, mais uniquement la **nue-propriété** :
- Prix nue-propriété (démembrement 10 ans, taux 30%) : **70 000€**
- Vous n'encaissez pas les dividendes pendant 10 ans
- Après 10 ans, vous récupérez la pleine propriété

**Avantages** :
- Achat à -30% par rapport à la pleine propriété
- Pas de fiscalité pendant le démembrement (pas de revenus fonciers imposables)
- Idéal pour préparer la retraite (revenus qui démarrent au moment où on en aura besoin)

## Les 4 pièges du démembrement

### Piège 1 — Loi Charasse (dividendes majorés en démembrement)
Si vous achetez la nue-propriété de parts sociales dans une société IS via emprunt, les dividendes issus des réserves antérieures peuvent être requalifiés.

### Piège 2 — Usufruit temporaire long
Un usufruit > 30 ans est requalifié en pleine propriété fiscalement.

### Piège 3 — Absence d'acte notarié
Le démembrement d'immobilier **doit être formalisé** par acte notarié (~1% du prix).

### Piège 4 — Confusion avec les droits d'usage
Le "droit d'usage et d'habitation" n'est PAS un usufruit. Il ne permet pas de louer.

## Les frais du démembrement

| Poste | Coût |
|-------|------|
| Acte notarié (donation démembrement) | 1 à 2% du prix + émoluments |
| Frais fiscaux (droits de donation) | Selon le lien de parenté |
| Rédaction fiscale et juridique | 500 à 2 000€ HT |
| Évaluation du bien | 500 à 2 000€ |

## Cas particulier : quasi-usufruit

Le **quasi-usufruit** s'applique aux biens consomptibles (argent, titres). L'usufruitier peut disposer du bien, mais doit restituer une valeur équivalente en fin d'usufruit.

**Exemple** : votre parent vous donne 500 000€ en usufruit à vie. À son décès, ses héritiers (dont vous) ont une **créance de restitution de 500 000€** sur l'héritage.

**Attention** : art. 774 bis CGI (loi de finances 2024) — le quasi-usufruit sur somme d'argent donnée est désormais soumis à droits de succession lors de la restitution.

## Simulation complète : transmettre 1M€ à 2 enfants (à 60 ans)

### Sans démembrement (donation directe pleine propriété)
- Base taxable/enfant : 500 000€ - 100 000€ = 400 000€
- Droits : ~78 000€/enfant → **156 000€**

### Avec démembrement (donation nue-propriété)
- Valeur nue-propriété à 60 ans : 60%
- Base transmise : 300 000€/enfant
- Abattement : 100 000€
- Base taxable : 200 000€/enfant
- Droits : ~38 000€/enfant → **76 000€**

**Économie : 80 000€**. Et à votre décès, transfert de l'usufruit **sans impôt**.

## FAQ

**Puis-je donner l'usufruit à mes enfants et garder la nue-propriété ?**
Techniquement oui, mais fiscalement peu avantageux. Le schéma classique = enfants nu-propriétaires, parents usufruitiers.

**Comment se termine un démembrement ?**
- Usufruit viager : au décès de l'usufruitier
- Usufruit temporaire : à l'échéance prévue
- Usufruit sur société : selon les statuts

**Puis-je vendre un bien démembré ?**
Nu-propriétaire et usufruitier doivent être d'accord. Vente simultanée des 2 droits, ou séparément (rare).

**Le démembrement est-il possible sur des parts de SASU/EURL ?**
Oui, mais complexe. Nécessite un avocat fiscaliste (2 000 à 5 000€).

---

**Sources officielles 2026 :**
- CGI - Art. 669 (barème usufruit) : legifrance.gouv.fr
- CGI - Art. 1133 (extinction sans impôt) : legifrance.gouv.fr
- CGI - Art. 774 bis (quasi-usufruit LF 2024) : legifrance.gouv.fr
- Code civil - Art. 578 à 624 (usufruit) : legifrance.gouv.fr`
  },

  'sci-couple-regime-matrimonial-2026': {
    id: '62',
    title: 'Créer une SCI en couple : régime matrimonial et transmission en 2026',
    slug: 'sci-couple-regime-matrimonial-2026',
    excerpt: 'Marié, pacsé ou concubin : comment structurer sa SCI en couple pour protéger le patrimoine, optimiser la fiscalité et anticiper la transmission.',
    category: 'Optimisation',
    tags: ['SCI', 'Couple', 'Régime matrimonial', 'Transmission', 'Immobilier'],
    readTime: 11,
    date: '2026-04-20',
    author: 'Bozzam',
    popular: true,
    fullContent: `## SCI en couple : le bon montage sauve 30-100% des droits de succession

Un couple qui achète un bien immobilier a **quatre options** de structure : indivision, SCI, tontine, achat via société d'exploitation. La SCI est **presque toujours la meilleure** pour les couples entrepreneurs. Voici pourquoi et comment la structurer.

## Les 3 régimes matrimoniaux en 2026

| Régime | Par défaut | Bien acquis pendant le mariage |
|--------|-----------|-------------------------------|
| **Communauté légale** (mariage sans contrat) | Oui | Bien commun (partagé 50/50) |
| **Séparation de biens** (contrat) | Non | Bien propre à l'acquéreur |
| **Communauté universelle** (rare) | Non | Tous les biens communs |
| **PACS** (par défaut = séparation) | Séparation | Bien propre à l'acquéreur |
| **Concubinage** | Aucun cadre | Bien propre à l'acquéreur |

### Impact sur la SCI
- **Communauté légale** : les parts SCI acquises pendant le mariage sont communes
- **Séparation** : parts SCI restent au nom du seul acquéreur
- **PACS/Concubinage** : parts SCI = bien propre uniquement

## Répartition des parts sociales dans la SCI en couple

### Modèle classique (protection équilibrée)
- 50/50 entre les deux conjoints
- Objet : détenir de l'immobilier (locatif ou pro)

### Modèle "dirigeant + conjoint" (freelance)
- 99% dirigeant / 1% conjoint (pour minimum 2 associés obligatoire)
- Avantage : contrôle total au dirigeant
- Inconvénient : peu de protection du conjoint

### Modèle "démembrement croisé"
- 50% pleine propriété × 2
- OU 50% usufruit/nue-propriété croisée pour protection en cas de décès

## Cas 1 — Couple marié en communauté légale, achat via SCI

### Structure recommandée
- SCI à l'IS (si loyers > IR barème progressif) ou IR (si loyers occasionnels)
- 50% parts à chaque conjoint
- Bien acheté avec l'argent du couple + emprunt

### Avantages
- ✅ Séparation nette du bien immobilier de l'activité freelance
- ✅ Transmission facilitée aux enfants
- ✅ Protection en cas de divorce (partage clair des parts sociales)
- ✅ Possibilité de faire entrer les enfants progressivement

## Cas 2 — Couple pacsé, freelance + salarié

### Structure recommandée
- SCI à l'IS ou IR selon usage
- 60/40 ou 70/30 selon apports respectifs
- Bail commercial si location à la SASU du freelance

### Avantages spécifiques
- ✅ Chacun garde ses parts en cas de rupture
- ✅ Fiscalité personnelle claire
- ✅ Transmission au conjoint via testament (droits réduits pour PACS)

### Attention
- PACS ne donne pas les mêmes droits successoraux que le mariage
- Testament fortement recommandé pour protéger le conjoint

## Cas 3 — Couple en concubinage

### Structure recommandée
- SCI à l'IS
- Répartition selon les apports
- **Testament + assurance-vie** croisée obligatoire

### Attention
Les concubins n'ont **aucune protection légale** en cas de décès. Sans testament, les héritiers du défunt (parents, frères/sœurs) héritent des parts. Le concubin survivant peut se retrouver sans logement.

**Solution** : chacun désigne l'autre bénéficiaire via testament + démembrement croisé + assurance-vie.

## La transmission : le vrai jeu du long terme

### Transmission aux enfants pendant la vie
- Donation de parts SCI tous les 15 ans
- Abattement 100 000€/parent/enfant
- Donation en nue-propriété (voir article démembrement) → optimisation ~50%

### Transmission au conjoint (décès)

| Statut | Droits |
|--------|--------|
| Marié communauté | Récupère 100% des biens communs + option 1/4 ou usufruit des biens propres |
| Marié séparation | Droits successoraux normaux (usufruit ou 1/4 selon présence enfants) |
| PACSé | 0€ de droits de succession (mais pas de réserve légale) |
| Concubin | Pas d'héritage sans testament, droits de succession à 60% |

**Avec SCI en couple** : le conjoint garde ses propres parts SCI (bien commun ou propre). Les parts du défunt sont transmises selon le testament ou la loi.

## Les 4 pièges à éviter

### Piège 1 — 100% des parts au dirigeant
En cas de divorce ou décès, le conjoint peut se retrouver sans rien. Toujours donner **au moins 30-50%** au conjoint.

### Piège 2 — Cotitularité (indivision au lieu de SCI)
L'indivision est un cauchemar en cas de rupture. La SCI évite ce problème avec des règles claires de gérance et cession.

### Piège 3 — Absence de statuts personnalisés
Les statuts SCI type ne prévoient pas les cas de divorce, décès, gérance. **Faites rédiger par un avocat** (500 à 2 000€).

### Piège 4 — Négliger la fiscalité du régime matrimonial
En communauté légale, la moitié de la SCI est un bien commun même si un seul l'a "achetée". Attention aux inégalités.

## Simulation : couple marié, SCI 50/50, bien 500k€, transmission après 20 ans

### Scénario sans SCI (bien en indivision)
- Décès du conjoint 1
- Transmission de 50% du bien (250k€) au conjoint 2 + enfants
- Complexité indivision entre conjoint 2 et enfants
- Droits de succession si enfants : ~15 000€
- **Total droits** : 15 000€ + coûts liquidation ~10 000€ = **25 000€**

### Scénario avec SCI 50/50
- Décès du conjoint 1
- Transmission de 50% des parts SCI au conjoint 2 + enfants
- Conjoint 2 peut désigner sa succession (parts vs pleine propriété)
- Donation progressive aux enfants (100k€ tous les 15 ans)
- **Total droits sur 20 ans** : ~5 000€

**Économie via SCI** : ~20 000€ + zéro contentieux indivision.

## Checklist création SCI en couple

- [ ] Choisir le régime matrimonial adapté (contrat avant mariage ou modification)
- [ ] Rédiger les statuts SCI (avocat recommandé)
- [ ] Prévoir clause de préemption entre associés
- [ ] Prévoir clause de sortie en cas de divorce
- [ ] Répartir les parts selon les apports + protection
- [ ] Faire un testament croisé (surtout PACS/concubin)
- [ ] Souscrire assurance-vie croisée
- [ ] Immatriculer au RCS (formulaire M0 SCI)
- [ ] Ouvrir compte bancaire SCI
- [ ] Comptabilité annuelle (SCI IS = compta complète)

---

**Sources officielles 2026 :**
- Code civil - Art. 1400 à 1580-3 (régimes matrimoniaux) : legifrance.gouv.fr
- Code civil - Art. 1832 à 1873 (sociétés civiles) : legifrance.gouv.fr
- CGI - Art. 779 (abattement 100k€/enfant) : legifrance.gouv.fr
- Pacs - Loi 99-944 : legifrance.gouv.fr`
  },

  'scpi-via-societe-freelance-2026': {
    id: '63',
    title: 'Investir en SCPI via société freelance : le guide 2026',
    slug: 'scpi-via-societe-freelance-2026',
    excerpt: 'Acheter des SCPI avec sa SASU, EURL ou holding : fiscalité IS, taux de rendement, comparatif détention personnelle vs société en 2026.',
    category: 'Optimisation',
    tags: ['SCPI', 'Immobilier papier', 'Société', 'Holding', 'IS'],
    readTime: 12,
    date: '2026-04-19',
    author: 'Bozzam',
    popular: true,
    fullContent: `## SCPI en société : le placement immobilier sans les tracas

Vous êtes freelance en SASU/EURL avec de la trésorerie disponible et vous voulez de l'immobilier sans gérer de locataires ? Les **SCPI** (Sociétés Civiles de Placement Immobilier) sont la solution. Encore faut-il choisir la bonne structure.

## SCPI : rappel du concept

Une SCPI collecte l'argent des investisseurs pour acheter et gérer un parc immobilier (bureaux, commerces, santé, résidentiel). Vous êtes associé au prorata de votre investissement.

### Rendement moyen 2025-2026

| Type SCPI | Rendement DVM moyen 2025 |
|-----------|--------------------------|
| Bureaux France | 4,3% |
| Commerces | 4,8% |
| Santé (EHPAD, cliniques) | 4,5% |
| Diversifiée internationale | 5,2% |
| Résidentiel | 3,5% |
| Logistique | 5,8% |

## Achat en société vs achat personnel : le vrai match

### Fiscalité personne physique (CTO / compte perso)

- Loyers reçus : imposés en **revenus fonciers**
- Barème IR + prélèvements sociaux **17,2%**
- TMI 30% + PS = ~47,2% du loyer parti en impôts

### Fiscalité société (SASU/EURL à l'IS)

- Loyers reçus : imposés à **l'IS 25%** (15% si bénéfice < 42 500€)
- Amortissement possible sur certaines SCPI
- Distribution en dividendes : flat tax 30% supplémentaire OU capitalisation dans la société

## Simulation : 100 000€ investis en SCPI à 4,5% de rendement

### Personne physique (TMI 30%)
| Poste | Montant |
|-------|---------|
| Loyer annuel brut | 4 500€ |
| IR (TMI 30%) | -1 350€ |
| Prélèvements sociaux (17,2%) | -774€ |
| **Net perçu** | **~2 376€/an** |

### Société SASU (IS 15% jusqu'à 42 500€)
| Poste | Montant |
|-------|---------|
| Loyer annuel brut | 4 500€ |
| IS 15% | -675€ |
| Résultat net société | 3 825€ |
| **Capitalisé dans société** | **3 825€/an** |
| **Ou distribué (flat tax 30%)** | **2 678€/an** |

**Rendement net réinvesti en société : +61% vs personne physique** (3 825€ vs 2 376€).

### Société via holding (régime mère-fille)

Encore mieux si la SCPI est via une filiale : régime mère-fille signifie que la remontée dans la holding coûte quasi 0% d'IS supplémentaire.

## Les SCPI à privilégier en société

### 1. SCPI de rendement (haut potentiel)
- Corum XL, Corum Origin, Pierval Santé, Kyaneos Pierre
- Rendement 5-7%
- Idéal pour société IS

### 2. SCPI internationales (fiscalité optimisée)
- Corum Origin (Europe hors France)
- Iroko Zen (Europe)
- Avantage : les loyers étrangers échappent parfois aux prélèvements sociaux, même en direct
- En société : même fiscalité IS

### 3. SCPI de plus-value
- Génèrent moins de loyer, plus de plus-value
- Détention long terme (15-20 ans)
- En société : plus-value à la revente imposée à l'IS 25%

## SCPI en pleine propriété vs démembrement

### Pleine propriété
- Vous recevez les loyers
- Fiscalité annuelle

### Nue-propriété seule (démembrement temporaire 5-15 ans)
- Vous achetez **20 à 40% moins cher**
- Vous ne recevez rien pendant le démembrement
- Récupération pleine propriété en fin (0€ d'impôt)
- Idéal en société pour capitaliser sans fiscalité

### Usufruit temporaire
- Réservé aux sociétés IS (pour amortir)
- Loyers pleins pendant la durée
- À la fin : le bien retourne au nu-propriétaire

## Combien investir en SCPI via société ?

| Trésorerie société | Recommandation SCPI |
|-------------------|---------------------|
| < 20 000€ | Trop petit — restez en placement bancaire |
| 20-50 000€ | 1 SCPI diversifiée |
| 50-200 000€ | 2-3 SCPI (diversification) |
| 200-500 000€ | 4-5 SCPI + démembrement partiel |
| > 500 000€ | Portefeuille complet + SCPI + immobilier direct |

## Emprunter pour acheter des SCPI en société ?

Certaines banques prêtent aux sociétés pour acheter des SCPI (jusqu'à 60% du montant, taux ~4-5%).

### Avantages
- Effet de levier : plus de rendement pour moins de cash
- Intérêts d'emprunt déductibles de l'IS

### Risques
- Vacance locative de la SCPI
- Baisse de valeur des parts
- Cotation limitée (5-10 ans pour sortir)

## Les 5 pièges à éviter

### Piège 1 — Investir en SCPI avec de la trésorerie court terme
Les SCPI sont un placement 10-15 ans minimum. Ne mettez pas d'argent dont vous aurez besoin dans 2 ans.

### Piège 2 — Ne pas diversifier
Une seule SCPI = risque de contreparties. Diversifiez sur 3-5 SCPI différentes.

### Piège 3 — Oublier les frais d'entrée
Les SCPI ont ~10% de frais d'entrée. Rentabilisés seulement après 4-5 ans.

### Piège 4 — Confondre TDVM et rendement réel
Le rendement DVM est brut de fiscalité. Le rendement réel après impôts peut être 30-50% inférieur en personne physique.

### Piège 5 — SCPI en société sans expert-comptable spécialisé
La comptabilité SCPI en société est technique. Assurez-vous que votre EC connaît le sujet.

## Cas concret : freelance IT, 200 000€ de trésorerie

### Option A — Sortir en dividendes puis SCPI perso
- Dividendes 200 000€ → flat tax 60 000€ → **140 000€ dispo**
- SCPI 140 000€ à 4,5% = 6 300€/an brut
- IR + PS 47,2% = -2 973€
- **Net annuel** : **~3 327€**

### Option B — SCPI direct via SASU
- 200 000€ investis directement
- Loyer 9 000€/an
- IS 15% = -1 350€
- **Net capitalisé société** : **~7 650€/an**

### Option C — Holding + SCI + SCPI
- Bénéfice holding via SCPI
- Régime mère-fille = quasi 0% IS
- **Net capitalisé holding** : **~8 700€/an**

**Écart entre A et C sur 15 ans : ~80 000€**.

## FAQ

**Peut-on acheter des SCPI en micro-entreprise ?**
Non. La micro n'est pas une société, l'achat serait en personne physique.

**Peut-on transférer des SCPI d'une personne physique à une société ?**
Oui via apport en nature (nécessite commissaire aux apports).

**Les SCPI en assurance-vie sont-elles mieux ?**
Fiscalité différente (barème AV après 8 ans très favorable). Mais moins de choix de SCPI et frais plus élevés.

**Les SCPI étrangères sont-elles éligibles à la société française ?**
Oui, mais attention à la convention fiscale et à la double imposition.

---

**Sources officielles 2026 :**
- CGI - Art. 239 (transparence fiscale SCPI) : legifrance.gouv.fr
- Code monétaire et financier - Art. L214-33 (SCPI) : legifrance.gouv.fr
- AMF - Réglementation SCPI : amf-france.org
- IEIF - Rendements SCPI : ieif.fr`
  },

  'cir-cii-jei-credits-impot-freelance-2026': {
    id: '64',
    title: 'CIR, CII et JEI : les crédits d\'impôt méconnus pour freelance en 2026',
    slug: 'cir-cii-jei-credits-impot-freelance-2026',
    excerpt: 'Crédit d\'Impôt Recherche, Crédit d\'Impôt Innovation, statut Jeune Entreprise Innovante : réduire son IS de 30% à 60%. Éligibilité, dossier, montants en 2026.',
    category: 'Optimisation',
    tags: ['CIR', 'CII', 'JEI', 'Crédit impôt', 'Innovation'],
    readTime: 13,
    date: '2026-04-18',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Les 3 dispositifs qui divisent votre IS par 2

Si vous êtes freelance IT, développeur, consultant en innovation, chercheur ou dirigeant d'une jeune société, vous êtes probablement éligible à un ou plusieurs des 3 dispositifs suivants. La plupart des freelances les ignorent complètement. Voici comment les activer en 2026.

## Vue d'ensemble

| Dispositif | Ce qu'il fait | Taux 2026 |
|-----------|--------------|-----------|
| **CIR** (Crédit d'Impôt Recherche) | Rembourse une partie des dépenses R&D | 30% des dépenses éligibles |
| **CII** (Crédit d'Impôt Innovation) | Rembourse dépenses de prototype innovant | 20% des dépenses (max 400k€) |
| **JEI** (Jeune Entreprise Innovante) | Exonération de charges sociales et d'IS | Jusqu'à 100% sur 7 ans |

## Le CIR (Crédit d'Impôt Recherche)

### Pour qui ?
Toute entreprise soumise à l'IS (SASU, EURL, SARL) ou à l'IR (BIC) qui réalise des travaux de **recherche fondamentale, appliquée ou de développement expérimental**.

### Ce qui est éligible en 2026
- Salaires des chercheurs et docteurs
- Dotations aux amortissements des immobilisations affectées à la R&D
- Frais de fonctionnement (50% des dépenses de personnel)
- Sous-traitance R&D à des organismes agréés (plafond 12M€)
- Défense de brevets, veille technologique (60k€/an)

### Taux 2026
- 30% des dépenses R&D **jusqu'à 100M€**
- 5% au-delà de 100M€

### Exemple chiffré — Freelance dev IA en SASU

| Poste | Montant annuel |
|-------|---------------|
| Salaire du dirigeant dev IA | 50 000€ |
| Amortissement matériel R&D | 5 000€ |
| Sous-traitance labo agréé | 15 000€ |
| Total dépenses R&D | 70 000€ |
| Forfait fonctionnement (50% des salaires) | +25 000€ |
| **Assiette CIR** | **95 000€** |
| **CIR = 30%** | **28 500€** |

**Effet net** : réduction d'IS de 28 500€. Si IS < CIR, remboursement en cash après 3 ans (immédiat pour PME/JEI).

## Le CII (Crédit d'Impôt Innovation)

### Pour qui ?
Uniquement les **PME au sens communautaire** (< 250 salariés, CA < 50M€ ou bilan < 43M€) qui conçoivent des **prototypes ou installations pilotes de produits nouveaux**.

### Ce qui est éligible
- Prototypage d'un produit nouveau
- Dépenses de conception, ingénierie
- Amortissement du matériel
- Sous-traitance de développement

### Taux 2026
**20%** des dépenses éligibles, dans la limite de **400 000€ de dépenses/an** (soit crédit max de 80 000€).

### Exemple — Freelance qui développe une SaaS innovante

| Poste | Montant |
|-------|---------|
| Salaire chargé dev SaaS | 60 000€ |
| Prototypage design | 8 000€ |
| Outils cloud (AWS/GCP) | 12 000€ |
| **Total éligible** | **80 000€** |
| **CII = 20%** | **16 000€** |

## Le statut JEI (Jeune Entreprise Innovante)

### Pour qui ?
Une entreprise qui :
- A **moins de 8 ans**
- Est une **PME communautaire**
- Réalise **au moins 15% de dépenses R&D** (ou 5% en Jeune Entreprise Universitaire — JEU)
- Est détenue à **> 50% par des personnes physiques** ou des JEI/JEU

### Avantages fiscaux
- **Exonération d'IS** :
  - 100% le 1er exercice bénéficiaire
  - 50% le 2ème exercice bénéficiaire
- **Exonération de CET** (CFE + CVAE) : 7 ans si accord de la commune
- **Exonération de taxe foncière** : 7 ans si accord de la commune

### Avantages sociaux (2026, très intéressant)
- **Exonération de cotisations patronales** sur les salaires des chercheurs et développeurs
- Plafonné à 4,5× SMIC par salarié
- Durée : 8 ans (loi de finances 2024 a maintenu ce dispositif)

### Exemple JEI : startup SaaS de 3 personnes

| Poste | Sans JEI | Avec JEI |
|-------|----------|----------|
| Salaires bruts totaux | 150 000€ | 150 000€ |
| Charges patronales normales (~42%) | 63 000€ | 63 000€ |
| **Exonération JEI** | 0€ | **-45 000€** |
| Coût total salaires | 213 000€ | **168 000€** |
| **Économie annuelle** | — | **45 000€** |

## Peut-on cumuler CIR + CII + JEI ?

**Oui**, mais règles précises :
- Une dépense peut ouvrir droit à **un seul crédit** (CIR OU CII, pas les deux)
- JEI se cumule avec CIR/CII sans restriction
- Total crédit d'impôts + subventions ne peut excéder **80% du projet R&D**

## La démarche pratique en 2026

### Étape 1 — Auto-diagnostic (gratuit)
- Site officiel : *entreprises.gouv.fr/CIR*
- BPI France propose un simulateur

### Étape 2 — Choix du prestataire (recommandé)
Le CIR/CII est complexe à monter. Prestataires spécialisés :
- Alma Consulting
- Neva Group
- FA-Consulting
- Coût : **10 à 20% du crédit obtenu** (success fee)

### Étape 3 — Rédaction du dossier technique
- Description des travaux R&D
- Justification du caractère innovant (état de l'art)
- Détail des dépenses
- Personnel affecté

### Étape 4 — Déclaration fiscale
- Formulaire **2069-A-SD** (CIR)
- Formulaire **2069-A-SD** aussi pour CII
- Formulaire **2049** pour JEI
- Joindre au dépôt de liasse fiscale

### Étape 5 — Contrôle possible
- L'administration peut demander un contrôle jusqu'à **3 ans après**
- 50% des CIR > 100k€ sont contrôlés
- Bien conserver toutes les preuves (contrats, mails, timesheets)

## Cas particulier : le "rescrit CIR"

Si vous avez un doute sur l'éligibilité, vous pouvez demander un **rescrit fiscal préalable** (formulaire 3966). L'administration répond en 3 à 6 mois. Sécurisation totale.

## Les pièges à éviter

1. **CIR sur le développement web classique** : non éligible (pas d'incertitude technique)
2. **CIR sur activité de conseil pure** : non éligible
3. **Ne pas documenter les travaux au fil de l'eau** : contrôle = catastrophe
4. **Sous-traiter à un organisme non agréé** : dépense non éligible
5. **Confondre "innovation commerciale" et "innovation technologique"** : seule la 2ème est éligible

## Simulation freelance IT SaaS — 3 ans (dépenses R&D 80k€/an)

| Poste | Année 1 | Année 2 | Année 3 |
|-------|---------|---------|---------|
| CIR (30% × 80k€) | 24 000€ | 24 000€ | 24 000€ |
| CII (si prototype) | 16 000€ | — | — |
| Exonération IS JEI (100% année 1) | 8 000€ | — | — |
| Exonération JEI charges sociales | 15 000€ | 15 000€ | 15 000€ |
| **Économie totale par an** | **~63 000€** | **~39 000€** | **~39 000€** |
| **Cumul 3 ans** | **~141 000€** |

**141 000€ de gain fiscal cumulé sur 3 ans** pour un projet SaaS classique — hors salaires financés indirectement.

## FAQ

**Peut-on demander le CIR rétroactivement ?**
Oui, sur les 3 dernières années.

**Le CIR est-il remboursé si l'entreprise n'a pas de bénéfice ?**
Oui, remboursement immédiat pour JEI, PME communautaires, entreprises en difficulté. Autres : après 3 ans.

**Combien coûte un dossier CIR par un prestataire ?**
Success fee 8-15% du crédit. Frais fixe : 1 000 à 3 000€ pour un premier dossier.

**Le CIR fonctionne-t-il en EURL à l'IR ?**
Oui, réduit de l'IR de l'associé.

---

**Sources officielles 2026 :**
- CGI - Art. 244 quater B (CIR) : legifrance.gouv.fr
- CGI - Art. 244 quater B bis (CII) : legifrance.gouv.fr
- CGI - Art. 44 sexies-0 A (JEI) : legifrance.gouv.fr
- BOFiP - BOI-BIC-RICI-10 (CIR) : bofip.impots.gouv.fr
- Ministère - Guide CIR/CII : entreprises.gouv.fr
- BPI - Simulateur CIR : bpifrance.fr`
  },

  'micro-vers-sasu-eurl-transformation-2026': {
    id: '65',
    title: 'Passer de micro-entreprise à SASU/EURL : la transformation en 2026',
    slug: 'micro-vers-sasu-eurl-transformation-2026',
    excerpt: 'Quand quitter la micro pour SASU ou EURL ? Procédure de transformation (fermeture + création), coûts, calendrier, cas concret chiffré.',
    category: 'Optimisation',
    tags: ['Micro-entreprise', 'SASU', 'EURL', 'Transformation', 'Statut'],
    readTime: 12,
    date: '2026-04-17',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Micro → SASU/EURL : le moment charnière du freelance

La micro-entreprise, c'est génial pour démarrer. Mais à partir d'un certain niveau de CA (ou de bénéfice), elle devient un **frein fiscal et social**. Voici quand et comment basculer en SASU ou EURL en 2026.

## Les 5 signaux qu'il faut quitter la micro

### Signal 1 — Vous approchez des plafonds de CA
| Type d'activité | Plafond 2026 |
|-----------------|--------------|
| BIC (achat/revente) | 188 700€ |
| BIC (services) | 77 700€ |
| BNC (libéral) | 77 700€ |

Une fois dépassés 2 années consécutives, sortie **obligatoire**.

### Signal 2 — Vos charges dépassent l'abattement forfaitaire
| Type | Abattement micro | Charges réelles > abattement ? |
|------|-----------------|------------------------------|
| BIC services | 50% | Charges > 50% du CA = perte micro |
| BNC | 34% | Charges > 34% du CA = perte micro |
| BIC achat/revente | 71% | Charges > 71% = perte micro |

### Signal 3 — Vous voulez optimiser via holding, dividendes, PER
La micro ne permet **aucune optimisation avancée**. Ni holding, ni régime mère-fille, ni dividendes.

### Signal 4 — Vous voulez recruter, ouvrir un local, investir
Difficile en micro : image, financement bancaire, TVA, engagement contractuel.

### Signal 5 — Vous voulez capitaliser sans sortir l'argent
Micro = tout est perso. SASU/EURL = capitalisation dans la société sans flat tax immédiate.

## Le seuil de bascule fiscal en 2026

### Freelance libéral BNC — TMI 30%

| CA | Charges réelles | Micro-BNC net | SASU net | Gagnant |
|----|-----------------|---------------|----------|---------|
| 40 000€ | 10 000€ | ~24 000€ | ~22 000€ | Micro |
| 60 000€ | 15 000€ | ~35 000€ | ~34 000€ | Neutre |
| **75 000€** | **20 000€** | **~42 000€** | **~43 500€** | **SASU** |
| 100 000€ | 25 000€ | ~55 000€ | ~60 000€ | SASU (+9%) |
| 150 000€ | 40 000€ | ~78 000€ | ~92 000€ | SASU (+18%) |

**Seuil de bascule : ~75 000€ de CA** (BNC libéral).

### Freelance services BIC — TMI 30%

Seuil de bascule : ~50 000€ de CA (BIC est moins avantageux en micro).

## SASU ou EURL ? Le choix rapide

| Critère | SASU (choix 1) | EURL (choix 2) |
|---------|----------------|----------------|
| Cotisations dirigeant | ~80% du brut | ~45% du bénéfice |
| Dividendes | Flat tax 30% | Cotisations TNS > 10% capital |
| Protection sociale | Forte (assimilé salarié) | Moyenne (TNS) |
| Idéal si | CA < 100k€, salaire + dividendes | CA > 100k€, rémunération pleine |

Voir article dédié SASU vs EURL pour plus de détails.

## La procédure de bascule en 2026

**Important** : ce n'est **PAS** une transformation. C'est une **fermeture de micro + création d'une société**.

### Étape 1 — Décision et calendrier
- Prévoir 3-6 mois de démarche
- Idéalement : bascule au **1er janvier** (année fiscale claire)
- Ou à mi-année si projection dépasse le seuil de la micro

### Étape 2 — Créer la SASU/EURL

| Poste | Coût |
|-------|------|
| Rédaction des statuts | 500 à 1 500€ (avocat) ou 0€ (modèle gratuit) |
| Annonce légale | ~200€ |
| Immatriculation RCS | 60€ (SASU/EURL) |
| Publication BODACC | Incluse |
| Total création | **~500 à 2 000€** |

### Étape 3 — Fermer la micro-entreprise
- Déclaration de cessation via **guichet-unique.inpi.fr**
- Gratuit
- Prévoir 15 jours

### Étape 4 — Transférer les contrats clients
- Attention : les contrats ne sont **pas automatiquement** transférés
- Signer un **avenant** avec chaque client (ou nouveau contrat)
- Rediriger la facturation vers la nouvelle SASU/EURL

### Étape 5 — Basculer les comptes bancaires
- Nouveau compte pro (obligatoire pour SASU/EURL)
- Transfert des CB, virements récurrents
- Prévoir 2-4 semaines

### Étape 6 — Gérer la fin de la micro
- Déclarer le CA de l'année de cessation
- Payer les cotisations URSSAF finales
- Radiation confirmée par CFE

## Coût total du basculement (freelance IT type)

| Poste | Coût |
|-------|------|
| Création SASU/EURL | 800€ |
| Comptabilité SASU (année 1) | 1 500€ |
| Avocat / conseil ponctuel | 500€ |
| Immatriculation, banque, admin | 200€ |
| **Total année 1** | **~3 000€** |

## Cas concret : bascule au 1er janvier

**Freelance BNC — 90 000€ de CA, 15 000€ de charges, TMI 30%**

### Avant (micro-BNC)
| Poste | Montant |
|-------|---------|
| CA | 90 000€ |
| Abattement 34% | -30 600€ |
| Base imposable | 59 400€ |
| IR (TMI 30%) | ~14 500€ |
| Cotisations URSSAF (22,2%) | ~20 000€ |
| **Net freelance** | **~55 500€** |

### Après (SASU, salaire 3 000€ net/mois + dividendes)
| Poste | Montant |
|-------|---------|
| CA | 90 000€ |
| Charges réelles | -15 000€ |
| Salaire brut | -33 600€ + charges 15 000€ |
| Bénéfice avant IS | 26 400€ |
| IS 15% | -3 960€ |
| Dividende distribuable | 22 440€ |
| Flat tax 30% | -6 732€ |
| Dividende net | 15 708€ |
| Salaire net + IR | ~28 000€ |
| **Net freelance** | **~43 708€** |

**Hmm, à 90k€ le net baisse !** Explication : les charges SASU sont lourdes en dessous de 100k€. Il faut monter à 120k€+ pour vraiment gagner.

### À 130 000€ de CA (mêmes proportions)
- Micro-BNC : **~78 000€ net**
- SASU optimisée : **~86 000€ net**
- **Gain SASU : +8 000€/an**

## Les 4 erreurs classiques

1. **Basculer trop tôt** : à 60-70k€, la micro est plus rentable
2. **Basculer trop tard** : après 2 années de dépassement, radiation forcée = stress
3. **Oublier de transférer les contrats** : perte juridique de la mission
4. **Sous-estimer les frais** : compta + admin = ~3-5k€/an récurrent en société

## FAQ

**Puis-je conserver mon SIREN ?**
Non. Nouvelle société = nouveau SIREN. C'est pourquoi ce n'est pas une "transformation" mais une "création + fermeture".

**Puis-je basculer en cours d'année ?**
Oui, mais 2 déclarations fiscales à faire (micro pour le début, société pour la fin). Complexe. Préférer le 1er janvier.

**Combien de temps prend le basculement ?**
1 à 3 mois si tout est prêt. Prévoyez 6 mois d'anticipation.

**Puis-je récupérer les biens de ma micro (ordi, matériel) dans la SASU ?**
Oui, en les vendant à la SASU (pas de plus-value si valeur d'achat = valeur de vente). Ou par apport en nature.

---

**Sources officielles 2026 :**
- CGI - Art. 50-0 et 102 ter (micro-entreprise) : legifrance.gouv.fr
- Code de commerce - L223-1 (SARL/EURL) et L227-1 (SAS/SASU) : legifrance.gouv.fr
- INPI - Guichet unique : guichet-unique.inpi.fr
- URSSAF - Cessation micro : autoentrepreneur.urssaf.fr`
  },

  'regime-reel-bnc-vs-abattement-34-2026': {
    id: '66',
    title: 'Régime réel BNC vs abattement 34% micro-BNC : quand basculer en 2026',
    slug: 'regime-reel-bnc-vs-abattement-34-2026',
    excerpt: 'Micro-BNC avec abattement 34% ou régime réel avec frais déductibles ? Le calcul exact du seuil de bascule en 2026 pour freelance libéral.',
    category: 'Optimisation',
    tags: ['BNC', 'Micro', 'Régime réel', 'Abattement', 'Libéral'],
    readTime: 11,
    date: '2026-04-16',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Le calcul qui peut vous faire économiser 3 000-8 000€/an

Vous êtes en libéral (consultant, coach, développeur, avocat, médecin, architecte, thérapeute) et vous facturez en BNC. Vous avez le choix entre :

- **Micro-BNC** : abattement forfaitaire **34%** (par défaut si CA < 77 700€)
- **Régime déclaration contrôlée (réel)** : déduction des frais réels

Voici la méthode exacte pour choisir en 2026.

## Rappel des deux régimes

### Micro-BNC (par défaut)
- Applicable si CA < **77 700€**
- Abattement **34%** appliqué automatiquement sur le CA
- Base imposable = CA × 66%
- **Aucune déclaration de charges** (pas de comptabilité complexe)
- **Aucune TVA** si franchise en base (< 39 100€ pour les libéraux en 2026)

### Régime déclaration contrôlée (réel)
- Toutes charges professionnelles déductibles
- Comptabilité tenue (recettes/dépenses)
- Déclaration 2035 obligatoire
- Adhésion à une AGA (Association de Gestion Agréée) recommandée pour éviter majoration 10%

## Le seuil de bascule : la formule

**Le régime réel devient avantageux dès que vos charges réelles dépassent 34% de votre CA.**

Cette formule s'applique à l'euro près. Voici comment vérifier.

### Étape 1 — Calculer vos charges annuelles

| Poste | Montant type |
|-------|-------------|
| Loyer bureau ou coworking | 3 000 à 8 000€ |
| Assurance RC Pro | 300 à 800€ |
| Matériel informatique amorti | 1 000 à 3 000€ |
| Logiciels et abonnements | 800 à 2 000€ |
| Téléphone + internet | 800 à 1 500€ |
| Frais kilométriques (véhicule) | 2 000 à 8 000€ |
| Restaurants professionnels | 500 à 2 500€ |
| Formation professionnelle | 500 à 3 000€ |
| Marketing/site web/pub | 500 à 5 000€ |
| Livres, veille | 200 à 800€ |
| Cotisation professionnelle | 200 à 1 500€ |
| Comptable (si régime réel) | 1 000 à 2 500€ |
| **Total charges typiques** | **10 000 à 40 000€** |

### Étape 2 — Comparer avec l'abattement 34%

Si CA = 60 000€, l'abattement micro = 20 400€.
- Charges > 20 400€ → **régime réel gagne**
- Charges < 20 400€ → **micro gagne**

## Le calcul chiffré : CA 60 000€, TMI 30%

### Micro-BNC
| Poste | Montant |
|-------|---------|
| CA | 60 000€ |
| Abattement 34% | -20 400€ |
| Base imposable | 39 600€ |
| IR (~15% moyenne) | -5 900€ |
| Cotisations URSSAF (BNC, 22,2%) | -13 320€ |
| **Net freelance** | **~40 780€** |

### Régime réel avec 25 000€ de charges
| Poste | Montant |
|-------|---------|
| CA | 60 000€ |
| Charges réelles | -25 000€ |
| Résultat imposable | 35 000€ |
| IR (~12%) | -4 200€ |
| Cotisations URSSAF (22,2%) sur 35k€ | -7 770€ |
| **Net freelance** | **~48 030€** |

**Gain régime réel : +7 250€/an**.

## Les 5 vraies raisons de passer au régime réel

### Raison 1 — Bureau physique ou coworking
Si vous louez un espace pro (~5 000-10 000€/an), le régime réel devient obligatoire.

### Raison 2 — Véhicule pro important
Kilométrique > 15 000 km/an = charges > 6 000€ = passage régime réel.

### Raison 3 — Investissement matériel initial
Ordinateur, matériel photo, équipement : régime réel permet amortissement + TVA récupérable.

### Raison 4 — Formation continue
Master, coaching, certifications à 5 000-15 000€ = régime réel obligatoire.

### Raison 5 — Sous-traitance
Si vous sous-traitez une partie de vos missions, les frais de sous-traitance sont déductibles au réel uniquement.

## Comment opter pour le régime réel ?

### Depuis la micro
- Envoyer une lettre au SIE (Service des Impôts des Entreprises)
- **Avant le 1er février** de l'année de l'option
- Durée d'option : **1 an minimum, tacitement reconductible**

### Depuis la création
- Choix dès la formalité initiale (P0 PL)

## Formalités du régime réel

### Comptabilité
- Livre-journal des recettes
- Registre des immobilisations
- Registre des amortissements
- Facturation numérotée

### Déclaration
- Formulaire **2035** annuel (déclaration contrôlée)
- Déclaration 2042-C (revenus BNC)
- Adhésion AGA (facultative mais recommandée)

### Coût
- Comptable : 1 500 à 3 000€/an
- AGA : 200 à 400€/an
- Total : ~2 000-3 500€/an de frais supplémentaires

## Le piège de l'AGA (Association de Gestion Agréée)

Depuis 2023, la majoration de 10% du bénéfice pour non-adhésion à une AGA a été **supprimée**. Mais l'AGA reste utile pour :

- Réduction fiscale liée à l'AGA (~100-300€/an)
- Sécurité du dossier fiscal
- Formation gratuite proposée

**Coût AGA** : 200 à 400€/an. Rentable dans 90% des cas.

## Cas particulier — Micro-BNC + option versement libératoire

Attention piège : le **versement libératoire de l'IR** (2,2% du CA en BNC) n'est pas cumulable avec le régime réel. Choisissez bien :

- Micro-BNC + versement libératoire : idéal si TMI ≥ 30% et charges < 34% du CA
- Micro-BNC classique : idéal si TMI < 30%
- Régime réel : idéal si charges > 34% du CA

## Tableau récap freelance libéral

| CA | Charges | Meilleur régime |
|----|---------|----------------|
| 30 000€ | 5 000€ | Micro-BNC |
| 30 000€ | 15 000€ | Régime réel |
| 60 000€ | 12 000€ | Micro-BNC |
| 60 000€ | 25 000€ | **Régime réel (+7k€)** |
| 77 000€ | 20 000€ | Micro-BNC |
| 77 000€ | 30 000€ | **Régime réel (+3k€)** |
| > 77 700€ | Toutes | **Régime réel obligatoire** |

## FAQ

**Puis-je changer d'avis en cours d'année ?**
Non, l'option régime réel est valable 1 an minimum.

**Le régime réel donne-t-il droit à la TVA ?**
Non. Ce sont deux choix indépendants. Vous pouvez être micro + assujetti TVA, ou réel + franchise TVA.

**Combien coûte un expert-comptable pour un dossier régime réel ?**
1 500 à 3 000€/an selon complexité et localisation.

**Faut-il un compte bancaire pro pour le régime réel ?**
Recommandé mais pas obligatoire. Séparer les flux est indispensable pour la clarté.

---

**Sources officielles 2026 :**
- CGI - Art. 102 ter (micro-BNC) : legifrance.gouv.fr
- CGI - Art. 96 (déclaration contrôlée) : legifrance.gouv.fr
- BOFiP - BOI-BNC-DECLA-10 (obligations BNC) : bofip.impots.gouv.fr
- Fédération des AGA : federationaga.org`
  },

  'self-rental-sci-societe-freelance-2026': {
    id: '67',
    title: 'Louer son local à sa société via SCI : le self-rental freelance 2026',
    slug: 'self-rental-sci-societe-freelance-2026',
    excerpt: 'Créer une SCI qui achète votre bureau et le loue à votre SASU/EURL : loyer déductible, capitalisation immobilière, transmission. Le montage patrimonial expliqué.',
    category: 'Optimisation',
    tags: ['SCI', 'Self-rental', 'Bureau', 'Immobilier pro', 'Holding'],
    readTime: 13,
    date: '2026-04-15',
    author: 'Bozzam',
    popular: true,
    fullContent: `## Le self-rental : le montage qui vous enrichit deux fois

Vous êtes freelance en SASU/EURL et vous louez un bureau (à votre coworking, à un bailleur). Ce loyer part **définitivement**. Et si vous vous louiez à vous-même ? C'est le **self-rental**, un des montages patrimoniaux les plus puissants pour un freelance.

## Le principe en 30 secondes

1. Vous créez une **SCI** (à l'IS de préférence)
2. La SCI achète un bien immobilier (bureau, local, appartement à usage pro)
3. La SCI **loue le bien à votre SASU/EURL** via un bail commercial ou professionnel
4. La SASU **déduit fiscalement le loyer** (~600-2 500€/mois de déduction)
5. La SCI **capitalise l'immobilier** (patrimoine qui appartient à vous personnellement)

### Résultat après 15-20 ans
- Bureau payé par votre société d'exploitation (loyer déductible)
- Vous avez un bien immobilier de 200 000 à 500 000€ dans votre patrimoine perso (via la SCI)
- Optimisation fiscale continue (économie IS + amortissement)

## Simulation complète — Bureau à 300 000€

### Hypothèses
- Achat via SCI à l'IS : 300 000€
- Apport : 60 000€ (via CCA de la holding ou perso)
- Crédit 20 ans à 4,2% : 240 000€
- Mensualité crédit : ~1 480€
- Loyer bail commercial payé par SASU à SCI : 1 800€/mois
- Charges SCI (taxe foncière, assurance, entretien) : 200€/mois

### Vue société d'exploitation SASU (année type)

| Poste | Montant annuel |
|-------|---------------|
| Loyer versé à la SCI | 21 600€ |
| **Économie IS (25%)** | **-5 400€** |
| **Coût net loyer pour la SASU** | **~16 200€** |

**Vs coût sans SCI** : loyer bailleur externe 20 000€/an → 15 000€ net.

Le self-rental coûte **~1 200€ de plus par an** en apparence à la société… mais l'argent revient dans votre patrimoine.

### Vue SCI (année type)

| Poste | Montant annuel |
|-------|---------------|
| Loyer perçu de SASU | 21 600€ |
| Intérêts d'emprunt déductibles | -9 500€ |
| Amortissement bien (2,5% sur 270k) | -6 750€ |
| Charges | -2 400€ |
| **Résultat IS** | **~2 950€** |
| IS 15% | -443€ |
| Résultat net SCI | **~2 507€** |

**Cash-flow SCI** :
- Entrées : 21 600€ (loyer)
- Sorties : mensualité crédit 17 760€ + charges 2 400€ = 20 160€
- **Cash-flow net avant impôt** : +1 440€/an

**Constat** : la SCI s'auto-finance. Vous ne mettez plus rien après l'apport initial.

### Vision globale sur 20 ans

| Poste | Montant sur 20 ans |
|-------|-------------------|
| Loyers cumulés versés | 432 000€ |
| Économie IS cumulée | 108 000€ |
| Cash-flow SCI cumulé | 30 000€ |
| Amortissement crédit remboursé | 240 000€ (patrimoine créé) |
| Valeur finale du bien (estimation) | 400 000€ (avec plus-value) |
| **Enrichissement patrimonial net** | **~400 000€** |

**Vs location externe** : loyer perdu 400 000€ sur 20 ans, aucun patrimoine créé.

## Les conditions pour que ça fonctionne

### Condition 1 — Loyer "au prix du marché"
L'administration fiscale vérifie que le loyer entre votre SCI et votre SASU est **au prix normal du marché** (art. 111 du CGI).

- Loyer sous-évalué : redressement à la SCI (loyer normal réintégré)
- Loyer sur-évalué : redressement à la SASU (fraction excessive requalifiée en distribution)

**Recommandation** : faire évaluer le loyer par 2 experts immobiliers.

### Condition 2 — Bail écrit et enregistré
- Bail commercial (durée 9 ans minimum) ou bail professionnel (6 ans)
- Enregistrement au SIE
- Loyers effectivement payés par virement

### Condition 3 — Substance économique
- La SCI doit avoir une **vraie activité** (compta, comptes bancaires, décisions collégiales)
- Pas d'utilisation privée mixte du bien
- Justification des travaux, entretien, etc.

## Les 4 variantes du self-rental

### Variante 1 — SCI directe (basique)
Vous détenez 99% + conjoint 1%. Simple, mais transmission moins optimisée.

### Variante 2 — SCI via holding (optimal)
Holding → SCI → bien. Régime mère-fille sur les dividendes SCI. Voir article dédié.

### Variante 3 — Démembrement (usufruit à la holding)
La holding achète l'**usufruit temporaire** (15-20 ans), vous personnellement la **nue-propriété**. À la fin, vous récupérez le bien en pleine propriété **sans impôt**.

### Variante 4 — Multi-SCI
Une SCI par bien = isolation du risque. Chaque SCI peut avoir un régime différent (IR/IS) selon usage.

## Le financement bancaire

### Point critique
Les banques peuvent être frileuses avec les SCI. Solutions :
- **Caution personnelle du dirigeant** (souvent exigée)
- **Nantissement de parts SCI**
- **Garantie sur d'autres biens** (résidence principale, par exemple)

### Effet de levier
Avec 20% d'apport, vous contrôlez un bien 5× plus cher. Sur 20 ans, l'effet de levier est massif (patrimoine créé = ~13× l'apport initial).

## Les 5 pièges du self-rental

### Piège 1 — Loyer non conforme au marché
Redressement fiscal + intérêts + pénalités. Toujours documenter.

### Piège 2 — SCI à l'IR alors que loyer à SASU
Les loyers deviennent revenus fonciers imposés dans votre déclaration perso → catastrophe.

### Piège 3 — Mélange usage pro / perso
Utiliser le "bureau" comme résidence secondaire = redressement + suppression amortissement.

### Piège 4 — Absence de bail
Sans bail, la SASU ne peut pas déduire le loyer. Perte fiscale majeure.

### Piège 5 — Sous-capitalisation SCI
Une SCI avec 1€ de capital et 300k€ de dette peut être requalifiée en abus de droit.

## Peut-on faire un self-rental si on est en micro-entreprise ?

**Non**. La micro n'est pas une société qui peut louer et déduire.

Solutions :
1. Passer en SASU/EURL puis créer la SCI
2. Créer une SCI qui loue à un tiers (bureau ou logement locatif)

## Peut-on louer sa résidence principale à sa société ?

**Attention** : très encadré.
- L'espace loué doit être **exclusivement à usage pro** (bureau dédié)
- Prorata surface pro / surface totale
- Loyer proportionnel à cette fraction
- La SASU doit inscrire ce bail à ses charges

Autre option : **indemnité forfaitaire "bureau à domicile"** (voir article dédié) — plus simple si vous êtes freelance.

## Cas concret : freelance IT — 20 ans plus tard

**Situation début** : 35 ans, freelance CA 120k€, achat bureau 250k€ avec SCI en 2026.

**Situation à 55 ans** :
- Bureau valorisé 350k€ (plus-value naturelle)
- Crédit intégralement remboursé
- Cash-flow SCI positif ~2 500€/mois (loyer sans crédit)
- Bien transmissible aux enfants avec Dutreil

**Bilan** : le bureau que vous avez "loué" à votre société pendant 20 ans devient votre **rente immobilière retraite** ou un actif transmissible sans droits.

## FAQ

**Le self-rental est-il légal ?**
Absolument, sous réserve de respecter les conditions (loyer marché, bail écrit, substance).

**Puis-je faire du self-rental sur un local commercial ?**
Oui, bail commercial classique (Loi 1953). Idéal si activité commerciale.

**Combien coûte le montage complet ?**
- SCI création : ~1 500€
- Bail commercial : ~500€ (avocat)
- Compta SCI annuelle : ~1 200€
- Total année 1 : ~3 000€
- Récurrent : ~1 500€/an

**La banque exige-t-elle une expertise pour prêter à la SCI ?**
Oui, souvent une expertise immobilière indépendante (~1 000€) est exigée.

---

**Sources officielles 2026 :**
- CGI - Art. 111 (distributions excessives) : legifrance.gouv.fr
- CGI - Art. 39-4 (déductibilité des loyers) : legifrance.gouv.fr
- Code de commerce - Art. L145 (bail commercial) : legifrance.gouv.fr
- Loi 86-1290 - Baux professionnels : legifrance.gouv.fr
- BOFiP - BOI-BIC-CHG-40-40 (loyers déductibles)`
  }
};
